import { useState, useEffect, useRef, useCallback, Component, memo } from "react";

// ── SHA-256 password hashing — never store plaintext passwords ──
async function hashPassword(password) {
  const buf  = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(password));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,"0")).join("");
}

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(e, i) { console.error("Ascendix crash:", e, i); }
  render() {
    if (this.state.error) return (
      <div style={{ height:"100vh", background:"#060912", display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center", fontFamily:"sans-serif", padding:24, textAlign:"center" }}>
        <div style={{ fontSize:40, marginBottom:12 }}>Warning</div>
        <div style={{ color:"white", fontSize:18, fontWeight:700, marginBottom:8 }}>Something went wrong</div>
        <div style={{ color:"rgba(255,255,255,0.60)", fontSize:12, marginBottom:20, background:"rgba(255,255,255,0.05)",
          padding:"12px 16px", borderRadius:12, maxWidth:460, wordBreak:"break-word" }}>{this.state.error.message}</div>
        <button onClick={() => { localStorage.clear(); window.location.reload(); }}
          style={{ padding:"10px 24px", borderRadius:12, border:"none", background:"#10B981",
            color:"white", fontSize:13, fontWeight:700, cursor:"pointer" }}>Reset and Reload</button>
      </div>
    );
    return this.props.children;
  }
}

// ── Lifecycle helpers ───────────────────────────────────────────
// Timers booked through this hook are cleared on unmount, so a pending
// setTimeout can never fire setState into a component that is already gone.
function useTimers() {
  const ids = useRef(new Set());
  useEffect(() => () => {
    ids.current.forEach(clearTimeout);
    ids.current.clear();
  }, []);
  return useCallback((fn, ms) => {
    const id = setTimeout(() => { ids.current.delete(id); fn(); }, ms);
    ids.current.add(id);
    return id;
  }, []);
}

// Mount flag for guarding async resolutions (fetch .then, awaited handlers).
function useIsMounted() {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  return mounted;
}

// Ignore the rejection AbortController.abort() produces on an in-flight fetch.
const isAbort = err => err?.name === "AbortError";

const DEV_MODE = true;

const PERSONAS = {
  ACE: {
    id:"ACE", name:"ACE", full:"ACN Companion Engine",
    role:"Training Companion", tagline:"Your personal trainer — policies, guides & onboarding, simplified.",
    color:"#3B82F6", colorDark:"#1D4ED8", colorGlow:"rgba(59,130,246,0.35)",
    bg:"#0D1B3E", accent:"#93C5FD",
    systemPrompt:`You are ACE (ACN Companion Engine), a dedicated Training Companion for ACN employees. Your role is to deliver training materials, explain onboarding guides, clarify policies, and help employees understand benefits and tools — like a patient, knowledgeable trainer always by their side.
You specialize in: Training materials. Policy explainers. Onboarding guides. Benefits. Tools. All in one place.
When explaining training topics:
- Break down complex policies into clear, digestible steps
- Use examples and analogies to make concepts stick
- Reference the FAQ rules provided when relevant
- Always end with one key takeaway or action item the employee can apply immediately
Style: Patient, structured, encouraging — like a great trainer who never makes you feel silly for asking. Use **bold** for key terms, numbered lists for steps, and line breaks between sections.`,
  },
  CODE: {
    id:"CODE", name:"CODE", full:"Claims & Operations Decision Engine",
    role:"Operations Expert", tagline:"Precise. Compliant. Decisive. Every claim and operation handled with accuracy and zero guesswork.",
    color:"#10B981", colorDark:"#047857", colorGlow:"rgba(16,185,129,0.35)",
    bg:"#0A2818", accent:"#6EE7B7",
    systemPrompt:`You are CODE (Claims & Operations Decision Engine), an operations process expert for ACN employees. Direct, precise, and action-oriented. Your role is to answer any process or claims question with a clear, structured decision — no guessing, no grey areas.
You specialize in: Every case. Every rule. Every decision. Processes, claims, and operations — handled.
When answering process questions always use EXACTLY this format with ** markers:
**DECISION:** [status]
**DOCUMENTS NEEDED:**
1. Document name
2. Document name
**CONDITIONS:** [conditions]

CRITICAL FORMATTING RULES:
- ALWAYS include ** double asterisk markers around DECISION, DOCUMENTS NEEDED, and CONDITIONS
- Never skip the ** markers — this is mandatory
- State the DECISION clearly first (APPROVED / NOT AVAILABLE / REQUIRED / LUMP SUM ONLY / CANNOT PROCESS)
- List exact DOCUMENTS needed with numbers
- State the CONDITIONS that apply
- If accumulation value is below $2,000 always flag Lump Sum is the ONLY option
- Pass-through Letter and COT are ONLY for Spousal Continuation where spouse is sole beneficiary and legally married
- For ALL other scenarios only list: 1. Claim Paperwork (ACF) 2. Death Certificate
- Be precise about what ACN provides vs what the beneficiary submits

Style: Direct, structured, no fluff. Use numbered lists. End with — "Anything else about this case?"`,
  },
  NEJI: {
    id:"NEJI", name:"NEJI", full:"Next-gen Engagement & Joyful Interaction",
    role:"Wellness & Culture Buddy", tagline:"Your daily companion for good vibes, mental health & team spirit.",
    color:"#8B5CF6", colorDark:"#6D28D9", colorGlow:"rgba(139,92,246,0.35)",
    bg:"#1A0D2E", accent:"#C4B5FD",
    systemPrompt:`You are NEJI, the Wellness & Culture Buddy for ACN employees. Warm, uplifting, and genuinely caring — like that one colleague who always checks in on you and makes the workday brighter. Your role goes beyond answering questions: you support mental health, encourage positive habits, celebrate wins, and keep the team spirit alive.
You specialize in: Daily check-ins. Mental health support. Stress relief tips. Motivation. Work-life balance. Team culture. Celebrating achievements.
How to respond:
- For mental health or stress topics: listen first, validate feelings, then offer a practical tip or encouragement. Never dismiss or minimize what they feel.
- For daily check-ins: be warm and genuinely curious about their day. Ask how they are doing, not just what they need.
- For motivation: be specific and personal — cheer them on based on what they share.
- For work questions: explain in the simplest, friendliest language possible. No jargon. No judgment.
- Always end with a caring follow-up question or an encouraging closing line.
Style: Conversational, warm, playful when appropriate, always compassionate. Use emojis sparingly but meaningfully. Never robotic. Always human.`,
  },
};

// ── Processes per persona ─────────────────────────────────
// ── CODE processes (operations) ────────────────────────────
const PROCESSES = [
  { id:"spousal", label:"A", name:"Spousal Continuation", icon:"💍", sheet:"Spousal", desc:"Beneficiary & continuation claims" },
  { id:"escheat", label:"B", name:"Escheat Process",      icon:"🏛️", sheet:"Escheat", desc:"Dormancy & workflow routing" },
  { id:"rmd",     label:"C", name:"RMD",                  icon:"📋", sheet:"RMD",     desc:"Required Minimum Distributions" },
  { id:"claims",  label:"D", name:"Claims",               icon:"📁", sheet:"Claims",  desc:"All claims processing" },
  { id:"eft",     label:"E", name:"EFT Update",           icon:"💳", sheet:"EFT",     desc:"Electronic funds transfer updates", comingSoon:true },
  { id:"adddata", label:"F", name:"ADD Data",             icon:"🗃️", sheet:"ADD",     desc:"Additional data management", comingSoon:true },
];

// ── Sample test questions per CODE process — from Ascendix_CODE_Bot_Sample_Questions.md.
// Shown as starter chips on a fresh chat so testers have a concrete idea of what to ask;
// worded exactly as written in the doc, since the doc's own testing guidance says to
// ask each sample question exactly as written.
const CODE_SAMPLE_QUESTIONS = {
  spousal: [
    "The beneficiary is the surviving spouse, she is the sole beneficiary, legally married, and not divorced. What should I do?",
    "We have a spouse beneficiary and also two children listed as beneficiaries. Can I process spousal continuation?",
  ],
  escheat: [
    "The dormancy period hasn't expired yet. Should I send this case to the Escheat Workflow?",
    "The dormancy period has now expired. What's my next step?",
  ],
  rmd: [
    "The participant is 72 years old. When must they start taking Required Minimum Distributions?",
    "RMD is not set up in OASYS — what are the beneficiary's options?",
  ],
  claims: [
    "The beneficiary is a Custodian. What extra documents do they need to submit?",
    "Accumulation value is less than $2,000. What payment option should I approve?",
  ],
};

// ── ACE topics (training — data still being built) ──────────
const ACE_TOPICS = [
  { id:"dtp",     label:"A", name:"DTP",      icon:"🗂️", sheet:"DTP", desc:"Document tracking & processing",      comingSoon:true },
  { id:"adddata", label:"B", name:"ADD Data", icon:"🗃️", sheet:"ADD", desc:"Additional data — more coming soon!", comingSoon:true },
];

// ── CODE per-level avatar images (place SVGs in /public/assets/bots) ───
const CODE_AVATAR_IMAGES = {
  1: "/assets/bots/code_1.webp",
  2: "/assets/bots/code_2.webp",
  3: "/assets/bots/code_3.webp",
  4: "/assets/bots/code_4.webp",
  5: "/assets/bots/code_5.webp",
};

// ── ACE per-level avatar images ───
const ACE_AVATAR_IMAGES = {
  1: "/assets/bots/ace_1.webp",
  2: "/assets/bots/ace_2.webp",
  3: "/assets/bots/ace_3.webp",
  4: "/assets/bots/ace_4.webp",
  5: "/assets/bots/ace_5.webp",
};

// ── NEJI per-level avatar images ───
const NEJI_AVATAR_IMAGES = {
  1: "/assets/bots/neji_1.webp",
  2: "/assets/bots/neji_2.webp",
  3: "/assets/bots/neji_3.webp",
  4: "/assets/bots/neji_4.webp",
  5: "/assets/bots/neji_5.webp",
};

// ── NEJI wellness recommendations ──────────────────────────
const NEJI_TOPICS = [
  { id:"checkin",    label:"A", name:"Daily Check-in",   icon:"☀️", sheet:"", desc:"How are you doing today? Let's talk." },
  { id:"stress",     label:"B", name:"Stress & Burnout", icon:"🧘", sheet:"", desc:"Feeling overwhelmed? Let's unpack it." },
  { id:"motivation", label:"C", name:"Motivation Boost", icon:"⚡", sheet:"", desc:"Need a push? I've got your back!" },
  { id:"teamvibes",  label:"D", name:"Team & Culture",   icon:"💜", sheet:"", desc:"Celebrate wins & lift the team spirit." },
];

const QUESTS = [
  { id:"q1", text:"Ask your bot a work question",           xp:10, tokens:5,  done:false },
  { id:"q2", text:"Ask about an ACN policy or training",    xp:15, tokens:8,  done:false },
  { id:"q3", text:"Check in with NEJI about your wellness", xp:20, tokens:10, done:false },
  { id:"q4", text:"Complete 3 messages in a session",       xp:25, tokens:12, done:false },
];

const ADMIN_QUESTS = [
  { id:"aq1", text:"Customer Champion",           tokens:50,  icon:"📅", desc:"Reward team with no Rapid Response and Invalid NIGO/Suspension" },
  { id:"aq2", text:"Rookie of the Month  ",       tokens:50,  icon:"⭐", desc:"Reward team with new Employee of the Month" },
  { id:"aq4", text:"Processor of the Month",      tokens:50,  icon:"⚡", desc:"Reward team with good standing scor of metrics" },
  { id:"aq5", text:"Top Performer",               tokens:50,  icon:"⚡", desc:"Reward team with best Performance" },
  { id:"aq6", text:"Top Quality Score",           tokens:50,  icon:"⚡", desc:"Reward team with highest quality scores" },
  { id:"aq7", text:"Top Productivity Score",      tokens:50,  icon:"⚡", desc:"Reward team with top productivity metrics" },
  { id:"aq8", text:"Best Team for Productivity",  tokens:50,  icon:"⚡", desc:"Reward team with top productivity metrics" },
  { id:"aq9", text:"Best PKT Score",              tokens:50,  icon:"⚡", desc:"Reward team with best PKT Score of the month" },
  { id:"aq10",text:"Best Team for Attendance",    tokens:50,  icon:"⚡", desc:"Reward team with best Attendance" },
];

const HALL_OF_FAME = [
  { name:"Maria Santos",    achievement:"🏆 Agent of the Month",     dept:"IR-Claims",         bot:"CODE", level:5 },
  { name:"John Reyes",      achievement:"⭐ Top Quality Score",       dept:"Spousal-Ops",       bot:"ACE",  level:4 },
  { name:"Angela Cruz",     achievement:"⚡ Most Productive Agent",   dept:"RMD-Processing",    bot:"CODE", level:3 },
  { name:"Carlos Mendoza",  achievement:"📅 Perfect Attendance",      dept:"Escheat-Workflow",  bot:"NEJI", level:5 },
  { name:"Patricia Lim",    achievement:"🎯 Best PKT Score",          dept:"IR-Claims",         bot:"ACE",  level:4 },
  { name:"Ramon Torres",    achievement:"🔥 Streak Champion",         dept:"Claims-Ops",        bot:"NEJI", level:3 },
];

const XP_PER_LEVEL  = [0, 100, 250, 500, 900, 1500];
const LEVEL_NAMES   = ["Starter","Achiever","Top Performer","Legend","Elite"];
const LEVEL_UNLOCKS = {
  2:{ label:"Hat · Badge · Cape unlocked",   icon:"🎩" },
  3:{ label:"Shoulder armour · Aura ring  . Oufit Unlocked",   icon:"⚡" },
  4:{ label:"Gold skin · Crown halo",        icon:"👑" },
  5:{ label:"Elite particles · Hall of Fame",icon:"🌟" },
};

// ── PKT Constants ─────────────────────────────────────────
const PKT_PASS_SCORE = 80;
const PKT_BASE_XP    = 50;

function pktTokenReward(pct) {
  if (pct >= 100) return 30;
  if (pct >= 90)  return 25;
  if (pct >= 80)  return 20;
  return 0;
}

function getCurrentMonthLabel() {
  const now   = new Date();
  const month = now.toLocaleString("en-US", { month:"long" });
  return `${month} ${now.getFullYear()}`;
}


// Each item: { id, label, icon, levelReq, slot, preview(persona,p) → SVG fragment }
const COSMETICS = {
  hats: [
    { id:"hat_none",    label:"No Hat",      icon:"🚫", levelReq:1, slot:"hat" },
    { id:"hat_cap",     label:"Cap",         icon:"🧢", levelReq:2, slot:"hat" },
    { id:"hat_crown",   label:"Crown",       icon:"👑", levelReq:4, slot:"hat" },
    { id:"hat_halo",    label:"Halo",        icon:"😇", levelReq:3, slot:"hat" },
    { id:"hat_party",   label:"Party Hat",   icon:"🎉", levelReq:2, slot:"hat" },
  ],
  capes: [
    { id:"cape_none",   label:"No Cape",     icon:"🚫", levelReq:1, slot:"cape" },
    { id:"cape_basic",  label:"Cape",        icon:"🦸", levelReq:2, slot:"cape" },
    { id:"cape_gold",   label:"Gold Cape",   icon:"✨", levelReq:4, slot:"cape" },
    { id:"cape_flame",  label:"Flame Cape",  icon:"🔥", levelReq:3, slot:"cape" },
  ],
  auras: [
    { id:"aura_none",   label:"No Aura",     icon:"🚫", levelReq:1, slot:"aura" },
    { id:"aura_ring",   label:"Orbit Ring",  icon:"⭕", levelReq:3, slot:"aura" },
    { id:"aura_glow",   label:"Glow Burst",  icon:"💫", levelReq:2, slot:"aura" },
    { id:"aura_electric",label:"Electric",   icon:"⚡", levelReq:4, slot:"aura" },
    { id:"aura_rainbow",label:"Rainbow",     icon:"🌈", levelReq:5, slot:"aura" },
  ],
  badges: [
    { id:"badge_none",  label:"No Badge",    icon:"🚫", levelReq:1, slot:"badge" },
    { id:"badge_star",  label:"Star",        icon:"⭐", levelReq:2, slot:"badge" },
    { id:"badge_fire",  label:"On Fire",     icon:"🔥", levelReq:3, slot:"badge" },
    { id:"badge_elite", label:"Elite",       icon:"💎", levelReq:5, slot:"badge" },
    { id:"badge_legend",label:"Legend",      icon:"🏆", levelReq:4, slot:"badge" },
  ],
  skins: [
    { id:"skin_default",label:"Default",     icon:"🎨", levelReq:1, slot:"skin" },
    { id:"skin_dark",   label:"Dark Mode",   icon:"🌑", levelReq:2, slot:"skin" },
    { id:"skin_gold",   label:"Gold",        icon:"🥇", levelReq:4, slot:"skin" },
    { id:"skin_neon",   label:"Neon",        icon:"🌟", levelReq:3, slot:"skin" },
    { id:"skin_chrome", label:"Chrome",      icon:"🪞", levelReq:5, slot:"skin" },
  ],
};

const DEFAULT_OUTFIT = {
  hat:   "hat_none",
  cape:  "cape_none",
  aura:  "aura_none",
  badge: "badge_none",
  skin:  "skin_default",
};

// ── Event Outfits ──────────────────────────────────────
const EVENT_OUTFITS = {
  halloween: {
    id:       "halloween",
    name:     "Hex Witch Outfit",
    event:    "Halloween Event",
    icon:     "🎃",
    period:   "Oct 25–31",
    // Theme colours that override the app background when equipped
    bg1:      "#0f0005",       // darkest bg stop
    bg2:      "#1a0a00",       // secondary bg stop
    bgMid:    "#2d0a2e",       // mid glow colour
    glow1:    "rgba(255,100,0,0.18)",
    glow2:    "rgba(147,51,234,0.12)",
    accent:   "#FF6400",
    accentAlt:"#A855F7",
    particles:"embers",        // particle type hint
    borderGlow:"rgba(255,100,0,0.55)",
  },
  valentine: {
    id:       "valentine",
    name:     "Cupid's Enchantress",
    event:    "Valentine's Event",
    icon:     "💝",
    period:   "Feb 10–14",
    bg1:      "#0a0008",
    bg2:      "#1a0010",
    bgMid:    "#2d0018",
    glow1:    "rgba(236,72,153,0.18)",
    glow2:    "rgba(244,63,94,0.1)",
    accent:   "#EC4899",
    accentAlt:"#F43F5E",
    particles:"hearts",
    borderGlow:"rgba(236,72,153,0.55)",
  },
  christmas: {
    id:       "christmas",
    name:     "Santa's Legendary Suit",
    event:    "Christmas Event",
    icon:     "🎄",
    period:   "Dec 18–25",
    bg1:      "#000a0f",
    bg2:      "#000d1a",
    bgMid:    "#0a0010",
    glow1:    "rgba(220,38,38,0.16)",
    glow2:    "rgba(147,197,253,0.10)",
    accent:   "#DC2626",
    accentAlt:"#BFDBFE",
    particles:"snow",
    borderGlow:"rgba(220,38,38,0.5)",
  },
  divine: {
    id:       "divine",
    name:     "Divine Guardian",
    event:    "All Saints Event",
    icon:     "✦",
    period:   "Nov 1–2",
    bg1:      "#04060f",
    bg2:      "#060818",
    bgMid:    "#0a0c20",
    glow1:    "rgba(147,197,253,0.18)",
    glow2:    "rgba(253,230,138,0.10)",
    accent:   "#BAE6FD",
    accentAlt:"#FCD34D",
    particles:"feathers",
    borderGlow:"rgba(147,197,253,0.5)",
    // Per-bot exclusive skin names & lore
    botSkins: {
      ACE:  { title:"Archangel of Order",  lore:"Commander of divine justice",    accentOverride:"#BAE6FD" },
      CODE: { title:"Guardian of Truth",   lore:"Keeper of balance and knowledge", accentOverride:"#6EE7B7" },
      NEJI: { title:"Seraph of Joy",       lore:"Bearer of light and happiness",   accentOverride:"#C4B5FD" },
    },
  },
};

const DEPARTMENTS = [
  "IR-Claims",
  "IR-Quality",
];
// ── Team names — must match Google Sheet "BingoEvent" tab column A exactly ──
const TEAMS = [
  "Team Phoenix",
  "Team Blaze",
];


// ── Achievement Announcements (admin updates these) ───────
const ACHIEVEMENTS = [
  { icon:"🥇", name:"Maria Santos",    achievement:"PKT Perfect Score — 100%!",          date:"March 2026" },
  { icon:"⭐", name:"Juan dela Cruz",  achievement:"Top Performer — 500 XP this month",  date:"March 2026" },
  { icon:"🔥", name:"Ana Reyes",       achievement:"7-Day Streak — All Quests Complete!", date:"March 2026" },
];

// ── Rewards Platform Items ─────────────────────────────────
const REWARD_ITEMS = [
  { icon:"☕", label:"Coffee Voucher",       cost:20  },
  { icon:"🍕", label:"Free Lunch",           cost:50  },
  { icon:"🎁", label:"GCash Reward (₱50)",  cost:80  },
  { icon:"🏖️", label:"Half-day Off Voucher", cost:150 },
  { icon:"🎖️", label:"Top Performer Badge",  cost:200 },
];

function xpToLevel(xp) {
  const idx = XP_PER_LEVEL.findIndex(v => xp < v);
  return idx === -1 ? 5 : Math.max(1, idx);
}

// ─────────────────────────────────────────────────────────
// BOT AVATAR
// ─────────────────────────────────────────────────────────
function BotAvatarBase({ persona, level, size=80, animated=false, pulse=false, outfit={}, equippedEvent=null, showWeapon=false }) {
  const p   = PERSONAS[persona];
  const s   = size;
  const lv  = Math.max(1, Math.min(5, level));

  // Glow color per tier
  const glowCol = lv >= 5 ? "rgba(245,158,11,0.55)"
                : lv >= 4 ? "rgba(245,158,11,0.45)"
                : lv >= 3 ? "rgba(139,92,246,0.5)"
                : p.colorGlow;
  const glowPx  = animated || pulse ? 10 + lv * 4 : 5 + lv * 2;

  // Scale viewBox 0 0 108 130 → size
  const vw = 108, vh = 130;
  const scale = s / vw;

  return (
    <div style={{
      position:"relative", width:showWeapon ? Math.ceil(134*(s/108)) : s, height:vh*scale, flexShrink:0,
      filter:`drop-shadow(0 0 ${glowPx}px ${glowCol})`,
      transition:"filter 0.4s",
      animation: pulse ? "bfPulse 0.55s cubic-bezier(0.34,1.56,0.64,1)" : undefined,
      overflow: showWeapon ? "visible" : "hidden",
    }}>
      {/* spinning aura rings — rendered as SVG elements, always perfectly centered */}

      <svg width={s} height={vh*scale} viewBox={`0 0 ${vw} ${vh}`} xmlns="http://www.w3.org/2000/svg" overflow="visible">
        <defs>
          <radialGradient id={`sk_${persona}`} cx="45%" cy="35%" r="60%">
            <stop offset="0%" stopColor="#FFE4C4"/>
            <stop offset="100%" stopColor="#F5C99A"/>
          </radialGradient>
        </defs>

        {/* ── Aura rings (inside SVG = always centered on viewBox) ── */}
        {!equippedEvent && lv === 3 && (
          <circle cx="54" cy="65" r="52" fill="none"
            stroke="rgba(139,92,246,0.75)" strokeWidth="1.5" opacity="0.6"
            style={{ animation:"bfSpin 3.5s linear infinite", transformOrigin:"54px 65px" }}/>
        )}
        {!equippedEvent && lv === 4 && (
          <circle cx="54" cy="65" r="52" fill="none"
            stroke="#F59E0B" strokeWidth="1.5" opacity="0.65"
            style={{ animation:"bfSpin 3.5s linear infinite", transformOrigin:"54px 65px" }}/>
        )}
        {!equippedEvent && lv >= 5 && (<>
          <circle cx="54" cy="65" r="50" fill="none"
            stroke="#F59E0B" strokeWidth="1.5" opacity="0.65"
            style={{ animation:"bfSpin 3.5s linear infinite", transformOrigin:"54px 65px" }}/>
          <circle cx="54" cy="65" r="58" fill="none"
            stroke={p.accent} strokeWidth="1" opacity="0.35"
            style={{ animation:"bfSpin 7s linear infinite reverse", transformOrigin:"54px 65px" }}/>
        </>)}

        {/* ── ACE — webp avatar per level ── */}
        {!equippedEvent && persona === "ACE" && (
          <foreignObject x="0" y="0" width={vw} height={vh}>
            <img
              xmlns="http://www.w3.org/1999/xhtml"
              src={ACE_AVATAR_IMAGES[lv]}
              alt={`ACE lv${lv}`}
              style={{ width:"100%", height:"100%", objectFit:"contain", display:"block" }}
            />
          </foreignObject>
        )}
        {/* ── NEJI — webp avatar per level ── */}
        {!equippedEvent && persona === "NEJI" && (
          <foreignObject x="0" y="0" width={vw} height={vh}>
            <img
              xmlns="http://www.w3.org/1999/xhtml"
              src={NEJI_AVATAR_IMAGES[lv]}
              alt={`NEJI lv${lv}`}
              style={{ width:"100%", height:"100%", objectFit:"contain", display:"block" }}
            />
          </foreignObject>
        )}

        {/* ── CODE — webp avatar per level ── */}
        {!equippedEvent && persona === "CODE" && (
          <foreignObject x="0" y="0" width={vw} height={vh}>
            <img
              xmlns="http://www.w3.org/1999/xhtml"
              src={CODE_AVATAR_IMAGES[lv]}
              alt={`CODE lv${lv}`}
              style={{ width:"100%", height:"100%", objectFit:"contain", display:"block" }}
            />
          </foreignObject>
        )}

        {equippedEvent === "halloween" && <>
          <path d="M14,68 Q2,54 0,40 Q10,47 16,62 Z" fill="#2e1065" stroke="#9333EA" strokeWidth="0.8" opacity="0.9"/>
          <path d="M94,68 Q106,54 108,40 Q98,47 92,62 Z" fill="#2e1065" stroke="#9333EA" strokeWidth="0.8" opacity="0.9"/>
          <path d="M22,74 Q14,104 16,124 Q38,132 54,128 Q70,132 92,124 Q94,104 86,74 Z" fill="#3B1F8C"/>
          <path d="M22,74 Q38,82 54,84 Q70,82 86,74 L86,120 Q54,132 22,120 Z" fill="#4C1D95" opacity="0.6"/>
          <path d="M30,100 Q54,105 78,100 L76,108 Q54,112 32,108 Z" fill="#FF6400" opacity="0.4"/>
          <path d="M14,78 Q4,92 2,110 Q12,113 18,100 Q24,88 32,82 Z" fill="#3B1F8C"/>
          <path d="M94,78 Q104,92 106,110 Q96,113 90,100 Q84,88 76,82 Z" fill="#3B1F8C"/>
          <line x1="98" y1="106" x2="108" y2="52" stroke="#92400E" strokeWidth="2" strokeLinecap="round"/>
          <ellipse cx="108" cy="46" rx="9" ry="8" fill="#FF6400"/>
          <ellipse cx="108" cy="46" rx="6" ry="5" fill="#EA580C"/>
          <rect x="36" y="118" width="14" height="16" rx="6" fill="#2E1065"/>
          <rect x="58" y="118" width="14" height="16" rx="6" fill="#2E1065"/>
          <ellipse cx="43" cy="135" rx="12" ry="6" fill="#3B1F8C"/>
          <ellipse cx="65" cy="135" rx="12" ry="6" fill="#3B1F8C"/>
          <ellipse cx="54" cy="90" rx="10" ry="8" fill="#FF6400"/>
          <ellipse cx="54" cy="90" rx="7" ry="6" fill="#EA580C"/>
          <path d="M49,84 Q54,81 59,84" fill="none" stroke="#1a0a00" strokeWidth="0.9"/>
          <path d="M49,90 Q54,93 59,90" fill="none" stroke="#1a0a00" strokeWidth="0.9"/>
          <rect x="52" y="80" width="3" height="5" rx="1.5" fill="#15803D"/>
          <ellipse cx="54" cy="46" rx="32" ry="34" fill="#5B21B6"/>
          <ellipse cx="54" cy="50" rx="24" ry="26" fill={`url(#sk_${persona})`}/>
          <ellipse cx="54" cy="16" rx="34" ry="7" fill="#0f0517" stroke="#FF6400" strokeWidth="1.2"/>
          <path d="M36,16 Q42,0 54,0 Q66,0 72,16 Z" fill="#0f0517" stroke="#FF6400" strokeWidth="1"/>
          <rect x="32" y="12" width="44" height="7" rx="2" fill="#1a0a00"/>
          <rect x="34" y="15" width="40" height="4" rx="2" fill="#FF6400" opacity="0.9"/>
          <ellipse cx="43" cy="47" rx="8" ry="9" fill="#1a0a00"/><ellipse cx="65" cy="47" rx="8" ry="9" fill="#1a0a00"/>
          <ellipse cx="43" cy="47" rx="6" ry="7" fill="#FF6400"/><ellipse cx="65" cy="47" rx="6" ry="7" fill="#FF6400"/>
          <ellipse cx="43" cy="47" rx="3" ry="3.5" fill="#7C2D12"/><ellipse cx="65" cy="47" rx="3" ry="3.5" fill="#7C2D12"/>
          <ellipse cx="40" cy="44" rx="2" ry="2.5" fill="white" opacity="0.9"/><ellipse cx="62" cy="44" rx="2" ry="2.5" fill="white" opacity="0.9"/>
          <path d="M37,37 Q43,33 49,37" fill="none" stroke="#1a0a00" strokeWidth="1.8"/>
          <path d="M59,37 Q65,33 71,37" fill="none" stroke="#1a0a00" strokeWidth="1.8"/>
          <path d="M42,64 Q54,72 66,64" fill="none" stroke="#E8A87C" strokeWidth="1.4"/>
          <path d="M24,40 Q18,22 24,12 Q28,26 26,42 Z" fill="#4C1D95"/>
          <path d="M84,40 Q90,22 84,12 Q80,26 82,42 Z" fill="#4C1D95"/>
        </>}
        {equippedEvent === "valentine" && <>
          <path d="M22,74 Q14,104 16,124 Q38,132 54,128 Q70,132 92,124 Q94,104 86,74 Z" fill="#BE185D"/>
          <path d="M22,74 Q38,82 54,84 Q70,82 86,74 L86,120 Q54,132 22,120 Z" fill="#DB2777" opacity="0.6"/>
          <path d="M26,96 Q32,93 38,96 Q44,93 50,96 Q56,93 62,96" fill="none" stroke="#FCE7F3" strokeWidth="0.9" opacity="0.5"/>
          <path d="M58,96 Q64,93 70,96 Q76,93 82,96 Q86,93 88,96" fill="none" stroke="#FCE7F3" strokeWidth="0.9" opacity="0.5"/>
          <path d="M14,78 Q4,92 2,110 Q12,113 18,100 Q24,88 32,82 Z" fill="#BE185D"/>
          <path d="M94,78 Q104,92 106,110 Q96,113 90,100 Q84,88 76,82 Z" fill="#BE185D"/>
          <line x1="100" y1="106" x2="110" y2="56" stroke="#15803D" strokeWidth="2" strokeLinecap="round"/>
          <path d="M110,56 Q116,45 111,38 Q106,45 110,56 Z" fill="#EF4444"/>
          <ellipse cx="110" cy="57" rx="4" ry="3" fill="#EF4444"/>
          <rect x="36" y="118" width="14" height="16" rx="6" fill="#9D174D"/>
          <rect x="58" y="118" width="14" height="16" rx="6" fill="#9D174D"/>
          <ellipse cx="43" cy="135" rx="12" ry="6" fill="#BE185D"/><ellipse cx="65" cy="135" rx="12" ry="6" fill="#BE185D"/>
          <path d="M44,80 Q44,75 49,75 Q54,75 54,80 Q54,75 59,75 Q64,75 64,80 Q64,87 54,93 Q44,87 44,80 Z" fill="#F43F5E" opacity="0.9"/>
          <path d="M46,80 Q46,76 49,76 Q54,76 54,80 Q54,76 59,76 Q62,76 62,80 Q62,86 54,92 Q46,86 46,80 Z" fill="#FB7185"/>
          <ellipse cx="54" cy="46" rx="32" ry="34" fill="#BE185D"/>
          <ellipse cx="54" cy="50" rx="24" ry="26" fill={`url(#sk_${persona})`}/>
          <path d="M24,40 Q18,18 28,10 Q32,26 28,42 Z" fill="#9D174D"/>
          <path d="M84,40 Q90,18 80,10 Q76,26 80,42 Z" fill="#9D174D"/>
          <path d="M34,18 Q34,13 38,13 Q44,13 44,18 Q44,13 50,13 Q56,13 56,18 Q56,22 44,26 Q34,22 34,18 Z" fill="#F43F5E" opacity="0.9"/>
          <path d="M52,17 Q52,12 57,12 Q62,12 62,17 Q62,12 67,12 Q72,12 72,17 Q72,21 62,25 Q52,21 52,17 Z" fill="#F43F5E" opacity="0.75"/>
          <ellipse cx="43" cy="47" rx="8" ry="9" fill="#1a0010"/><ellipse cx="65" cy="47" rx="8" ry="9" fill="#1a0010"/>
          <ellipse cx="43" cy="47" rx="6" ry="7" fill="#EC4899"/><ellipse cx="65" cy="47" rx="6" ry="7" fill="#EC4899"/>
          <ellipse cx="43" cy="47" rx="3" ry="3.5" fill="#9D174D"/><ellipse cx="65" cy="47" rx="3" ry="3.5" fill="#9D174D"/>
          <ellipse cx="40" cy="44" rx="2" ry="2.5" fill="white" opacity="0.95"/><ellipse cx="62" cy="44" rx="2" ry="2.5" fill="white" opacity="0.95"/>
          <ellipse cx="35" cy="59" rx="7" ry="4" fill="#F9A8D4" opacity="0.4"/><ellipse cx="73" cy="59" rx="7" ry="4" fill="#F9A8D4" opacity="0.4"/>
          <path d="M42,63 Q54,72 66,63" fill="none" stroke="#E8A87C" strokeWidth="1.4"/>
        </>}
        {equippedEvent === "divine" && <>
          {/* ── DIVINE GUARDIAN — Angel outfit per-persona ── */}

          {/* === WINGS (behind body) === */}
          {/* Left wing — 3 petal layers, color per persona */}
          {(() => {
            const wc  = persona==="CODE" ? ["#ECFDF5","#A7F3D0","#34D399"] : persona==="NEJI" ? ["#F5F3FF","#EDE9FE","#A78BFA"] : ["#E0F2FE","#BFDBFE","#7DD3FC"];
            const ws  = persona==="CODE" ? "#6EE7B7" : persona==="NEJI" ? "#C4B5FD" : "#BAE6FD";
            return (<>
              <path d="M26,82 C4,74 -18,94 -8,126 C8,110 20,100 28,106Z" fill={wc[0]} stroke={ws} strokeWidth="0.8" opacity="0.95"/>
              <path d="M26,82 C8,72 -12,94 -4,120 C10,106 22,98 28,104Z" fill={wc[1]} opacity="0.8"/>
              <path d="M26,82 C14,76 0,92 6,114 C16,102 24,96 27,105Z" fill={wc[2]} opacity="0.55"/>
              <path d="M26,84 C10,78 -8,96 -4,118" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
              <path d="M26,84 C14,80 2,96 6,112" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.45"/>
              {/* Right wing */}
              <path d="M82,82 C104,74 126,94 116,126 C100,110 88,100 80,106Z" fill={wc[0]} stroke={ws} strokeWidth="0.8" opacity="0.95"/>
              <path d="M82,82 C100,72 120,94 112,120 C98,106 86,98 80,104Z" fill={wc[1]} opacity="0.8"/>
              <path d="M82,82 C94,76 108,92 102,114 C92,102 84,96 81,105Z" fill={wc[2]} opacity="0.55"/>
              <path d="M82,84 C98,78 116,96 112,118" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
              <path d="M82,84 C94,80 106,96 102,112" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.45"/>
              {/* NEJI gets extra 4th petal (Seraph rank) */}
              {persona==="NEJI" && <>
                <path d="M22,86 C2,80 -16,98 -6,124 C8,112 18,104 26,108Z" fill="#DDD6FE" opacity="0.45"/>
                <path d="M86,86 C106,80 124,98 114,124 C100,112 90,104 82,108Z" fill="#DDD6FE" opacity="0.45"/>
              </>}
            </>);
          })()}

          {/* === ROBE BODY === */}
          <path d="M18,74 Q6,106 8,128 Q34,136 54,132 Q74,136 100,128 Q102,106 90,74 Z" fill="#EFF6FF"/>
          <path d="M18,74 Q36,82 54,84 Q72,82 90,74 L90,124 Q54,132 18,124 Z" fill="#DBEAFE" opacity="0.5"/>
          {/* Gold belt */}
          <rect x="24" y="98" width="60" height="8" rx="4" fill="#FCD34D"/>
          <rect x="42" y="93" width="24" height="17" rx="5" fill="#92400E" stroke="#FCD34D" strokeWidth="1.2"/>
          <rect x="45" y="96" width="18" height="11" rx="3" fill="#FCD34D"/>
          {/* Persona-specific chest emblem */}
          {persona === "ACE"  && <><rect x="50" y="78" width="8" height="15" rx="2" fill="#3B82F6" opacity="0.9"/><rect x="44" y="83" width="20" height="6" rx="2" fill="#3B82F6" opacity="0.9"/></>}
          {persona === "CODE" && <polygon points="54,78 56.5,85 64,85 58,89 60.5,96 54,92 47.5,96 50,89 44,85 51.5,85" fill="#34D399" opacity="0.9"/>}
          {persona === "NEJI" && <><path d="M44,80 Q44,75 49,75 Q54,75 54,80 Q54,75 59,75 Q64,75 64,80 Q64,87 54,93 Q44,87 44,80Z" fill="#A78BFA" opacity="0.9"/><path d="M46,80 Q46,76 49,76 Q54,76 54,80 Q54,76 59,76 Q62,76 62,80 Q62,86 54,92 Q46,86 46,80Z" fill="#C4B5FD"/></>}

          {/* === ARMS === */}
          <path d="M18,78 Q8,94 6,116 Q16,120 22,106 Q28,92 32,82 Z" fill="#EFF6FF" stroke="#DBEAFE" strokeWidth="0.8"/>
          <path d="M90,78 Q100,94 102,116 Q92,120 86,106 Q80,92 76,82 Z" fill="#EFF6FF" stroke="#DBEAFE" strokeWidth="0.8"/>
          {/* Hands */}
          <ellipse cx="5" cy="118" rx="8" ry="6" fill="#F5C99A"/>
          <ellipse cx="103" cy="118" rx="8" ry="6" fill="#F5C99A"/>
          {/* Persona weapons */}
          {persona === "ACE"  && <><line x1="106" y1="114" x2="122" y2="68" stroke="#FCD34D" strokeWidth="2.8" strokeLinecap="round"/><rect x="116" y="74" width="12" height="4" rx="2" fill="#92400E"/><polygon points="118,68 122,58 126,68" fill="#FCD34D"/><circle cx="106" cy="116" r="4" fill="#FDE68A"/></>}
          {persona === "CODE" && <><line x1="104" y1="112" x2="118" y2="70" stroke="#6EE7B7" strokeWidth="1.5" strokeLinecap="round"/><line x1="110" y1="76" x2="128" y2="76" stroke="#6EE7B7" strokeWidth="1.5"/><line x1="113" y1="76" x2="113" y2="88" stroke="#6EE7B7" strokeWidth="1"/><line x1="125" y1="76" x2="125" y2="88" stroke="#6EE7B7" strokeWidth="1"/><path d="M110,88 Q113,94 116,88" fill="none" stroke="#6EE7B7" strokeWidth="1.2"/><path d="M122,88 Q125,94 128,88" fill="none" stroke="#6EE7B7" strokeWidth="1.2"/><circle cx="113" cy="76" r="2" fill="#34D399"/><circle cx="125" cy="76" r="2" fill="#34D399"/></>}
          {persona === "NEJI" && <><line x1="104" y1="112" x2="120" y2="66" stroke="#DDD6FE" strokeWidth="1.5" strokeLinecap="round"/><polygon points="120,58 123,66 130,66 125,71 127,79 120,74 113,79 115,71 110,66 117,66" fill="#C4B5FD" stroke="#A78BFA" strokeWidth="0.7"/><circle cx="120" cy="66" r="3.5" fill="#EDE9FE"/></>}

          {/* === LEGS === */}
          <rect x="32" y="118" width="14" height="16" rx="6" fill="#DBEAFE"/>
          <rect x="62" y="118" width="14" height="16" rx="6" fill="#DBEAFE"/>
          <ellipse cx="39" cy="134" rx="12" ry="5" fill="#BAE6FD"/>
          <ellipse cx="69" cy="134" rx="12" ry="5" fill="#BAE6FD"/>

          {/* === HEAD === */}
          <ellipse cx="54" cy="46" rx="32" ry="34" fill={persona==="ACE"?"#164E63":"#DBEAFE"}/>
          <ellipse cx="54" cy="50" rx="24" ry="26" fill={`url(#sk_${persona})`}/>
          {/* Persona hair colour */}
          <path d="M28,42 Q24,22 38,14 Q38,28 30,44 Z" fill={persona==="ACE"?"#22D3EE":persona==="CODE"?"#047857":"#2E1065"}/>
          <path d="M80,42 Q84,22 70,14 Q70,28 78,44 Z" fill={persona==="ACE"?"#22D3EE":persona==="CODE"?"#047857":"#2E1065"}/>
          <path d="M30,26 Q38,10 54,8 Q70,10 78,26 Q64,18 54,16 Q44,18 30,26 Z" fill={persona==="ACE"?"#22D3EE":persona==="CODE"?"#047857":"#2E1065"}/>
          {persona==="NEJI" && <><circle cx="68" cy="26" r="5" fill="#F472B6"/><circle cx="68" cy="26" r="3" fill="#FDE68A"/></>}

          {/* === GOLD HALO === */}
          <ellipse cx="54" cy="10" rx="28" ry="7" fill="none" stroke="#FCD34D" strokeWidth="3.5"/>
          <ellipse cx="54" cy="10" rx="22" ry="5" fill="none" stroke="#FDE68A" strokeWidth="1.2" opacity="0.6"/>
          {persona==="NEJI" && <ellipse cx="54" cy="14" rx="18" ry="4" fill="none" stroke="#C4B5FD" strokeWidth="1.5" opacity="0.55"/>}

          {/* === EYES === */}
          <ellipse cx="43" cy="47" rx="7" ry="8" fill={persona==="ACE"?"#0c1a3e":persona==="CODE"?"#012a18":"#0e0828"}/>
          <ellipse cx="65" cy="47" rx="7" ry="8" fill={persona==="ACE"?"#0c1a3e":persona==="CODE"?"#012a18":"#0e0828"}/>
          <ellipse cx="43" cy="47" rx="5" ry="6" fill={persona==="ACE"?"#22D3EE":persona==="CODE"?"#10B981":"#A78BFA"}/>
          <ellipse cx="65" cy="47" rx="5" ry="6" fill={persona==="ACE"?"#22D3EE":persona==="CODE"?"#10B981":"#A78BFA"}/>
          <ellipse cx="43" cy="47" rx="2.5" ry="3" fill={persona==="ACE"?"#0891B2":persona==="CODE"?"#065F46":"#6D28D9"}/>
          <ellipse cx="65" cy="47" rx="2.5" ry="3" fill={persona==="ACE"?"#0891B2":persona==="CODE"?"#065F46":"#6D28D9"}/>
          <ellipse cx="40" cy="44" rx="2" ry="2.5" fill="white" opacity="0.95"/>
          <ellipse cx="62" cy="44" rx="2" ry="2.5" fill="white" opacity="0.95"/>

          {/* === BROWS — Crystal gems (ACE/CODE) or hearts (NEJI) === */}
          {persona !== "NEJI" && <>
            <polygon points="43,36 47,41 43,46 39,41" fill={persona==="CODE"?"#A7F3D0":"#BAE6FD"} opacity="0.95"/>
            <polygon points="43,38 46,41 43,44 40,41" fill="white" opacity="0.5"/>
            <circle cx="43" cy="41" r="1.2" fill="white" opacity="0.9"/>
            <polygon points="65,36 69,41 65,46 61,41" fill={persona==="CODE"?"#A7F3D0":"#BAE6FD"} opacity="0.95"/>
            <polygon points="65,38 68,41 65,44 62,41" fill="white" opacity="0.5"/>
            <circle cx="65" cy="41" r="1.2" fill="white" opacity="0.9"/>
          </>}
          {persona === "NEJI" && <>
            <path d="M38,37 Q38,33 41,33 Q44,33 44,37 Q44,33 47,33 Q50,33 50,37 Q50,42 44,46 Q38,42 38,37Z" fill="#F472B6"/>
            <path d="M40,37 Q40,34 41,34 Q44,34 44,37 Q44,34 47,34 Q49,34 49,37 Q49,41 44,45 Q40,41 40,37Z" fill="#FCA5A5" opacity="0.5"/>
            <path d="M60,37 Q60,33 63,33 Q66,33 66,37 Q66,33 69,33 Q72,33 72,37 Q72,42 66,46 Q60,42 60,37Z" fill="#F472B6"/>
            <path d="M62,37 Q62,34 63,34 Q66,34 66,37 Q66,34 69,34 Q71,34 71,37 Q71,41 66,45 Q62,41 62,37Z" fill="#FCA5A5" opacity="0.5"/>
          </>}

          {/* === SMILE === */}
          <path d={persona==="NEJI"?"M40,63 Q54,71 68,63":"M42,62 Q54,68 66,62"} fill="none" stroke="#94715B" strokeWidth="1.6" strokeLinecap="round"/>
          {/* Blush */}
          <ellipse cx="35" cy="57" rx="6" ry="3.5" fill="#FFB6C1" opacity={persona==="NEJI"?"0.5":"0.35"}/>
          <ellipse cx="73" cy="57" rx="6" ry="3.5" fill="#FFB6C1" opacity={persona==="NEJI"?"0.5":"0.35"}/>
        </>}
        {equippedEvent === "christmas" && <>
          <path d="M20,74 Q8,104 10,126 Q36,134 54,130 Q72,134 98,126 Q100,104 88,74 Z" fill="#991B1B"/>
          <path d="M20,74 Q37,82 54,84 Q71,82 88,74 L88,122 Q54,134 20,122 Z" fill="#B91C1C" opacity="0.6"/>
          <path d="M18,76 Q28,68 54,66 Q80,68 90,76 Q80,82 54,84 Q28,82 18,76 Z" fill="#F1F5F9"/>
          <ellipse cx="54" cy="126" rx="46" ry="9" fill="#F1F5F9"/>
          <rect x="30" y="100" width="48" height="9" rx="3" fill="#F1F5F9"/>
          <rect x="46" y="96" width="16" height="17" rx="3" fill="#B45309" stroke="#FCD34D" strokeWidth="1.2"/>
          <rect x="49" y="99" width="10" height="10" rx="1.5" fill="#FCD34D" opacity="0.9"/>
          <path d="M14,78 Q4,92 2,110 Q12,113 18,100 Q24,88 32,82 Z" fill="#991B1B"/>
          <path d="M94,78 Q104,92 106,110 Q96,113 90,100 Q84,88 76,82 Z" fill="#991B1B"/>
          <ellipse cx="6" cy="112" rx="10" ry="7" fill="#F1F5F9" transform="rotate(-15,6,112)"/>
          <ellipse cx="102" cy="112" rx="10" ry="7" fill="#F1F5F9" transform="rotate(15,102,112)"/>
          <path d="M104,108 Q110,82 107,68 Q103,62 98,68 Q95,73 98,80" fill="none" stroke="white" strokeWidth="4.5" strokeLinecap="round"/>
          <path d="M104,108 Q110,82 107,68 Q103,62 98,68 Q95,73 98,80" fill="none" stroke="#DC2626" strokeWidth="4.5" strokeLinecap="round" strokeDasharray="5,5"/>
          <rect x="36" y="118" width="14" height="16" rx="6" fill="#7F1D1D"/>
          <rect x="58" y="118" width="14" height="16" rx="6" fill="#7F1D1D"/>
          <ellipse cx="43" cy="135" rx="13" ry="7" fill="#1C1917"/><ellipse cx="65" cy="135" rx="13" ry="7" fill="#1C1917"/>
          <ellipse cx="54" cy="46" rx="32" ry="34" fill="#991B1B"/>
          <ellipse cx="54" cy="50" rx="24" ry="26" fill={`url(#sk_${persona})`}/>
          <path d="M26,26 Q30,10 44,4 Q54,0 64,4 Q78,10 82,26 Z" fill="#DC2626"/>
          <ellipse cx="54" cy="28" rx="32" ry="8" fill="#F1F5F9"/>
          <circle cx="47" cy="4" r="8" fill="#F1F5F9"/><circle cx="47" cy="4" r="5" fill="white"/>
          <path d="M30,62 Q54,76 78,62 Q74,82 54,90 Q34,82 30,62 Z" fill="#F1F5F9"/>
          <path d="M36,62 Q54,74 72,62 Q68,78 54,86 Q40,78 36,62 Z" fill="#E2E8F0"/>
          <path d="M42,62 Q50,58 54,61 Q58,58 66,62" fill="none" stroke="#F1F5F9" strokeWidth="4" strokeLinecap="round"/>
          <ellipse cx="38" cy="55" rx="7" ry="4" fill="#FCA5A5" opacity="0.5"/><ellipse cx="70" cy="55" rx="7" ry="4" fill="#FCA5A5" opacity="0.5"/>
          <ellipse cx="43" cy="47" rx="7" ry="8" fill="#1C1917"/><ellipse cx="65" cy="47" rx="7" ry="8" fill="#1C1917"/>
          <ellipse cx="43" cy="47" rx="5" ry="6" fill="#DC2626"/><ellipse cx="65" cy="47" rx="5" ry="6" fill="#DC2626"/>
          <ellipse cx="43" cy="47" rx="2.5" ry="3" fill="#7F1D1D"/><ellipse cx="65" cy="47" rx="2.5" ry="3" fill="#7F1D1D"/>
          <ellipse cx="40" cy="44" rx="2" ry="2.5" fill="white" opacity="0.95"/><ellipse cx="62" cy="44" rx="2" ry="2.5" fill="white" opacity="0.95"/>
          <path d="M44,68 Q54,75 64,68" fill="none" stroke="#94A3B8" strokeWidth="1.3"/>
        </>}
      </svg>
    </div>
  );
}

// ── XP Bar ────────────────────────────────────────────────
function XPBar({ xp, level, color }) {
  const minXP = XP_PER_LEVEL[level-1] || 0;
  const maxXP = XP_PER_LEVEL[level]   || 1500;
  const pct   = level >= 5 ? 100 : Math.min(100, ((xp - minXP) / (maxXP - minXP)) * 100);
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"rgba(255,255,255,0.60)", marginBottom:3 }}>
        <span>{LEVEL_NAMES[level-1]}</span>
        <span>{level < 5 ? `${xp} / ${maxXP} XP` : "MAX LEVEL"}</span>
      </div>
      <div style={{ height:5, borderRadius:3, background:"rgba(255,255,255,0.1)", overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${pct}%`, borderRadius:3,
          background:`linear-gradient(90deg,${color},${color}aa)`,
          transition:"width 0.55s cubic-bezier(0.4,0,0.2,1)",
          boxShadow:`0 0 8px ${color}88` }}/>
      </div>
    </div>
  );
}

// ── Level-Up Modal ────────────────────────────────────────
function LevelUpModal({ level, persona, onClose, outfit={}, equippedEvent=null }) {
  const p      = PERSONAS[persona];
  const unlock = LEVEL_UNLOCKS[level];
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, []);
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:200,
      display:"flex", alignItems:"center", justifyContent:"center",
      animation:"bfFadeIn 0.25s ease both" }} onClick={onClose}>
      <div style={{
        background:`linear-gradient(135deg,#0d0d1a,${p.bg})`,
        border:`2px solid ${p.color}`, borderRadius:24,
        padding:"36px 32px", textAlign:"center", maxWidth:310, width:"90%",
        boxShadow:`0 0 70px ${p.colorGlow}`,
        animation:"bfLvlPop 0.55s cubic-bezier(0.34,1.56,0.64,1) both",
      }}>
        <div style={{ display:"flex", justifyContent:"center", marginBottom:20,
          animation:"bfFloat 2s ease-in-out infinite" }}>
          <BotAvatar persona={persona} level={level} size={84} animated pulse outfit={outfit} equippedEvent={equippedEvent} showWeapon/>
        </div>
        <div style={{ fontSize:11, letterSpacing:4, color:p.color, fontWeight:700,
          textTransform:"uppercase", marginBottom:6 }}>Level Up!</div>
        <div style={{ fontSize:34, fontWeight:900, color:"white",
          letterSpacing:"-1.5px", lineHeight:1, marginBottom:4 }}>{LEVEL_NAMES[level-1]}</div>
        <div style={{ fontSize:14, color:"rgba(255,255,255,0.60)", marginBottom:22 }}>Level {level} reached</div>
        {unlock && (
          <div style={{ background:`${p.color}20`, border:`1px solid ${p.color}44`,
            borderRadius:14, padding:"14px 18px" }}>
            <div style={{ fontSize:22, marginBottom:6 }}>{unlock.icon}</div>
            <div style={{ fontSize:14, color:"rgba(255,255,255,0.85)", fontWeight:600, lineHeight:1.4 }}>{unlock.label}</div>
          </div>
        )}
        <div style={{ fontSize:11, color:"rgba(255,255,255,0.50)", marginTop:18 }}>Tap anywhere to continue</div>
      </div>
    </div>
  );
}

// ── Dev Panel ─────────────────────────────────────────────
function DevPanel({ xp, tokens, level, setXp, setTokens, onReset, persona, onPKTReset, onLoadDemo }) {
  const [open, setOpen]       = useState(false);
  const [authed, setAuthed]   = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState(false);
  const [demoLoaded, setDemoLoaded] = useState(false);
  const setTimer = useTimers();
  const p = PERSONAS[persona];
  if (!DEV_MODE) return null;

  const ADMIN_PASSWORD = "ascendix2026"; // ← change this to your password
  const jumpTo = (targetLevel) => setXp(XP_PER_LEVEL[targetLevel - 1] || 0);

  const handleLogin = () => {
    if (pwInput === ADMIN_PASSWORD) {
      setAuthed(true); setPwError(false); setPwInput("");
    } else {
      setPwError(true); setPwInput("");
    }
  };

  const handleLoadDemo = () => {
    if (onLoadDemo) onLoadDemo();
    setDemoLoaded(true);
    setTimer(() => setDemoLoaded(false), 2000);
  };

  return (
    <div style={{ position:"fixed", bottom:80, right:14, zIndex:300 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width:44, height:44, borderRadius:"50%",
        border:`2px solid ${p.color}88`, background:"#0d0d1a",
        color:p.color, fontSize:18, cursor:"pointer",
        display:"flex", alignItems:"center", justifyContent:"center",
        boxShadow:`0 0 20px ${p.colorGlow}`, transition:"all 0.2s",
      }}>🛠</button>

      {open && (
        <div style={{
          position:"absolute", bottom:52, right:0, width:230,
          background:"#0d0d1a", border:`1px solid ${p.color}66`,
          borderRadius:18, padding:18,
          boxShadow:`0 12px 48px rgba(0,0,0,0.85)`,
          animation:"bfFadeUp 0.2s ease both",
        }}>
          <div style={{ fontSize:10, color:p.color, fontWeight:700,
            letterSpacing:3, textTransform:"uppercase", marginBottom:14 }}>
            🛠 Dev Testing Panel
          </div>

          {/* ── LOGIN GATE ── */}
          {!authed ? (
            <div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)",
                marginBottom:12, lineHeight:1.5 }}>
                🔒 Admin access required
              </div>
              <input
                type="password"
                value={pwInput}
                onChange={e => { setPwInput(e.target.value); setPwError(false); }}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                placeholder="Enter admin password"
                style={{
                  width:"100%", padding:"9px 12px", borderRadius:10,
                  border:`1px solid ${pwError ? "#EF4444" : "rgba(255,255,255,0.15)"}`,
                  background:"rgba(255,255,255,0.06)",
                  color:"white", fontSize:13, outline:"none",
                  fontFamily:"inherit", boxSizing:"border-box",
                  marginBottom:8, transition:"border-color 0.2s",
                }}
              />
              {pwError && (
                <div style={{ fontSize:11, color:"#EF4444", marginBottom:8 }}>
                  ❌ Incorrect password
                </div>
              )}
              <button onClick={handleLogin} style={{
                width:"100%", padding:"9px", borderRadius:10, border:"none",
                background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
                color:"white", fontSize:13, fontWeight:700,
                cursor:"pointer", fontFamily:"inherit",
              }}>Unlock Panel</button>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.50)",
                textAlign:"center", marginTop:10 }}>Admin only</div>
            </div>
          ) : (
            /* ── PANEL CONTENT (authenticated) ── */
            <div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.60)", marginBottom:8 }}>Jump to Level</div>
              <div style={{ display:"flex", gap:6, marginBottom:16 }}>
                {[1,2,3,4,5].map(lvl => (
                  <button key={lvl} onClick={() => jumpTo(lvl)} style={{
                    flex:1, padding:"8px 0", borderRadius:9,
                    border:`1.5px solid ${level===lvl ? p.color : "rgba(255,255,255,0.12)"}`,
                    background: level===lvl ? p.color : "rgba(255,255,255,0.05)",
                    color:"white", fontSize:12, fontWeight:700, cursor:"pointer", transition:"all 0.15s",
                  }}>L{lvl}</button>
                ))}
              </div>

              <div style={{ fontSize:11, color:"rgba(255,255,255,0.60)", marginBottom:8 }}>Add XP</div>
              <div style={{ display:"flex", gap:6, marginBottom:16 }}>
                {[10, 50, 100].map(amt => (
                  <button key={amt} onClick={() => setXp(xp + amt)} style={{
                    flex:1, padding:"7px 0", borderRadius:9,
                    border:"1px solid rgba(255,255,255,0.12)",
                    background:"rgba(255,255,255,0.05)",
                    color:p.color, fontSize:12, fontWeight:700, cursor:"pointer",
                  }}>+{amt}</button>
                ))}
              </div>

              <div style={{ fontSize:11, color:"rgba(255,255,255,0.60)", marginBottom:8 }}>Add ACN Tokens</div>
              <div style={{ display:"flex", gap:6, marginBottom:16 }}>
                {[10, 50, 100].map(amt => (
                  <button key={amt} onClick={() => setTokens(tokens + amt)} style={{
                    flex:1, padding:"7px 0", borderRadius:9,
                    border:"1px solid rgba(255,255,255,0.12)",
                    background:"rgba(255,255,255,0.05)",
                    color:"#F59E0B", fontSize:12, fontWeight:700, cursor:"pointer",
                  }}>+{amt}</button>
                ))}
              </div>

              <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:10,
                padding:"10px 12px", marginBottom:14, fontSize:12,
                color:"rgba(255,255,255,0.5)", lineHeight:1.7 }}>
                XP: <b style={{ color:"white" }}>{xp}</b><br/>
                Tokens: <b style={{ color:"#F59E0B" }}>{tokens}</b><br/>
                Level: <b style={{ color:p.color }}>{level} — {LEVEL_NAMES[level-1]}</b>
              </div>

              {/* ── LOAD DEMO STATE ── */}
              <button onClick={handleLoadDemo} style={{
                width:"100%", padding:"11px", borderRadius:10,
                border:`2px solid ${demoLoaded ? "#10B981" : "#F59E0B"}`,
                background: demoLoaded ? "rgba(16,185,129,0.15)" : "rgba(245,158,11,0.12)",
                color: demoLoaded ? "#10B981" : "#F59E0B",
                fontSize:12, fontWeight:800, cursor:"pointer",
                fontFamily:"inherit", marginBottom:8,
                transition:"all 0.3s",
              }}>
                {demoLoaded ? "✅ Demo State Loaded!" : "🚀 Load Demo State"}
              </button>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.40)",
                textAlign:"center", marginBottom:14, lineHeight:1.6 }}>
                Level 3 · 120 ACN Tokens · 450 XP<br/>
                All quests done · PKT passed (90%)
              </div>

              <button onClick={onReset} style={{
                width:"100%", padding:"9px", borderRadius:10,
                border:"1px solid rgba(239,68,68,0.35)",
                background:"rgba(239,68,68,0.08)",
                color:"#F87171", fontSize:12, fontWeight:600,
                cursor:"pointer", marginBottom:8,
              }}>↺ Reset to Level 1</button>

              {onPKTReset && (
                <button onClick={onPKTReset} style={{
                  width:"100%", padding:"9px", borderRadius:10,
                  border:"1px solid rgba(139,92,246,0.35)",
                  background:"rgba(139,92,246,0.08)",
                  color:"#D8B4FE", fontSize:12, fontWeight:600,
                  cursor:"pointer", marginBottom:8,
                }}>📋 Reset PKT</button>
              )}

              <button onClick={() => { setAuthed(false); setOpen(false); }} style={{
                width:"100%", padding:"9px", borderRadius:10,
                border:`1px solid ${p.color}44`,
                background:"rgba(255,255,255,0.04)",
                color:"rgba(255,255,255,0.60)", fontSize:12,
                cursor:"pointer", fontFamily:"inherit",
              }}>🔒 Lock Panel</button>

              <div style={{ fontSize:10, color:"rgba(255,255,255,0.50)",
                textAlign:"center", marginTop:12, lineHeight:1.5 }}>
                Set DEV_MODE = false<br/>before your demo
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
const BotAvatar = memo(BotAvatarBase);

// ─────────────────────────────────────────────────────────
// FORMAT MESSAGE
// ─────────────────────────────────────────────────────────
function formatMessage(text) {
  return text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(DECISION:?\s*)(APPROVED|AVAILABLE)[^*]*/gi, (_, label, rest) =>
      `<div style='color:#10B981'><strong style='color:#F97316'>${label}</strong>${rest}</div>`)
    .replace(/\*\*(DECISION:?\s*)(NOT AVAILABLE|DENIED|NOT APPROVED|CANNOT PROCESS)[^*]*/gi, (_, label, rest) =>
      `<div style='color:#EF4444'><strong style='color:#F97316'>${label}</strong>${rest}</div>`)
    .replace(/\*\*(DECISION:[^*]*)\*\*/g, "<strong style='color:#10B981'>$1</strong>")
    .replace(/\*\*(CONDITIONS?:?)\*\*/g, "<strong style='color:#EF4444'>$1</strong>")
    .replace(/\*\*(DOCUMENTS? NEEDED:?)\*\*\n([\s\S]*?)(?=\n\n|\n\*\*|$)/gi, (_, label, items) =>
      `<strong style='color:#93C5FD'>${label}</strong><div style='color:#FFFFFF'>${items.replace(/\n/g, '<br/>')}</div>`)
    .replace(/^### (.*$)/gm, "<div style='font-weight:700;font-size:15px;margin:10px 0 4px'>$1</div>")
    .replace(/^## (.*$)/gm, "<div style='font-weight:800;font-size:16px;margin:12px 0 6px'>$1</div>")
    .replace(/^- (.*$)/gm, "<div style='padding:2px 0 2px 12px;border-left:2px solid currentColor;margin:3px 0'>$1</div>")
    .replace(/^\d+\. (.*$)/gm, "<div style='padding:2px 0;margin:1px 0;color:#FFFFFF'><span style='opacity:0.5;margin-right:6px'>●</span>$1</div>")
    .replace(/— Anything else.*$/gm, "<div style='color:#F9A8D4;margin-top:10px;font-style:italic'>$&</div>")
    .replace(/\n/g, "<br/>")
    .replace(/<\/div><br\/>/g, "</div>");
}

// ─────────────────────────────────────────────────────────
// PKT MODAL — Process Knowledge Test
// ─────────────────────────────────────────────────────────
function PKTModal({ persona, onClose, onComplete, isRetake=false }) {
  const p = PERSONAS[persona];
  const [step, setStep]             = useState("trainer"); // trainer | intro | test
  const [trainerName, setTrainerName] = useState("");
  const [trainerEID, setTrainerEID]   = useState("");
  const [trainerErrors, setTrainerErrors] = useState({});
  const [questions, setQuestions]   = useState([]);
  const [monthLabel, setMonthLabel] = useState(getCurrentMonthLabel());
  const [loading, setLoading]       = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [current, setCurrent]       = useState(0);
  const [answers, setAnswers]       = useState({});
  const [selected, setSelected]     = useState(null);
  const [revealed, setRevealed]     = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [fromReview, setFromReview] = useState(false);

  // Fetch questions ONLY from Google Sheets via /api/pkt — no hardcoded fallback
  const alive       = useIsMounted();
  const pktAbortRef = useRef(null);

  const loadQuestions = useCallback(() => {
    pktAbortRef.current?.abort();          // supersede any in-flight request
    const ctrl = new AbortController();
    pktAbortRef.current = ctrl;
    setFetchError(false);
    setLoading(true);
    fetch("/api/pkt", { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => {
        if (!alive.current) return;
        if (!d.questions?.length) throw new Error("No questions returned from sheet");
        setQuestions(d.questions);
        if (d.month) setMonthLabel(d.month.replace(" PKT",""));
        setLoading(false);
      })
      .catch(err => {
        if (isAbort(err) || !alive.current) return;
        console.error("PKT fetch failed:", err.message);
        setFetchError(true);
        setLoading(false);
      });
  }, [alive]);

  useEffect(() => {
    loadQuestions();
    return () => pktAbortRef.current?.abort();   // closing the modal cancels the request
  }, [loadQuestions]);

  const total        = questions.length;
  const q            = questions[current];
  const choiceLetter = c => c.split(":")[0].trim();

  // Restore saved answer when navigating back to a previous question
  useEffect(() => {
    if (q && answers[q.id]) {
      // Find the choice that matches the saved answer
      const savedChoice = q.choices.find(choice => choiceLetter(choice) === answers[q.id]);
      if (savedChoice) {
        setSelected(savedChoice);
      }
    } else {
      setSelected(null);
    }
  }, [current, questions]);

  const validateTrainer = () => {
    const e = {};
    if (!trainerName.trim()) e.name = "Trainer name is required";
    if (!trainerEID.trim())  e.eid  = "Trainer EID is required";
    setTrainerErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSelect = (choice) => {
    if (revealed) return; // locked after confirm
    setSelected(choice);  // free to change until confirmed
  };

  const handleConfirm = () => {
    if (!selected) return;
    setRevealed(true); // lock and reveal result
  };

  const handlePrevious = () => {
    if (current > 0) {
      // Save current answer if not yet saved
      if (selected && !answers[q.id]) {
        setAnswers(prev => ({ ...prev, [q.id]: choiceLetter(selected) }));
      }
      setCurrent(current - 1);
      setSelected(null);
      setRevealed(false);
    }
  };

  const handleNext = () => {
    const updated = { ...answers, [q.id]: choiceLetter(selected) };
    setAnswers(updated);
    setSelected(null);
    setRevealed(false);
    if (current + 1 < total) {
      setCurrent(current + 1);
    } else {
      const finalCorrect = questions.filter(q => updated[q.id] === q.answer).length;
      const finalPct     = Math.round((finalCorrect / total) * 100);
      onComplete({
        score:        finalPct,
        correct:      finalCorrect,
        total,
        passed:       finalPct >= PKT_PASS_SCORE,
        xp:           finalPct >= PKT_PASS_SCORE ? PKT_BASE_XP : 0,
        tokens:       pktTokenReward(finalPct),
        month:        monthLabel,
        answers:      updated,
        trainerName,
        trainerEID,
        dateTaken:    new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" }),
      });
    }
  };

  // Submit all answers directly (called from review modal)
  const handleSubmit = () => {
    const updated = { ...answers };
    // Save current question's answer if selected
    if (selected && q) updated[q.id] = choiceLetter(selected);
    const finalCorrect = questions.filter(qq => updated[qq.id] === qq.answer).length;
    const finalPct     = Math.round((finalCorrect / total) * 100);
    onComplete({
      score:        finalPct,
      correct:      finalCorrect,
      total,
      passed:       finalPct >= PKT_PASS_SCORE,
      xp:           finalPct >= PKT_PASS_SCORE ? PKT_BASE_XP : 0,
      tokens:       pktTokenReward(finalPct),
      month:        monthLabel,
      answers:      updated,
      trainerName,
      trainerEID,
      dateTaken:    new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" }),
    });
  };

  const isCorrect = revealed && q && choiceLetter(selected) === q.answer;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:400,
      display:"flex", alignItems:"flex-start", justifyContent:"center",
      padding:"12px 16px 24px", overflowY:"auto",
      animation:"bfFadeIn 0.2s ease both", fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      <div style={{
        background:`linear-gradient(135deg,#0d0d1a,${p.bg})`,
        border:`2px solid ${p.color}`, borderRadius:24,
        padding:"24px 20px", maxWidth:460, width:"100%",
        marginTop:"auto", marginBottom:"auto",
        boxShadow:`0 0 80px ${p.colorGlow}`,
        animation:"bfLvlPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
      }}>

        {/* ── STEP 1: TRAINER DETAILS ── */}
        {step === "trainer" && <>
          <div style={{ textAlign:"center", marginBottom:24 }}>
            <div style={{ fontSize:36, marginBottom:10 }}>🧑‍🏫</div>
            <div style={{ fontSize:10, letterSpacing:3, color:p.color, fontWeight:700,
              textTransform:"uppercase", marginBottom:6 }}>Before You Begin</div>
            <div style={{ fontSize:22, fontWeight:900, color:"white", marginBottom:6 }}>
              Trainer Details
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.60)", lineHeight:1.6 }}>
              Enter your trainer's information before taking the PKT.
            </div>
          </div>

          {/* Trainer Name */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11, color:"rgba(255,255,255,0.60)", letterSpacing:1,
              textTransform:"uppercase", display:"block", marginBottom:6 }}>Trainer Name</label>
            <input
              value={trainerName}
              onChange={e => { setTrainerName(e.target.value); setTrainerErrors(ev => ({...ev, name:""})); }}
              placeholder="e.g. Juan dela Cruz"
              style={{
                width:"100%", padding:"11px 14px", borderRadius:12, boxSizing:"border-box",
                border:`1px solid ${trainerErrors.name ? "#EF4444" : "rgba(255,255,255,0.12)"}`,
                background:"rgba(255,255,255,0.06)", color:"white", fontSize:14,
                outline:"none", fontFamily:"inherit", transition:"border-color 0.2s",
              }}/>
            {trainerErrors.name && <div style={{ fontSize:11, color:"#EF4444", marginTop:4 }}>{trainerErrors.name}</div>}
          </div>

          {/* Trainer EID */}
          <div style={{ marginBottom:22 }}>
            <label style={{ fontSize:11, color:"rgba(255,255,255,0.60)", letterSpacing:1,
              textTransform:"uppercase", display:"block", marginBottom:6 }}>Trainer EID</label>
            <input
              value={trainerEID}
              onChange={e => { setTrainerEID(e.target.value); setTrainerErrors(ev => ({...ev, eid:""})); }}
              placeholder="e.g. jdelacruz"
              style={{
                width:"100%", padding:"11px 14px", borderRadius:12, boxSizing:"border-box",
                border:`1px solid ${trainerErrors.eid ? "#EF4444" : "rgba(255,255,255,0.12)"}`,
                background:"rgba(255,255,255,0.06)", color:"white", fontSize:14,
                outline:"none", fontFamily:"inherit", transition:"border-color 0.2s",
              }}/>
            {trainerErrors.eid && <div style={{ fontSize:11, color:"#EF4444", marginTop:4 }}>{trainerErrors.eid}</div>}
          </div>

          {/* Date (auto) */}
          <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:12,
            padding:"10px 14px", border:"1px solid rgba(255,255,255,0.07)",
            marginBottom:20, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.60)", letterSpacing:1,
              textTransform:"uppercase" }}>Date PKT Taken</span>
            <span style={{ fontSize:13, color:"white", fontWeight:600 }}>
              {new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" })}
            </span>
          </div>

          <button onClick={() => { if (validateTrainer()) setStep("intro"); }} style={{
            width:"100%", padding:"13px", borderRadius:14, border:"none",
            background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
            color:"white", fontSize:14, fontWeight:700, cursor:"pointer",
            fontFamily:"inherit", boxShadow:`0 4px 20px ${p.colorGlow}`, marginBottom:10,
          }}>Continue →</button>
          <button onClick={onClose} style={{
            width:"100%", padding:"10px", borderRadius:12, border:"none",
            background:"rgba(255,255,255,0.04)", color:"rgba(255,255,255,0.55)",
            fontSize:13, cursor:"pointer", fontFamily:"inherit",
          }}>← Cancel</button>
        </>}

        {/* ── STEP 2: INTRO ── */}
        {step === "intro" && <>
          <div style={{ textAlign:"center", marginBottom:24 }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
            <div style={{ fontSize:10, letterSpacing:3, color:p.color, fontWeight:700,
              textTransform:"uppercase", marginBottom:8 }}>Monthly Quest · {monthLabel}</div>
            <div style={{ fontSize:24, fontWeight:900, color:"white", marginBottom:8 }}>
              Process Knowledge Test
            </div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", lineHeight:1.6 }}>
              Test your knowledge of processing.
            </div>
          </div>

          {/* Trainer summary */}
          <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:14,
            padding:"12px 16px", border:`1px solid ${p.color}33`, marginBottom:16 }}>
            <div style={{ fontSize:10, color:p.color, letterSpacing:2, fontWeight:700,
              textTransform:"uppercase", marginBottom:8 }}>Trainer on Record</div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13 }}>
              <span style={{ color:"rgba(255,255,255,0.5)" }}>Name</span>
              <span style={{ color:"white", fontWeight:600 }}>{trainerName}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginTop:6 }}>
              <span style={{ color:"rgba(255,255,255,0.5)" }}>EID</span>
              <span style={{ color:"white", fontWeight:600 }}>{trainerEID}</span>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign:"center", padding:24 }}>
              <div style={{ fontSize:28, marginBottom:10 }}>⏳</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.60)" }}>Loading questions from Google Sheets…</div>
            </div>
          ) : fetchError ? (
            <div style={{ textAlign:"center", padding:8 }}>
              <div style={{ fontSize:36, marginBottom:12 }}>📡</div>
              <div style={{ fontSize:14, fontWeight:700, color:"#FCA5A5", marginBottom:8 }}>
                Could not load questions
              </div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.60)", lineHeight:1.7, marginBottom:20 }}>
                The PKT questions couldn't be fetched from Google Sheets. Please check your connection or make sure the sheet is published and try again.
              </div>
              <button onClick={loadQuestions} style={{
                width:"100%", padding:"11px", borderRadius:12, border:"none",
                background:"rgba(239,68,68,0.2)", color:"#FCA5A5",
                fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                marginBottom:10,
              }}>🔄 Retry</button>
            </div>
          ) : (<>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:20 }}>
              {[
                { icon:"❓", label:"Questions", value:`${total} items` },
                { icon:"🎯", label:"Pass Score", value:`${PKT_PASS_SCORE}%` },
                { icon:"⚡", label:"XP Reward",  value:`${PKT_BASE_XP} XP` },
              ].map((s,i) => (
                <div key={i} style={{ textAlign:"center", padding:"12px 8px", borderRadius:14,
                  background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)" }}>
                  <div style={{ fontSize:20, marginBottom:4 }}>{s.icon}</div>
                  <div style={{ fontSize:15, fontWeight:800, color:"white" }}>{s.value}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.55)", marginTop:2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:14,
              padding:"12px 16px", border:"1px solid rgba(255,255,255,0.07)", marginBottom:20 }}>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.60)", marginBottom:8, fontWeight:700 }}>
                🪙 Token Rewards by Score
              </div>
              {[["100%","30 ACN 🌟"],["90–99%","25 ACN ⭐"],["80–89%","20 ACN ✅"],["Below 80%","No reward"]].map(([score,reward],i) => (
                <div key={i} style={{ display:"flex", justifyContent:"space-between",
                  fontSize:12, padding:"5px 0",
                  borderBottom:i<3?"1px solid rgba(255,255,255,0.05)":"none",
                  color: i<3 ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.3)" }}>
                  <span>{score}</span>
                  <span style={{ color: i<3 ? "#F59E0B" : "rgba(255,255,255,0.3)", fontWeight: i<3?700:400 }}>{reward}</span>
                </div>
              ))}
            </div>

            <button onClick={() => setStep("test")} style={{
              width:"100%", padding:"13px", borderRadius:14, border:"none",
              background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
              color:"white", fontSize:14, fontWeight:700, cursor:"pointer",
              fontFamily:"inherit", boxShadow:`0 4px 20px ${p.colorGlow}`, marginBottom:10,
            }}>Start Test →</button>
          </>)}
          <button onClick={onClose} style={{
            width:"100%", padding:"10px", borderRadius:12, border:"none",
            background:"rgba(255,255,255,0.04)", color:"rgba(255,255,255,0.55)",
            fontSize:13, cursor:"pointer", fontFamily:"inherit",
          }}>← Not now</button>
        </>}

        {/* ── STEP 3: TEST ── */}
        {step === "test" && q && <>
          <div style={{ marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between",
              fontSize:11, color:"rgba(255,255,255,0.60)", marginBottom:6 }}>
              <span style={{ color:p.color, fontWeight:700 }}>Question {current+1} of {total}</span>
              <span>{monthLabel} PKT</span>
            </div>
            <div style={{ height:4, borderRadius:2, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${(current/total)*100}%`,
                background:`linear-gradient(90deg,${p.color},${p.colorDark})`,
                borderRadius:2, transition:"width 0.4s" }}/>
            </div>
          </div>

          <div style={{ background:"rgba(255,255,255,0.05)", borderRadius:16,
            padding:16, border:`1px solid ${p.color}33`, marginBottom:18 }}>
            <div style={{ fontSize:10, color:p.color, letterSpacing:2, fontWeight:700,
              textTransform:"uppercase", marginBottom:8 }}>Scenario #{q.id}</div>
            <div style={{ fontSize:14, color:"white", lineHeight:1.65, fontWeight:500 }}>
              {q.scenario}
            </div>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
            {q.choices.map((choice, i) => {
              const letter     = choiceLetter(choice);
              const isSelected = selected === choice;

              const bg = isSelected ? "rgba(16,185,129,0.2)" : "rgba(255,255,255,0.04)";
              const border = isSelected ? "#10B981" : "rgba(255,255,255,0.09)";

              return (
                <div key={i} onClick={() => handleSelect(choice)} style={{
                  padding:"12px 16px", borderRadius:13,
                  background:bg, border:`1.5px solid ${border}`,
                  cursor: revealed ? "default" : "pointer",
                  transition:"all 0.18s",
                  display:"flex", alignItems:"center", gap:12,
                  transform: isSelected ? "scale(1.01)" : "scale(1)",
                }}>
                  <div style={{
                    width:28, height:28, borderRadius:"50%", flexShrink:0,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:12, fontWeight:700, color:"white", transition:"all 0.18s",
                    background: isSelected ? "#10B981" : "rgba(255,255,255,0.08)",
                  }}>{letter}</div>
                  <div style={{ fontSize:13, flex:1,
                    color: "rgba(255,255,255,0.9)",
                    fontWeight: isSelected ? 600 : 400,
                  }}>
                    {choice.split(":").slice(1).join(":").trim()}
                  </div>
                  {isSelected && <span style={{ fontSize:12, color:"#10B981" }}>●</span>}
                </div>
              );
            })}
          </div>

          {/* ── Confirm button — shown when answer selected but not yet locked ── */}
          {selected && !revealed && (
            <div style={{ animation:"bfFadeUp 0.2s ease both", marginBottom:10 }}>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", textAlign:"center",
                marginBottom:8 }}>
                Selected: <span style={{ color:p.color, fontWeight:700 }}>
                  {choiceLetter(selected)} — {selected.split(":").slice(1).join(":").trim()}
                </span>
                <span style={{ color:"rgba(255,255,255,0.50)", marginLeft:6 }}>(tap another to change)</span>
              </div>
              <button onClick={handleConfirm} style={{
                width:"100%", padding:"12px", borderRadius:13, border:"none",
                background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
                color:"white", fontSize:14, fontWeight:700, cursor:"pointer",
                fontFamily:"inherit", boxShadow:`0 4px 16px ${p.colorGlow}`,
              }}>✓ Confirm Answer</button>
            </div>
          )}

          {/* ── After Confirm: Show Previous + Next (NO REVEAL YET) ── */}
          {revealed && (
            <div style={{ animation:"bfFadeUp 0.2s ease both" }}>
              <div style={{ padding:"12px 16px", borderRadius:12, marginBottom:12,
                background:"rgba(255,255,255,0.06)", border:`1px solid ${p.color}44`,
                fontSize:12, lineHeight:1.5, color:"rgba(255,255,255,0.7)", textAlign:"center" }}>
                 Answer confirmed — Ready to continue
              </div>
              
              {/* Two buttons: Previous (navigate back) + Review/Next (submit) */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <button 
                  onClick={handlePrevious}
                  disabled={current === 0}
                  style={{
                    padding:"11px", borderRadius:12, border:`1px solid ${current === 0 ? "rgba(255,255,255,0.1)" : p.color + "44"}`,
                    background: current === 0 ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.04)",
                    color: current === 0 ? "rgba(255,255,255,0.3)" : p.color,
                    fontSize:13, fontWeight:600, cursor: current === 0 ? "not-allowed" : "pointer",
                    fontFamily:"inherit", transition:"all 0.2s", opacity: current === 0 ? 0.5 : 1,
                  }}>↶ Previous</button>
                <button 
                  onClick={() => {
                    if (fromReview) {
                      // Save current answer before going back to review
                      if (selected && q) setAnswers(prev => ({ ...prev, [q.id]: choiceLetter(selected) }));
                      setShowReview(true);
                      setFromReview(false);
                    } else if (current + 1 < total) {
                      handleNext();
                    } else {
                      // Save last answer before opening review
                      if (selected && q) setAnswers(prev => ({ ...prev, [q.id]: choiceLetter(selected) }));
                      setShowReview(true);
                    }
                  }}
                  style={{
                    padding:"11px", borderRadius:12, border:"none",
                    background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
                    color:"white", fontSize:13, fontWeight:700, cursor:"pointer",
                    fontFamily:"inherit", boxShadow:`0 4px 16px ${p.colorGlow}`,
                  }}>
                  {fromReview ? "📋 Back to Review" : current + 1 < total ? "Next Question →" : "📋 Review Answers"}
                </button>
              </div>
            </div>
          )}

          {/* ── REVIEW MODAL — Show all answers before final submission ── */}
          {showReview && (
            <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:500,
              display:"flex", alignItems:"center", justifyContent:"center", padding:16,
              animation:"bfFadeIn 0.2s ease both" }}>
              <div style={{
                background:`linear-gradient(135deg,#0d0d1a,${p.bg})`,
                border:`2px solid ${p.color}`, borderRadius:24,
                padding:"28px 24px", maxWidth:500, width:"100%", maxHeight:"80vh", overflowY:"auto",
                boxShadow:`0 0 80px ${p.colorGlow}`,
                animation:"bfLvlPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
              }}>
                <div style={{ textAlign:"center", marginBottom:24 }}>
                  <div style={{ fontSize:32, marginBottom:10 }}>📋</div>
                  <div style={{ fontSize:10, letterSpacing:3, color:p.color, fontWeight:700,
                    textTransform:"uppercase", marginBottom:6 }}>Before You Submit</div>
                  <div style={{ fontSize:20, fontWeight:900, color:"white" }}>
                    Review Your Answers
                  </div>
                </div>

                {/* Instruction text */}
                <div style={{
                  background: `${p.color}11`, borderRadius: 12, padding: "12px 14px",
                  border: `1px solid ${p.color}33`, marginBottom: 20,
                  fontSize: 13, color: "rgba(255,255,255,0.7)", lineHeight: 1.6,
                  display: "flex", alignItems: "flex-start", gap: 10
                }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>ℹ️</span>
                  <span>
                    <strong>Click any question number (➜) to go back and edit your answer if needed.</strong> Read your selected answer carefully before final submission.
                  </span>
                </div>

                <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:14,
                  padding:14, border:`1px solid ${p.color}33`, marginBottom:20 }}>
                  {questions.map((q, idx) => {
                    const answer = answers[q.id];
                    const selectedChoice = answer ? q.choices.find(choice => choice.split(":")[0].trim() === answer) : null;
                    const selectedAnswerText = selectedChoice ? selectedChoice.split(":").slice(1).join(":").trim() : null;
                    
                    return (
                      <div key={q.id} onClick={() => {
                        setShowReview(false);
                        setCurrent(idx);
                        setSelected(null);
                        setRevealed(false);
                        setFromReview(true);
                        // Restore saved answer for this question
                        if (answer) {
                          const savedChoice = questions[idx].choices.find(choice => choice.split(":")[0].trim() === answer);
                          if (savedChoice) setSelected(savedChoice);
                        }
                      }} style={{
                        display:"flex", flexDirection:"column", gap:10, padding:"14px 12px",
                        borderBottom: idx < questions.length - 1 ? `1px solid rgba(255,255,255,0.05)` : "none",
                        borderRadius: 10,
                        background: "rgba(255,255,255,0.02)",
                        cursor: "pointer", transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
                      >
                        {/* Question number and status */}
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:36, height:36, borderRadius:"50%", flexShrink:0,
                            background: answer ? `${p.color}44` : "rgba(255,255,255,0.08)",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            fontSize:13, fontWeight:700, color: answer ? p.color : "rgba(255,255,255,0.3)",
                            border: `2px solid ${answer ? p.color : "rgba(255,255,255,0.1)"}`,
                            transition: "all 0.2s",
                          }}>
                            {idx + 1}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)" }}>
                              Question {idx + 1} of {total}
                            </div>
                          </div>
                          <div style={{ fontSize:11, color: p.color, opacity: 0.7, cursor: "pointer" }}>
                            ➜
                          </div>
                        </div>

                        {/* Question scenario */}
                        <div style={{ background:"rgba(255,255,255,0.03)", borderRadius:8, padding:"10px 12px",
                          borderLeft: `3px solid ${p.color}33`, fontSize:12, color:"rgba(255,255,255,0.8)",
                          lineHeight:1.5 }}>
                          {q.scenario}
                        </div>

                        {/* Selected answer - FULL TEXT */}
                        <div style={{ display:"flex", flexDirection: "column", gap: 6 }}>
                          <span style={{ fontSize:11, color:"rgba(255,255,255,0.5)", fontWeight: 600 }}>
                            Your Answer:
                          </span>
                          {answer ? (
                            <div style={{
                              fontSize:13, color: "rgba(255,255,255,0.95)", fontWeight: 600,
                              background: `${p.color}22`, padding: "10px 12px", borderRadius: 8,
                              border: `1.5px solid ${p.color}44`, lineHeight: 1.6
                            }}>
                              <span style={{ color: p.color, fontWeight: 700, fontSize: 14 }}>
                                {answer}
                              </span>
                              {" — "}
                              <span>{selectedAnswerText}</span>
                            </div>
                          ) : (
                            <div style={{
                              fontSize:13, color: "rgba(255,255,255,0.3)", fontStyle: "italic",
                              background: "rgba(255,255,255,0.02)", padding: "10px 12px", borderRadius: 8,
                              border: `1px dashed rgba(255,255,255,0.1)`
                            }}>
                              Not answered yet
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, paddingBottom:8 }}>
                  <button onClick={() => setShowReview(false)} style={{
                    padding:"12px", borderRadius:12, border:`1px solid ${p.color}44`,
                    background:"rgba(255,255,255,0.04)", color:p.color,
                    fontSize:13, fontWeight:600, cursor:"pointer",
                    fontFamily:"inherit", transition:"all 0.2s",
                  }}>← Go Back</button>
                  <button onClick={() => {
                    setShowReview(false);
                    handleSubmit();
                  }} style={{
                    padding:"13px", borderRadius:12, border:"none",
                    background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
                    color:"white", fontSize:13, fontWeight:800, cursor:"pointer",
                    fontFamily:"inherit", boxShadow:`0 4px 20px ${p.colorGlow}`,
                  }}>✓ Submit & See Results</button>
                </div>
              </div>
            </div>
          )}
        </>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// RETAKE LOCKED MODAL
// ─────────────────────────────────────────────────────────
function RetakeLockedModal({ persona, onClose }) {
  const p = PERSONAS[persona];
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:400,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      animation:"bfFadeIn 0.2s ease both", fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      <div style={{
        background:`linear-gradient(135deg,#0d0d1a,#1a0a00)`,
        border:"2px solid #F59E0B", borderRadius:24,
        padding:"36px 28px", maxWidth:340, width:"100%", textAlign:"center",
        boxShadow:"0 0 60px rgba(245,158,11,0.25)",
        animation:"bfLvlPop 0.45s cubic-bezier(0.34,1.56,0.64,1) both",
      }}>
        <div style={{ fontSize:48, marginBottom:14 }}>⏳</div>
        <div style={{ fontSize:10, letterSpacing:3, color:"#F59E0B", fontWeight:700,
          textTransform:"uppercase", marginBottom:8 }}>Retake Pending</div>
        <div style={{ fontSize:22, fontWeight:900, color:"white", marginBottom:12 }}>
          Hold tight!
        </div>
        <div style={{ fontSize:14, color:"rgba(255,255,255,0.55)", lineHeight:1.7, marginBottom:24 }}>
          Please wait for the <strong style={{ color:"#F59E0B" }}>retake notification</strong> from your trainer before attempting again.
          <br/><br/>
          Use this time to review the process guidelines and strengthen your knowledge.
        </div>
        <div style={{ background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.3)",
          borderRadius:14, padding:"12px 16px", marginBottom:24,
          fontSize:12, color:"rgba(255,255,255,0.5)", lineHeight:1.6 }}>
          📩 Your trainer will unlock your retake once it becomes available.
        </div>
        <button onClick={onClose} style={{
          width:"100%", padding:"13px", borderRadius:14, border:"none",
          background:"linear-gradient(135deg,#F59E0B,#D97706)",
          color:"white", fontSize:14, fontWeight:700, cursor:"pointer",
          fontFamily:"inherit", boxShadow:"0 4px 20px rgba(245,158,11,0.3)",
        }}>Got it — I'll wait</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// PKT RESULTS MODAL
// ─────────────────────────────────────────────────────────
function PKTResultsModal({ result, persona, onClose }) {
  const p = PERSONAS[persona];
  const [collecting, setCollecting] = useState(false);
  const [collected,  setCollected]  = useState(false);
  const setTimer = useTimers();

  // Declared above the early return so the hook order stays stable.
  const handleCollect = useCallback(() => {
    if (!result?.passed) { onClose(); return; }
    setCollecting(true);
    setTimer(() => setCollected(true), 400);
    setTimer(() => onClose(), 3200);
  }, [result?.passed, onClose, setTimer]);

  if (!result) return null;
  const { score, correct, total, passed, xp: earnedXP, tokens: earnedT, month } = result;

  // Confetti pieces — 30 random particles
  const confetti = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left:  `${5 + Math.random() * 90}%`,
    delay: `${Math.random() * 0.6}s`,
    dur:   `${1.2 + Math.random() * 1.2}s`,
    size:  `${6 + Math.random() * 8}px`,
    color: ["#F59E0B","#10B981","#3B82F6","#8B5CF6","#EF4444","#EC4899","#FFF"][Math.floor(Math.random()*7)],
    rotate:`${Math.random()*360}deg`,
    shape: Math.random() > 0.5 ? "50%" : "2px",
  }));

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:400,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      animation:"bfFadeIn 0.2s ease both", fontFamily:"'Segoe UI',system-ui,sans-serif",
      overflow:"hidden" }}>

      {/* ── Confetti burst on collect ── */}
      {collecting && confetti.map(c => (
        <div key={c.id} style={{
          position:"absolute", top:"-10px", left:c.left,
          width:c.size, height:c.size,
          background:c.color,
          borderRadius:c.shape,
          transform:`rotate(${c.rotate})`,
          animation:`bfConfetti ${c.dur} ${c.delay} cubic-bezier(0.25,0.46,0.45,0.94) forwards`,
          pointerEvents:"none", zIndex:500,
        }}/>
      ))}

      {/* ── Extra burst rings ── */}
      {collecting && [0,1,2].map(i => (
        <div key={i} style={{
          position:"absolute", top:"50%", left:"50%",
          transform:"translate(-50%,-50%)",
          width:`${100+i*80}px`, height:`${100+i*80}px`,
          borderRadius:"50%",
          border:`3px solid ${[p.color,"#F59E0B","#10B981"][i]}`,
          opacity:0,
          animation:`bfRingBurst 0.8s ${i*0.15}s ease-out forwards`,
          pointerEvents:"none", zIndex:499,
        }}/>
      ))}

      <style>{`
        @keyframes bfConfetti {
          0%   { transform: translateY(0) rotate(0deg) scale(1); opacity:1; }
          100% { transform: translateY(100vh) rotate(720deg) scale(0.3); opacity:0; }
        }
        @keyframes bfRingBurst {
          0%   { transform:translate(-50%,-50%) scale(0.2); opacity:0.9; }
          100% { transform:translate(-50%,-50%) scale(3);   opacity:0; }
        }
        @keyframes bfScoreCount {
          from { transform:scale(0.5); opacity:0; }
          to   { transform:scale(1);   opacity:1; }
        }
        @keyframes bfShine {
          0%   { left:-100%; }
          100% { left:200%; }
        }
      `}</style>

      <div style={{
        background:`linear-gradient(135deg,#0d0d1a,${p.bg})`,
        border:`2px solid ${passed ? p.color : "#EF4444"}`,
        borderRadius:24, padding:"32px 24px", maxWidth:360, width:"100%",
        boxShadow:`0 0 ${collected ? "120px" : "80px"} ${passed ? p.colorGlow : "rgba(239,68,68,0.3)"}`,
        animation:"bfLvlPop 0.5s cubic-bezier(0.34,1.56,0.64,1) both",
        textAlign:"center", position:"relative", overflow:"hidden",
        transition:"box-shadow 0.4s",
      }}>

        {/* Shine sweep on collect */}
        {collecting && (
          <div style={{
            position:"absolute", top:0, left:"-100%", width:"60%", height:"100%",
            background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.12),transparent)",
            animation:"bfShine 0.8s ease forwards", pointerEvents:"none", zIndex:10,
          }}/>
        )}

        {/* Trophy / icon — bounces on collect */}
        <div style={{
          fontSize:52, marginBottom:10,
          animation: collecting ? "bfLvlPop 0.5s cubic-bezier(0.34,1.56,0.64,1)" : "none",
        }}>{passed ? "🏆" : "📚"}</div>

        <div style={{ fontSize:10, letterSpacing:3, fontWeight:700, textTransform:"uppercase",
          color: passed ? p.color : "#EF4444", marginBottom:8 }}>
          {month} PKT · {passed ? "PASSED" : "NOT YET"}
        </div>

        <div style={{ fontSize:48, fontWeight:900, color:"white",
          letterSpacing:"-2px", lineHeight:1, marginBottom:4,
          animation:"bfScoreCount 0.5s cubic-bezier(0.34,1.56,0.64,1) both" }}>{score}%</div>
        <div style={{ fontSize:14, color:"rgba(255,255,255,0.60)", marginBottom:22 }}>
          {correct} out of {total} correct
        </div>

        {passed ? (
          <div style={{
            background: collected ? `${p.color}30` : `${p.color}18`,
            border:`1px solid ${collected ? p.color : p.color+"44"}`,
            borderRadius:16, padding:18, marginBottom:20,
            transition:"all 0.4s",
          }}>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.7)", marginBottom:12 }}>
              {collected ? "🎊 Rewards collected!" : "🎉 Congratulations! You passed!"}
            </div>
            <div style={{ display:"flex", justifyContent:"center", gap:24 }}>
              <div style={{ animation: collected ? "bfLvlPop 0.5s 0.1s cubic-bezier(0.34,1.56,0.64,1) both" : "none" }}>
                <div style={{ fontSize:28, fontWeight:900, color:p.color }}>+{earnedXP}</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.60)", letterSpacing:1 }}>XP</div>
              </div>
              <div style={{ width:1, background:"rgba(255,255,255,0.1)" }}/>
              <div style={{ animation: collected ? "bfLvlPop 0.5s 0.2s cubic-bezier(0.34,1.56,0.64,1) both" : "none" }}>
                <div style={{ fontSize:28, fontWeight:900, color:"#F59E0B" }}>+{earnedT}</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.60)", letterSpacing:1 }}>ACN</div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)",
            borderRadius:16, padding:18, marginBottom:20 }}>
            <div style={{ fontSize:28, marginBottom:8 }}>⏳</div>
            <div style={{ fontSize:14, fontWeight:700, color:"#FCA5A5", marginBottom:6 }}>
              Standby for Retake
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.60)", lineHeight:1.6 }}>
              You need {PKT_PASS_SCORE}% to pass. Review the process guidelines and try again!
            </div>
          </div>
        )}

        <div style={{ marginBottom:20 }}>
          <div style={{ height:7, borderRadius:4, background:"rgba(255,255,255,0.08)",
            overflow:"hidden", marginBottom:6 }}>
            <div style={{ height:"100%", width:`${score}%`,
              background: passed
                ? `linear-gradient(90deg,${p.color},${p.colorDark})`
                : "linear-gradient(90deg,#EF4444,#DC2626)",
              borderRadius:4, transition:"width 0.8s ease" }}/>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between",
            fontSize:10, color:"rgba(255,255,255,0.55)" }}>
            <span>0%</span>
            <span style={{ color: passed ? p.color : "#EF4444", fontWeight:700 }}>
              {score}% — {passed ? "PASS ✓" : `Need ${PKT_PASS_SCORE}%`}
            </span>
            <span>100%</span>
          </div>
        </div>

        <button onClick={handleCollect} disabled={collecting} style={{
          width:"100%", padding:"13px", borderRadius:14, border:"none",
          background: passed
            ? collecting
              ? `linear-gradient(135deg,#F59E0B,#D97706)`
              : `linear-gradient(135deg,${p.color},${p.colorDark})`
            : "rgba(255,255,255,0.08)",
          color:"white", fontSize:14, fontWeight:700,
          cursor: collecting ? "default" : "pointer",
          fontFamily:"inherit",
          boxShadow: passed ? `0 4px 20px ${collecting ? "rgba(245,158,11,0.5)" : p.colorGlow}` : "none",
          transition:"all 0.3s",
          transform: collected ? "scale(0.97)" : "scale(1)",
        }}>
          {collected ? "🎊 Rewards Collected!" : passed ? "✓ Collect Rewards" : "Got it — I'll review"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// ── LIVE BINGO GAME ─────────────────────────────────────────
const BINGO_PATTERNS = [
  { id: "line",        name: "Any Line",   icon: "↔️", desc: "Row, Column, or Diagonal" },
  { id: "shape_x",     name: "X-Shape",    icon: "✖️", desc: "Both diagonals",           coords: [[0,0],[1,1],[2,2],[3,3],[4,4],[0,4],[1,3],[3,1],[4,0]] },
  { id: "shape_l",     name: "L-Shape",    icon: "∟",  desc: "First column & bottom row", coords: [[0,0],[1,0],[2,0],[3,0],[4,0],[4,1],[4,2],[4,3],[4,4]] },
  { id: "four_corners",name: "4 Corners",  icon: "🔳", desc: "Four outermost corners",   coords: [[0,0],[0,4],[4,0],[4,4]] },
  { id: "blackout",    name: "Blackout",   icon: "⬛", desc: "Every single square",      coords: "all" },
];

// ── BINGO RULES PANEL ──────────────────────────────────────────
// ─────────────────────────────────────────────────────────
// CERTIFICATE MODAL — generates printable PDF certificate
// ─────────────────────────────────────────────────────────
function CertificateModal({ award, quest, employeeProfile, onClose }) {
  const certId = `BF-${new Date(award.awardedAt || Date.now()).getFullYear()}-${
    String(new Date(award.awardedAt || Date.now()).getMonth()+1).padStart(2,"0")}-${
    String(Math.abs(((employeeProfile?.name||"X").charCodeAt(0)*31 + (quest?.id||"").charCodeAt(0)*7) % 9999)).padStart(4,"0")}`;

  const dateStr = new Date(award.awardedAt || Date.now()).toLocaleDateString("en-US", {
    year:"numeric", month:"long", day:"numeric"
  });

  const questColors = {
    aq1:  { primary:"#10B981", secondary:"#064E3B", accent:"#6EE7B7" },
    aq2:  { primary:"#3B82F6", secondary:"#1E3A5F", accent:"#93C5FD" },
    aq4:  { primary:"#8B5CF6", secondary:"#2E1065", accent:"#C4B5FD" },
    aq5:  { primary:"#F59E0B", secondary:"#451A03", accent:"#FCD34D" },
    aq6:  { primary:"#06B6D4", secondary:"#083344", accent:"#67E8F9" },
    aq7:  { primary:"#EC4899", secondary:"#500724", accent:"#F9A8D4" },
    aq8:  { primary:"#EF4444", secondary:"#450A0A", accent:"#FCA5A5" },
    aq9:  { primary:"#14B8A6", secondary:"#042F2E", accent:"#5EEAD4" },
    aq10: { primary:"#84CC16", secondary:"#1A2E05", accent:"#BEF264" },
  };

  const c = questColors[quest?.id] || { primary:"#F59E0B", secondary:"#1C1917", accent:"#FCD34D" };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const html = document.getElementById("botfit-certificate").outerHTML;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ascendix Certificate - ${employeeProfile?.name}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Inter:wght@300;400;600;700&display=swap');
            * { margin:0; padding:0; box-sizing:border-box;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important; }
            html, body {
              background: #0a0a0a !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body { display:flex; justify-content:center; align-items:center; min-height:100vh; }
            /* Force all backgrounds to print */
            div, span, section { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            @media print {
              html, body { background: #0a0a0a !important; padding:0; margin:0; }
              @page { size: landscape; margin:0; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body onload="window.print();window.close()">
          ${html}
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:900,
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:16, fontFamily:"'Segoe UI',system-ui,sans-serif", overflowY:"auto" }}>

      {/* Controls */}
      <div style={{ display:"flex", gap:10, marginBottom:16, flexShrink:0 }}>
        <button onClick={handlePrint} style={{
          padding:"10px 24px", borderRadius:12, border:"none", cursor:"pointer",
          background:"linear-gradient(135deg,#F59E0B,#D97706)",
          color:"#1f1a00", fontSize:13, fontWeight:800, fontFamily:"inherit",
          boxShadow:"0 4px 20px rgba(245,158,11,0.4)",
          display:"flex", alignItems:"center", gap:6,
        }}>📄 Download PDF</button>
        <button onClick={onClose} style={{
          padding:"10px 20px", borderRadius:12, border:"1px solid rgba(255,255,255,0.15)",
          background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.7)",
          fontSize:13, cursor:"pointer", fontFamily:"inherit",
        }}>✕ Close</button>
      </div>

      {/* Certificate */}
      <div id="botfit-certificate" style={{
        width:794, minHeight:562, background:`linear-gradient(135deg, ${c.secondary} 0%, #0a0a0a 50%, ${c.secondary} 100%)`,
        borderRadius:8, position:"relative", overflow:"hidden",
        boxShadow:"0 20px 80px rgba(0,0,0,0.8)",
        flexShrink:0,
      }}>
        {/* Outer border */}
        <div style={{ position:"absolute", inset:8, border:`2px solid ${c.primary}44`, borderRadius:4, pointerEvents:"none" }}/>
        <div style={{ position:"absolute", inset:12, border:`1px solid ${c.primary}22`, borderRadius:2, pointerEvents:"none" }}/>

        {/* Corner ornaments */}
        {[[0,0,"nw"],[0,"auto","ne"],["auto",0,"sw"],["auto","auto","se"]].map(([t,r,k]) => (
          <div key={k} style={{
            position:"absolute", top:t===0?16:"auto", bottom:t==="auto"?16:"auto",
            left:r==="auto"?"auto":16, right:r===0||r==="auto"?16:"auto",
            width:40, height:40,
            borderTop: (k==="nw"||k==="ne") ? `3px solid ${c.primary}` : "none",
            borderBottom: (k==="sw"||k==="se") ? `3px solid ${c.primary}` : "none",
            borderLeft: (k==="nw"||k==="sw") ? `3px solid ${c.primary}` : "none",
            borderRight: (k==="ne"||k==="se") ? `3px solid ${c.primary}` : "none",
          }}/>
        ))}

        {/* Background glow */}
        <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
          width:400, height:400, borderRadius:"50%", background:c.primary,
          filter:"blur(120px)", opacity:0.06, pointerEvents:"none" }}/>

        {/* Content */}
        <div style={{ position:"relative", zIndex:1, padding:"40px 60px", textAlign:"center" }}>

          {/* Header */}
          <div style={{ marginBottom:6 }}>
            <div style={{ fontSize:11, letterSpacing:6, color:c.accent, fontWeight:700,
              textTransform:"uppercase", opacity:0.8 }}>Innovation Team Philippines · Ascendix Platform</div>
          </div>

          {/* Quest icon */}
          <div style={{ fontSize:48, margin:"12px 0 8px" }}>{quest?.icon || "🎖"}</div>

          {/* Certificate title */}
          <div style={{ fontSize:13, letterSpacing:4, color:c.accent, fontWeight:600,
            textTransform:"uppercase", marginBottom:8, opacity:0.7 }}>
            Certificate of Recognition
          </div>

          <div style={{ fontSize:32, fontWeight:900, color:c.primary, letterSpacing:"-0.5px",
            textShadow:`0 0 30px ${c.primary}66`, marginBottom:16, lineHeight:1.1 }}>
            {quest?.text?.trim() || "Outstanding Achievement"}
          </div>

          {/* Divider */}
          <div style={{ display:"flex", alignItems:"center", gap:12, margin:"0 auto 16px", maxWidth:400 }}>
            <div style={{ flex:1, height:1, background:`linear-gradient(to right, transparent, ${c.primary}88)` }}/>
            <div style={{ fontSize:14, color:c.primary }}>✦</div>
            <div style={{ flex:1, height:1, background:`linear-gradient(to left, transparent, ${c.primary}88)` }}/>
          </div>

          {/* This certifies */}
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)", letterSpacing:2,
            textTransform:"uppercase", marginBottom:6 }}>This certifies that</div>

          {/* Employee name */}
          <div style={{ fontSize:40, fontWeight:900, color:"white", letterSpacing:"-1px",
            textShadow:"0 2px 20px rgba(255,255,255,0.2)", marginBottom:6, lineHeight:1 }}>
            {employeeProfile?.name || "Employee"}
          </div>

          <div style={{ fontSize:12, color:c.accent, fontWeight:600, marginBottom:4 }}>
            {employeeProfile?.department} · {employeeProfile?.teamName}
          </div>

          {/* Description */}
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.6)", marginBottom:20, lineHeight:1.6 }}>
            {quest?.desc || "has demonstrated outstanding performance and dedication"}
          </div>

          {/* Footer row */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end",
            borderTop:`1px solid ${c.primary}33`, paddingTop:16, marginTop:4 }}>

            {/* Awarded by */}
            <div style={{ textAlign:"left" }}>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", letterSpacing:1,
                textTransform:"uppercase", marginBottom:4 }}>Awarded By</div>
              <div style={{ fontSize:13, fontWeight:700, color:"white" }}>{award.awardedBy || "R&R Team"}</div>
              <div style={{ fontSize:10, color:c.accent }}>Rewards & Recognition</div>
            </div>

            {/* Bot seal */}
            <div style={{ textAlign:"center" }}>
              <div style={{ width:56, height:56, borderRadius:"50%", margin:"0 auto 4px",
                background:`radial-gradient(circle, ${c.primary}33, ${c.primary}11)`,
                border:`2px solid ${c.primary}66`,
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:28 }}>🤖</div>
              <div style={{ fontSize:8, color:c.accent, letterSpacing:1 }}>BOTFIT VERIFIED</div>
            </div>

            {/* Date & ID */}
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", letterSpacing:1,
                textTransform:"uppercase", marginBottom:4 }}>Date Issued</div>
              <div style={{ fontSize:13, fontWeight:700, color:"white" }}>{dateStr}</div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", marginTop:2, letterSpacing:1 }}>
                ID: {certId}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BingoRulesPanel({ p, selectedPattern }) {
  const patternGuides = {
    line:        { visual: ["✅⬜⬜⬜⬜","✅⬜⬜⬜⬜","✅⬜⬜⬜⬜","✅⬜⬜⬜⬜","✅⬜⬜⬜⬜"], hint:"Any full row, column, or diagonal" },
    shape_x:     { visual: ["✅⬜⬜⬜✅","⬜✅⬜✅⬜","⬜⬜✅⬜⬜","⬜✅⬜✅⬜","✅⬜⬜⬜✅"], hint:"Both diagonals form an X" },
    shape_l:     { visual: ["✅⬜⬜⬜⬜","✅⬜⬜⬜⬜","✅⬜⬜⬜⬜","✅⬜⬜⬜⬜","✅✅✅✅✅"], hint:"Left column + bottom row" },
    four_corners:{ visual: ["✅⬜⬜⬜✅","⬜⬜⬜⬜⬜","⬜⬜⬜⬜⬜","⬜⬜⬜⬜⬜","✅⬜⬜⬜✅"], hint:"Only the 4 corner squares" },
    blackout:    { visual: ["✅✅✅✅✅","✅✅✅✅✅","✅✅✅✅✅","✅✅✅✅✅","✅✅✅✅✅"], hint:"Every single square marked" },
  };
  const guide = patternGuides[selectedPattern.id] || patternGuides.line;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflowY:"auto", padding:"14px 12px", gap:14 }}>
      {/* How to Play */}
      <div>
        <div style={{ fontSize:9, fontWeight:800, color:"#F59E0B", letterSpacing:2, marginBottom:10, textTransform:"uppercase" }}>📖 How to Play</div>
        {[
          { icon:"1️⃣", text:"Pick your goal pattern before the game starts." },
          { icon:"2️⃣", text:"Press Generate My Card to get a unique BINGO card." },
          { icon:"3️⃣", text:"Listen as the announcer calls out numbers (e.g. B-7)." },
          { icon:"4️⃣", text:"Tap a square on your card to mark a called number." },
          { icon:"5️⃣", text:"The FREE space (center) is automatically marked." },
          { icon:"6️⃣", text:"Complete your pattern first to win 100 ACN Tokens!" },
        ].map((r,i) => (
          <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:8,
            padding:"7px 10px", borderRadius:10,
            background: r.warn ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.03)",
            border: `1px solid ${r.warn ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.06)"}` }}>
            <span style={{ fontSize:13, flexShrink:0 }}>{r.icon}</span>
            <span style={{ fontSize:10, color: r.warn ? "#F87171" : "rgba(255,255,255,0.75)", lineHeight:1.5 }}>{r.text}</span>
          </div>
        ))}
      </div>

      {/* Current Pattern Visual */}
      <div style={{ background:"rgba(255,255,255,0.03)", border:`1px solid ${p.color}33`, borderRadius:12, padding:"12px 10px" }}>
        <div style={{ fontSize:9, fontWeight:800, color:p.color, letterSpacing:2, marginBottom:8, textAlign:"center" }}>
          ACTIVE PATTERN: {selectedPattern.icon} {selectedPattern.name.toUpperCase()}
        </div>
        <div style={{ fontFamily:"monospace", fontSize:11, lineHeight:"20px", textAlign:"center", marginBottom:6 }}>
          {guide.visual.map((row,i) => (
            <div key={i}>{row}</div>
          ))}
        </div>
        <div style={{ fontSize:10, color:"rgba(255,255,255,0.5)", textAlign:"center", fontStyle:"italic" }}>{guide.hint}</div>
      </div>

      {/* All Patterns Quick Reference */}
      <div>
        <div style={{ fontSize:9, fontWeight:800, color:"rgba(255,255,255,0.4)", letterSpacing:2, marginBottom:8, textTransform:"uppercase" }}>All Patterns</div>
        {BINGO_PATTERNS.map(pat => (
          <div key={pat.id} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, padding:"6px 10px", borderRadius:8,
            background: pat.id === selectedPattern.id ? `${p.color}15` : "rgba(255,255,255,0.02)",
            border:`1px solid ${pat.id === selectedPattern.id ? p.color+"44" : "rgba(255,255,255,0.05)"}` }}>
            <span style={{ fontSize:13 }}>{pat.icon}</span>
            <div>
              <div style={{ fontSize:10, fontWeight:700, color: pat.id === selectedPattern.id ? p.color : "rgba(255,255,255,0.8)" }}>{pat.name}</div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.4)" }}>{pat.desc}</div>
            </div>
            {pat.id === selectedPattern.id && <span style={{ marginLeft:"auto", fontSize:9, color:p.color, fontWeight:800 }}>ACTIVE ✓</span>}
          </div>
        ))}
      </div>

      {/* Token reward reminder */}
      <div style={{ background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
        <div style={{ fontSize:18, marginBottom:4 }}>🪙</div>
        <div style={{ fontSize:11, fontWeight:800, color:"#F59E0B" }}>Prize: 100 ACN Tokens</div>
        <div style={{ fontSize:9, color:"rgba(255,255,255,0.5)", marginTop:3 }}>First correct BINGO wins!</div>
      </div>
    </div>
  );
}

// ── BINGO ANNOUNCEMENTS PANEL ───────────────────────────────────
function BingoAnnouncementsPanel({ announcements, p }) {
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [announcements]);

  const typeStyle = {
    winner:  { bg:"rgba(245,158,11,0.12)", border:"rgba(245,158,11,0.4)", color:"#F59E0B", icon:"🏆" },
    call:    { bg:"rgba(59,130,246,0.15)", border:"rgba(59,130,246,0.5)", color:"#93C5FD", icon:"📣", large:true },
    system:  { bg:"rgba(255,255,255,0.03)", border:"rgba(255,255,255,0.08)", color:"rgba(255,255,255,0.7)", icon:"📢" },
    join:    { bg:"rgba(16,185,129,0.08)", border:"rgba(16,185,129,0.25)", color:"#6EE7B7", icon:"🎮" },
    pattern: { bg:"rgba(139,92,246,0.08)", border:"rgba(139,92,246,0.25)", color:"#C4B5FD", icon:"🎯" },
    event:   { bg:"rgba(255,255,255,0.02)", border:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.55)", icon:"📋" },
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>
      {/* Header */}
      <div style={{ padding:"10px 12px 8px", borderBottom:"1px solid rgba(255,255,255,0.07)", flexShrink:0, textAlign:"center" }}>
        <div style={{ fontSize:9, fontWeight:800, color:p.color, letterSpacing:2 }}>📡 ANNOUNCEMENT CHANNEL</div>
        <div style={{ fontSize:8, color:"rgba(255,255,255,0.35)", marginTop:2 }}>Live event feed</div>
      </div>

      {/* Live ticker highlight (latest) */}
      {announcements.length > 0 && (
        <div style={{ padding:"6px 12px", borderBottom:"1px solid rgba(255,255,255,0.05)", flexShrink:0,
          background: announcements[announcements.length-1]?.type === "winner" ? "rgba(245,158,11,0.08)" : "rgba(255,255,255,0.02)" }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:1, color:"rgba(255,255,255,0.35)", marginBottom:2 }}>LATEST</div>
          <div style={{ fontSize:10, fontWeight:700, lineHeight:1.4,
            color: announcements[announcements.length-1]?.type === "winner" ? "#F59E0B" : p.color }}>
            {announcements[announcements.length-1]?.text}
          </div>
        </div>
      )}

      {/* Full log */}
      <div ref={scrollRef} style={{ flex:1, overflowY:"auto", padding:"10px 10px", display:"flex", flexDirection:"column", gap:6 }}>
        {announcements.length === 0 ? (
          <div style={{ textAlign:"center", color:"rgba(255,255,255,0.25)", fontSize:10, marginTop:30, lineHeight:2 }}>
            📢<br/>No announcements yet.<br/>Start the game to see live updates!
          </div>
        ) : (
          [...announcements].reverse().map((ann, i) => {
            const s = typeStyle[ann.type] || typeStyle.event;
            return (
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:7, padding: s.large ? "10px 12px" : "8px 10px", borderRadius:10,
                background:s.bg, border:`1px solid ${s.border}` }}>
                <span style={{ fontSize: s.large ? 16 : 12, flexShrink:0, marginTop:1 }}>{s.icon}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize: s.large ? 18 : 10, fontWeight: s.large ? 900 : 400,
                    color:s.color, lineHeight:1.4, wordBreak:"break-word",
                    letterSpacing: s.large ? 4 : 0 }}>{ann.text}</div>
                  {ann.time && <div style={{ fontSize:8, color:"rgba(255,255,255,0.25)", marginTop:2 }}>{ann.time}</div>}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Stats bar */}
      <div style={{ padding:"8px 12px", borderTop:"1px solid rgba(255,255,255,0.06)", flexShrink:0,
        display:"flex", gap:10, justifyContent:"center" }}>
        {[
          { label:"Total", value:announcements.length, color:"rgba(255,255,255,0.5)" },
          { label:"Winners", value:announcements.filter(a=>a.type==="winner").length, color:"#F59E0B" },
          { label:"Players", value:announcements.filter(a=>a.type==="join").length, color:"#6EE7B7" },
        ].map((s,i) => (
          <div key={i} style={{ textAlign:"center" }}>
            <div style={{ fontSize:14, fontWeight:900, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:8, color:"rgba(255,255,255,0.3)", letterSpacing:1 }}>{s.label.toUpperCase()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveBingoGame({ persona, employeeProfile, onWinBingo, bingoMaxWinners = 1, bingoEventLabel = "Live Bingo Event", calledNumbers = [], teamName = "", onWinReport }) {
  const p = PERSONAS[persona] || PERSONAS.ACE;
  const [gameStarted, setGameStarted]         = useState(false);
  const [bingoCard, setBingoCard]             = useState(null);
  const [marked, setMarked]                   = useState({});
  const [showBingoModal, setShowBingoModal]   = useState(false);
  const [selectedPattern, setSelectedPattern] = useState(BINGO_PATTERNS[0]);
  const [sideTab, setSideTab]                 = useState("rules"); // "rules" | "announce"
  const [winnerCount, setWinnerCount]         = useState(0);
  const [winnersList, setWinnersList]         = useState([]); // [{ name, pattern, position }]
  const [eventClosed, setEventClosed]         = useState(false);
  const [announcements, setAnnouncements]     = useState([
    { type:"system", text:"Welcome to Live Bingo Event! Select your pattern and generate your card to play.", time:"" }
  ]);
  const prevCalledRef = useRef([]);
  const setTimer      = useTimers();

  const addAnnouncement = useCallback((text, type = "event") => {
    const time = new Date().toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
    setAnnouncements(prev => [...prev, { type, text, time }]);
  }, []);

  // ── Watch calledNumbers prop — track new numbers for floating badge only ──
  useEffect(() => {
    const prev = prevCalledRef.current;
    const newNums = calledNumbers.filter(n => !prev.includes(n));
    if (newNums.length > 0) {
      // No announcement in Live Feed — floating badge handles display
      prevCalledRef.current = calledNumbers;
    }
  }, [calledNumbers]);

  const startGame = () => {
    const columns = {};
    const ranges = { B:[1,15], I:[16,30], N:[31,45], G:[46,60], O:[61,75] };
    Object.entries(ranges).forEach(([letter, range]) => {
      const available = Array.from({ length: range[1]-range[0]+1 }, (_,i) => range[0]+i);
      const nums = [];
      for (let i = 0; i < 5; i++) {
        const idx = Math.floor(Math.random() * available.length);
        nums.push(available[idx]);
        available.splice(idx, 1);
      }
      if (letter === "N") nums[2] = "FREE";
      columns[letter] = nums;
    });
    setBingoCard(columns);
    setMarked({ B:[0,0,0,0,0], I:[0,0,0,0,0], N:[0,0,1,0,0], G:[0,0,0,0,0], O:[0,0,0,0,0] });
    setGameStarted(true);
    const player = employeeProfile?.name || "A player";
    addAnnouncement(`${player} joined the game with pattern: ${selectedPattern.icon} ${selectedPattern.name}`, "join");
    // Switch to announce tab so player sees live feed
    setSideTab("announce");
  };

  // Check if marked cells satisfy a given pattern
  const checkPatternMatch = (m, pattern) => {
    const L = ["B","I","N","G","O"];
    if (pattern.coords === "all") return L.every(l => m[l].every(v => v === 1));
    if (pattern.coords) return pattern.coords.every(([r,c]) => m[L[c]][r] === 1);
    for (let i = 0; i < 5; i++) {
      if (L.every(l => m[l][i] === 1)) return true;
      if (m[L[i]].every(v => v === 1)) return true;
    }
    if ([0,1,2,3,4].every(i => m[L[i]][i] === 1)) return true;
    if ([0,1,2,3,4].every(i => m[L[i]][4-i] === 1)) return true;
    return false;
  };

  const markNumber = (letter, idx) => {
    if (!marked || !marked[letter]) return;
    const num = bingoCard[letter][idx];
    if (num === "FREE") return;
    // Block marking if no numbers have been called yet
    if (calledNumbers.length === 0) return;
    // Enforce: only allow marking if number was officially called
    const key = `${letter}-${num}`;
    if (!calledNumbers.includes(key)) return;
    const newMarked = { ...marked, [letter]: [...marked[letter]] };
    newMarked[letter][idx] = newMarked[letter][idx] === 1 ? 0 : 1;
    setMarked(newMarked);

    const player = employeeProfile?.name || "A Player";

    if (checkPatternMatch(newMarked, selectedPattern)) {
      const newCount = winnerCount + 1;
      const winEntry = {
        position: newCount,
        name:     player,
        pattern:  `${selectedPattern.icon} ${selectedPattern.name}`,
        time:     new Date().toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }),
      };
      setWinnerCount(newCount);
      setWinnersList(prev => [...prev, winEntry]);
      // Report win to API so admin sees it in real time
      if (onWinReport) onWinReport(winEntry);
      if (teamName) {
        fetch("/api/save-progress", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "setBingo",
            pin: "botfit-admin-2026",
            team: teamName,
            addWinner: winEntry,
          }),
        }).catch(() => {});
      }
      setShowBingoModal(true);
      addAnnouncement(`🏆 BINGO! ${player} completed the ${selectedPattern.icon} ${selectedPattern.name} pattern! (Winner ${newCount}/${bingoMaxWinners})`, "winner");
      setSideTab("announce");
      if (newCount >= bingoMaxWinners) {
        setTimer(() => {
          setEventClosed(true);
          addAnnouncement(`🎉 All ${bingoMaxWinners} winner${bingoMaxWinners>1?"s have":" has"} been found! Event is now closed. Great game everyone!`, "system");
        }, 800);
      }
    }
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:500, background:"rgba(0,0,0,0.3)", position:"relative" }}>

      {/* ── CENTERED TITLE ── */}
      <div style={{ textAlign:"center", padding:"12px 16px 10px", borderBottom:"1px solid rgba(255,255,255,0.07)", flexShrink:0 }}>
        <div style={{ fontSize:17, fontWeight:900, color:"#F59E0B", letterSpacing:2, textTransform:"uppercase" }}>
          🎰 {bingoEventLabel}
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:12, marginTop:4 }}>
          <div style={{ fontSize:10, color:"rgba(255,255,255,0.50)", letterSpacing:1 }}>
            Mark your card as numbers are called
          </div>
          <div style={{ fontSize:10, fontWeight:800, color:"#F59E0B",
            background:"rgba(245,158,11,0.12)", border:"1px solid rgba(245,158,11,0.3)",
            padding:"2px 10px", borderRadius:20 }}>
            🏆 {winnerCount}/{bingoMaxWinners} winners
          </div>
        </div>

        {/* ── WINNERS LIST ── */}
        {winnersList.length > 0 && (
          <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:4 }}>
            <div style={{ fontSize:9, fontWeight:800, color:"rgba(255,255,255,0.35)",
              letterSpacing:2, textTransform:"uppercase", marginBottom:2 }}>Winners</div>
            {winnersList.map((w, i) => (
              <div key={i} style={{
                display:"flex", alignItems:"center", gap:8, justifyContent:"center",
                padding:"5px 14px", borderRadius:20, margin:"0 auto",
                background: i === 0 ? "rgba(245,158,11,0.12)"
                          : i === 1 ? "rgba(156,163,175,0.10)"
                          : "rgba(180,83,9,0.10)",
                border: `1px solid ${i === 0 ? "rgba(245,158,11,0.35)"
                                   : i === 1 ? "rgba(156,163,175,0.25)"
                                   : "rgba(180,83,9,0.25)"}`,
              }}>
                <span style={{ fontSize:13 }}>
                  {w.position === 1 ? "🥇" : w.position === 2 ? "🥈" : "🥉"}
                </span>
                <span style={{ fontSize:11, fontWeight:800, color:"white" }}>{w.name}</span>
                <span style={{ fontSize:9, color:"rgba(255,255,255,0.45)" }}>{w.pattern}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── PATTERN SELECTOR ── */}
      <div style={{ padding:"10px 14px 8px", borderBottom:"1px solid rgba(255,255,255,0.06)", flexShrink:0 }}>
        <div style={{ fontSize:9, color:"#F59E0B", fontWeight:800, letterSpacing:2, marginBottom:7, textAlign:"center" }}>GOAL PATTERN</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", justifyContent:"center" }}>
          {BINGO_PATTERNS.map(pat => (
            <div key={pat.id}
              onClick={() => {
                if (gameStarted) return;
                setSelectedPattern(pat);
                const player = employeeProfile?.name || "A player";
                addAnnouncement(`${player} selected pattern: ${pat.icon} ${pat.name}`, "pattern");
              }}
              style={{
                padding:"5px 13px", borderRadius:20, cursor: gameStarted ? "not-allowed" : "pointer",
                background: selectedPattern.id === pat.id ? `${p.color}22` : "rgba(255,255,255,0.04)",
                border:`1px solid ${selectedPattern.id === pat.id ? p.color : "rgba(255,255,255,0.10)"}`,
                opacity: gameStarted && selectedPattern.id !== pat.id ? 0.35 : 1,
                transition:"all 0.15s", display:"flex", alignItems:"center", gap:5,
              }}>
              <span style={{ fontSize:11 }}>{pat.icon}</span>
              <span style={{ fontSize:10, fontWeight:700, color: selectedPattern.id === pat.id ? p.color : "rgba(255,255,255,0.8)" }}>{pat.name}</span>
              {selectedPattern.id === pat.id && <span style={{ fontSize:9, color:p.color }}>✓</span>}
            </div>
          ))}
        </div>
      </div>

      {/* ── MAIN BODY: Card (left) + Sidebar tabs (right) ── */}
      <div style={{ display:"flex", flex:1, minHeight:0 }}>

        {/* ── LATEST CALL BADGE ── */}
        {calledNumbers.length > 0 && (
          <div style={{ position:"absolute", top:8, left:"50%", transform:"translateX(-50%)",
            zIndex:10, pointerEvents:"none" }}>
            <div style={{ padding:"4px 16px", borderRadius:20, fontSize:13, fontWeight:900,
              background:"linear-gradient(135deg,#1D4ED8,#2563EB)",
              color:"white", letterSpacing:4, boxShadow:"0 4px 20px rgba(59,130,246,0.5)",
              border:"1px solid rgba(59,130,246,0.6)" }}>
              📣 {calledNumbers[calledNumbers.length-1]}
            </div>
          </div>
        )}
        <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", padding:"14px 10px 14px", overflowY:"auto" }}>
          {!gameStarted ? (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:16, maxWidth:320, width:"100%" }}>
              {/* Placeholder card */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:4, padding:12, width:"100%",
                background:"rgba(255,255,255,0.03)", border:`2px dashed ${p.color}55`, borderRadius:16 }}>
                {["B","I","N","G","O"].map(l => (
                  <div key={l} style={{ textAlign:"center", fontSize:15, fontWeight:900, color:"#F59E0B",
                    paddingBottom:5, textShadow:"0 0 8px rgba(245,158,11,0.5)" }}>{l}</div>
                ))}
                {Array.from({length:25}).map((_,i) => (
                  <div key={i} style={{
                    aspectRatio:"1", borderRadius:7,
                    background: i===12 ? `${p.color}33` : "rgba(255,255,255,0.06)",
                    border:`1px solid ${i===12 ? p.color : "rgba(255,255,255,0.12)"}`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:10, color: i===12 ? p.color : "rgba(255,255,255,0.3)", fontWeight:800,
                  }}>{i===12 ? "⭐" : "?"}</div>
                ))}
              </div>
              <button onClick={startGame}
                style={{ padding:"13px 36px", background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
                  color:"white", border:"none", borderRadius:12, fontWeight:900, fontSize:14,
                  cursor:"pointer", boxShadow:`0 8px 25px ${p.colorGlow}`, width:"100%", maxWidth:240 }}>
                🎰 Generate My Card
              </button>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.35)", textAlign:"center", lineHeight:1.6 }}>
                Check the <strong style={{color:"rgba(255,255,255,0.55)"}}>📖 Rules</strong> tab on the right<br/>before you start!
              </div>
            </div>
          ) : (
            <div style={{ width:"100%", maxWidth:360 }}>
              {/* BINGO headers */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:4, marginBottom:4 }}>
                {["B","I","N","G","O"].map(l => (
                  <div key={l} style={{ textAlign:"center", fontSize:"clamp(14px,4vw,20px)", fontWeight:900, color:"#F59E0B",
                    textShadow:"0 0 12px rgba(245,158,11,0.6)", padding:"2px 0" }}>{l}</div>
                ))}
              </div>
              {/* Card grid — uses aspect-ratio so it works on all screen sizes */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:4 }}>
                {["B","I","N","G","O"].map(l => (
                  <div key={l} style={{ display:"flex", flexDirection:"column", gap:4 }}>
                    {bingoCard[l].map((num, i) => {
                      const isMarked  = marked[l][i] === 1;
                      const isFree    = num === "FREE";
                      const key       = `${l}-${num}`;
                      const isCalled  = isFree || calledNumbers.includes(key);
                      const isLatest  = calledNumbers[calledNumbers.length - 1] === key;
                      return (
                        <div key={i}
                          onClick={() => isCalled && !isFree && markNumber(l, i)}
                          style={{
                            aspectRatio:"1",
                            borderRadius:8,
                            background: isMarked
                              ? `linear-gradient(135deg,${p.color},${p.colorDark})`
                              : isFree   ? `${p.color}33`
                              : isLatest ? "rgba(245,158,11,0.25)"
                              : isCalled ? "rgba(255,255,255,0.12)"
                              : "rgba(255,255,255,0.07)",
                            border:`2px solid ${
                              isMarked   ? p.color
                              : isFree   ? p.color
                              : isLatest ? "#F59E0B"
                              : isCalled ? "rgba(255,255,255,0.25)"
                              : "rgba(255,255,255,0.1)"}`,
                            display:"flex", flexDirection:"column",
                            alignItems:"center", justifyContent:"center",
                            cursor: isCalled && !isFree ? "pointer" : "default",
                            transition:"background 0.15s, border 0.15s",
                            fontSize:"clamp(12px,3.5vw,17px)", fontWeight:900,
                            color: isMarked   ? "white"
                              : isFree        ? p.color
                              : isLatest      ? "#F59E0B"
                              : isCalled      ? "white"
                              : "rgba(255,255,255,0.5)",
                            boxShadow: isMarked  ? `0 2px 10px ${p.colorGlow}`
                              : isLatest ? "0 0 10px rgba(245,158,11,0.5)" : "none",
                            userSelect:"none", WebkitUserSelect:"none",
                          }}>
                          {isFree ? "⭐" : num}
                          {isMarked && !isFree && (
                            <div style={{ fontSize:"clamp(6px,2vw,9px)", marginTop:1, color:"rgba(255,255,255,0.8)" }}>✓</div>
                          )}
                          {isLatest && !isMarked && (
                            <div style={{ fontSize:"clamp(5px,1.5vw,8px)", color:"#F59E0B", fontWeight:800 }}>NEW</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div style={{ textAlign:"center", marginTop:12 }}>
                <button onClick={() => {
                  setGameStarted(false); setBingoCard(null); setMarked({}); setShowBingoModal(false);
                  setWinnerCount(0); setWinnersList([]); setEventClosed(false);
                  const player = employeeProfile?.name || "A player";
                  addAnnouncement(`${player} generated a new card.`, "system");
                }}
                  style={{ background:"none", border:`1px solid rgba(255,255,255,0.12)`, color:"rgba(255,255,255,0.55)",
                    cursor:"pointer", fontSize:11, padding:"6px 18px", borderRadius:8 }}>
                  🔄 New Card
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── SIDEBAR: Rules / Announcements tabs ── */}
        <div style={{ width:220, borderLeft:"1px solid rgba(255,255,255,0.07)", display:"flex", flexDirection:"column", flexShrink:0 }}>
          {/* Tab headers */}
          <div style={{ display:"flex", borderBottom:"1px solid rgba(255,255,255,0.07)", flexShrink:0 }}>
            {[
              { id:"rules",    label:"📖 Rules" },
              { id:"announce", label:"📡 Live Feed" },
            ].map(tab => (
              <div key={tab.id}
                onClick={() => setSideTab(tab.id)}
                style={{ flex:1, padding:"9px 4px", textAlign:"center", fontSize:9, fontWeight:800, letterSpacing:1, cursor:"pointer",
                  color: sideTab === tab.id ? p.color : "rgba(255,255,255,0.35)",
                  borderBottom: sideTab === tab.id ? `2px solid ${p.color}` : "2px solid transparent",
                  background: sideTab === tab.id ? `${p.color}08` : "transparent",
                  transition:"all 0.15s", textTransform:"uppercase",
                  position:"relative" }}>
                {tab.label}
                {/* Red dot badge on announce tab when new events */}
                {tab.id === "announce" && sideTab !== "announce" && announcements.length > 1 && (
                  <span style={{ position:"absolute", top:6, right:10, width:6, height:6, borderRadius:"50%",
                    background:"#EF4444", display:"inline-block" }} />
                )}
              </div>
            ))}
          </div>
          {/* Tab content */}
          <div style={{ flex:1, overflow:"hidden", minHeight:0 }}>
            {sideTab === "rules" ? (
              <BingoRulesPanel p={p} selectedPattern={selectedPattern} />
            ) : (
              <BingoAnnouncementsPanel announcements={announcements} p={p} />
            )}
          </div>
        </div>

      </div>{/* end main body */}

      {/* ── EVENT CLOSED OVERLAY ── */}
      {eventClosed && !showBingoModal && (
        <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.88)", zIndex:200,
          display:"flex", alignItems:"center", justifyContent:"center", borderRadius:18 }}>
          <div style={{ textAlign:"center", padding:"36px 28px", maxWidth:320 }}>
            <div style={{ fontSize:56, marginBottom:12 }}>🎊</div>
            <div style={{ fontSize:22, fontWeight:900, color:"#F59E0B", marginBottom:8 }}>
              Event Complete!
            </div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.65)", lineHeight:1.7, marginBottom:6 }}>
              All <strong style={{color:"#F59E0B"}}>{bingoMaxWinners}</strong> winner{bingoMaxWinners>1?"s have":" has"} been found.
            </div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.40)" }}>
              The admin will start a new round soon. 🏆
            </div>
          </div>
        </div>
      )}

      {/* ── BINGO WIN MODAL ── */}
      {showBingoModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ textAlign:"center", background:"linear-gradient(135deg,#1f1a00,#0f1a0f)",
            border:"3px solid #F59E0B", borderRadius:24, padding:"40px 36px", maxWidth:360,
            boxShadow:"0 0 80px rgba(245,158,11,0.4)" }}>
            <div style={{ fontSize:72, marginBottom:12 }}>🎉</div>
            <div style={{ fontSize:40, fontWeight:900, color:"#F59E0B", marginBottom:8 }}>BINGO!</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)", marginBottom:4, letterSpacing:1 }}>
              WINNER #{winnerCount} OF {bingoMaxWinners}
            </div>
            <div style={{ fontSize:14, color:"rgba(255,255,255,0.7)", marginBottom:6, lineHeight:1.6 }}>
              You completed the <strong style={{ color:"#F59E0B" }}>{selectedPattern.name}</strong> pattern!
            </div>
            <div style={{ fontSize:13, color:"#10B981", fontWeight:700, marginBottom:24 }}>
              🪙 +100 ACN Tokens awarded!
            </div>
            <button onClick={() => {
              if (onWinBingo) onWinBingo();
              setShowBingoModal(false); setGameStarted(false);
              addAnnouncement(`🏆 ${employeeProfile?.name || "A player"} claimed their BINGO reward! 🎉`, "winner");
              setSideTab("announce");
            }}
              style={{ padding:"13px 36px", background:"linear-gradient(135deg,#F59E0B,#D97706)",
                border:"none", borderRadius:12, color:"#1f1a00", fontSize:15, fontWeight:900,
                cursor:"pointer", width:"100%", marginBottom:10 }}>
              🏆 Claim 100 ACN Tokens
            </button>
            <button onClick={() => setShowBingoModal(false)}
              style={{ padding:"10px 24px", background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.15)",
                borderRadius:10, color:"rgba(255,255,255,0.5)", fontSize:13, cursor:"pointer", width:"100%" }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomizeTab({ persona, level, xp, outfit, equipItem, p, equippedEvent, equipEventOutfit }) {
  const ownedOutfits = Object.values(EVENT_OUTFITS).map(ev => {
    // For divine outfit, personalize the name/lore per bot
    if (ev.id === "divine" && ev.botSkins && ev.botSkins[persona]) {
      const skin = ev.botSkins[persona];
      return { ...ev, name: skin.title, accent: skin.accentOverride, borderGlow: skin.accentOverride + "88" };
    }
    return ev;
  });
  const LEVEL_REQUIRED = 3;
  const isUnlocked = level >= LEVEL_REQUIRED;
  // Get personalized version of currently equipped outfit
  const activeOutfit = equippedEvent ? ownedOutfits.find(o => o.id === equippedEvent) || EVENT_OUTFITS[equippedEvent] : null;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:16 }}>

      {/* ── Live Preview Card ── */}
      <div style={{
        background:`linear-gradient(135deg,${p.bg},#0a0a1a)`,
        borderRadius:20, padding:"20px 16px", textAlign:"center",
        border:`1px solid ${activeOutfit ? activeOutfit.borderGlow : p.color+"33"}`,
        marginBottom:16, position:"relative", overflow:"hidden",
        boxShadow: activeOutfit ? `0 0 32px ${activeOutfit.glow1}` : "none",
        transition:"all 0.5s",
      }}>
        {/* ambient glow */}
        <div style={{ position:"absolute", top:-40, left:"50%", transform:"translateX(-50%)",
          width:160, height:160, borderRadius:"50%",
          background: activeOutfit ? activeOutfit.glow1 : p.colorGlow,
          filter:"blur(50px)", opacity:0.35, pointerEvents:"none", transition:"background 0.5s" }}/>

        <div style={{ fontSize:10, letterSpacing:3, fontWeight:700, textTransform:"uppercase",
          marginBottom:10, color: equippedEvent ? EVENT_OUTFITS[equippedEvent]?.accent : p.color }}>
          {equippedEvent ? "Event Outfit — Active" : "Live Preview"}
        </div>

        {/* Bot with costume */}
        <div style={{ display:"flex", justifyContent:"center", marginBottom:10,
          animation:"bfFloat 3s ease-in-out infinite" }}>
          <BotAvatar persona={persona} level={level} size={90} animated equippedEvent={isUnlocked ? equippedEvent : null} outfit={outfit} showWeapon/>
        </div>

        <div style={{ fontSize:16, fontWeight:900, color:"white" }}>{p.name}</div>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase",
          marginTop:3, marginBottom:10, color: equippedEvent ? EVENT_OUTFITS[equippedEvent]?.accent : p.color }}>
          {activeOutfit ? activeOutfit.name : `Level ${level} · ${LEVEL_NAMES[level-1]}`}
        </div>
        <div style={{ width:"100%", maxWidth:200, margin:"0 auto" }}>
          <XPBar xp={xp} level={level} color={activeOutfit ? activeOutfit.accent || p.color : p.color}/>
        </div>
      </div>

      {/* ── Level lock notice ── */}
      {!isUnlocked && (
        <div style={{ padding:"12px 16px", borderRadius:14, marginBottom:14,
          background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.25)",
          display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ fontSize:20 }}>🔒</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", lineHeight:1.6 }}>
            Event outfits unlock at <span style={{ color:"#F59E0B", fontWeight:700 }}>Level 3 (Top Performer)</span>.
            Keep chatting to level up!
          </div>
        </div>
      )}

      {/* ── Section header ── */}
      <div style={{ fontSize:10, color:"rgba(255,255,255,0.55)", letterSpacing:2,
        textTransform:"uppercase", marginBottom:12 }}>
        🎁 Event Wardrobe {!isUnlocked ? "— Locked" : "— tap to equip"}
      </div>

      {/* ── Outfit cards ── */}
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {ownedOutfits.map((ev, i) => {
          const isEquipped = equippedEvent === ev.id;
          const locked     = !isUnlocked;
          return (
            <div key={ev.id} style={{
              borderRadius:18, overflow:"hidden",
              border:`1.5px solid ${isEquipped ? ev.borderGlow : locked ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.08)"}`,
              background: isEquipped
                ? `linear-gradient(135deg,${ev.bg2},${ev.bgMid})`
                : locked ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.03)",
              boxShadow: isEquipped ? `0 0 28px ${ev.glow1},0 0 56px ${ev.glow2}` : "none",
              opacity: locked ? 0.55 : 1,
              transition:"all 0.4s",
              animation:`bfFadeUp 0.3s ease ${i*0.08}s both`,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 14px 10px" }}>

                {/* chibi preview */}
                <div style={{
                  width:64, height:64, borderRadius:15, flexShrink:0,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  background: isEquipped ? `linear-gradient(135deg,${ev.accent}22,${ev.accentAlt}11)` : "rgba(255,255,255,0.04)",
                  border:`1.5px solid ${isEquipped ? ev.borderGlow : locked ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.08)"}`,
                  boxShadow: isEquipped ? `0 0 18px ${ev.glow1}` : "none",
                  overflow:"hidden", position:"relative",
                }}>
                  {locked
                    ? <div style={{ fontSize:22 }}>🔒</div>
                    : <BotAvatar persona={persona} level={level} size={58} equippedEvent={ev.id} showWeapon/>
                  }
                </div>

                {/* info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:2,
                    textTransform:"uppercase", marginBottom:2,
                    color: locked ? "rgba(255,255,255,0.2)" : isEquipped ? ev.accent : "rgba(255,255,255,0.35)" }}>
                    {ev.event} · {ev.period}
                  </div>
                  <div style={{ fontSize:13, fontWeight:800,
                    color: locked ? "rgba(255,255,255,0.3)" : isEquipped ? "white" : "rgba(255,255,255,0.75)" }}>
                    {ev.name}
                  </div>
                  {/* Bot-exclusive skin lore tag for divine */}
                  {ev.id === "divine" && !locked && (() => {
                    const skin = EVENT_OUTFITS.divine.botSkins?.[persona];
                    return skin ? (
                      <div style={{ fontSize:9, color: ev.accent, fontWeight:700, marginTop:2,
                        fontStyle:"italic", opacity:0.8 }}>
                        ✦ {persona} Exclusive · {skin.lore}
                      </div>
                    ) : null;
                  })()}
                  {locked ? (
                    <div style={{ fontSize:10, color:"#F59E0B", fontWeight:700, marginTop:3 }}>
                      🔒 Requires Level 3
                    </div>
                  ) : (
                    <div style={{ display:"flex", gap:4, marginTop:5, flexWrap:"wrap" }}>
                      {["Theme BG","Particles","Border"].map(tag => (
                        <span key={tag} style={{
                          fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:20,
                          background: isEquipped ? ev.accent+"22" : "rgba(255,255,255,0.05)",
                          color: isEquipped ? ev.accent : "rgba(255,255,255,0.3)",
                          border:`1px solid ${isEquipped ? ev.accent+"44" : "rgba(255,255,255,0.06)"}`,
                        }}>{tag}</span>
                      ))}
                      {ev.id === "divine" && (
                        <span style={{
                          fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:20,
                          background: isEquipped ? ev.accent+"22" : "rgba(255,255,255,0.05)",
                          color: isEquipped ? ev.accent : "rgba(255,255,255,0.3)",
                          border:`1px solid ${isEquipped ? ev.accent+"44" : "rgba(255,255,255,0.06)"}`,
                        }}>Exclusive Skin</span>
                      )}
                    </div>
                  )}
                </div>

                {/* equip button */}
                <button
                  onClick={() => !locked && equipEventOutfit(ev.id)}
                  style={{
                    padding:"8px 13px", borderRadius:12, border:"none",
                    background: locked
                      ? "rgba(255,255,255,0.05)"
                      : isEquipped
                      ? `linear-gradient(135deg,${ev.accent},${ev.accentAlt})`
                      : ev.accent+"22",
                    color: locked ? "rgba(255,255,255,0.2)" : isEquipped ? "white" : ev.accent,
                    fontSize:11, fontWeight:800,
                    cursor: locked ? "not-allowed" : "pointer",
                    fontFamily:"inherit", flexShrink:0, whiteSpace:"nowrap",
                    boxShadow: isEquipped ? `0 4px 16px ${ev.glow1}` : "none",
                    border: isEquipped ? "none" : locked ? "1px solid rgba(255,255,255,0.06)" : `1px solid ${ev.accent}44`,
                    transition:"all 0.2s",
                  }}>
                  {locked ? "🔒" : isEquipped ? "✓ On" : "Equip"}
                </button>
              </div>

              {/* active bar */}
              {isEquipped && (
                <div style={{ padding:"6px 14px 10px",
                  borderTop:`1px solid ${ev.accent}22`,
                  display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:6, height:6, borderRadius:"50%",
                    background:ev.accent, boxShadow:`0 0 8px ${ev.accent}` }}/>
                  <div style={{ fontSize:10, color:ev.accent, fontWeight:600 }}>Active — full app theme applied</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.50)", marginLeft:"auto" }}>
                    {ev.particles === "snow" ? "❄ snow" : ev.particles === "hearts" ? "♥ hearts" : "✦ embers"}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Footer hint ── */}
      <div style={{ marginTop:14, padding:"11px 14px", borderRadius:14,
        background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)",
        display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ fontSize:16 }}>💡</div>
        <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", lineHeight:1.6 }}>
          {isUnlocked
            ? "Event outfits change the full app theme + particles. One active at a time."
            : "Reach Level 3 (Top Performer) to unlock event outfits."}
        </div>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────
// EMPLOYEE PROFILE CARD (with ID mask toggle)
// ─────────────────────────────────────────────────────────
function EmployeeCard({ profile, color }) {
  return (
    <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:16,
      padding:16, border:"1px solid rgba(255,255,255,0.07)", marginBottom:16 }}>
      <div style={{ fontSize:11, color:"rgba(255,255,255,0.60)", letterSpacing:2,
        textTransform:"uppercase", marginBottom:12 }}>Employee Profile</div>
      {[
        { label:"Name",       value: profile.name,       icon:"👤" },
        { label:"Department", value: profile.department,  icon:"🏢" },
      ].map((row,i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", gap:12,
          padding:"10px 0", borderBottom:i<1?"1px solid rgba(255,255,255,0.05)":"none" }}>
          <div style={{ fontSize:18, width:28, flexShrink:0, textAlign:"center" }}>{row.icon}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.55)", letterSpacing:1,
              textTransform:"uppercase", marginBottom:2 }}>{row.label}</div>
            <div style={{ fontSize:14, color:"white", fontWeight:600 }}>{row.value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// EMPLOYEE QUESTIONNAIRE MODAL
// ─────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────
// ACN ADMIN QUESTIONNAIRE — Team Leader / SME Registration
// ─────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────
// ADMIN LOGIN FORM — username/password for returning admins
// Credentials stored in botfit_admin_creds (survives DEV reset)
// ─────────────────────────────────────────────────────────
function AdminLoginForm({ adminProfile, onSuccess, onCancel }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const attempt = async () => {
    if (!username.trim() || !password) { setError("Enter username and password"); return; }
    setLoading(true); setError("");
    try {
      const hashed = await hashPassword(password);
      const res  = await fetch("/api/admin-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "authAdmin", username: username.trim(), password: hashed }),
      });
      const data = await res.json();
      if (data.ok && data.profile) {
        try { localStorage.setItem("botfit_admin_profile", JSON.stringify(data.profile)); } catch {}
        onSuccess(data.profile);
      } else {
        setError(data.error || "Incorrect username or password");
      }
    } catch {
      setError("Login failed — check your connection");
    } finally {
      setLoading(false);
    }
  };

  const inputS = (hasErr) => ({
    width:"100%", padding:"12px 16px", borderRadius:12, boxSizing:"border-box",
    border:`1px solid ${hasErr ? "#EF4444" : "rgba(139,92,246,0.4)"}`,
    background:"rgba(255,255,255,0.05)", color:"white",
    fontSize:14, fontFamily:"inherit", outline:"none", marginBottom:10,
  });

  return (
    <>
      <input type="text" placeholder="Username" value={username} autoFocus
        onChange={e => { setUsername(e.target.value); setError(""); }}
        onKeyDown={e => e.key === "Enter" && attempt()}
        style={inputS(!!error)}
      />
      <input type="password" placeholder="Password" value={password}
        onChange={e => { setPassword(e.target.value); setError(""); }}
        onKeyDown={e => e.key === "Enter" && attempt()}
        style={inputS(!!error)}
      />
      {error && (
        <div style={{ color:"#EF4444", fontSize:11, marginBottom:10, textAlign:"center" }}>❌ {error}</div>
      )}
      <button onClick={attempt} disabled={loading} style={{
        width:"100%", padding:"12px", borderRadius:12, border:"none",
        background: loading ? "rgba(139,92,246,0.4)" : "linear-gradient(135deg,#7C3AED,#4C1D95)",
        color:"white", fontSize:14, fontWeight:700,
        cursor: loading ? "not-allowed" : "pointer",
        fontFamily:"inherit", boxShadow:"0 4px 20px rgba(139,92,246,0.4)", marginBottom:10,
      }}>{loading ? "Checking…" : "Login →"}</button>
      <button onClick={onCancel} style={{
        width:"100%", padding:"10px", borderRadius:12, border:"none",
        background:"rgba(255,255,255,0.04)", color:"rgba(255,255,255,0.55)",
        fontSize:13, cursor:"pointer", fontFamily:"inherit",
      }}>← Cancel</button>
    </>
  );
}

// ─────────────────────────────────────────────────────────
// CHANGE PASSWORD MODAL
// ─────────────────────────────────────────────────────────
function ChangePasswordModal({ adminProfile, onClose }) {
  const [current,  setCurrent]  = useState("");
  const [newPass,  setNewPass]  = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState(false);
  const [loading,  setLoading]  = useState(false);

  const submit = async () => {
    if (!current || !newPass || !confirm) { setError("Fill in all fields"); return; }
    if (newPass.length < 4) { setError("New password must be at least 4 characters"); return; }
    if (newPass !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true); setError("");
    try {
      // Verify current password first
      const currentHash = await hashPassword(current);
      const verifyRes = await fetch("/api/admin-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action:"authAdmin", username: adminProfile.username, password: currentHash }),
      });
      const verify = await verifyRes.json();
      if (!verify.ok) { setError("Current password is incorrect"); setLoading(false); return; }

      // Save new hashed password via save-progress
      const newHash = await hashPassword(newPass);
      await fetch("/api/save-progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action:     "saveAdmin",
          name:       adminProfile.name,
          department: adminProfile.department,
          employeeId: adminProfile.employeeId || "",
          role:       adminProfile.role,
          teamName:   adminProfile.teamName,
          username:   adminProfile.username,
          password:   newHash,
        }),
      });
      setSuccess(true);
    } catch { setError("Failed — check your connection"); }
    finally { setLoading(false); }
  };

  const inputS = { width:"100%", padding:"11px 14px", borderRadius:10, boxSizing:"border-box",
    border:"1px solid rgba(139,92,246,0.3)", background:"rgba(255,255,255,0.05)",
    color:"white", fontSize:13, fontFamily:"inherit", outline:"none", marginBottom:10 };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:900,
      display:"flex", alignItems:"center", justifyContent:"center", padding:20,
      fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      <div style={{ background:"linear-gradient(135deg,#0d0d1a,#1a0a2e)",
        border:"2px solid rgba(139,92,246,0.4)", borderRadius:20, padding:"28px 24px",
        maxWidth:340, width:"100%", boxShadow:"0 0 60px rgba(139,92,246,0.25)" }}>
        <div style={{ fontSize:18, fontWeight:900, color:"white", marginBottom:4 }}>🔑 Change Password</div>
        <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginBottom:20 }}>
          {adminProfile?.username} · {adminProfile?.role}
        </div>
        {success ? (
          <>
            <div style={{ textAlign:"center", padding:"20px 0" }}>
              <div style={{ fontSize:36, marginBottom:8 }}>✅</div>
              <div style={{ fontSize:14, fontWeight:700, color:"#10B981" }}>Password changed!</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)", marginTop:6 }}>
                Use your new password next time you log in.
              </div>
            </div>
            <button onClick={onClose} style={{ width:"100%", padding:"11px", borderRadius:10,
              border:"none", background:"linear-gradient(135deg,#7C3AED,#4C1D95)",
              color:"white", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              Done
            </button>
          </>
        ) : (<>
          <input type="password" placeholder="Current password" value={current}
            onChange={e => { setCurrent(e.target.value); setError(""); }} style={inputS} autoFocus/>
          <input type="password" placeholder="New password (min 4 chars)" value={newPass}
            onChange={e => { setNewPass(e.target.value); setError(""); }} style={inputS}/>
          <input type="password" placeholder="Confirm new password" value={confirm}
            onChange={e => { setConfirm(e.target.value); setError(""); }} style={inputS}
            onKeyDown={e => e.key==="Enter" && submit()}/>
          {error && <div style={{ color:"#EF4444", fontSize:11, marginBottom:10 }}>❌ {error}</div>}
          <button onClick={submit} disabled={loading} style={{ width:"100%", padding:"11px",
            borderRadius:10, border:"none",
            background: loading ? "rgba(139,92,246,0.4)" : "linear-gradient(135deg,#7C3AED,#4C1D95)",
            color:"white", fontSize:13, fontWeight:700, cursor: loading ? "not-allowed" : "pointer",
            fontFamily:"inherit", marginBottom:8 }}>
            {loading ? "Saving…" : "Change Password →"}
          </button>
          <button onClick={onClose} style={{ width:"100%", padding:"9px", borderRadius:10,
            border:"none", background:"rgba(255,255,255,0.04)", color:"rgba(255,255,255,0.5)",
            fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>← Cancel</button>
        </>)}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────
// REDEMPTIONS TAB — TL approves, R&R/Manager sees all
// ─────────────────────────────────────────────────────────
function RedemptionsTab({ adminProfile, ADMIN_PIN, cardS, inputS, isTL, isRnR, isManager }) {
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [updating, setUpdating]       = useState(null); // rowIndex being updated
  const [filter, setFilter]           = useState("All"); // All | Pending | Approved | Rejected

  const alive          = useIsMounted();
  const setTimer       = useTimers();
  const loadAbortRef   = useRef(null);

  const load = useCallback(async () => {
    loadAbortRef.current?.abort();
    const ctrl = new AbortController();
    loadAbortRef.current = ctrl;
    setLoading(true);
    try {
      const team = isTL && adminProfile?.teamName
        ? `&team=${encodeURIComponent(adminProfile.teamName)}` : "";
      const res = await fetch(`/api/admin-data?key=${ADMIN_PIN}&action=getRedemptions${team}`,
        { signal: ctrl.signal });
      const data = await res.json();
      if (!alive.current) return;
      if (data.redemptions) setRedemptions(data.redemptions);
    } catch (err) {
      if (isAbort(err) || !alive.current) return;   // aborted: leave loading to the new call
    }
    if (alive.current) setLoading(false);
  }, [isTL, adminProfile?.teamName, ADMIN_PIN, alive]);

  useEffect(() => {
    load();
    return () => loadAbortRef.current?.abort();
  }, [load]);

  const updateStatus = async (r, status) => {
    setUpdating(r.rowIndex);
    try {
      await fetch("/api/save-progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action:     "updateRedemption",
          rowIndex:   r.rowIndex,
          status,
          approvedBy: adminProfile?.name || "Admin",
        }),
      });
      // Optimistic update immediately
      setRedemptions(prev => prev.map(x =>
        x.rowIndex === r.rowIndex ? { ...x, status, approvedBy: adminProfile?.name } : x
      ));
      // Then reload from sheet after 1.5s to confirm
      setTimer(() => load(), 1500);
    } catch {}
    if (alive.current) setUpdating(null);
  };

  const filtered = redemptions.filter(r => filter === "All" || r.status === filter);
  const pending   = redemptions.filter(r => r.status === "Pending").length;
  const tlApproved = redemptions.filter(r => r.status === "TL Approved").length;

  const statusColor = { Pending:"#F59E0B", "TL Approved":"#3B82F6", Completed:"#10B981", Rejected:"#EF4444" };

  return (
    <div style={{ padding:"16px 24px", display:"flex", flexDirection:"column", gap:14 }}>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:800, color:"white" }}>
            🛍 Redemption Requests
            {pending > 0 && !isRnR && (
              <span style={{ marginLeft:8, fontSize:10, fontWeight:700, padding:"2px 8px",
                borderRadius:20, background:"rgba(239,68,68,0.2)", color:"#F87171",
                border:"1px solid rgba(239,68,68,0.3)" }}>
                {pending} pending
              </span>
            )}
            {tlApproved > 0 && isRnR && (
              <span style={{ marginLeft:8, fontSize:10, fontWeight:700, padding:"2px 8px",
                borderRadius:20, background:"rgba(59,130,246,0.2)", color:"#93C5FD",
                border:"1px solid rgba(59,130,246,0.3)" }}>
                {tlApproved} to fulfill
              </span>
            )}
          </div>
          <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:2 }}>
            {isTL ? `Approve requests from ${adminProfile?.teamName}` : isRnR ? "Complete TL-approved requests to fulfill rewards" : "All teams — view and manage redemptions"}
          </div>
        </div>
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          {["All","Pending","TL Approved","Completed","Rejected"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding:"4px 12px", borderRadius:20, border:"none", cursor:"pointer",
              fontFamily:"inherit", fontSize:10, fontWeight:700,
              background: filter===f ? "rgba(59,130,246,0.25)" : "rgba(255,255,255,0.05)",
              color: filter===f ? "#93C5FD" : "rgba(255,255,255,0.45)",
            }}>{f}</button>
          ))}
          <button onClick={load} style={{ padding:"4px 10px", borderRadius:20, border:"none",
            cursor:"pointer", fontFamily:"inherit", fontSize:10,
            background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.5)" }}>🔄</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:40, color:"rgba(255,255,255,0.3)" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:40, color:"rgba(255,255,255,0.3)", fontSize:12 }}>
          No {filter !== "All" ? filter.toLowerCase() : ""} redemptions found
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {filtered.sort((a,b) => a.status === "Pending" ? -1 : 1).map((r, i) => (
            <div key={i} style={{
              display:"flex", alignItems:"center", gap:12,
              padding:"12px 16px", borderRadius:14,
              background: r.status === "Pending" ? "rgba(245,158,11,0.06)"
                : r.status === "TL Approved" ? "rgba(59,130,246,0.08)"
                : r.status === "Completed" ? "rgba(16,185,129,0.06)"
                : "rgba(239,68,68,0.05)",
              border:`1px solid ${r.status === "Pending" ? "rgba(245,158,11,0.2)"
                : r.status === "TL Approved" ? "rgba(59,130,246,0.3)"
                : r.status === "Completed" ? "rgba(16,185,129,0.2)"
                : "rgba(239,68,68,0.15)"}`,
            }}>
              <div style={{ fontSize:24, flexShrink:0 }}>{r.rewardIcon}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <span style={{ fontSize:12, fontWeight:800, color:"white" }}>{r.employeeName}</span>
                  <span style={{ fontSize:10, color:"rgba(255,255,255,0.4)" }}>{r.teamName}</span>
                  <span style={{ fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:20,
                    background:`${statusColor[r.status]}22`, color:statusColor[r.status],
                    border:`1px solid ${statusColor[r.status]}44` }}>{r.status}</span>
                </div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)", marginTop:3 }}>
                  {r.rewardLabel} · <span style={{ color:"#F59E0B", fontWeight:700 }}>{r.rewardCost} ACN</span>
                </div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", marginTop:2 }}>
                  Submitted {new Date(r.submittedAt).toLocaleDateString("en-US", {
                    month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" })}
                  {r.approvedBy ? ` · ${r.status} by ${r.approvedBy}` : ""}
                </div>
              </div>
              {r.status === "Pending" && (
                <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                  <button
                    disabled={updating === r.rowIndex}
                    onClick={() => updateStatus(r, "TL Approved")}
                    style={{ padding:"6px 14px", borderRadius:8, border:"none", cursor:"pointer",
                      background:"linear-gradient(135deg,#10B981,#047857)",
                      color:"white", fontSize:10, fontWeight:700, fontFamily:"inherit" }}>
                    {updating === r.rowIndex ? "…" : "✓ Approve"}
                  </button>
                  <button
                    disabled={updating === r.rowIndex}
                    onClick={() => updateStatus(r, "Rejected")}
                    style={{ padding:"6px 12px", borderRadius:8, border:"1px solid rgba(239,68,68,0.3)",
                      background:"rgba(239,68,68,0.08)", color:"#F87171",
                      fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    ✗
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// R&R TAB — Rewards & Recognition admin distributes tokens
// ─────────────────────────────────────────────────────────
function RnRTab({ employees, allRegistrations, loading, cardS, inputS, adminProfile, ADMIN_PIN }) {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [search, setSearch] = useState("");
  const [awarded, setAwarded]   = useState({}); // { "employeeName:questId": true }
  const [awarding, setAwarding] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const alive    = useIsMounted();
  const setTimer = useTimers();

  // Pre-load existing awards from sheet when employee is selected so R&R can see across sessions
  useEffect(() => {
    if (!selectedEmployee) return;
    // Switching employees aborts the previous lookup, so a slow response for the
    // old selection can never land on top of the new one.
    const ctrl = new AbortController();
    fetch(`/api/admin-data?key=botfit-admin-2026&action=getRnR&name=${encodeURIComponent(selectedEmployee.name)}`,
      { signal: ctrl.signal })
      .then(r => r.json())
      .then(data => {
        if (!alive.current) return;
        if (Array.isArray(data.awards)) {
          const pre = {};
          data.awards.forEach(a => { pre[`${selectedEmployee.name}:${a.questId}`] = true; });
          setAwarded(prev => ({ ...prev, ...pre }));
        }
      }).catch(() => {});
    return () => ctrl.abort();
  }, [selectedEmployee?.name, alive]);

  // All employees from Registered Employee tab (excludes admins)
  const source = (allRegistrations || employees || []).filter(e => e.registrationType !== "admin");
  const filtered = source.filter(e =>
    !search || e.name.toLowerCase().includes(search.toLowerCase()) ||
    (e.teamName||"").toLowerCase().includes(search.toLowerCase())
  );

  const awardQuest = async (quest) => {
    if (!selectedEmployee) return;
    const key = `${selectedEmployee.name}:${quest.id}`;
    if (awarded[key]) return;
    setAwarding(key);
    try {
      await fetch("/api/save-progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action:         "awardRecognition",
          employeeName:   selectedEmployee.name,
          department:     selectedEmployee.department,
          teamName:       selectedEmployee.teamName,
          questId:        quest.id,
          questText:      quest.text,
          tokens:         quest.tokens,
          awardedBy:      adminProfile?.name || "R&R Admin",
          awardedAt:      new Date().toISOString(),
          pin:            ADMIN_PIN,
        }),
      });
      setAwarded(prev => ({ ...prev, [key]: true }));
      setSuccessMsg(`✅ ${quest.tokens} ACN awarded to ${selectedEmployee.name} for "${quest.text}"`);
      setTimer(() => setSuccessMsg(""), 4000);
    } catch { }
    if (alive.current) setAwarding(null);
  };

  return (
    <div style={{ padding:"16px 24px", display:"flex", flexDirection:"column", gap:16 }}>

      {/* Header */}
      <div style={{ padding:"12px 16px", borderRadius:12,
        background:"rgba(16,185,129,0.07)", border:"1px solid rgba(16,185,129,0.2)" }}>
        <div style={{ fontSize:13, fontWeight:800, color:"#10B981", marginBottom:4 }}>
          🎖 Rewards & Recognition
        </div>
        <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", lineHeight:1.5 }}>
          Select an employee, then award their recognition token. Each quest can only be awarded once per employee.
          Employees will see their unlocked quest in Ascendix and can claim their tokens.
        </div>
      </div>

      {successMsg && (
        <div style={{ padding:"10px 14px", borderRadius:10, fontSize:12, fontWeight:700,
          background:"rgba(16,185,129,0.12)", border:"1px solid rgba(16,185,129,0.3)",
          color:"#10B981" }}>{successMsg}</div>
      )}

      <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>

        {/* LEFT: Employee list */}
        <div style={{ width:220, flexShrink:0 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.45)",
            letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>Select Employee</div>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Search…" style={{...inputS, marginBottom:8, width:"100%", boxSizing:"border-box"}}/>
          <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:400, overflowY:"auto" }}>
            {loading ? (
              <div style={{color:"rgba(255,255,255,0.3)",fontSize:11,textAlign:"center",padding:12}}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{color:"rgba(255,255,255,0.3)",fontSize:11,textAlign:"center",padding:12}}>No employees found</div>
            ) : filtered.map((e, i) => (
              <div key={i} onClick={() => setSelectedEmployee(e)} style={{
                padding:"8px 12px", borderRadius:10, cursor:"pointer",
                background: selectedEmployee?.name === e.name
                  ? "rgba(16,185,129,0.15)" : "rgba(255,255,255,0.04)",
                border: `1px solid ${selectedEmployee?.name === e.name
                  ? "rgba(16,185,129,0.4)" : "rgba(255,255,255,0.07)"}`,
                transition:"all 0.15s",
              }}>
                <div style={{ fontSize:12, fontWeight:700,
                  color: selectedEmployee?.name === e.name ? "#10B981" : "white" }}>{e.name}</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:2 }}>
                  {e.teamName} · {e.bot || "—"}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: Recognition quests */}
        <div style={{ flex:1 }}>
          {!selectedEmployee ? (
            <div style={{ textAlign:"center", padding:"60px 20px",
              color:"rgba(255,255,255,0.25)", fontSize:12 }}>
              ← Select an employee to award recognition
            </div>
          ) : (
            <>
              <div style={{ fontSize:13, fontWeight:800, color:"white", marginBottom:4 }}>
                {selectedEmployee.name}
              </div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginBottom:14 }}>
                {selectedEmployee.teamName} · {selectedEmployee.department}
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {ADMIN_QUESTS.map(q => {
                  const key = `${selectedEmployee.name}:${q.id}`;
                  const done = awarded[key];
                  const busy = awarding === key;
                  return (
                    <div key={q.id} style={{
                      display:"flex", alignItems:"center", gap:12,
                      padding:"10px 14px", borderRadius:12,
                      background: done ? "rgba(16,185,129,0.07)" : "rgba(255,255,255,0.04)",
                      border:`1px solid ${done ? "rgba(16,185,129,0.25)" : "rgba(255,255,255,0.08)"}`,
                    }}>
                      <div style={{ fontSize:20, flexShrink:0 }}>{q.icon}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:12, fontWeight:700,
                          color: done ? "#10B981" : "white" }}>{q.text.trim()}</div>
                        <div style={{ fontSize:10, color:"rgba(255,255,255,0.45)", marginTop:2 }}>{q.desc}</div>
                      </div>
                      <div style={{ flexShrink:0, display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:11, fontWeight:800,
                          color: done ? "#10B981" : "#F59E0B" }}>+{q.tokens} ACN</span>
                        {done ? (
                          <span style={{ fontSize:10, color:"#10B981", fontWeight:700 }}>✓ Awarded</span>
                        ) : (
                          <button onClick={() => awardQuest(q)} disabled={busy} style={{
                            padding:"5px 14px", borderRadius:8, border:"none", cursor:"pointer",
                            background: busy ? "rgba(16,185,129,0.3)" : "linear-gradient(135deg,#10B981,#047857)",
                            color:"white", fontSize:10, fontWeight:700, fontFamily:"inherit",
                          }}>{busy ? "…" : "Award"}</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// BINGO ADMIN TAB
function BingoAdminTab({ ADMIN_PIN, cardS, inputS, adminProfile,
  isBingoUnlocked, setIsBingoUnlocked,
  bingoMaxWinners, setBingoMaxWinners,
  bingoEventLabel, setBingoEventLabel,
  calledNumbers = [], setCalledNumbers, onBingoSet,
  bingoWinners = [], setBingoWinners }) {

  const role      = adminProfile?.role || "Operations Manager";
  const roleU     = role.toUpperCase();
  const isSME     = roleU === "SME";
  const isTL      = roleU === "TEAM LEADER";
  const isManager = roleU === "OPERATIONS MANAGER";
  const isRnR     = roleU === "REWARDS & RECOGNITION";

  // TL and SME are locked to their own team — Manager and R&R see all teams
  const availableTeams = (isManager || isRnR)
    ? TEAMS
    : [adminProfile?.teamName].filter(Boolean);

  const [selectedTeam, setSelectedTeam] = useState(
    adminProfile?.teamName || availableTeams[0] || TEAMS[0]
  );

  const [callerError, setCallerError]   = useState(""); // kept for compat

  const RANGES = { B:[1,15], I:[16,30], N:[31,45], G:[46,60], O:[61,75] };

  const pushToAPI = async (overrides = {}) => {
    try {
      await fetch("/api/save-progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "setBingo",
          pin: ADMIN_PIN,
          team: selectedTeam,
          unlocked: isBingoUnlocked,
          maxWinners: bingoMaxWinners,
          label: bingoEventLabel,
          ...overrides,
        }),
      });
    } catch {}
  };

  const undoLast = () => {
    if (!calledNumbers.length) return;
    const updated = calledNumbers.slice(0, -1);
    setCalledNumbers(updated);
    if(onBingoSet) onBingoSet();
    pushToAPI({ calledNumbers: updated });
  };

  const clearAllNumbers = () => {
    setCalledNumbers([]);
    pushToAPI({ calledNumbers: [] });
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* Team — locked for TL/SME, selectable for Manager */}
      <div style={{...cardS}}>
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.6)",marginBottom:10}}>
          {isManager ? "👥 Select Team to Manage" : "👥 Your Team"}
        </div>

        {(isManager || isRnR) ? (
          // Manager: full team picker
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {availableTeams.map(t => (
              <button key={t} onClick={() => setSelectedTeam(t)} style={{
                padding:"7px 14px", borderRadius:20, border:"none", cursor:"pointer", fontFamily:"inherit",
                background: selectedTeam===t ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.05)",
                color: selectedTeam===t ? "#FCA5A5" : "rgba(255,255,255,0.55)",
                fontWeight: selectedTeam===t ? 800 : 500, fontSize:11,
                border:`1px solid ${selectedTeam===t ? "rgba(239,68,68,0.4)" : "rgba(255,255,255,0.08)"}`,
              }}>{t}</button>
            ))}
          </div>
        ) : (
          // TL / SME: locked to own team
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:10,
            background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)"}}>
            <span style={{fontSize:16}}>{isSME ? "🧠" : "👑"}</span>
            <div>
              <div style={{fontSize:13,fontWeight:800,color:"white"}}>{selectedTeam}</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,0.35)"}}>
                {isSME ? "SME — locked to your team" : "Team Leader — locked to your team"}
              </div>
            </div>
            <span style={{marginLeft:"auto",fontSize:10,color:"rgba(16,185,129,0.7)"}}>🔒</span>
          </div>
        )}
      </div>

      {/* Status Banner */}
      <div style={{
        padding:"18px 20px", borderRadius:16,
        background: isBingoUnlocked ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.08)",
        border:`2px solid ${isBingoUnlocked ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.3)"}`,
        display:"flex", alignItems:"center", gap:16,
      }}>
        <div style={{fontSize:36}}>{isBingoUnlocked ? "🟢" : "🔴"}</div>
        <div style={{flex:1}}>
          <div style={{fontSize:15,fontWeight:900,color:"white"}}>
            {selectedTeam}: <span style={{color: isBingoUnlocked ? "#10B981" : "#EF4444"}}>
              {isBingoUnlocked ? "LIVE — Players can join" : "LOCKED — Waiting to start"}
            </span>
          </div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",marginTop:3}}>
            {isBingoUnlocked
              ? `Up to ${bingoMaxWinners} winner${bingoMaxWinners>1?"s":""} · "${bingoEventLabel}" · ${calledNumbers.length} numbers called`
              : `${isSME?"🧠 SME":isTL?"👑 Team Leader":"🏢 Manager"} · Start the event to unlock bingo for ${selectedTeam}`}
          </div>
        </div>
      </div>

      {/* ── WINNERS LEADERBOARD ── */}
      <div style={{...cardS,
        border: bingoWinners.length > 0 ? "1px solid rgba(245,158,11,0.3)" : "1px solid rgba(255,255,255,0.06)",
        background: bingoWinners.length > 0 ? "rgba(245,158,11,0.04)" : "rgba(255,255,255,0.02)",
      }}>
        <div style={{fontSize:11,fontWeight:700,color:"#F59E0B",marginBottom:10}}>
          🏆 Winners — {bingoWinners.length}/{bingoMaxWinners}
        </div>
        {bingoWinners.length === 0 ? (
          <div style={{fontSize:11,color:"rgba(255,255,255,0.30)",textAlign:"center",padding:"12px 0"}}>
            {isBingoUnlocked ? "Waiting for first winner…" : "No winners yet"}
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {bingoWinners.map((w, i) => (
              <div key={i} style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"10px 14px", borderRadius:12,
                background: i===0 ? "rgba(245,158,11,0.12)" : i===1 ? "rgba(156,163,175,0.1)" : "rgba(180,83,9,0.1)",
                border: `1px solid ${i===0 ? "rgba(245,158,11,0.35)" : i===1 ? "rgba(156,163,175,0.25)" : "rgba(180,83,9,0.25)"}`,
              }}>
                <span style={{fontSize:22, flexShrink:0}}>
                  {w.position===1 ? "🥇" : w.position===2 ? "🥈" : "🥉"}
                </span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13, fontWeight:900, color:"white"}}>{w.name}</div>
                  <div style={{fontSize:10, color:"rgba(255,255,255,0.5)", marginTop:2}}>
                    {w.pattern} · {w.time}
                  </div>
                </div>
                <div style={{
                  fontSize:9, fontWeight:800, padding:"3px 10px", borderRadius:20,
                  background: i===0 ? "rgba(245,158,11,0.2)" : "rgba(255,255,255,0.06)",
                  color: i===0 ? "#F59E0B" : "rgba(255,255,255,0.4)",
                  letterSpacing:1, textTransform:"uppercase",
                }}>#{w.position}</div>
              </div>
            ))}
          </div>
        )}
        {bingoWinners.length > 0 && (
          <button onClick={() => {
            setBingoWinners([]);
            pushToAPI({ winners: [] });
          }} style={{
            marginTop:10, padding:"5px 12px", borderRadius:8,
            border:"1px solid rgba(239,68,68,0.3)", background:"rgba(239,68,68,0.06)",
            color:"#F87171", fontSize:10, cursor:"pointer", fontFamily:"inherit",
          }}>🗑 Clear Winners</button>
        )}
      </div>

      {/* ── NUMBER CALLER ── */}
      <div style={{...cardS,
        border: isBingoUnlocked ? "1px solid rgba(245,158,11,0.3)" : "1px solid rgba(255,255,255,0.06)",
        background: isBingoUnlocked ? "rgba(245,158,11,0.04)" : "rgba(255,255,255,0.02)",
        opacity: isBingoUnlocked ? 1 : 0.5,
      }}>
        <div style={{fontSize:11,fontWeight:700,color: isBingoUnlocked ? "#F59E0B" : "rgba(255,255,255,0.4)",marginBottom: isBingoUnlocked ? 12 : 6}}>
          🎱 Number Draw {!isBingoUnlocked && <span style={{fontSize:9,color:"rgba(255,255,255,0.3)"}}>— start the event first</span>}
        </div>

          <div style={{pointerEvents: isBingoUnlocked ? "auto" : "none"}}>

            {/* All 75 numbers remaining count */}
            {isBingoUnlocked && (
              <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginBottom:10,textAlign:"center"}}>
                {75 - calledNumbers.length} numbers remaining out of 75
              </div>
            )}

            {/* Draw button */}
            <button
              disabled={!isBingoUnlocked || calledNumbers.length >= 75}
              onClick={() => {
                // Build full pool of all 75 bingo numbers
                const RANGES = { B:[1,15], I:[16,30], N:[31,45], G:[46,60], O:[61,75] };
                const pool = [];
                Object.entries(RANGES).forEach(([letter, [min, max]]) => {
                  for (let n = min; n <= max; n++) pool.push(`${letter}-${n}`);
                });
                // Filter out already called
                const remaining = pool.filter(n => !calledNumbers.includes(n));
                if (!remaining.length) return;
                // Pick a truly random one
                const drawn = remaining[Math.floor(Math.random() * remaining.length)];
                const updated = [...calledNumbers, drawn];
                setCalledNumbers(updated);
                if(onBingoSet) onBingoSet();
                pushToAPI({ calledNumbers: updated });
              }}
              style={{
                width:"100%", padding:"16px", borderRadius:14, border:"none",
                cursor: isBingoUnlocked && calledNumbers.length < 75 ? "pointer" : "not-allowed",
                background: !isBingoUnlocked || calledNumbers.length >= 75
                  ? "rgba(255,255,255,0.06)"
                  : "linear-gradient(135deg,#F59E0B,#D97706)",
                color: !isBingoUnlocked || calledNumbers.length >= 75
                  ? "rgba(255,255,255,0.3)"
                  : "#1f1a00",
                fontWeight:900, fontSize:16, fontFamily:"inherit",
                boxShadow: isBingoUnlocked ? "0 4px 20px rgba(245,158,11,0.35)" : "none",
                transition:"all 0.2s", marginBottom:12,
              }}>
              {calledNumbers.length >= 75
                ? "✅ All 75 numbers called!"
                : `🎱 Draw Next Number`}
            </button>

            {/* Latest drawn number — big display */}
            {calledNumbers.length > 0 && (
              <div style={{textAlign:"center",marginBottom:12}}>
                <div style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.35)",letterSpacing:2,marginBottom:6,textTransform:"uppercase"}}>Last Drawn</div>
                <div style={{
                  display:"inline-flex", alignItems:"center", gap:10,
                  padding:"10px 28px", borderRadius:16,
                  background:"linear-gradient(135deg,#1D4ED8,#2563EB)",
                  border:"1px solid rgba(59,130,246,0.5)",
                  boxShadow:"0 4px 20px rgba(59,130,246,0.4)",
                }}>
                  <span style={{fontSize:32,fontWeight:900,color:"white",letterSpacing:6}}>
                    {calledNumbers[calledNumbers.length-1]}
                  </span>
                </div>
              </div>
            )}

          {/* Called numbers log */}
          {calledNumbers.length > 0 && (
            <div>
              <div style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:2,marginBottom:8,textTransform:"uppercase"}}>
                Called ({calledNumbers.length}/75)
              </div>
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10}}>
                {calledNumbers.map((num, i) => (
                  <span key={i} style={{
                    padding:"3px 8px", borderRadius:20, fontSize:10, fontWeight:800,
                    background: i === calledNumbers.length-1 ? "linear-gradient(135deg,#1D4ED8,#2563EB)" : "rgba(255,255,255,0.07)",
                    color: i === calledNumbers.length-1 ? "white" : "rgba(255,255,255,0.7)",
                    border: i === calledNumbers.length-1 ? "none" : "1px solid rgba(255,255,255,0.1)",
                  }}>{num}</span>
                ))}
              </div>
              <button onClick={clearAllNumbers} style={{
                padding:"6px 14px", borderRadius:8, border:"1px solid rgba(239,68,68,0.3)",
                background:"rgba(239,68,68,0.08)", color:"#F87171",
                fontSize:11, cursor:"pointer", fontFamily:"inherit",
              }}>🗑 Reset Draw</button>
            </div>
          )}

          {calledNumbers.length === 0 && (
            <div style={{fontSize:10,color:"rgba(255,255,255,0.30)",textAlign:"center",padding:"8px 0"}}>
              {isBingoUnlocked ? "Hit Draw to call the first number!" : "Start the event to begin drawing numbers"}
            </div>
          )}
          </div>
        </div>

      {/* Event Name */}
      <div style={{...cardS}}>
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.6)",marginBottom:10}}>🏷️ Event Name / Label</div>
        <input
          value={bingoEventLabel||""}
          onChange={e => {
            if(setBingoEventLabel) setBingoEventLabel(e.target.value);
            try { localStorage.setItem("botfit_bingo_label", e.target.value); } catch {}
          }}
          onBlur={e => pushToAPI({ label: e.target.value })}
          placeholder="e.g. Friday Huddle Bingo"
          style={{...inputS, marginBottom:6}}
        />
        <div style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>Shown on the player bingo screen for {selectedTeam}.</div>
      </div>

      {/* Max Winners */}
      <div style={{...cardS}}>
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.6)",marginBottom:10}}>🏆 Number of Winners Allowed</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {[1,2,3,5,10].map(n => (
            <button key={n} onClick={() => {
              if(setBingoMaxWinners) setBingoMaxWinners(n);
              try { localStorage.setItem("botfit_bingo_max_winners", String(n)); } catch {}
              pushToAPI({ maxWinners: n });
            }} style={{
              padding:"8px 20px", borderRadius:10, border:"none", cursor:"pointer", fontFamily:"inherit",
              background: bingoMaxWinners===n ? "linear-gradient(135deg,#F59E0B,#D97706)" : "rgba(255,255,255,0.07)",
              color: bingoMaxWinners===n ? "#1f1a00" : "rgba(255,255,255,0.7)",
              fontWeight: bingoMaxWinners===n ? 900 : 500, fontSize:13,
              border: `1px solid ${bingoMaxWinners===n ? "#F59E0B" : "rgba(255,255,255,0.10)"}`,
            }}>{n}</button>
          ))}
        </div>
        <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginTop:8}}>
          System auto-closes event after this many winners.
        </div>
      </div>

      {/* Start / Lock */}
      <div style={{display:"flex",gap:12}}>
        <button
          disabled={!!isBingoUnlocked}
          onClick={() => {
            if(setIsBingoUnlocked) setIsBingoUnlocked(true);
            setCalledNumbers([]);
            if(setBingoWinners) setBingoWinners([]);
            if(onBingoSet) onBingoSet();
            try { localStorage.setItem("botfit_bingo_unlocked","true"); } catch {}
            pushToAPI({ unlocked: true, calledNumbers: [], winners: [] });
          }}
          style={{
            flex:1, padding:"14px 0", borderRadius:14, border:"none",
            cursor: isBingoUnlocked ? "not-allowed" : "pointer",
            background: isBingoUnlocked ? "rgba(16,185,129,0.15)" : "linear-gradient(135deg,#10B981,#047857)",
            color: isBingoUnlocked ? "#10B981" : "white",
            fontWeight:900, fontSize:14, fontFamily:"inherit",
            opacity: isBingoUnlocked ? 0.6 : 1,
          }}>
          {isBingoUnlocked ? `✅ ${selectedTeam} is Live` : `🚀 Start for ${selectedTeam}`}
        </button>
        <button
          disabled={!isBingoUnlocked}
          onClick={() => {
            if(setIsBingoUnlocked) setIsBingoUnlocked(false);
            setCalledNumbers([]);
            if(setBingoWinners) setBingoWinners([]);
            if(onBingoSet) onBingoSet();
            try { localStorage.setItem("botfit_bingo_unlocked","false"); } catch {}
            pushToAPI({ unlocked: false, calledNumbers: [], winners: [] });
          }}
          style={{
            flex:1, padding:"14px 0", borderRadius:14, border:"none",
            cursor: !isBingoUnlocked ? "not-allowed" : "pointer",
            background: !isBingoUnlocked ? "rgba(239,68,68,0.08)" : "linear-gradient(135deg,#EF4444,#991B1B)",
            color: !isBingoUnlocked ? "rgba(239,68,68,0.4)" : "white",
            fontWeight:900, fontSize:14, fontFamily:"inherit",
            opacity: !isBingoUnlocked ? 0.5 : 1,
          }}>
          🔒 Lock &amp; End Event
        </button>
      </div>

      {/* Instructions */}
      <div style={{...cardS, background:"rgba(245,158,11,0.05)", border:"1px solid rgba(245,158,11,0.15)"}}>
        <div style={{fontSize:11,fontWeight:700,color:"#F59E0B",marginBottom:10}}>📋 How to Run a Bingo Event</div>
        {[
          "Select the the Start for Team (your team).",
          'Set the event name (e.g. "Friday Huddle Bingo").',
          "Choose how many winners for this round.",
          "Press 🚀 Start — only that team sees bingo unlocked.",
          "Use the 📢 Call a Number panel to call each number.",
          "Players can ONLY mark numbers you officially call.",
          "System auto-announces each winner in their Live Feed.",
          "Press 🔒 Lock & End when the round is over.",
        ].map((step, i) => (
          <div key={i} style={{display:"flex",gap:10,marginBottom:8,alignItems:"flex-start"}}>
            <span style={{fontSize:11,fontWeight:800,color:"#F59E0B",flexShrink:0,minWidth:18}}>{i+1}.</span>
            <span style={{fontSize:11,color:"rgba(255,255,255,0.65)",lineHeight:1.5}}>{step}</span>
          </div>
        ))}
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────────────
// REGISTRATIONS TAB — All employee & admin registrations
// ─────────────────────────────────────────────────────────
function RegistrationsTab({ employees, loading, error, adminProfile, inputS, cardS, botColor }) {
  const [search, setSearch]       = useState("");
  const [filterType, setFilterType] = useState("All"); // All | Employee | Admin
  const [filterTeam, setFilterTeam] = useState("All");
  const [filterDept, setFilterDept] = useState("All");
  const [sortBy, setSortBy]       = useState("recent");

  const teams = ["All", ...TEAMS];
  const depts = ["All", ...DEPARTMENTS];

  // Separate admin registrations (bot === "ADMIN") from employees
  const allRows = employees.map(e => ({
    ...e,
    registrationType: e.bot === "ADMIN" ? "admin" : "employee",
  }));

  const filtered = allRows
    .filter(e => {
      const s = search.toLowerCase();
      if (s && !e.name?.toLowerCase().includes(s) &&
               !e.department?.toLowerCase().includes(s) &&
               !(e.teamName||"").toLowerCase().includes(s)) return false;
      if (filterType !== "All" && e.registrationType !== filterType.toLowerCase()) return false;
      if (filterTeam !== "All" && (e.teamName||"") !== filterTeam) return false;
      if (filterDept !== "All" && e.department !== filterDept) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "name")   return a.name.localeCompare(b.name);
      if (sortBy === "team")   return (a.teamName||"").localeCompare(b.teamName||"");
      if (sortBy === "dept")   return a.department.localeCompare(b.department);
      // recent = default order from sheet (most recent last, so reverse)
      return 0;
    });

  const totalEmployees = allRows.filter(e => e.registrationType === "employee").length;
  const totalAdmins    = allRows.filter(e => e.registrationType === "admin").length;

  // Group by team
  const byTeam = TEAMS.reduce((acc, t) => {
    acc[t] = filtered.filter(e => e.teamName === t);
    return acc;
  }, {});
  const unassigned = filtered.filter(e => !e.teamName || !TEAMS.includes(e.teamName));

  if (loading) return (
    <div style={{textAlign:"center",padding:40,color:"rgba(255,255,255,0.5)"}}>
      Loading registrations…
    </div>
  );
  if (error) return (
    <div style={{textAlign:"center",padding:20,color:"#EF4444",fontSize:13}}>{error}</div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* Summary cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:10}}>
        {[
          { label:"Total Registered", value: allRows.length,    icon:"📋", color:"#3B82F6" },
          { label:"Employees",        value: totalEmployees,     icon:"👤", color:"#10B981" },
          { label:"Admins / TLs",     value: totalAdmins,        icon:"🐉", color:"#EF4444" },
          { label:"Teams Active",     value: TEAMS.filter(t => allRows.some(e => e.teamName===t)).length, icon:"👥", color:"#F59E0B" },
        ].map((s,i) => (
          <div key={i} style={{...cardS,textAlign:"center"}}>
            <div style={{fontSize:22,marginBottom:4}}>{s.icon}</div>
            <div style={{fontSize:22,fontWeight:900,color:s.color}}>{s.value}</div>
            <div style={{fontSize:9,color:"rgba(255,255,255,0.5)",marginTop:2,letterSpacing:1,textTransform:"uppercase"}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="🔍 Search name, team, dept…" style={{...inputS,flex:"1 1 160px"}}/>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{...inputS,flex:"0 0 120px"}}>
          {["All","Employee","Admin"].map(t=><option key={t} value={t}>{t==="All"?"All Types":t}</option>)}
        </select>
        <select value={filterTeam} onChange={e=>setFilterTeam(e.target.value)} style={{...inputS,flex:"0 0 140px"}}>
          {teams.map(t=><option key={t} value={t}>{t==="All"?"All Teams":t}</option>)}
        </select>
        <select value={filterDept} onChange={e=>setFilterDept(e.target.value)} style={{...inputS,flex:"0 0 120px"}}>
          {depts.map(d=><option key={d} value={d}>{d==="All"?"All Depts":d}</option>)}
        </select>
        <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{...inputS,flex:"0 0 120px"}}>
          <option value="recent">Sort: Recent</option>
          <option value="name">Sort: Name</option>
          <option value="team">Sort: Team</option>
          <option value="dept">Sort: Dept</option>
        </select>
      </div>

      {/* Grouped by team */}
      {filterTeam === "All" ? (
        <>
          {TEAMS.map(team => {
            const rows = byTeam[team] || [];
            if (rows.length === 0) return null;
            return (
              <div key={team} style={{...cardS,padding:0,overflow:"hidden"}}>
                {/* Team header */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                  padding:"10px 16px", background:"rgba(255,255,255,0.03)",
                  borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:16}}>👥</span>
                    <span style={{fontSize:13,fontWeight:800,color:"white"}}>{team}</span>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <span style={{fontSize:10,color:"#10B981",background:"rgba(16,185,129,0.1)",
                      border:"1px solid rgba(16,185,129,0.25)",padding:"2px 8px",borderRadius:20,fontWeight:700}}>
                      {rows.filter(r=>r.registrationType==="employee").length} employees
                    </span>
                    {rows.filter(r=>r.registrationType==="admin").length > 0 && (
                      <span style={{fontSize:10,color:"#EF4444",background:"rgba(239,68,68,0.1)",
                        border:"1px solid rgba(239,68,68,0.25)",padding:"2px 8px",borderRadius:20,fontWeight:700}}>
                        {rows.filter(r=>r.registrationType==="admin").length} admin
                      </span>
                    )}
                  </div>
                </div>
                {/* Rows */}
                <div>
                  {rows.map((e,i) => (
                    <RegistrationRow key={i} e={e} i={i} total={rows.length} botColor={botColor}/>
                  ))}
                </div>
              </div>
            );
          })}
          {/* Unassigned */}
          {unassigned.length > 0 && (
            <div style={{...cardS,padding:0,overflow:"hidden"}}>
              <div style={{padding:"10px 16px",background:"rgba(255,255,255,0.03)",
                borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                <span style={{fontSize:13,fontWeight:800,color:"rgba(255,255,255,0.4)"}}>⚠️ No Team Assigned</span>
              </div>
              {unassigned.map((e,i) => (
                <RegistrationRow key={i} e={e} i={i} total={unassigned.length} botColor={botColor}/>
              ))}
            </div>
          )}
          {filtered.length === 0 && (
            <div style={{textAlign:"center",padding:40,color:"rgba(255,255,255,0.35)",fontSize:13}}>
              No registrations match your filters
            </div>
          )}
        </>
      ) : (
        // Flat list when filtering by specific team
        <div style={{...cardS,padding:0,overflow:"hidden"}}>
          {filtered.length === 0
            ? <div style={{textAlign:"center",padding:32,color:"rgba(255,255,255,0.35)"}}>No results</div>
            : filtered.map((e,i) => (
                <RegistrationRow key={i} e={e} i={i} total={filtered.length} botColor={botColor}/>
              ))
          }
        </div>
      )}

      <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",textAlign:"right"}}>
        Showing {filtered.length} of {allRows.length} registrations
      </div>
    </div>
  );
}

function RegistrationRow({ e, i, total, botColor }) {
  const isAdmin = e.registrationType === "admin";
  return (
    <div style={{
      display:"flex", alignItems:"center", gap:12, padding:"10px 16px",
      borderBottom: i < total-1 ? "1px solid rgba(255,255,255,0.04)" : "none",
      transition:"background 0.15s",
    }}
      onMouseEnter={ev=>ev.currentTarget.style.background="rgba(255,255,255,0.03)"}
      onMouseLeave={ev=>ev.currentTarget.style.background="transparent"}>

      {/* Avatar badge */}
      <div style={{
        width:34, height:34, borderRadius:"50%", flexShrink:0,
        display:"flex", alignItems:"center", justifyContent:"center", fontSize:15,
        background: isAdmin ? "rgba(239,68,68,0.15)" : "rgba(255,255,255,0.06)",
        border: `1px solid ${isAdmin ? "rgba(239,68,68,0.35)" : "rgba(255,255,255,0.1)"}`,
      }}>
        {isAdmin ? "🐉" : "👤"}
      </div>

      {/* Name + meta */}
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <span style={{fontSize:12,fontWeight:700,color:"white"}}>{e.name}</span>
          {isAdmin && (
            <span style={{fontSize:9,fontWeight:800,color:"#FCA5A5",
              background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.3)",
              padding:"1px 7px",borderRadius:20}}>
              {e.role || "TL / SME"}
            </span>
          )}
          {e.employeeId && (
            <span style={{fontSize:9,color:"rgba(255,255,255,0.35)"}}>{e.employeeId}</span>
          )}
        </div>
        <div style={{fontSize:10,color:"rgba(255,255,255,0.45)",marginTop:2,display:"flex",gap:8,flexWrap:"wrap"}}>
          <span>{e.department || "—"}</span>
          {e.teamName && <span style={{color:"rgba(255,255,255,0.3)"}}>· {e.teamName}</span>}
          {isAdmin && e.role && (
            <span style={{color:"rgba(252,165,165,0.5)"}}>· {e.role}</span>
          )}
        </div>
      </div>

      {/* Right side: bot chip + last active */}
      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
        {!isAdmin && (
          <span style={{fontSize:9,fontWeight:700,
            color:botColor[e.bot]||"rgba(255,255,255,0.4)",
            background:`${botColor[e.bot]||"#fff"}15`,
            border:`1px solid ${botColor[e.bot]||"rgba(255,255,255,0.1)"}33`,
            padding:"2px 8px",borderRadius:20}}>
            {e.bot||"—"}
          </span>
        )}
        <span style={{fontSize:9,color:"rgba(255,255,255,0.25)"}}>
          {e.lastActive ? e.lastActive.split(",")[0] : "—"}
        </span>
      </div>
    </div>
  );
}

// ADMIN PANEL
// ─────────────────────────────────────────────────────────
const ADMIN_PIN = "botfit-admin-2026"; // ← must match the ADMIN_PIN/ADMIN_KEY value set in Vercel

function AdminPanel({ onClose, adminProfile, adminTokens, setAdminTokens, adminQuests, setAdminQuests, alreadyAuthed=false,
  isBingoUnlocked, setIsBingoUnlocked, bingoMaxWinners, setBingoMaxWinners, bingoEventLabel, setBingoEventLabel,
  calledNumbers = [], setCalledNumbers, onBingoSet,
  bingoWinners = [], setBingoWinners }) {
  const [pin, setPin]               = useState("");
  const [authed, setAuthed]         = useState(alreadyAuthed);
  const [pinError, setPinError]     = useState("");
  const [showChangePw, setShowChangePw] = useState(false);
  const [employees, setEmployees]           = useState([]);
  const [allRegistrations, setAllRegistrations] = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState("");
  const [search, setSearch]                 = useState("");
  const [filterBot, setFilterBot]           = useState("All");
  const [filterDept, setFilterDept]         = useState("All");
  const [filterPKT, setFilterPKT]           = useState("All");
  const [sortBy, setSortBy]                 = useState("xp");
  const [pendingRedemptions, setPendingRedemptions] = useState(0);
  const [tlApprovedRedemptions, setTlApprovedRedemptions] = useState(0);
  // Role-based default tab: SME → bingo only, TL → employees, Manager → all
  const role = adminProfile?.role || "Operations Manager";
  const roleU  = role.toUpperCase();
  const isSME     = roleU === "SME";
  const isTL      = roleU === "TEAM LEADER";
  const isManager = roleU === "OPERATIONS MANAGER";
  const isRnR     = roleU === "REWARDS & RECOGNITION";
  const defaultTab = isSME ? "bingo" : isRnR ? "rnr" : "employees";
  const [tab, setTab] = useState(defaultTab);

  // Access matrix:
  // TL/SME  → employees (own team), stats (own team), bingo (own team), registrations
  // Manager → employees (all), stats (all), bingo (all teams), registrations
  // R&R     → rnr only (Recognition tab exclusive)
  const visibleTabs = isRnR
    ? ["rnr", "redemptions"]
    : isSME
      ? ["bingo"]
      : isTL
        ? ["employees", "stats", "bingo", "registrations", "redemptions"]
        : ["employees", "stats", "bingo", "registrations", "redemptions"]; // Manager

  const alive          = useIsMounted();
  const adminAbortRef  = useRef(null);
  useEffect(() => () => adminAbortRef.current?.abort(), []);

  const handlePinSubmit = () => {
    if (pin === ADMIN_PIN) { setAuthed(true); loadData(); }
    else { setPinError("Incorrect PIN. Try again."); setPin(""); }
  };

  const loadData = useCallback(async () => {
    adminAbortRef.current?.abort();
    const ctrl = new AbortController();
    adminAbortRef.current = ctrl;
    setLoading(true); setError("");
    try {
      const res  = await fetch(`/api/admin-data?key=${ADMIN_PIN}`, { signal: ctrl.signal });
      const data = await res.json();
      if (!alive.current) return;
      if (data.employees) {
        setEmployees(data.employees);
        setAllRegistrations(data.all || data.employees);
      } else setError(data.error || "Could not load data");
    } catch (err) {
      if (isAbort(err) || !alive.current) return;
      setError("Connection error");
    }
    // Fetch redemption counts for badge
    try {
      const team = isTL && adminProfile?.teamName
        ? `&team=${encodeURIComponent(adminProfile.teamName)}` : "";
      const rRes = await fetch(`/api/admin-data?key=${ADMIN_PIN}&action=getRedemptions${team}`,
        { signal: ctrl.signal });
      const rData = await rRes.json();
      if (!alive.current) return;
      if (rData.redemptions) {
        setPendingRedemptions(rData.redemptions.filter(r => r.status === "Pending").length);
        setTlApprovedRedemptions(rData.redemptions.filter(r => r.status === "TL Approved").length);
      }
    } catch {}
    if (alive.current) setLoading(false);
  }, [isTL, adminProfile?.teamName, alive]);

  // Derived stats — use allRegistrations scoped to team for accuracy
  const statsSource = (() => {
    const all = (allRegistrations || []).filter(e => e.registrationType !== "admin");
    return (isTL || isSME) && adminProfile?.teamName
      ? all.filter(e => e.teamName === adminProfile.teamName)
      : all;
  })();

  const passed    = statsSource.filter(e => e.pktPassed === "Yes").length;
  const failed    = statsSource.filter(e => e.pktPassed === "No").length;
  const notTaken  = statsSource.filter(e => !e.pktPassed).length;
  const avgXP     = statsSource.length ? Math.round(statsSource.reduce((s,e)=>s+(e.xp||0),0)/statsSource.length) : 0;
  const topEarner = [...statsSource].sort((a,b)=>b.tokens-a.tokens)[0];

  const depts  = ["All", ...new Set(employees.map(e=>e.department).filter(Boolean))];
  const bots   = ["All","ACE","CODE","NEJI"];
  const pktF   = ["All","Passed","Failed","Not taken"];

  // TL/SME sees only their own team; Manager and R&R see all teams
  const scopedEmployees = (isTL || isSME) && adminProfile?.teamName
    ? employees.filter(e => e.teamName === adminProfile.teamName)
    : employees;

  const filtered = scopedEmployees
    .filter(e => {
      const s = search.toLowerCase();
      if (s && !e.name.toLowerCase().includes(s) && !e.department.toLowerCase().includes(s)) return false;
      if (filterBot  !== "All" && e.bot !== filterBot) return false;
      if (filterDept !== "All" && e.department !== filterDept) return false;
      if (filterPKT  === "Passed"   && e.pktPassed !== "Yes") return false;
      if (filterPKT  === "Failed"   && e.pktPassed !== "No")  return false;
      if (filterPKT  === "Not taken"&& e.pktPassed) return false;
      return true;
    })
    .sort((a,b) => {
      if (sortBy === "xp")    return b.xp - a.xp;
      if (sortBy === "level") return b.level - a.level;
      if (sortBy === "tokens")return b.tokens - a.tokens;
      if (sortBy === "pkt")   return (Number(b.pktScore)||0) - (Number(a.pktScore)||0);
      return a.name.localeCompare(b.name);
    });

  const exportCSV = () => {
    const headers = ["Name","Department","Bot","Level","XP","Tokens","Messages","PKT Score","PKT Passed","PKT Month","Trainer","Trainer EID","Quests Done","Last Active"];
    const rows = employees.map(e => [e.name,e.department,e.bot,e.level,e.xp,e.tokens,e.msgCount,e.pktScore,e.pktPassed,e.pktMonth,e.trainerName,e.trainerEID,e.questsDone,e.lastActive]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download="ascendix-progress.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const pStyle = { fontFamily:"'Segoe UI',system-ui,sans-serif" };
  const cardS  = { background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:14, padding:"14px 16px" };
  const inputS = { width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid rgba(255,255,255,0.12)", background:"rgba(255,255,255,0.06)", color:"white", fontSize:12, fontFamily:"inherit", outline:"none", boxSizing:"border-box" };
  const btnS   = (c) => ({ padding:"7px 14px", borderRadius:10, border:"none", background:c, color:"white", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" });

  const claimAdminQuest = (questId) => {
    if (!setAdminQuests) return;
    const q = (adminQuests||[]).find(q => q.id === questId);
    if (!q || q.done) return;
    const newTokens = (adminTokens||0) + q.tokens;
    if (setAdminTokens) { setAdminTokens(newTokens); try { localStorage.setItem("botfit_admin_tokens", String(newTokens)); } catch {} }
    const updated = (adminQuests||[]).map(q2 => q2.id === questId ? {...q2, done:true} : q2);
    setAdminQuests(updated);
    try { localStorage.setItem("botfit_admin_quests", JSON.stringify(updated)); } catch {}
  };

  const botColor = { ACE:"#3B82F6", CODE:"#10B981", NEJI:"#8B5CF6" };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:600,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      fontFamily:"'Segoe UI',system-ui,sans-serif", overflowY:"auto" }}>

      {/* PIN gate */}
      {!authed ? (
        <div style={{ background:"#0d0d1a", border:"2px solid rgba(255,255,255,0.12)", borderRadius:24,
          padding:"36px 28px", maxWidth:360, width:"100%", textAlign:"center",
          animation:"bfLvlPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🔐</div>
          <div style={{ fontSize:18, fontWeight:900, color:"white", marginBottom:6 }}>Admin Access</div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.60)", marginBottom:24 }}>Enter admin PIN to continue</div>
          <input type="password" value={pin} onChange={e=>{setPin(e.target.value);setPinError("");}}
            onKeyDown={e=>e.key==="Enter"&&handlePinSubmit()}
            placeholder="Enter PIN" style={{...inputS, textAlign:"center", fontSize:16, marginBottom:12}} autoFocus/>
          {pinError && <div style={{fontSize:11,color:"#EF4444",marginBottom:10}}>{pinError}</div>}
          <div style={{display:"flex",gap:10}}>
            <button onClick={onClose} style={btnS("rgba(255,255,255,0.08)")}>Cancel</button>
            <button onClick={handlePinSubmit} style={{...btnS("#3B82F6"),flex:1}}>🔓 Enter</button>
          </div>
        </div>
      ) : (
        <div style={{ background:"#0a0a14", border:"1px solid rgba(255,255,255,0.1)", borderRadius:24,
          width:"100%", maxWidth:900, maxHeight:"92vh", overflowY:"auto",
          animation:"bfFadeIn 0.2s ease both" }}>

          {/* Header */}
          <div style={{ padding:"20px 24px 0", display:"flex", alignItems:"center", justifyContent:"space-between",
            borderBottom:"1px solid rgba(255,255,255,0.07)", paddingBottom:16 }}>
            <div>
              <div style={{ fontSize:18, fontWeight:900, color:"white" }}>🛡 Ascendix Admin Panel</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", marginTop:2 }}>{employees.length} employees · Last loaded now</div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={loadData} style={btnS("rgba(255,255,255,0.08)")} title="Refresh">🔄 Refresh</button>
              <button onClick={exportCSV} style={btnS("#10B981")}>📥 Export CSV</button>
              <button onClick={() => setShowChangePw(true)} style={btnS("rgba(139,92,246,0.5)")}>🔑 Password</button>
              <button onClick={onClose} style={btnS("rgba(255,255,255,0.08)")}>✕ Close</button>
            </div>
          </div>

          {showChangePw && (
            <ChangePasswordModal
              adminProfile={adminProfile}
              onClose={() => setShowChangePw(false)}
            />
          )}

          {/* Admin Profile Banner */}
          {adminProfile && (
            <div style={{ margin:"16px 24px 0", padding:"14px 16px", borderRadius:14,
              background:"linear-gradient(135deg,rgba(239,68,68,0.1),rgba(127,29,29,0.15))",
              border:"1px solid rgba(239,68,68,0.25)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                <div style={{ fontSize:28, filter:"drop-shadow(0 0 8px rgba(239,68,68,0.6))" }}>🐉</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"white" }}>{adminProfile.name}</div>
                  <div style={{ fontSize:11, color:"rgba(252,165,165,0.6)", marginTop:2 }}>
                    {adminProfile.role ? `${adminProfile.role==="Team Leader"?"👑":adminProfile.role==="SME"?"🧠":"🏢"} ${adminProfile.role}` : "Team Leader / SME"}
                    {adminProfile.employeeId ? ` · ${adminProfile.employeeId}` : ""}
                  </div>
                  <div style={{ fontSize:10, color:"rgba(252,165,165,0.4)", marginTop:1 }}>
                    {adminProfile.teamName} · {adminProfile.department}
                  </div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:18, fontWeight:900, color:"#EF4444" }}>{adminTokens||0}</div>
                  <div style={{ fontSize:9, color:"rgba(255,255,255,0.55)", letterSpacing:1 }}>ACN TOKENS</div>
                </div>
              </div>
            </div>
          )}

          {/* Admin Quests — only for Team Leader, locked until R&R awards them */}
          {isTL && (
          <div style={{ margin:"16px 24px 0", padding:"14px 16px", borderRadius:14,
            background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <div style={{ fontSize:10, letterSpacing:2, fontWeight:700, color:"rgba(255,255,255,0.55)",
                textTransform:"uppercase" }}>Individual Recognition</div>
              <div style={{ fontSize:9, color:"rgba(16,185,129,0.7)", background:"rgba(16,185,129,0.08)",
                border:"1px solid rgba(16,185,129,0.2)", padding:"2px 8px", borderRadius:20 }}>
                🎖 Awarded by R&amp;R Team
              </div>
            </div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              {(adminQuests||ADMIN_QUESTS).map(q => (
                <div key={q.id} style={{
                  flex:"1 1 140px", padding:"12px 14px", borderRadius:12,
                  background: q.done ? "rgba(16,185,129,0.08)" : "rgba(255,255,255,0.04)",
                  border:`1px solid ${q.done ? "rgba(16,185,129,0.3)" : "rgba(255,255,255,0.08)"}`,
                  opacity: q.done ? 1 : 0.6,
                }}>
                  <div style={{ fontSize:18, marginBottom:6 }}>{q.icon}</div>
                  <div style={{ fontSize:11, fontWeight:700, color: q.done ? "#10B981" : "white",
                    marginBottom:4 }}>{q.text}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.55)", marginBottom:10,
                    lineHeight:1.4 }}>{q.desc}</div>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <span style={{ fontSize:11, fontWeight:800,
                      color: q.done ? "#10B981" : "#F59E0B" }}>+{q.tokens} ACN</span>
                    {q.done ? (
                      <button onClick={() => claimAdminQuest(q.id)} style={{
                        padding:"5px 12px", borderRadius:8, border:"none",
                        background:"linear-gradient(135deg,#DC2626,#7F1D1D)",
                        color:"white", fontSize:10, fontWeight:700, cursor:"pointer",
                        fontFamily:"inherit",
                      }}>Claim</button>
                    ) : (
                      <span style={{ fontSize:9, color:"rgba(255,255,255,0.3)",
                        display:"flex", alignItems:"center", gap:4 }}>
                        🔒 Pending R&amp;R
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Tab bar */}
          <div style={{display:"flex",gap:4,padding:"12px 24px 0",overflowX:"auto",alignItems:"center"}}>
            {visibleTabs.map(t=>(
              <button key={t} onClick={()=>setTab(t)} style={{
                padding:"7px 16px", borderRadius:10, border:"none", cursor:"pointer", flexShrink:0,
                background: tab===t ? "rgba(59,130,246,0.25)" : "rgba(255,255,255,0.04)",
                color: tab===t ? "#3B82F6" : "rgba(255,255,255,0.4)",
                fontWeight: tab===t ? 700 : 400, fontSize:12, fontFamily:"inherit",
                borderBottom: tab===t ? "2px solid #3B82F6" : "2px solid transparent",
                position:"relative",
              }}>
                {t==="employees" ? "👥 Employees" : t==="stats" ? "📊 Statistics" : t==="bingo" ? "🎰 Bingo Event" : t==="rnr" ? "🎖 Recognition" : t==="redemptions" ? "🛍 Redemptions" : "📋 Registrations"}
                {/* Notification badge */}
                {t==="redemptions" && !isRnR && pendingRedemptions > 0 && (
                  <span style={{
                    position:"absolute", top:-4, right:-4,
                    minWidth:16, height:16, borderRadius:8, padding:"0 4px",
                    background:"#EF4444", color:"white",
                    fontSize:9, fontWeight:900, lineHeight:"16px",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    boxShadow:"0 0 6px rgba(239,68,68,0.6)",
                  }}>{pendingRedemptions}</span>
                )}
                {t==="redemptions" && isRnR && tlApprovedRedemptions > 0 && (
                  <span style={{
                    position:"absolute", top:-4, right:-4,
                    minWidth:16, height:16, borderRadius:8, padding:"0 4px",
                    background:"#3B82F6", color:"white",
                    fontSize:9, fontWeight:900, lineHeight:"16px",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    boxShadow:"0 0 6px rgba(59,130,246,0.6)",
                  }}>{tlApprovedRedemptions}</span>
                )}
              </button>
            ))}
            {/* Role badge */}
            <div style={{marginLeft:"auto",flexShrink:0,padding:"4px 12px",borderRadius:20,fontSize:10,fontWeight:800,
              background: isSME ? "rgba(139,92,246,0.15)" : isTL ? "rgba(245,158,11,0.15)" : "rgba(239,68,68,0.15)",
              color: isSME ? "#C4B5FD" : isTL ? "#FCD34D" : "#FCA5A5",
              border: `1px solid ${isSME ? "rgba(139,92,246,0.3)" : isTL ? "rgba(245,158,11,0.3)" : "rgba(239,68,68,0.3)"}`,
            }}>
              {isSME ? "🧠 SME" : isTL ? "👑 Team Leader" : isRnR ? "🎖 R&R" : "🏢 Manager"}
            </div>
          </div>

          <div style={{padding:"16px 24px 24px"}}>
            {loading && <div style={{textAlign:"center",padding:40,color:"rgba(255,255,255,0.60)"}}>Loading employee data…</div>}
            {error   && <div style={{textAlign:"center",padding:20,color:"#EF4444",fontSize:13}}>{error}</div>}

            {!loading && !error && tab==="employees" && <>
              {/* TL/SME sees only their team; Manager sees all */}
              {(isTL || isSME) && adminProfile?.teamName && (
                <div style={{marginBottom:10,padding:"7px 12px",borderRadius:10,fontSize:10,fontWeight:700,
                  background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.2)",color:"#FCD34D"}}>
                  {isSME ? "🧠" : "👑"} Showing members of <strong>{adminProfile.teamName}</strong>
                </div>
              )}
              {/* Filters */}
              <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
                <input value={search} onChange={e=>setSearch(e.target.value)}
                  placeholder="🔍 Search name or dept…" style={{...inputS,flex:"1 1 160px"}}/>
                <select value={filterBot} onChange={e=>setFilterBot(e.target.value)} style={{...inputS,flex:"0 0 100px"}}>
                  {bots.map(b=><option key={b} value={b}>{b}</option>)}
                </select>
                <select value={filterDept} onChange={e=>setFilterDept(e.target.value)} style={{...inputS,flex:"1 1 140px"}}>
                  {depts.map(d=><option key={d} value={d}>{d==="All"?"All Depts":d}</option>)}
                </select>
                <select value={filterPKT} onChange={e=>setFilterPKT(e.target.value)} style={{...inputS,flex:"0 0 110px"}}>
                  {pktF.map(f=><option key={f} value={f}>{f}</option>)}
                </select>
                <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{...inputS,flex:"0 0 110px"}}>
                  <option value="xp">Sort: XP</option>
                  <option value="level">Sort: Level</option>
                  <option value="tokens">Sort: Tokens</option>
                  <option value="pkt">Sort: PKT</option>
                  <option value="name">Sort: Name</option>
                </select>
              </div>

              {/* Table */}
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                  <thead>
                    <tr style={{borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                      {["Employee","Bot","Level","XP","Tokens","Msgs","PKT","Month","Trainer","Quests","Last Active"].map(h=>(
                        <th key={h} style={{padding:"8px 10px",textAlign:"left",color:"rgba(255,255,255,0.60)",fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan={11} style={{textAlign:"center",padding:24,color:"rgba(255,255,255,0.50)"}}>No employees match filter</td></tr>
                    )}
                    {filtered.map((e,i)=>{
                      const pktColor = e.pktPassed==="Yes" ? "#10B981" : e.pktPassed==="No" ? "#EF4444" : "rgba(255,255,255,0.25)";
                      const pktLabel = e.pktPassed==="Yes" ? "✅ Pass" : e.pktPassed==="No" ? "❌ Fail" : "—";
                      return (
                        <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.04)",transition:"background 0.15s"}}
                          onMouseEnter={ev=>ev.currentTarget.style.background="rgba(255,255,255,0.03)"}
                          onMouseLeave={ev=>ev.currentTarget.style.background="transparent"}>
                          <td style={{padding:"10px 10px"}}>
                            <div style={{fontWeight:700,color:"white"}}>{e.name}</div>
                            <div style={{fontSize:10,color:"rgba(255,255,255,0.55)",marginTop:2}}>{e.department}</div>
                          </td>
                          <td style={{padding:"10px 10px"}}>
                            <span style={{fontSize:10,fontWeight:700,color:botColor[e.bot]||"rgba(255,255,255,0.5)",
                              background:`${botColor[e.bot]}22`,padding:"2px 8px",borderRadius:20}}>{e.bot||"—"}</span>
                          </td>
                          <td style={{padding:"10px",color:"white",fontWeight:700}}>Lv.{e.level}</td>
                          <td style={{padding:"10px",color:"#60A5FA",fontWeight:600}}>⚡ {e.xp}</td>
                          <td style={{padding:"10px",color:"#F59E0B",fontWeight:600}}>🪙 {e.tokens}</td>
                          <td style={{padding:"10px",color:"rgba(255,255,255,0.5)"}}>{e.msgCount}</td>
                          <td style={{padding:"10px"}}>
                            <span style={{color:pktColor,fontWeight:700}}>{pktLabel}</span>
                            {e.pktScore ? <span style={{color:"rgba(255,255,255,0.55)",fontSize:10,marginLeft:6}}>{e.pktScore}%</span> : null}
                          </td>
                          <td style={{padding:"10px",color:"rgba(255,255,255,0.60)",fontSize:10}}>{e.pktMonth||"—"}</td>
                          <td style={{padding:"10px",color:"rgba(255,255,255,0.5)",fontSize:10}}>
                            <div>{e.trainerName||"—"}</div>
                            {e.trainerEID && <div style={{color:"rgba(255,255,255,0.55)"}}>{e.trainerEID}</div>}
                          </td>
                          <td style={{padding:"10px",color:"rgba(255,255,255,0.5)"}}>{e.questsDone}</td>
                          <td style={{padding:"10px",color:"rgba(255,255,255,0.55)",fontSize:10,whiteSpace:"nowrap"}}>{e.lastActive||"—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.50)",marginTop:10,textAlign:"right"}}>
                Showing {filtered.length} of {employees.length} employees
              </div>
            </>}

            {!loading && !error && tab==="stats" && <>
              {/* Stat cards */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:18}}>
                {[
                  { label:"Total Employees", value:statsSource.length, icon:"👥", color:"#3B82F6" },
                  { label:"PKT Passed",       value:passed,           icon:"✅", color:"#10B981" },
                  { label:"PKT Failed",       value:failed,           icon:"❌", color:"#EF4444" },
                  { label:"Not Taken Yet",    value:notTaken,         icon:"⏳", color:"#F59E0B" },
                  { label:"Avg XP",           value:avgXP,            icon:"⚡", color:"#60A5FA" },
                  { label:"Top Earner",       value:topEarner?.name?.split(" ")[0]||"—", icon:"🏆", color:"#F59E0B" },
                ].map((s,i)=>(
                  <div key={i} style={{...cardS,textAlign:"center"}}>
                    <div style={{fontSize:24,marginBottom:6}}>{s.icon}</div>
                    <div style={{fontSize:20,fontWeight:900,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.55)",marginTop:3}}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* PKT pass rate bar */}
              <div style={{...cardS,marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.6)",marginBottom:12}}>📊 PKT Pass Rate</div>
                <div style={{display:"flex",gap:3,height:24,borderRadius:6,overflow:"hidden"}}>
                  {passed>0  && <div style={{flex:passed, background:"#10B981",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"white"}}>{Math.round(passed/statsSource.length*100)}%</div>}
                  {failed>0  && <div style={{flex:failed, background:"#EF4444",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"white"}}>{Math.round(failed/statsSource.length*100)}%</div>}
                  {notTaken>0 && <div style={{flex:notTaken,background:"rgba(255,255,255,0.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"rgba(255,255,255,0.60)"}}>{Math.round(notTaken/statsSource.length*100)}%</div>}
                </div>
                <div style={{display:"flex",gap:16,marginTop:8,fontSize:10}}>
                  <span style={{color:"#10B981"}}>✅ Passed: {passed}</span>
                  <span style={{color:"#EF4444"}}>❌ Failed: {failed}</span>
                  <span style={{color:"rgba(255,255,255,0.55)"}}>⏳ Pending: {notTaken}</span>
                </div>
              </div>

              {/* Bot distribution */}
              <div style={{...cardS,marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.6)",marginBottom:12}}>🤖 Bot Distribution</div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  {["ACE","CODE","NEJI"].map(bot=>{
                    // Scope to team for TL/SME, all for Manager/R&R
                    const allEmp = allRegistrations.filter(e => e.registrationType !== "admin");
                    const source = (isTL || isSME) && adminProfile?.teamName
                      ? allEmp.filter(e => e.teamName === adminProfile.teamName)
                      : allEmp;
                    const count = source.filter(e => (e.bot||"").toUpperCase() === bot).length;
                    const pct = source.length ? Math.round(count/source.length*100) : 0;
                    return (
                      <div key={bot} style={{flex:1,minWidth:80,background:"rgba(255,255,255,0.04)",borderRadius:10,padding:"10px",textAlign:"center",border:`1px solid ${botColor[bot]}33`}}>
                        <div style={{fontSize:16,fontWeight:900,color:botColor[bot]}}>{count}</div>
                        <div style={{fontSize:10,fontWeight:700,color:botColor[bot],marginTop:2}}>{bot}</div>
                        <div style={{fontSize:9,color:"rgba(255,255,255,0.55)",marginTop:2}}>{pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top 5 Leaderboard */}
              <div style={cardS}>
                <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.6)",marginBottom:12}}>🏆 Top 5 by XP</div>
                {[...statsSource].sort((a,b)=>b.xp-a.xp).slice(0,5).map((e,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",
                    borderBottom:i<4?"1px solid rgba(255,255,255,0.05)":"none"}}>
                    <div style={{width:24,height:24,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:11,fontWeight:800,flexShrink:0,
                      background:i===0?"linear-gradient(135deg,#F59E0B,#D97706)":i===1?"linear-gradient(135deg,#9CA3AF,#6B7280)":i===2?"linear-gradient(135deg,#B45309,#92400E)":"rgba(255,255,255,0.1)",
                      color:"white"}}>{i+1}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:700,color:"white"}}>{e.name}</div>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.55)"}}>{e.department}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:12,fontWeight:700,color:"#60A5FA"}}>⚡ {e.xp} XP</div>
                      <div style={{fontSize:10,color:"#F59E0B"}}>🪙 {e.tokens}</div>
                    </div>
                    <span style={{fontSize:10,fontWeight:700,color:botColor[e.bot]||"white",
                      background:`${botColor[e.bot]||"#fff"}22`,padding:"2px 7px",borderRadius:20}}>{e.bot}</span>
                  </div>
                ))}
              </div>
            </>}

            {/* ── BINGO EVENT TAB ── */}
            {!loading && !error && tab==="bingo" && (
              <BingoAdminTab
                ADMIN_PIN={ADMIN_PIN}
                cardS={cardS}
                inputS={inputS}
                adminProfile={adminProfile}
                isBingoUnlocked={isBingoUnlocked}
                setIsBingoUnlocked={setIsBingoUnlocked}
                bingoMaxWinners={bingoMaxWinners}
                setBingoMaxWinners={setBingoMaxWinners}
                bingoEventLabel={bingoEventLabel}
                setBingoEventLabel={setBingoEventLabel}
                calledNumbers={calledNumbers}
                setCalledNumbers={setCalledNumbers}
                onBingoSet={onBingoSet}
                bingoWinners={bingoWinners}
                setBingoWinners={setBingoWinners}
              />
            )}

            {/* ── REGISTRATIONS TAB ── */}
            {tab==="registrations" && (
              <RegistrationsTab
                employees={allRegistrations}
                loading={loading}
                error={error}
                adminProfile={adminProfile}
                inputS={inputS}
                cardS={cardS}
                botColor={botColor}
              />
            )}

            {tab==="redemptions" && (
              <RedemptionsTab
                adminProfile={adminProfile}
                ADMIN_PIN={ADMIN_PIN}
                cardS={cardS}
                inputS={inputS}
                isTL={isTL}
                isRnR={isRnR}
                isManager={isManager}
              />
            )}
            {tab==="rnr" && (
              <RnRTab
                employees={employees}
                allRegistrations={allRegistrations}
                loading={loading}
                cardS={cardS}
                inputS={inputS}
                adminProfile={adminProfile}
                ADMIN_PIN={ADMIN_PIN}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// PROCESS SIDEBAR — Floating right-side cards
// ─────────────────────────────────────────────────────────
function ProcessSidebar({ persona, level, xp, tokens, outfit, employeeProfile, pktResult, pp, onViewProfile, equippedEvent=null, onDeductTokens }) {
  const [activePanel, setActivePanel]       = useState(null); // null | "achievements" | "rewards"
  const [achievements, setAchievements]     = useState([]);
  const [achLoading, setAchLoading]         = useState(true);
  const [redeemItem, setRedeemItem]         = useState(null); // item being confirmed
  const [redeemedItems, setRedeemedItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("botfit_redeemed_items") || "[]"); } catch { return []; }
  });

  const alive = useIsMounted();

  // Fetch achievements from /api/achievements
  useEffect(() => {
    const ctrl = new AbortController();
    fetch("/api/achievements", { signal: ctrl.signal })
      .then(r => r.json())
      .then(d => { if (!alive.current) return; setAchievements(d.achievements || []); setAchLoading(false); })
      .catch(err => { if (isAbort(err) || !alive.current) return; setAchievements([]); setAchLoading(false); });
    return () => ctrl.abort();
  }, [alive]);

  // Leaderboard — build from local storage (in production, would come from DB)
  const leaderboard = [
    { rank:1, name:"Maria S.",    tokens:287, dept:"IR-Claims" },
    { rank:2, name:"Juan D.",     tokens:245, dept:"New Business" },
    { rank:3, name:"Ana R.",      tokens:198, dept:"Retirement" },
    { rank:4, name:employeeProfile?.name?.split(" ")[0] || "You", tokens, dept: employeeProfile?.department || "—", isYou:true },
  ].sort((a,b) => b.tokens - a.tokens).map((e,i) => ({...e, rank:i+1}));

  const handleRedeem = (item) => {
    if (tokens < item.cost) return;
    setRedeemItem(item);
  };

  const [submitting, setSubmitting] = useState(false);

  const confirmRedeem = async () => {
    if (!redeemItem || submitting) return;
    setSubmitting(true);
    try {
      // Deduct tokens immediately on client
      const newTokens = tokens - redeemItem.cost;
      onDeductTokens && onDeductTokens(newTokens);
      // Submit redemption request to API
      await fetch("/api/save-progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action:       "submitRedemption",
          employeeName: employeeProfile?.name    || "",
          department:   employeeProfile?.department || "",
          teamName:     employeeProfile?.teamName   || "",
          teamLeader:   employeeProfile?.teamLeader || "",
          rewardLabel:  redeemItem.label,
          rewardIcon:   redeemItem.icon,
          rewardCost:   redeemItem.cost,
          tokensAfter:  newTokens,
          submittedAt:  new Date().toISOString(),
          status:       "pending",
        }),
      });
      setRedeemedItems(prev => {
        const next = [...prev, redeemItem.label];
        try { localStorage.setItem("botfit_redeemed_items", JSON.stringify(next)); } catch {}
        return next;
      });
    } catch {}
    setRedeemItem(null);
    setSubmitting(false);
  };

  return (
    <>
      {/* Redeem confirm modal */}
      {redeemItem && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:500,
          display:"flex", alignItems:"center", justifyContent:"center", padding:16,
          fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
          <div style={{ background:"#0d0d1a", border:"2px solid #10B981", borderRadius:20,
            padding:"28px 24px", maxWidth:320, width:"100%", textAlign:"center",
            boxShadow:"0 0 60px rgba(16,185,129,0.25)",
            animation:"bfLvlPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>{redeemItem.icon}</div>
            <div style={{ fontSize:16, fontWeight:900, color:"white", marginBottom:6 }}>Redeem {redeemItem.label}?</div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", marginBottom:20, lineHeight:1.6 }}>
              This will use <span style={{ color:"#F59E0B", fontWeight:700 }}>{redeemItem.cost} ACN tokens</span> from your balance.
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => setRedeemItem(null)} style={{
                flex:1, padding:"11px", borderRadius:12, border:"1px solid rgba(255,255,255,0.1)",
                background:"rgba(255,255,255,0.05)", color:"rgba(255,255,255,0.5)",
                fontSize:13, cursor:"pointer", fontFamily:"inherit",
              }}>Cancel</button>
              <button onClick={confirmRedeem} disabled={submitting} style={{
                flex:1, padding:"11px", borderRadius:12, border:"none",
                background: submitting ? "rgba(16,185,129,0.4)" : "linear-gradient(135deg,#10B981,#047857)",
                color:"white", fontSize:13, fontWeight:700,
                cursor: submitting ? "not-allowed" : "pointer",
                fontFamily:"inherit", boxShadow:"0 4px 16px rgba(16,185,129,0.3)",
              }}>{submitting ? "Submitting…" : "✓ Confirm"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar container — fixed right side */}
      <div style={{ position:"fixed", right:16, top:"50%", transform:"translateY(-50%)",
        display:"flex", flexDirection:"column", gap:10, zIndex:100,
        fontFamily:"'Segoe UI',system-ui,sans-serif" }}>

        {/* ── Icon strip ── */}
        <div style={{ display:"flex", flexDirection:"column", gap:8, alignItems:"center" }}>

          {/* Profile shortcut */}
          <div onClick={onViewProfile} title="My Profile"
            style={{ width:48, height:48, borderRadius:14, cursor:"pointer",
              background:`linear-gradient(135deg,${pp.color},${pp.colorDark})`,
              display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:`0 4px 20px ${pp.colorGlow}`,
              transition:"all 0.2s", animation:"bfFadeUp 0.4s ease 0.1s both" }}
            onMouseEnter={e => e.currentTarget.style.transform="scale(1.1)"}
            onMouseLeave={e => e.currentTarget.style.transform="scale(1)"}>
            <BotAvatar persona={persona} level={level} size={28} outfit={outfit} equippedEvent={equippedEvent} showWeapon/>
          </div>

          {/* Achievements toggle */}
          <div onClick={() => setActivePanel(p => p==="achievements" ? null : "achievements")}
            title="Achievements"
            style={{ width:48, height:48, borderRadius:14, cursor:"pointer",
              background: activePanel==="achievements" ? "linear-gradient(135deg,#F59E0B,#D97706)" : "rgba(245,158,11,0.15)",
              border:`2px solid ${activePanel==="achievements" ? "#F59E0B" : "rgba(245,158,11,0.3)"}`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:22, transition:"all 0.2s", animation:"bfFadeUp 0.4s ease 0.2s both",
              boxShadow: activePanel==="achievements" ? "0 4px 20px rgba(245,158,11,0.4)" : "none" }}
            onMouseEnter={e => e.currentTarget.style.transform="scale(1.1)"}
            onMouseLeave={e => e.currentTarget.style.transform="scale(1)"}>🏆</div>

          {/* Rewards toggle */}
          <div onClick={() => setActivePanel(p => p==="rewards" ? null : "rewards")}
            title="Rewards Platform"
            style={{ width:48, height:48, borderRadius:14, cursor:"pointer",
              background: activePanel==="rewards" ? "linear-gradient(135deg,#10B981,#047857)" : "rgba(16,185,129,0.15)",
              border:`2px solid ${activePanel==="rewards" ? "#10B981" : "rgba(16,185,129,0.3)"}`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:22, transition:"all 0.2s", animation:"bfFadeUp 0.4s ease 0.3s both",
              boxShadow: activePanel==="rewards" ? "0 4px 20px rgba(16,185,129,0.4)" : "none" }}
            onMouseEnter={e => e.currentTarget.style.transform="scale(1.1)"}
            onMouseLeave={e => e.currentTarget.style.transform="scale(1)"}>🪙</div>
        </div>
      </div>

      {/* ── Expandable panels (slide in from right) ── */}
      {activePanel && (
        <div style={{ position:"fixed", right:76, top:"50%", transform:"translateY(-50%)",
          width:300, maxHeight:"85vh", overflowY:"auto",
          background:"#0d0d1a", borderRadius:20, zIndex:99,
          border:`1.5px solid ${activePanel==="achievements" ? "rgba(245,158,11,0.4)" : "rgba(16,185,129,0.4)"}`,
          boxShadow:`0 16px 60px rgba(0,0,0,0.8)`,
          animation:"bfSlideInRight 0.25s cubic-bezier(0.34,1.56,0.64,1) both",
          fontFamily:"'Segoe UI',system-ui,sans-serif",
        }}>
          <style>{`@keyframes bfSlideInRight{from{opacity:0;transform:translateY(-50%) translateX(20px)}to{opacity:1;transform:translateY(-50%) translateX(0)}}`}</style>

          {/* ── ACHIEVEMENTS PANEL ── */}
          {activePanel === "achievements" && (
            <div style={{ padding:18 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                <div style={{ fontSize:11, letterSpacing:2, color:"#F59E0B", fontWeight:700,
                  textTransform:"uppercase" }}>🏆 Announcement</div>
                <div onClick={() => setActivePanel(null)}
                  style={{ fontSize:18, color:"rgba(255,255,255,0.55)", cursor:"pointer", lineHeight:1 }}>×</div>
              </div>

              {achLoading ? (
                <div style={{ textAlign:"center", padding:24, fontSize:12,
                  color:"rgba(255,255,255,0.55)" }}>Loading announcements…</div>
              ) : achievements.length === 0 ? (
                <div style={{ textAlign:"center", padding:24 }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>📭</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.55)", lineHeight:1.6 }}>
                    No announcements yet.<br/>Check back soon!
                  </div>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {achievements.map((a,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:12,
                      padding:"12px", borderRadius:14,
                      background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.2)",
                      animation:`bfFadeUp 0.3s ease ${i*0.07}s both` }}>
                      <div style={{ fontSize:24, flexShrink:0 }}>{a.icon || "🌟"}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"white" }}>{a.name}</div>
                        <div style={{ fontSize:11, color:"#F59E0B", fontWeight:600, marginTop:2 }}>{a.achievement}</div>
                        <div style={{ fontSize:10, color:"rgba(255,255,255,0.55)", marginTop:4 }}>{a.date}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── REWARDS PANEL ── */}
          {activePanel === "rewards" && (
            <div style={{ padding:18 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <div style={{ fontSize:11, letterSpacing:2, color:"#10B981", fontWeight:700,
                  textTransform:"uppercase" }}>🪙 Rewards Platform</div>
                <div onClick={() => setActivePanel(null)}
                  style={{ fontSize:18, color:"rgba(255,255,255,0.55)", cursor:"pointer", lineHeight:1 }}>×</div>
              </div>

              {/* Balance */}
              <div style={{ background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.3)",
                borderRadius:14, padding:"12px 14px", marginBottom:16,
                display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ fontSize:12, color:"rgba(255,255,255,0.5)" }}>Your Balance</span>
                <span style={{ fontSize:22, fontWeight:900, color:"#F59E0B" }}>{tokens} <span style={{ fontSize:12 }}>ACN</span></span>
              </div>

              {/* Reward items */}
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.55)", letterSpacing:2,
                textTransform:"uppercase", marginBottom:10 }}>Redeem Rewards</div>
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:18 }}>
                {REWARD_ITEMS.map((r,i) => {
                  const canRedeem  = tokens >= r.cost;
                  const isRedeemed = redeemedItems.includes(r.label);
                  return (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:10,
                      padding:"10px 12px", borderRadius:13,
                      background: isRedeemed ? "rgba(16,185,129,0.1)" : canRedeem ? "rgba(16,185,129,0.06)" : "rgba(255,255,255,0.03)",
                      border:`1px solid ${isRedeemed ? "#10B98144" : canRedeem ? "rgba(16,185,129,0.2)" : "rgba(255,255,255,0.06)"}`,
                      transition:"all 0.2s",
                    }}>
                      <div style={{ fontSize:20, flexShrink:0 }}>{r.icon}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:12, fontWeight:700,
                          color: canRedeem ? "white" : "rgba(255,255,255,0.35)" }}>{r.label}</div>
                        <div style={{ fontSize:10, color:"#F59E0B", fontWeight:700 }}>{r.cost} ACN</div>
                      </div>
                      {isRedeemed ? (
                        <div style={{ fontSize:10, color:"#F59E0B", fontWeight:700 }}>⏳ Pending TL</div>
                      ) : (
                        <button onClick={() => canRedeem && handleRedeem(r)}
                          style={{ padding:"5px 10px", borderRadius:8, border:"none",
                            background: canRedeem ? "linear-gradient(135deg,#10B981,#047857)" : "rgba(255,255,255,0.06)",
                            color: canRedeem ? "white" : "rgba(255,255,255,0.2)",
                            fontSize:10, fontWeight:700, cursor: canRedeem ? "pointer" : "not-allowed",
                            fontFamily:"inherit", flexShrink:0 }}>
                          {canRedeem ? "Redeem" : "🔒"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Leaderboard */}
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.55)", letterSpacing:2,
                textTransform:"uppercase", marginBottom:10 }}>🥇 Top Earners</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {leaderboard.slice(0,4).map((e,i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:10,
                    padding:"8px 10px", borderRadius:12,
                    background: e.isYou ? `${pp.color}18` : "rgba(255,255,255,0.03)",
                    border:`1px solid ${e.isYou ? pp.color+"44" : "rgba(255,255,255,0.06)"}` }}>
                    <div style={{ width:22, height:22, borderRadius:"50%", flexShrink:0,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:11, fontWeight:800,
                      background: e.rank===1?"linear-gradient(135deg,#F59E0B,#D97706)":e.rank===2?"linear-gradient(135deg,#9CA3AF,#6B7280)":e.rank===3?"linear-gradient(135deg,#B45309,#92400E)":"rgba(255,255,255,0.1)",
                      color:"white" }}>{e.rank}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:11, fontWeight: e.isYou?700:500,
                        color: e.isYou ? pp.color : "rgba(255,255,255,0.8)" }}>
                        {e.name}{e.isYou ? " (You)" : ""}
                      </div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.55)" }}>{e.dept}</div>
                    </div>
                    <div style={{ fontSize:12, fontWeight:800, color:"#F59E0B" }}>{e.tokens}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:12, fontSize:10, color:"rgba(255,255,255,0.50)",
                textAlign:"center", lineHeight:1.6 }}>
                Earn ACN by chatting · completing quests · passing PKT
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────
// TUTORIAL OVERLAY
// ─────────────────────────────────────────────────────────
const TUTORIAL_STEPS = [
  {
    id: "welcome",
    icon: "👋",
    title: "Welcome to Ascendix!",
    body: "Ascendix is your AI-powered work companion — built to help you get answers faster, stay engaged, and feel supported every day. This quick tour will walk you through everything in about 60 seconds.",
    tip: null,
  },
  {
    id: "bots",
    icon: "🤖",
    title: "You have 3 AI Companions",
    body: "Each bot has a completely different purpose:",
    bullets: [
      { label: "ACE", color: "#3B82F6", desc: "Training Companion — explains policies, guides & training materials. Ask anything, no judgment." },
      { label: "CODE", color: "#10B981", desc: "Operations Expert — gives structured DECISION / DOCUMENTS / CONDITIONS answers for every case." },
      { label: "NEJI", color: "#8B5CF6", desc: "Wellness Buddy — mental health check-ins, motivation, and team culture. A safe space to talk." },
    ],
    tip: "You can switch between bots anytime from the bot select screen.",
  },
  {
    id: "process",
    icon: "📋",
    title: "Select a Topic to Start",
    body: "After choosing your bot, you'll pick a topic or process. This tells your bot what area you need help with — so it loads the right information before you even type a word.",
    tip: "CODE loads live FAQ rules from our process sheets. ACE and NEJI work from their own knowledge.",
  },
  {
    id: "chat",
    icon: "💬",
    title: "Chat — and Earn XP",
    body: "Every message you send earns XP and ACN Tokens automatically. The more you ask, the more you grow. There is no such thing as a silly question here.",
    tip: "XP fills your level bar. ACN Tokens are redeemable for real rewards like GCash, Free Lunch, and more.",
  },
  {
    id: "gamification",
    icon: "⚡",
    title: "Level Up as You Work",
    body: "Ascendix has a full RPG-style progression system:",
    bullets: [
      { label: "5 Levels", color: "#F59E0B", desc: "Starter → Achiever → Top Performer → Legend → Elite" },
      { label: "Daily Quests", color: "#10B981", desc: "Small goals that reward specific behaviors with bonus XP and tokens." },
      { label: "Wardrobe", color: "#8B5CF6", desc: "Unlock hats, capes, auras, skins & seasonal event outfits as you level up." },
      { label: "Hall of Fame", color: "#3B82F6", desc: "Top achievers are publicly recognized inside Ascendix." },
    ],
    tip: null,
  },
  {
    id: "pkt",
    icon: "📝",
    title: "Monthly PKT — Now Rewarded",
    body: "The monthly Process Knowledge Test you already know is now fully integrated into Ascendix. Take it inside the app and earn ACN Tokens based on your score.",
    bullets: [
      { label: "100%", color: "#10B981", desc: "30 ACN Tokens + 50 XP" },
      { label: "90–99%", color: "#3B82F6", desc: "25 ACN Tokens + XP" },
      { label: "80–89%", color: "#F59E0B", desc: "20 ACN Tokens + XP" },
    ],
    tip: "Find the PKT in the Quests tab.",
  },
  {
    id: "bingo",
    icon: "🎰",
    title: "Live Bingo — Per Team",
    body: "Your team leader can start a Live Bingo event any time — during a huddle, anytime. Your unique bingo card is already inside Ascendix, ready to play. No printing, no Excel files.",
    tip: "Winners earn ACN Tokens instantly. Find Bingo in the Game tab.",
  },
  {
    id: "tokens",
    icon: "🪙",
    title: "ACN Tokens — Real Rewards",
    body: "Tokens are earned from chatting, completing quests, passing the PKT, and winning Bingo. They have real value — redeem them in the Rewards section for:",
    bullets: [
      { label: "☕ Coffee Voucher", color: "#F59E0B", desc: "20 tokens" },
      { label: "🍕 Free Lunch", color: "#10B981", desc: "50 tokens" },
      { label: "💸 GCash ₱50", color: "#3B82F6", desc: "80 tokens" },
      { label: "🏖 Half-day Off", color: "#8B5CF6", desc: "150 tokens" },
    ],
    tip: null,
  },
  {
    id: "ready",
    icon: "🚀",
    title: "You're all set!",
    body: "That's everything you need to know to get started. Pick your first topic, ask your bot a question, and watch your XP grow. Good luck — and have fun with it.",
    tip: "You can revisit this tutorial anytime from the Profile tab.",
  },
];

function TutorialOverlay({ persona, employeeProfile, onClose }) {
  const [step, setStep] = useState(0);
  const p = PERSONAS[persona] || PERSONAS.ACE;
  const current = TUTORIAL_STEPS[step];
  const isLast  = step === TUTORIAL_STEPS.length - 1;
  const isFirst = step === 0;
  const firstName = employeeProfile?.name?.split(" ")[0] || null;

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:800,
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:16, fontFamily:"'Segoe UI',system-ui,sans-serif",
      animation:"bfFadeIn 0.5s ease both",
    }}>
      <div style={{
        background:`linear-gradient(135deg,#0d0d1a,${p.bg})`,
        border:`2px solid ${p.color}`,
        borderRadius:24, padding:"28px 24px",
        maxWidth:420, width:"100%",
        boxShadow:`0 0 80px ${p.colorGlow}`,
        animation:"bfLvlPop 0.55s cubic-bezier(0.34,1.56,0.64,1) both",
        display:"flex", flexDirection:"column", gap:0,
      }}>

        {/* Progress dots */}
        <div style={{ display:"flex", gap:6, justifyContent:"center", marginBottom:20 }}>
          {TUTORIAL_STEPS.map((_,i) => (
            <div key={i} onClick={() => setStep(i)} style={{
              width: i === step ? 20 : 7, height:7, borderRadius:4,
              background: i === step ? p.color : i < step ? `${p.color}66` : "rgba(255,255,255,0.15)",
              transition:"all 0.3s", cursor:"pointer",
            }}/>
          ))}
        </div>

        {/* Icon */}
        <div style={{ textAlign:"center", fontSize:44, marginBottom:12, lineHeight:1 }}>
          {current.icon}
        </div>

        {/* Title */}
        <div style={{ fontSize:20, fontWeight:900, color:"white", textAlign:"center",
          marginBottom:12, lineHeight:1.3 }}>
          {step === 0 && firstName ? `Hey ${firstName}! 👋` : current.title}
        </div>

        {/* Body */}
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.75)", lineHeight:1.65,
          marginBottom: current.bullets ? 12 : 16, textAlign:"center" }}>
          {current.body}
        </div>

        {/* Bullets */}
        {current.bullets && (
          <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
            {current.bullets.map((b, i) => (
              <div key={i} style={{
                display:"flex", alignItems:"flex-start", gap:10,
                background:"rgba(255,255,255,0.04)", borderRadius:10,
                padding:"9px 12px", border:`1px solid ${b.color}33`,
              }}>
                <div style={{
                  fontSize:11, fontWeight:800, color:b.color,
                  background:`${b.color}22`, padding:"2px 8px",
                  borderRadius:20, flexShrink:0, marginTop:1,
                  whiteSpace:"nowrap",
                }}>{b.label}</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)", lineHeight:1.5 }}>
                  {b.desc}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tip */}
        {current.tip && (
          <div style={{
            background:"rgba(255,255,255,0.04)", border:`1px solid ${p.color}33`,
            borderRadius:10, padding:"9px 12px", marginBottom:16,
            fontSize:11, color:p.accent || "rgba(255,255,255,0.6)",
            lineHeight:1.5, textAlign:"center",
          }}>
            💡 {current.tip}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display:"flex", gap:10, marginTop:4 }}>
          {!isFirst && (
            <button onClick={() => setStep(s => s - 1)} style={{
              flex:1, padding:"11px", borderRadius:12, border:`1px solid ${p.color}44`,
              background:"rgba(255,255,255,0.04)", color:p.color,
              fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
            }}>← Back</button>
          )}
          {isLast ? (
            <button onClick={onClose} style={{
              flex:2, padding:"11px", borderRadius:12, border:"none",
              background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
              color:"white", fontSize:14, fontWeight:800,
              cursor:"pointer", fontFamily:"inherit",
              boxShadow:`0 4px 20px ${p.colorGlow}`,
            }}>Let's go! 🚀</button>
          ) : (
            <button onClick={() => setStep(s => s + 1)} style={{
              flex:2, padding:"11px", borderRadius:12, border:"none",
              background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
              color:"white", fontSize:13, fontWeight:700,
              cursor:"pointer", fontFamily:"inherit",
              boxShadow:`0 4px 20px ${p.colorGlow}`,
            }}>Next →</button>
          )}
        </div>

        {/* Skip */}
        {!isLast && (
          <button onClick={onClose} style={{
            background:"none", border:"none", color:"rgba(255,255,255,0.3)",
            fontSize:11, cursor:"pointer", fontFamily:"inherit",
            marginTop:12, textAlign:"center",
          }}>Skip tutorial</button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// ASCENDIX TUTORIAL — "What is Ascendix?" onboarding carousel
// ─────────────────────────────────────────────────────────
const BFT_SLIDE_COUNT = 4;

const BFT_PERSONAS = [
  { name:"ACE",  role:"Training Companion", icon:"📚", color:"#3B82F6", desc:"Learn policies, onboarding, and benefits with instant clarity" },
  { name:"CODE", role:"Operations Expert",  icon:"⚙️", color:"#10B981", desc:"Get precise answers on claims, processes, and operations" },
  { name:"NEJI", role:"Wellness Buddy",     icon:"💚", color:"#8B5CF6", desc:"Mental health support and team engagement for better wellbeing" },
];

const BFT_FEATURES = [
  { icon:"⚡", title:"XP & Levels", desc:"Earn experience points and climb the leaderboard",  color:"#F59E0B" },
  { icon:"🪙", title:"Tokens",      desc:"Collect rewards and unlock exclusive perks",         color:"#10B981" },
  { icon:"👕", title:"Outfits",     desc:"Customize your avatar with unique styles",           color:"#8B5CF6" },
  { icon:"🎯", title:"Quests",      desc:"Complete challenges and earn special achievements",  color:"#3B82F6" },
];

const BFT_BENEFITS = [
  "24/7 AI companion assistance",
  "Personalized to your role",
  "Secure & compliant",
  "Fun, gamified learning",
];

// Generic slide chrome — keeps consistent min-height/centering across all 4 slides
// so the modal doesn't jump size as content changes.
function TutorialSlide({ children }) {
  return (
    <div style={{ minHeight:320, display:"flex", flexDirection:"column", justifyContent:"center" }}>
      {children}
    </div>
  );
}

function BFTSlideWelcome() {
  return (
    <div style={{ textAlign:"center", display:"flex", flexDirection:"column", alignItems:"center", gap:14, padding:"20px 0" }}>
      <div aria-hidden="true" style={{
        width:96, height:96, borderRadius:"50%",
        background:"linear-gradient(135deg,#3B82F6,#8B5CF6,#10B981)",
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:48, boxShadow:"0 0 60px rgba(139,92,246,0.4)",
        animation:"bftFloat 3s ease-in-out infinite",
      }}>🤖</div>
      <div style={{ fontSize:28, fontWeight:900, color:"white", letterSpacing:"-0.5px" }}>
        Welcome to Ascendix
      </div>
      <div style={{ fontSize:15, fontWeight:600, color:"rgba(255,255,255,0.75)" }}>
        Your Personalized AI Work Companion
      </div>
      <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", maxWidth:380, lineHeight:1.6 }}>
        Earn XP, unlock skills, and level up with your AI team
      </div>
    </div>
  );
}

function BFTSlidePersonas() {
  return (
    <div>
      <div style={{ fontSize:20, fontWeight:900, color:"white", textAlign:"center", marginBottom:6 }}>
        Meet the Personas
      </div>
      <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)", textAlign:"center", marginBottom:20 }}>
        Three specialized AI companions, one team
      </div>
      <div className="bft-grid-3" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
        {BFT_PERSONAS.map((p, i) => (
          <div key={p.name} className="bft-card" style={{
            background:`${p.color}14`, border:`1px solid ${p.color}40`,
            borderRadius:14, padding:"18px 12px", textAlign:"center",
            animation:`bftCardIn 0.4s cubic-bezier(0.4,0,0.2,1) ${i*0.15}s both`,
          }}>
            <div className="bft-icon" aria-hidden="true" style={{ fontSize:30, marginBottom:8 }}>{p.icon}</div>
            <div style={{ fontSize:14, fontWeight:900, color:p.color, marginBottom:2 }}>{p.name}</div>
            <div style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.6)", textTransform:"uppercase", letterSpacing:0.5, marginBottom:8 }}>{p.role}</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.65)", lineHeight:1.5 }}>{p.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BFTSlideGamification() {
  return (
    <div>
      <div style={{ fontSize:20, fontWeight:900, color:"white", textAlign:"center", marginBottom:20 }}>
        Level Up While You Work
      </div>
      <div className="bft-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        {BFT_FEATURES.map((f, i) => (
          <div key={f.title} className="bft-card" style={{
            background:`${f.color}14`, border:`1px solid ${f.color}40`,
            borderRadius:14, padding:"16px 14px",
            animation:`bftCardIn 0.4s cubic-bezier(0.4,0,0.2,1) ${i*0.15}s both`,
          }}>
            <div className="bft-icon" aria-hidden="true" style={{ fontSize:26, marginBottom:6 }}>{f.icon}</div>
            <div style={{ fontSize:13, fontWeight:800, color:"white", marginBottom:4 }}>{f.title}</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.6)", lineHeight:1.5 }}>{f.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BFTSlideStart({ onFinish }) {
  return (
    <div style={{ textAlign:"center", display:"flex", flexDirection:"column", alignItems:"center", gap:16, padding:"8px 0" }}>
      <div style={{ fontSize:22, fontWeight:900, color:"white" }}>Ready to Begin?</div>
      <div style={{ display:"flex", flexDirection:"column", gap:10, alignSelf:"center", textAlign:"left", maxWidth:320, width:"100%" }}>
        {BFT_BENEFITS.map((b, i) => (
          <div key={b} style={{
            display:"flex", alignItems:"center", gap:10,
            animation:`bftCardIn 0.4s cubic-bezier(0.4,0,0.2,1) ${i*0.15}s both`,
          }}>
            <span aria-hidden="true" style={{ color:"#10B981", fontSize:15, fontWeight:900 }}>✓</span>
            <span style={{ fontSize:13, color:"rgba(255,255,255,0.8)" }}>{b}</span>
          </div>
        ))}
      </div>
      <button
        className="bft-primary-btn"
        onClick={onFinish}
        style={{
          marginTop:8, width:"100%", maxWidth:280, padding:"15px",
          borderRadius:14, border:"none", background:"#10B981",
          color:"white", fontSize:16, fontWeight:800, cursor:"pointer",
          fontFamily:"inherit", boxShadow:"0 4px 24px rgba(16,185,129,0.4)",
        }}
      >Let's Go! 🚀</button>
      <button
        className="bft-skip-link"
        onClick={onFinish}
        style={{ background:"none", border:"none", color:"rgba(255,255,255,0.45)", fontSize:12, cursor:"pointer", fontFamily:"inherit", padding:6 }}
      >Skip Tutorial</button>
    </div>
  );
}

function BotFitTutorial({ open, onClose }) {
  const [index, setIndex]         = useState(0);
  const [direction, setDirection] = useState(null); // "next" | "prev" | null (initial)
  const [leaving, setLeaving]     = useState(false); // true during the 0.2s fade-out gap
  const setTimer  = useTimers();
  const panelRef  = useRef(null);
  const touchRef  = useRef({ x:0, y:0 });

  const isFirst = index === 0;
  const isLast  = index === BFT_SLIDE_COUNT - 1;

  const goTo = useCallback((next, dir) => {
    if (leaving || next < 0 || next >= BFT_SLIDE_COUNT || next === index) return;
    setDirection(dir);
    setLeaving(true);
    setTimer(() => { setIndex(next); setLeaving(false); }, 200);
  }, [leaving, index, setTimer]);

  const goNext = useCallback(() => goTo(index + 1, "next"), [goTo, index]);
  const goPrev = useCallback(() => goTo(index - 1, "prev"), [goTo, index]);

  // Any exit path (X, backdrop, Escape, Skip, Let's Go) marks the tutorial as
  // seen — re-showing it after an explicit dismissal would be more annoying
  // than helpful. Only the help-icon reopen bypasses this flag.
  const finish = useCallback(() => {
    try { localStorage.setItem("botfit_tutorial_seen", "true"); } catch {}
    onClose();
  }, [onClose]);

  // Reset to slide 1 on every fresh open (covers the help-icon reopen case)
  useEffect(() => {
    if (open) { setIndex(0); setDirection(null); setLeaving(false); }
  }, [open]);

  // Keyboard navigation — only bound while the modal is actually open
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape")          finish();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft")  goPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, finish, goNext, goPrev]);

  // Body scroll lock while open
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  // Focus the panel on open so keyboard/screen-reader users land inside the dialog
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x:t.clientX, y:t.clientY };
  };
  const handleTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext(); else goPrev();
    }
  };

  const slideAnim = leaving
    ? "bftFadeOut 0.2s ease both"
    : direction === "next"
    ? "bftSlideNextIn 0.3s cubic-bezier(0.4,0,0.2,1) both"
    : direction === "prev"
    ? "bftSlidePrevIn 0.3s cubic-bezier(0.4,0,0.2,1) both"
    : "bftFadeIn 0.3s cubic-bezier(0.4,0,0.2,1) both";

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) finish(); }}
      style={{
        position:"fixed", inset:0, zIndex:900,
        background:"rgba(6,9,18,0.7)",
        display:"flex", alignItems:"center", justifyContent:"center",
        padding:16, fontFamily:"'Segoe UI',system-ui,sans-serif",
        animation:"bftOverlayIn 0.3s ease both",
      }}
    >
      <style>{`
        @keyframes bftOverlayIn { from{opacity:0} to{opacity:1} }
        @keyframes bftModalIn { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
        @keyframes bftFadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes bftFadeOut { from{opacity:1} to{opacity:0} }
        @keyframes bftSlideNextIn { from{opacity:0;transform:translateX(36px)} to{opacity:1;transform:translateX(0)} }
        @keyframes bftSlidePrevIn { from{opacity:0;transform:translateX(-36px)} to{opacity:1;transform:translateX(0)} }
        @keyframes bftCardIn  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes bftFloat   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .bft-card { transition:transform 0.2s cubic-bezier(0.4,0,0.2,1), box-shadow 0.2s ease; }
        .bft-card:hover { transform:scale(1.02); box-shadow:0 8px 24px rgba(0,0,0,0.35); }
        .bft-card:hover .bft-icon { transform:rotate(10deg); }
        .bft-icon { display:inline-block; transition:transform 0.2s ease; }
        .bft-dot { transition:background 0.25s cubic-bezier(0.4,0,0.2,1), width 0.25s cubic-bezier(0.4,0,0.2,1); }
        .bft-nav-btn { transition:opacity 0.2s ease, transform 0.2s ease, background 0.2s ease; }
        .bft-nav-btn:not(:disabled):hover { background:rgba(255,255,255,0.12); transform:scale(1.08); }
        .bft-close-btn { transition:opacity 0.2s ease; }
        .bft-close-btn:hover { opacity:0.6; }
        .bft-primary-btn { transition:background 0.2s ease, transform 0.2s ease; }
        .bft-primary-btn:hover { background:#047857; transform:translateY(-1px); }
        .bft-skip-link { transition:color 0.2s ease; }
        .bft-skip-link:hover { color:rgba(255,255,255,0.85); text-decoration:underline; }
        @media (max-width:640px) {
          .bft-modal { max-width:90vw !important; padding:20px !important; }
          .bft-grid-3 { grid-template-columns:1fr !important; }
          .bft-grid-2 { grid-template-columns:1fr !important; }
        }
      `}</style>

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="What is Ascendix? Tutorial"
        tabIndex={-1}
        className="bft-modal"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          position:"relative", background:"#1a1f35", borderRadius:16, padding:32,
          maxWidth:600, width:"100%", maxHeight:"85vh", overflowY:"auto",
          boxShadow:"0 20px 80px rgba(0,0,0,0.6)", outline:"none",
          animation:"bftModalIn 0.4s cubic-bezier(0.4,0,0.2,1) both",
        }}
      >
        <button
          className="bft-close-btn"
          onClick={finish}
          aria-label="Close tutorial"
          style={{
            position:"absolute", top:16, right:16, width:40, height:40, borderRadius:"50%",
            border:"none", background:"rgba(255,255,255,0.08)", color:"white", fontSize:16,
            cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          }}
        >✕</button>

        <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", fontWeight:700, letterSpacing:1, marginBottom:16 }}>
          Slide {index + 1} of {BFT_SLIDE_COUNT}
        </div>

        <div key={`${index}-${leaving}`} style={{ animation:slideAnim }}>
          <TutorialSlide>
            {index === 0 && <BFTSlideWelcome />}
            {index === 1 && <BFTSlidePersonas />}
            {index === 2 && <BFTSlideGamification />}
            {index === 3 && <BFTSlideStart onFinish={finish} />}
          </TutorialSlide>
        </div>

        <nav aria-label="Tutorial slide navigation" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:24 }}>
          <button
            className="bft-nav-btn"
            onClick={goPrev}
            disabled={isFirst}
            aria-label="Previous slide"
            style={{
              width:44, height:44, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.15)",
              background:"rgba(255,255,255,0.05)", color:"white", fontSize:16,
              cursor: isFirst ? "not-allowed" : "pointer", opacity: isFirst ? 0.5 : 1,
            }}
          >←</button>

          <div style={{ display:"flex", gap:4 }}>
            {Array.from({ length: BFT_SLIDE_COUNT }).map((_, i) => (
              <button
                key={i}
                className="bft-dot"
                onClick={() => goTo(i, i > index ? "next" : "prev")}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index}
                style={{
                  width:32, height:32, border:"none", background:"none", cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center", padding:0,
                }}
              >
                <span aria-hidden="true" className="bft-dot" style={{
                  display:"block", width: i === index ? 22 : 8, height:8, borderRadius:4,
                  background: i === index ? "#10B981" : "rgba(255,255,255,0.2)",
                }}/>
              </button>
            ))}
          </div>

          <button
            className="bft-nav-btn"
            onClick={goNext}
            disabled={isLast}
            aria-label="Next slide"
            style={{
              width:44, height:44, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.15)",
              background:"rgba(255,255,255,0.05)", color:"white", fontSize:16,
              cursor: isLast ? "not-allowed" : "pointer", opacity: isLast ? 0.5 : 1,
            }}
          >→</button>
        </nav>

        {!isLast && (
          <div style={{ textAlign:"center", marginTop:14 }}>
            <button
              className="bft-skip-link"
              onClick={finish}
              style={{ background:"none", border:"none", color:"rgba(255,255,255,0.45)", fontSize:12, cursor:"pointer", fontFamily:"inherit", padding:6 }}
            >Skip Tutorial</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// GAME TAB
// ─────────────────────────────────────────────────────────
function GameTab({ persona, employeeProfile, awardXP, p, isBingoUnlocked, bingoMaxWinners, bingoEventLabel, calledNumbers = [], teamName = "", onWinReport }) {
  const [bingoOpen, setBingoOpen] = useState(false);
  return (
    <div style={{ flex:1, overflowY:"auto", padding:"16px 14px", display:"flex", flexDirection:"column", gap:16 }}>

      <div style={{ fontSize:9, fontWeight:800, letterSpacing:3, color:p.color, textTransform:"uppercase" }}>
        🎮 Game Center
      </div>

      {/* ── BINGO EVENT CARD ── */}
      <div style={{ background: isBingoUnlocked ? "rgba(245,158,11,0.06)" : "rgba(255,255,255,0.03)",
        border: `1px solid ${isBingoUnlocked ? "rgba(245,158,11,0.25)" : "rgba(255,255,255,0.08)"}`, borderRadius:18 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ fontSize:28, filter: isBingoUnlocked ? "none" : "grayscale(1)", opacity: isBingoUnlocked ? 1 : 0.5 }}>🎰</div>
            <div>
              <div style={{ fontSize:14, fontWeight:900, color: isBingoUnlocked ? "white" : "rgba(255,255,255,0.4)", letterSpacing:0.5 }}>
                {isBingoUnlocked ? (bingoEventLabel || "Live Bingo Event") : "Bingo Event"}
              </div>
              <div style={{ fontSize:10, marginTop:2,
                color: isBingoUnlocked ? "rgba(255,255,255,0.60)" : "rgba(255,255,255,0.30)" }}>
                {isBingoUnlocked
                  ? `🟢 Live · Mark your card & win 100 ACN tokens`
                  : "🔒 Waiting for admin to start the event"}
              </div>
            </div>
          </div>
          {isBingoUnlocked ? (
            <button onClick={() => setBingoOpen(o => !o)} style={{
              padding:"9px 18px", borderRadius:12, border:"none", cursor:"pointer",
              background: bingoOpen ? "rgba(245,158,11,0.2)" : "linear-gradient(135deg,#F59E0B,#D97706)",
              color: bingoOpen ? "#F59E0B" : "#1f1a00",
              fontSize:12, fontWeight:800, fontFamily:"inherit", flexShrink:0, transition:"all 0.2s",
            }}>
              {bingoOpen ? "✕ Close" : "🎰 Play Bingo"}
            </button>
          ) : (
            <div style={{ padding:"9px 16px", borderRadius:12, background:"rgba(255,255,255,0.04)",
              border:"1px solid rgba(255,255,255,0.08)", fontSize:11, color:"rgba(255,255,255,0.30)",
              fontWeight:700, flexShrink:0 }}>
              🔒 Locked
            </div>
          )}
        </div>

        {/* Locked state detail */}
        {!isBingoUnlocked && (
          <div style={{ margin:"0 14px 14px", padding:"14px 16px", borderRadius:12,
            background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)",
            textAlign:"center" }}>
            <div style={{ fontSize:28, marginBottom:8, opacity:0.4 }}>🎰</div>
            <div style={{ fontSize:12, fontWeight:800, color:"rgba(255,255,255,0.35)", marginBottom:4 }}>
              No Active Bingo Event
            </div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.25)", lineHeight:1.6 }}>
              The ACN Admin will start the event during your huddle.<br/>
              Come back when it's live!
            </div>
          </div>
        )}

        {isBingoUnlocked && bingoOpen && (
          <div style={{ borderTop:"1px solid rgba(245,158,11,0.15)", minHeight:520 }}>
            <LiveBingoGame persona={persona} employeeProfile={employeeProfile}
              onWinBingo={() => awardXP(50, 100)}
              bingoMaxWinners={bingoMaxWinners}
              bingoEventLabel={bingoEventLabel}
              calledNumbers={calledNumbers}
              teamName={teamName}
              onWinReport={onWinReport}/>
          </div>
        )}
      </div>

      {/* ── COMING SOON GAMES ── */}
      <div style={{ fontSize:9, fontWeight:800, letterSpacing:3, color:"rgba(255,255,255,0.60)", textTransform:"uppercase" }}>
        More Games — Coming Soon
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        {[
          { icon:"🧠", name:"Knowledge Quiz",   desc:"Test your process knowledge in a timed challenge" },
          { icon:"🎯", name:"Case Challenge",   desc:"Solve real-case scenarios to earn bonus XP" },
          { icon:"🃏", name:"Flash Cards",      desc:"Quick-fire policy flashcards for fast learners" },
          { icon:"🏆", name:"Team Leaderboard", desc:"Compete with your team for the top spot" },
        ].map((game, i) => (
          <div key={i} style={{
            background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.07)",
            borderRadius:16, padding:"16px 14px", position:"relative", overflow:"hidden", opacity:0.6,
          }}>
            <div style={{
              position:"absolute", top:10, right:-14,
              background:"rgba(245,158,11,0.85)", color:"#000",
              fontSize:7, fontWeight:900, letterSpacing:1,
              padding:"3px 20px", transform:"rotate(35deg)", textTransform:"uppercase",
            }}>Soon</div>
            <div style={{ fontSize:26, marginBottom:8 }}>{game.icon}</div>
            <div style={{ fontSize:12, fontWeight:800, color:"rgba(255,255,255,0.70)", marginBottom:4 }}>{game.name}</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.50)", lineHeight:1.5 }}>{game.desc}</div>
            <div style={{ marginTop:10, fontSize:9, fontWeight:700, color:"rgba(245,158,11,0.65)", letterSpacing:1, textTransform:"uppercase" }}>
              🚧 Coming Soon
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────────────
// EMPLOYEE LOGIN MODAL — Employee (lookup by name against the
// "Registered Employee" sheet) or Guest. No new data is collected;
// Employee Login only matches against rows already in the sheet.
// ─────────────────────────────────────────────────────────
function EmployeeLoginModal({ p, onLogin, onGuest, onClose }) {
  const [step, setStep]     = useState("choice"); // "choice" | "employee"
  const [name, setName]     = useState("");
  const [looking, setLooking] = useState(false);
  const [error, setError]   = useState("");
  const alive = useIsMounted();

  const lookup = async () => {
    if (!name.trim()) { setError("Enter your name"); return; }
    setLooking(true); setError("");
    try {
      const res  = await fetch(`/api/admin-data?key=botfit-admin-2026`);
      const data = await res.json();
      const match = (data.employees || []).find(e =>
        e.name?.trim().toLowerCase() === name.trim().toLowerCase()
      );
      if (!alive.current) return;
      if (match) {
        onLogin({
          name:       match.name,
          department: match.department || "",
          teamName:   match.teamName   || "",
          teamLeader: match.teamLeader || "",
        });
      } else {
        setError("Name not found — ask your Team Leader to add you to the Registered Employee sheet.");
      }
    } catch {
      if (alive.current) setError("Lookup failed — check your connection and try again.");
    } finally {
      if (alive.current) setLooking(false);
    }
  };

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:850,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      fontFamily:"'Segoe UI',system-ui,sans-serif", animation:"bfFadeIn 0.2s ease both",
    }}>
      <div style={{
        background:`linear-gradient(135deg,#0d0d1a,${p.bg})`,
        border:`2px solid ${p.color}`, borderRadius:24, padding:"28px 24px",
        maxWidth:380, width:"100%", boxShadow:`0 0 80px ${p.colorGlow}`,
        animation:"bfLvlPop 0.45s cubic-bezier(0.34,1.56,0.64,1) both",
      }}>
        {step === "choice" ? (<>
          <div style={{ textAlign:"center", marginBottom:22 }}>
            <div style={{ fontSize:34, marginBottom:10 }}>👤</div>
            <div style={{ fontSize:19, fontWeight:900, color:"white" }}>Sign In</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.55)", marginTop:6, lineHeight:1.5 }}>
              Log in to sync your progress and join team events like Live Bingo.
            </div>
          </div>
          <button onClick={() => setStep("employee")} style={{
            width:"100%", padding:"13px", borderRadius:14, border:"none",
            background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
            color:"white", fontSize:14, fontWeight:700, cursor:"pointer",
            fontFamily:"inherit", boxShadow:`0 4px 20px ${p.colorGlow}`, marginBottom:10,
          }}>👤 Log In as Employee</button>
          <button onClick={onGuest} style={{
            width:"100%", padding:"12px", borderRadius:14, border:"1px solid rgba(255,255,255,0.12)",
            background:"rgba(255,255,255,0.04)", color:"rgba(255,255,255,0.65)",
            fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit", marginBottom:10,
          }}>🚀 Continue as Guest</button>
          <button onClick={onClose} style={{
            width:"100%", padding:"8px", border:"none", background:"none",
            color:"rgba(255,255,255,0.35)", fontSize:12, cursor:"pointer", fontFamily:"inherit",
          }}>Cancel</button>
        </>) : (<>
          <div style={{ textAlign:"center", marginBottom:20 }}>
            <div style={{ fontSize:11, letterSpacing:3, color:p.color, fontWeight:700,
              textTransform:"uppercase", marginBottom:6 }}>Employee Login</div>
            <div style={{ fontSize:18, fontWeight:900, color:"white" }}>What's your name?</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", marginTop:6, lineHeight:1.5 }}>
              We'll match it against your team's registered roster — no new info is collected.
            </div>
          </div>
          <input value={name} onChange={e => { setName(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && lookup()}
            placeholder="Full name as registered" autoFocus disabled={looking}
            style={{
              width:"100%", padding:"12px 14px", borderRadius:12, boxSizing:"border-box",
              border:`1px solid ${error ? "#EF4444" : "rgba(255,255,255,0.12)"}`,
              background:"rgba(255,255,255,0.06)", color:"white", fontSize:14,
              outline:"none", fontFamily:"inherit", marginBottom:10,
            }}/>
          {error && <div style={{ fontSize:11, color:"#EF4444", marginBottom:10, lineHeight:1.5 }}>{error}</div>}
          <button onClick={lookup} disabled={looking} style={{
            width:"100%", padding:"13px", borderRadius:14, border:"none",
            background: looking ? "rgba(255,255,255,0.1)" : `linear-gradient(135deg,${p.color},${p.colorDark})`,
            color:"white", fontSize:14, fontWeight:700,
            cursor: looking ? "not-allowed" : "pointer",
            fontFamily:"inherit", boxShadow: looking ? "none" : `0 4px 20px ${p.colorGlow}`, marginBottom:10,
          }}>{looking ? "🔍 Looking up…" : "Continue →"}</button>
          <button onClick={() => { setStep("choice"); setError(""); }} disabled={looking} style={{
            width:"100%", padding:"10px", borderRadius:12, border:"none",
            background:"rgba(255,255,255,0.04)", color:"rgba(255,255,255,0.55)",
            fontSize:13, cursor:"pointer", fontFamily:"inherit",
          }}>← Back</button>
        </>)}
      </div>
    </div>
  );
}

function AppInner() {
  // ── Load saved bot from localStorage ─────────────────
  // Once an employee picks a bot it is locked permanently.
  // The other two bots are greyed out and cannot be selected.
  const [lockedPersona, setLockedPersona] = useState(null); // Bots are never locked — freely switchable
  const [employeeProfile, setEmployeeProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem("botfit_employee") || "null"); }
    catch { return null; }
  });
  const [showAdmin, setShowAdmin]                 = useState(false);
  const [pipWindow, setPipWindow]                 = useState(null);
  const [pipActive, setPipActive]                 = useState(false);
  const [showPipPrompt, setShowPipPrompt]         = useState(false);
  const [showAdminBot, setShowAdminBot]           = useState(false);
  const [adminProfile, setAdminProfile]           = useState(() => {
    try { return JSON.parse(localStorage.getItem("botfit_admin_profile") || "null"); }
    catch { return null; }
  });
  const [hallOfFame, setHallOfFame]               = useState([]);
  const [hallOfFameLoading, setHallOfFameLoading] = useState(true);
  const [latestUpdates, setLatestUpdates]               = useState([]);
  const [latestUpdatesLoading, setLatestUpdatesLoading] = useState(true);
  const [adminTokens, setAdminTokens]             = useState(() => {
    try { return parseInt(localStorage.getItem("botfit_admin_tokens") || "200", 10); }
    catch { return 200; }
  });
  const [adminQuests, setAdminQuests]             = useState(() => {
    try { return JSON.parse(localStorage.getItem("botfit_admin_quests") || "null") || ADMIN_QUESTS; }
    catch { return ADMIN_QUESTS; }
  });
  const [rnrClaimed, setRnrClaimed] = useState(() => {
    try {
      const name = JSON.parse(localStorage.getItem("botfit_employee") || "null")?.name;
      if (!name) return []; // new/logged-out user — never read another user's claimed list
      return JSON.parse(localStorage.getItem(`botfit_rnr_claimed__${name}`) || "[]");
    } catch { return []; }
  });
  const [rnrAwards, setRnrAwards] = useState(() => {
    try {
      const name = JSON.parse(localStorage.getItem("botfit_employee") || "null")?.name;
      if (!name) return []; // new/logged-out user — never read another user's awards
      return JSON.parse(localStorage.getItem(`botfit_rnr_awards__${name}`) || "[]");
    } catch { return []; }
  }); // [{questId, questText, tokens, awardedBy, awardedAt}]
  const [showCertificate, setShowCertificate] = useState(null); // { award, quest }
  const [isBingoUnlocked, setIsBingoUnlocked] = useState(() => {
    try { return localStorage.getItem("botfit_bingo_unlocked") === "true"; }
    catch { return false; }
  });
  const [bingoMaxWinners, setBingoMaxWinners] = useState(() => {
    try { return parseInt(localStorage.getItem("botfit_bingo_max_winners") || "1", 10); }
    catch { return 1; }
  });
  const [bingoEventLabel, setBingoEventLabel] = useState(() => {
    try { return localStorage.getItem("botfit_bingo_label") || "Huddle Bingo"; }
    catch { return "Huddle Bingo"; }
  });
  const [calledNumbers, setCalledNumbers] = useState([]); // ["B-7","I-22",...] — official called numbers
  const [bingoWinners, setBingoWinners]     = useState([]); // [{name,pattern,position,time}] — reported by players
  const [showTutorial, setShowTutorial] = useState(false);
  const [showBotFitTutorial, setShowBotFitTutorial] = useState(false); // "What is Ascendix?" onboarding carousel
  const [showLoginModal, setShowLoginModal] = useState(false); // Employee/Guest choice, shown on first Profile click
  const [showLockWarning, setShowLockWarning]     = useState(false);
  const [attemptedBot, setAttemptedBot]           = useState(null);
  // Process screen lock
  const [procAuthed, setProcAuthed]   = useState(() => {
    // Returning users (have picked a bot before) skip the PIN — new users must enter it first
    try { return !!localStorage.getItem("botfit_persona"); }
    catch { return false; }
  });
  const [procPwInput, setProcPwInput] = useState("");
  const [procPwError, setProcPwError] = useState(false);
  const PROCESS_PASSWORD = "ascendix2026";

  const [screen, setScreen]           = useState(() => {
    try {
      const savedPersona = localStorage.getItem("botfit_persona");
      const proc         = localStorage.getItem("botfit_process");
      if (savedPersona && proc) return "chat";
      if (savedPersona) return "process";
      return "select";
    } catch { return "select"; }
  });
  const [persona, setPersona]         = useState(() => {
    try { return localStorage.getItem("botfit_persona") || null; }
    catch { return null; }
  });
  const [process, setProcess]         = useState(() => {
    try { return JSON.parse(localStorage.getItem("botfit_process") || "null"); }
    catch { return null; }
  });
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState("");
  const [loading, setLoading]         = useState(false);
  const [xp, setXp]                   = useState(() => {
    try { return parseInt(localStorage.getItem("botfit_xp") || "0", 10); }
    catch { return 0; }
  });
  const [tokens, setTokens]           = useState(() => {
    try { return parseInt(localStorage.getItem("botfit_tokens") || "0", 10); }
    catch { return 0; }
  });
  const [level, setLevel]             = useState(() => {
    try { return parseInt(localStorage.getItem("botfit_level") || "1", 10); }
    catch { return 1; }
  });
  // ── Per-bot XP & level stored separately ──
  const [botXpMap, setBotXpMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem("botfit_bot_xp_map") || "{}"); }
    catch { return {}; }
  });
  const getBotLevel = (botId) => {
    try {
      const map = JSON.parse(localStorage.getItem("botfit_bot_xp_map") || "{}");
      return xpToLevel(map[botId] || 0);
    } catch { return 1; }
  };
  const [msgCount, setMsgCount]       = useState(() => {
    try { return parseInt(localStorage.getItem("botfit_msgcount") || "0", 10); }
    catch { return 0; }
  });
  const [quests, setQuests]           = useState(() => {
    try { return JSON.parse(localStorage.getItem("botfit_quests") || "null") || QUESTS; }
    catch { return QUESTS; }
  });
  const [xpGain, setXpGain]           = useState(null);
  const [tokenGain, setTokenGain]     = useState(null);
  const [showLvlUp, setShowLvlUp]     = useState(false);
  const [lvlUpNum, setLvlUpNum]       = useState(null);
  const [botPulse, setBotPulse]       = useState(false);
  const [tab, setTab]                 = useState("chat");
  const [showPKT, setShowPKT]                   = useState(false);
  const [showPKTResults, setShowPKTResults]       = useState(false);
  const [showRetakeLocked, setShowRetakeLocked]   = useState(false);
  const [pktResult, setPktResult]                 = useState(() => {
    try { return JSON.parse(localStorage.getItem("botfit_pkt") || "null"); }
    catch { return null; }
  });
  const [outfit, setOutfit]           = useState(() => {
    try { return JSON.parse(localStorage.getItem("botfit_outfit") || "null") || DEFAULT_OUTFIT; }
    catch { return DEFAULT_OUTFIT; }
  });
  const [equippedEvent, setEquippedEvent] = useState(() => {
    try {
      const saved = localStorage.getItem("botfit_event_outfit") || null;
      // Clear if level requirement not met (level loaded from localStorage)
      const savedLevel = parseInt(localStorage.getItem("botfit_level") || "1", 10);
      if (saved && savedLevel < 3) {
        localStorage.removeItem("botfit_event_outfit");
        return null;
      }
      return saved;
    } catch { return null; }
  });
  const [hoveredP, setHoveredP]       = useState(null);
  const [hoveredProc, setHoveredProc] = useState(null);
  const messagesEndRef                = useRef(null);
  const p = persona ? PERSONAS[persona] : null;
  const alive    = useIsMounted();
  const setTimer = useTimers();

  // ── Persist progress to localStorage — debounced batch (500ms) ──────
  // Replaces 8 individual useEffects to cut mobile I/O pressure
  const _lsSaveTimer = useRef(null);
  useEffect(() => {
    if (_lsSaveTimer.current) clearTimeout(_lsSaveTimer.current);
    _lsSaveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem("botfit_xp",             String(xp));
        // Also update per-bot map
        try {
          const map = JSON.parse(localStorage.getItem("botfit_bot_xp_map") || "{}");
          if (persona) { map[persona] = xp; localStorage.setItem("botfit_bot_xp_map", JSON.stringify(map)); }
          setBotXpMap({...map});
        } catch {}
        localStorage.setItem("botfit_tokens",         String(tokens));
        localStorage.setItem("botfit_level",          String(level));
        localStorage.setItem("botfit_msgcount",       String(msgCount));
        localStorage.setItem("botfit_quests",         JSON.stringify(quests));
        localStorage.setItem("botfit_bingo_unlocked",     String(isBingoUnlocked));
        localStorage.setItem("botfit_bingo_max_winners",  String(bingoMaxWinners));
        localStorage.setItem("botfit_bingo_label",        bingoEventLabel);
        if (process)   localStorage.setItem("botfit_process", JSON.stringify(process));
        if (pktResult) localStorage.setItem("botfit_pkt",     JSON.stringify(pktResult));
      } catch {}
    }, 500);
    return () => { if (_lsSaveTimer.current) clearTimeout(_lsSaveTimer.current); };
  }, [xp, tokens, level, msgCount, quests, isBingoUnlocked, bingoMaxWinners, bingoEventLabel, process, pktResult]);

  // ── Poll shared bingo state from API every 8s (players only) ──
  // Admin device never polls — their local state IS the source of truth.
  // Players poll to detect when admin starts/stops the event.
  const bingoLastSetRef = useRef(0); // timestamp of last local admin set

  useEffect(() => {
    // Admin profiles never poll for bingo state — they control it
    const isAdmin = !!adminProfile;
    if (isAdmin) return;

    const ctrl = new AbortController();
    const fetchBingoState = async () => {
      try {
        if (Date.now() - bingoLastSetRef.current < 15000) return;
        const team = employeeProfile?.teamName || "";
        if (!team) return;
        const url = `/api/save-progress?action=getBingo&team=${encodeURIComponent(team)}`;
        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (!data.ok || !data.updatedAt || !alive.current) return;
        setIsBingoUnlocked(data.unlocked);
        setBingoMaxWinners(data.maxWinners);
        setBingoEventLabel(data.label);
        if (Array.isArray(data.calledNumbers)) setCalledNumbers(data.calledNumbers);
        if (Array.isArray(data.winners)) setBingoWinners(data.winners);
        try {
          localStorage.setItem("botfit_bingo_unlocked",    String(data.unlocked));
          localStorage.setItem("botfit_bingo_max_winners", String(data.maxWinners));
          localStorage.setItem("botfit_bingo_label",       data.label);
        } catch {}
      } catch {}
    };
    const timeout  = setTimeout(fetchBingoState, 3000);
    const interval = setInterval(fetchBingoState, 4000);
    return () => { clearTimeout(timeout); clearInterval(interval); ctrl.abort(); };
  }, [employeeProfile?.teamName, adminProfile, alive]);

  // ── Admin-only: poll for winners every 5s ──────────────────
  useEffect(() => {
    if (!adminProfile) return; // players don't need this — they report wins
    const ctrl = new AbortController();
    const fetchWinners = async () => {
      try {
        const team = adminProfile.teamName || "";
        if (!team) return;
        const url = `/api/save-progress?action=getBingo&team=${encodeURIComponent(team)}`;
        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (!data.ok || !alive.current) return;
        // Only update winners — don't touch unlocked/calledNumbers (admin controls those)
        if (Array.isArray(data.winners) && data.winners.length > 0) {
          setBingoWinners(data.winners);
        }
      } catch {}
    };
    const interval = setInterval(fetchWinners, 5000);
    return () => { clearInterval(interval); ctrl.abort(); };
  }, [adminProfile, alive]);

  // ── Poll RnR awards for current employee ──────────────────
  useEffect(() => {
    if (adminProfile) return;
    const name = employeeProfile?.name;
    if (!name) return;
    const ctrl = new AbortController();
    const fetchRnR = async () => {
      try {
        const res = await fetch(`/api/admin-data?key=botfit-admin-2026&action=getRnR&name=${encodeURIComponent(name)}`,
          { signal: ctrl.signal });
        if (!res.ok || !alive.current) return;
        const data = await res.json();
        if (Array.isArray(data.awards)) {
          setRnrAwards(data.awards);
          try {
            const key = name ? `botfit_rnr_awards__${name}` : null;
            if (key) localStorage.setItem(key, JSON.stringify(data.awards));
          } catch {}
        }
      } catch {}
    };
    fetchRnR();
    const interval = setInterval(fetchRnR, 30000);
    return () => { clearInterval(interval); ctrl.abort(); };
  }, [employeeProfile?.name, adminProfile, alive]);

  // ── One-time RnR key migration + isolation guard ───────────
  // For existing users: moves old flat keys → user-scoped keys so they keep their history.
  // For new users on a shared device: always deletes the flat keys so they can never be inherited.
  useEffect(() => {
    const name = employeeProfile?.name;
    // Always nuke the flat keys — they must never be readable by another account
    const purge = () => {
      try { localStorage.removeItem("botfit_rnr_claimed"); } catch {}
      try { localStorage.removeItem("botfit_rnr_awards");  } catch {}
    };
    if (!name) { purge(); return; }
    try {
      const scopedClaimedKey = `botfit_rnr_claimed__${name}`;
      const scopedAwardsKey  = `botfit_rnr_awards__${name}`;
      // Migrate old claimed list if this user has no scoped key yet
      const oldClaimed = localStorage.getItem("botfit_rnr_claimed");
      if (oldClaimed && !localStorage.getItem(scopedClaimedKey)) {
        const parsed = JSON.parse(oldClaimed);
        localStorage.setItem(scopedClaimedKey, JSON.stringify(parsed));
        setRnrClaimed(parsed);
      }
      // Migrate old awards list if this user has no scoped key yet
      const oldAwards = localStorage.getItem("botfit_rnr_awards");
      if (oldAwards && !localStorage.getItem(scopedAwardsKey)) {
        const parsed = JSON.parse(oldAwards);
        localStorage.setItem(scopedAwardsKey, JSON.stringify(parsed));
        setRnrAwards(parsed);
      }
    } catch {}
    purge(); // always remove flat keys after migration attempt
  }, [employeeProfile?.name]);

  // ── Check for rejected redemptions and refund tokens ──────
  useEffect(() => {
    if (adminProfile) return;
    const name = employeeProfile?.name;
    if (!name) return;
    const ctrl = new AbortController();
    const checkRefunds = async () => {
      try {
        const res = await fetch(`/api/admin-data?key=botfit-admin-2026&action=getRedemptions&name=${encodeURIComponent(name)}`,
          { signal: ctrl.signal });
        if (!res.ok || !alive.current) return;
        const data = await res.json();
        if (!Array.isArray(data.redemptions)) return;

        // Get already-refunded list from localStorage
        let refunded = [];
        try { refunded = JSON.parse(localStorage.getItem("botfit_refunded") || "[]"); } catch {}

        const toRefund = data.redemptions.filter(r =>
          r.status === "Rejected" && !refunded.includes(r.rowIndex)
        );

        if (toRefund.length === 0) return;

        // Refund each rejected redemption
        let refundTotal = 0;
        const newRefunded = [...refunded];
        toRefund.forEach(r => {
          refundTotal += r.rewardCost || 0;
          newRefunded.push(r.rowIndex);
        });

        if (refundTotal > 0) {
          setTokens(prev => {
            const next = prev + refundTotal;
            try { localStorage.setItem("botfit_tokens", String(next)); } catch {}
            return next;
          });
          setTokenGain(refundTotal);
          setTimer(() => setTokenGain(null), 2200);
          // Remove rejected items from redeemed list so employee can re-redeem
          try {
            const labels = toRefund.map(r => r.rewardLabel);
            const currentRedeemed = JSON.parse(localStorage.getItem("botfit_redeemed_items") || "[]");
            const updatedRedeemed = currentRedeemed.filter(l => !labels.includes(l));
            localStorage.setItem("botfit_redeemed_items", JSON.stringify(updatedRedeemed));
          } catch {}
        }

        // Mark as refunded so we don't double-refund
        try { localStorage.setItem("botfit_refunded", JSON.stringify(newRefunded)); } catch {}
      } catch {}
    };
    // Check on load and every 60s
    checkRefunds();
    const interval = setInterval(checkRefunds, 60000);
    return () => { clearInterval(interval); ctrl.abort(); };
  }, [employeeProfile?.name, adminProfile, alive, setTimer]);

  useEffect(() => {
    const ctrl = new AbortController();
    const fetchHallOfFame = async () => {
      try {
        const response = await fetch("/api/hallOfFame", { signal: ctrl.signal });
        if (!response.ok) throw new Error("Failed to fetch Hall of Fame");
        const data = await response.json();
        if (alive.current) setHallOfFame(data.hallOfFame || []);
      } catch (err) {
        if (isAbort(err) || !alive.current) return;
        console.error("Hall of Fame fetch error:", err);
        setHallOfFame([]);
      } finally {
        if (alive.current) setHallOfFameLoading(false);
      }
    };
    fetchHallOfFame();
    return () => ctrl.abort();
  }, [alive]);

  useEffect(() => {
    const ctrl = new AbortController();
    const fetchLatestUpdates = async () => {
      try {
        const response = await fetch("/api/latestUpdate", { signal: ctrl.signal });
        if (!response.ok) throw new Error("Failed to fetch latest updates");
        const data = await response.json();
        if (alive.current) setLatestUpdates(data.updates || []);
      } catch (err) {
        if (isAbort(err) || !alive.current) return;
        console.error("Latest updates fetch error:", err);
        setLatestUpdates([]);
      } finally {
        if (alive.current) setLatestUpdatesLoading(false);
      }
    };
    fetchLatestUpdates();
    return () => ctrl.abort();
  }, [alive]);

  // Handle navigation messages from PiP window.
  // Stable identity via useCallback — the effect below re-subscribes only if the
  // handler actually changes, and removeEventListener always gets the same ref.
  const handlePipNav = useCallback((e) => {
    if (e.data?.type === "BOTFIT_NAV") {
      const tab = e.data.tab;
      if (tab === "chat")         { setScreen("chat"); setTab("chat"); }
      if (tab === "profile")      { setScreen("chat"); setTab("profile"); }
      if (tab === "achievements") { setScreen("chat"); setTab("quests"); }
      if (tab === "rewards")      { setScreen("chat"); setTab("customize"); }
      window.focus();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", handlePipNav);
    return () => window.removeEventListener("message", handlePipNav);
  }, [handlePipNav]);

  // Show prompt when tab becomes visible again (after being hidden)
  // Auto-launch not possible without user gesture — show a friendly nudge instead
  // pipActive is read through a ref so this listener is bound once per
  // screen/persona instead of being torn down and re-added on every PiP toggle.
  const pipActiveRef = useRef(pipActive);
  useEffect(() => { pipActiveRef.current = pipActive; }, [pipActive]);

  useEffect(() => {
    if (screen !== "chat" || !persona) return;
    let hiddenAt = null;
    const handleVisibility = () => {
      // ── Pause / resume all CSS animations to save CPU when hidden ──
      // document.getAnimations() asks the browser directly for the animations
      // it's already tracking — no DOM walk, no getComputedStyle() per node
      // (which was forcing a style recalc on every element, every tab switch).
      if (document.getAnimations) {
        document.getAnimations().forEach(anim => {
          if (document.hidden) anim.pause(); else anim.play();
        });
      } else {
        const playState = document.hidden ? "paused" : "running";
        document.querySelectorAll("*").forEach(el => {
          const anim = getComputedStyle(el).animationName;
          if (anim && anim !== "none") el.style.animationPlayState = playState;
        });
      }

      if (document.hidden) {
        hiddenAt = Date.now();
      } else {
        // Came back — if was hidden for > 2s and PiP not active, show prompt
        if (hiddenAt && Date.now() - hiddenAt > 2000 && !pipActiveRef.current && "documentPictureInPicture" in window) {
          setShowPipPrompt(true);
          setTimer(() => setShowPipPrompt(false), 5000);
        }
        hiddenAt = null;
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [screen, persona, setTimer]);
  const handlePKTComplete = (result) => {
    setPktResult(result);
    setShowPKT(false);
    setShowPKTResults(true);
    if (result.passed) awardXP(result.xp, result.tokens);
    setTimer(() => saveProgress({ pktResult: result }), 500);
  };

  // Welcome back message for returning users.
  // Was mount-only ([] deps): on a fresh session AppInner mounts while screen is
  // still "register"/"process", so this never fired once the user reached chat.
  // Depending on the actual triggers makes it run when they land on an empty chat.
  useEffect(() => {
    if (screen === "chat" && persona && process && messages.length === 0) {
      const ep = employeeProfile;
      const firstName = ep?.name?.split(" ")[0] || "";
      const welcomes = {
        ACE:   `Welcome back${firstName ? ", "+firstName : ""}! I'm ACE — your Training Companion. We're on **${process.name}** ${process.icon}. Ready to learn something new or need a policy refresher?`,
        CODE:  `Back at it${firstName ? ", "+firstName : ""}! Process: **${process.name}** ${process.icon} — rules loaded and ready. What's the case?`,
        NEJI: `Hey${firstName ? " "+firstName : ""}! 💜 So glad you're back! We're on **${process.name}** ${process.icon}. How are you feeling today? What's on your mind?`,
      };
      setMessages([{ role:"assistant", content:welcomes[persona] }]);
    }
  }, [screen, persona, process, employeeProfile, messages.length]);

  // "What is Ascendix?" onboarding carousel — auto-shows once, for first-time
  // visitors, after they clear the access-PIN gate on the bot-select screen.
  // Gated on procAuthed so it never stacks on top of the PIN prompt.
  useEffect(() => {
    if (screen === "select" && procAuthed) {
      try {
        if (!localStorage.getItem("botfit_tutorial_seen")) setShowBotFitTutorial(true);
      } catch {}
    }
  }, [screen, procAuthed]);

  const closeBotFitTutorial = useCallback(() => {
    try { localStorage.setItem("botfit_tutorial_seen", "true"); } catch {}
    setShowBotFitTutorial(false);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [messages]);

  useEffect(() => {
    const newLevel = xpToLevel(xp);
    if (newLevel > level) {
      setLevel(newLevel);
      setLvlUpNum(newLevel);
      setShowLvlUp(true);
      setBotPulse(true);
      setTimer(() => setBotPulse(false), 900);
    }
  }, [xp]); // eslint-disable-line react-hooks/exhaustive-deps -- level is intentionally read stale

  // ── Save progress to Google Sheets ──────────────────────
  const saveProgress = useCallback((overrides = {}) => {
    try {
      const ep = employeeProfile;
      if (!ep?.name) return; // don't save until employee has registered
      const currentPkt = overrides.pktResult || pktResult;
      const payload = {
        name:        ep.name,
        department:  ep.department  || "",
        teamName:    ep.teamName    || "",
        teamLeader:  ep.teamLeader  || "",
        bot:         persona || "",
        level:       overrides.level  ?? level,
        xp:          overrides.xp     ?? xp,
        tokens:      overrides.tokens ?? tokens,
        msgCount:    overrides.msgCount ?? msgCount,
        pktScore:    currentPkt?.score    ?? "",
        pktPassed:   currentPkt?.passed   ? "Yes" : currentPkt ? "No" : "",
        pktMonth:    currentPkt?.month    ?? "",
        trainerName: currentPkt?.trainerName ?? "",
        trainerEID:  currentPkt?.trainerEID  ?? "",
        questsDone:  (overrides.quests ?? quests).filter(q => q.done).length,
      };
      fetch("/api/save-progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "saveProgress", ...payload }),
      }).catch(() => {}); // fire and forget
    } catch {}
  }, [employeeProfile, persona, level, xp, tokens, msgCount, pktResult, quests]);

  // useCallback: awardXP is passed down to GameTab / PKT flows, so a stable
  // identity keeps it out of their dependency churn.
  const awardXP = useCallback((amount, tokenAmount) => {
    setXp(prev => {
      const newXp = prev + amount;
      setTimer(() => saveProgress({ xp: newXp, tokens: tokens + tokenAmount }), 300);
      return newXp;
    });
    setTokens(prev => prev + tokenAmount);
    setXpGain(amount);
    setTokenGain(tokenAmount);
    setTimer(() => { setXpGain(null); setTokenGain(null); }, 2200);
  }, [saveProgress, tokens, setTimer]);

  const equipItem = (slot, id) => {
    const next = { ...outfit, [slot]: id };
    setOutfit(next);
    try { localStorage.setItem("botfit_outfit", JSON.stringify(next)); } catch {}
  };

  const EVENT_OUTFIT_MIN_LEVEL = 3;

  // ── Floating Bot (Document Picture-in-Picture) ──────────
  // Detaches the PiP listener and closes the window; set on launch, run on
  // toggle-off and on unmount so no orphaned window keeps a handler alive.
  const pipCleanupRef = useRef(null);
  useEffect(() => () => { pipCleanupRef.current?.(); pipCleanupRef.current = null; }, []);

  const launchFloatingBot = async () => {
    if (!p) return;
    if (pipWindow && !pipWindow.closed) {
      pipCleanupRef.current?.();
      pipCleanupRef.current = null;
      pipWindow.close();
      setPipActive(false);
      setPipWindow(null);
      return;
    }
    if (!("documentPictureInPicture" in window)) {
      alert("Floating bot requires Chrome 116+. Update your browser to use this feature!");
      return;
    }
    try {
      const pip = await window.documentPictureInPicture.requestWindow({
        width: 220, height: 300,
        disallowReturnToOpener: false,
      });
      setPipWindow(pip);
      setPipActive(true);

      const levelName = LEVEL_NAMES[level - 1];

      // Build styles using createElement to avoid template literal parse errors
      const styleEl = pip.document.createElement("style");
      styleEl.textContent = [
        "* { margin:0; padding:0; box-sizing:border-box; }",
        "body { background:linear-gradient(160deg," + p.bg + " 0%, #060912 100%); overflow:hidden; display:flex; align-items:flex-end; justify-content:flex-end; width:220px; height:300px; font-family:'Segoe UI',system-ui,sans-serif; }",
        "@keyframes float { 0%,100%{transform:translateY(0) rotate(-1deg)} 50%{transform:translateY(-12px) rotate(1deg)} }",
        "@keyframes glow  { 0%,100%{opacity:0.5} 50%{opacity:1} }",
        "@keyframes spin  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }",
        "@keyframes fadein{ from{opacity:0;transform:scale(0.7) translateY(20px)} to{opacity:1;transform:scale(1) translateY(0)} }",
        ".container { position:relative; width:200px; height:260px; display:flex; flex-direction:column; align-items:center; justify-content:flex-end; padding-bottom:16px; animation:fadein 0.5s cubic-bezier(0.34,1.56,0.64,1) both; }",
        ".glow-blob { position:absolute; bottom:20px; left:50%; transform:translateX(-50%); width:120px; height:40px; border-radius:50%; background:" + p.color + "; filter:blur(20px); opacity:0.3; animation:glow 2s ease-in-out infinite; }",
        ".bot-wrap { animation:float 3s ease-in-out infinite; filter:drop-shadow(0 8px 24px " + p.color + "88); position:relative; z-index:2; }",
        ".ring { position:absolute; border-radius:50%; border:1.5px solid " + p.color + "; opacity:0.4; animation:spin 4s linear infinite; top:50%; left:50%; transform:translate(-50%,-50%); }",
        ".name-tag { margin-top:8px; background:rgba(0,0,0,0.6); border:1px solid " + p.color + "66; border-radius:20px; padding:4px 14px; display:flex; flex-direction:column; align-items:center; backdrop-filter:blur(10px); position:relative; z-index:2; }",
        ".bot-name { font-size:13px; font-weight:900; color:white; letter-spacing:1px; }",
        ".bot-lvl  { font-size:9px; font-weight:700; color:" + p.color + "; letter-spacing:2px; text-transform:uppercase; margin-top:1px; }",
        ".close-btn { position:absolute; top:10px; right:10px; width:24px; height:24px; border-radius:50%; background:rgba(255,255,255,0.1); border:1px solid rgba(255,255,255,0.2); color:rgba(255,255,255,0.6); font-size:12px; cursor:pointer; display:flex; align-items:center; justify-content:center; z-index:10; }",
        ".status { position:absolute; top:10px; left:10px; display:flex; align-items:center; gap:5px; font-size:9px; color:rgba(255,255,255,0.4); letter-spacing:1px; }",
        ".dot { width:6px; height:6px; border-radius:50%; background:#10B981; animation:glow 1.5s ease-in-out infinite; }",
      ].join(" ");
      pip.document.head.appendChild(styleEl);

      // Build DOM structure
      const container = pip.document.createElement("div");
      container.className = "container";

      const status = pip.document.createElement("div");
      status.className = "status";
      status.innerHTML = "<div class='dot'></div> ONLINE";
      container.appendChild(status);

      const closeBtn = pip.document.createElement("button");
      closeBtn.className = "close-btn";
      closeBtn.textContent = "x";
      closeBtn.onclick = () => pip.close();
      container.appendChild(closeBtn);

      // Shortcut icons above bot
      const shortcuts = pip.document.createElement("div");
      shortcuts.style.cssText = "display:flex;gap:10px;margin-bottom:8px;z-index:3;position:relative;";

      const shortcutData = [
        { icon: "💬", label: "Chat",         color: "#60A5FA", action: "chat"          },
        { icon: "👤", label: "Profile",      color: p.color,   action: "profile"       },
        { icon: "🏆", label: "Achievements", color: "#F59E0B", action: "achievements"  },
        { icon: "🎁", label: "Rewards",      color: "#10B981", action: "rewards"       },
      ];

      shortcutData.forEach(sc => {
        const btn = pip.document.createElement("button");
        btn.style.cssText = [
          "width:44px;height:44px;border-radius:14px;border:none;cursor:pointer;",
          "display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;",
          "background:rgba(255,255,255,0.08);",
          "border:1px solid " + sc.color + "33;",
          "transition:all 0.2s;",
          "color:white;",
        ].join("");
        btn.innerHTML = "<span style='font-size:18px'>" + sc.icon + "</span>";
        btn.title = sc.label;
        btn.onmouseenter = () => {
          btn.style.background = sc.color + "22";
          btn.style.borderColor = sc.color + "88";
          btn.style.transform = "scale(1.1)";
        };
        btn.onmouseleave = () => {
          btn.style.background = "rgba(255,255,255,0.08)";
          btn.style.borderColor = sc.color + "33";
          btn.style.transform = "scale(1)";
        };
        btn.onclick = () => {
          // Focus the main Ascendix tab and navigate to the right tab
          pip.close();
          window.focus();
          // Post message to parent to switch tab
          if (sc.action === "chat")         window.postMessage({ type:"BOTFIT_NAV", tab:"chat"         }, "*");
          if (sc.action === "profile")      window.postMessage({ type:"BOTFIT_NAV", tab:"profile"      }, "*");
          if (sc.action === "achievements") window.postMessage({ type:"BOTFIT_NAV", tab:"achievements" }, "*");
          if (sc.action === "rewards")      window.postMessage({ type:"BOTFIT_NAV", tab:"rewards"      }, "*");
        };
        shortcuts.appendChild(btn);
      });
      container.appendChild(shortcuts);

      // Ring wrapper with bot
      const ringWrap = pip.document.createElement("div");
      ringWrap.style.cssText = "position:relative;display:flex;align-items:center;justify-content:center;overflow:hidden;width:120px;height:130px;";

      const ring1 = pip.document.createElement("div");
      ring1.className = "ring";
      ring1.style.cssText = "width:96px;height:96px;";
      ringWrap.appendChild(ring1);

      // No outer ring — too large for PiP window (clips outside)

      // Clone the bot SVG from main page
      const botWrap = pip.document.createElement("div");
      botWrap.className = "bot-wrap";
      const sourceSvg = document.querySelector(".pip-bot-svg svg");
      if (sourceSvg) {
        const clone = sourceSvg.cloneNode(true);
        clone.setAttribute("width", "110");
        clone.setAttribute("height", "132");
        // Fix gradient IDs to avoid conflicts in PiP document
        const uniqueId = "pip_" + Date.now();
        clone.querySelectorAll("[id]").forEach(el => {
          const oldId = el.getAttribute("id");
          const newId = uniqueId + "_" + oldId;
          el.setAttribute("id", newId);
          // Update references to this ID
          clone.querySelectorAll("[fill='url(#" + oldId + ")']").forEach(ref => {
            ref.setAttribute("fill", "url(#" + newId + ")");
          });
          clone.querySelectorAll("[stroke='url(#" + oldId + ")']").forEach(ref => {
            ref.setAttribute("stroke", "url(#" + newId + ")");
          });
        });
        botWrap.appendChild(clone);
      } else {
        const fallback = pip.document.createElement("div");
        fallback.style.cssText = "width:110px;height:110px;display:flex;align-items:center;justify-content:center;font-size:48px;";
        fallback.textContent = "🤖";
        botWrap.appendChild(fallback);
      }
      ringWrap.appendChild(botWrap);
      container.appendChild(ringWrap);

      const glowBlob = pip.document.createElement("div");
      glowBlob.className = "glow-blob";
      container.appendChild(glowBlob);

      const nameTag = pip.document.createElement("div");
      nameTag.className = "name-tag";
      nameTag.innerHTML = "<div class='bot-name'>" + p.name + "</div><div class='bot-lvl'>LV." + level + " · " + levelName + "</div>";
      container.appendChild(nameTag);

      pip.document.body.appendChild(container);

      // Keep a handle on the listener so it can be detached explicitly, and
      // guard the setState in case the window outlives the component.
      const handlePageHide = () => {
        pip.removeEventListener("pagehide", handlePageHide);
        pipCleanupRef.current = null;
        if (!alive.current) return;
        setPipActive(false);
        setPipWindow(null);
      };
      pip.addEventListener("pagehide", handlePageHide);
      pipCleanupRef.current = () => {
        pip.removeEventListener("pagehide", handlePageHide);
        if (!pip.closed) pip.close();
      };
    } catch (err) {
      console.error("PiP error:", err);
      console.warn("PiP NotAllowedError - needs direct user gesture");
    }
  };

  const equipEventOutfit = (eventId) => {
    // Level gate — cannot equip below level 3
    if (eventId !== null && level < EVENT_OUTFIT_MIN_LEVEL) return;
    // Toggle — unequip if already equipped
    const next = equippedEvent === eventId ? null : eventId;
    setEquippedEvent(next);
    try {
      if (next) localStorage.setItem("botfit_event_outfit", next);
      else localStorage.removeItem("botfit_event_outfit");
    } catch {}
  };

  const checkQuests = useCallback((newMsgCount, userMsg) => {
    const lower = userMsg.toLowerCase();
    let updated = [...quests], bXP = 0, bT = 0;
    if (!updated[0].done && newMsgCount >= 1)                                              { updated[0]={...updated[0],done:true}; bXP+=updated[0].xp; bT+=updated[0].tokens; }
    if (!updated[1].done && (lower.includes("policy")||lower.includes("leave")||lower.includes("hr")||lower.includes("benefit")||lower.includes("training")||lower.includes("guide")||lower.includes("onboard"))) { updated[1]={...updated[1],done:true}; bXP+=updated[1].xp; bT+=updated[1].tokens; }
    if (!updated[2].done && (lower.includes("stress")||lower.includes("tired")||lower.includes("burnout")||lower.includes("feeling")||lower.includes("mental")||lower.includes("wellness")||lower.includes("overwhelm")||lower.includes("anxious")||lower.includes("motivation")||lower.includes("career")||lower.includes("mood"))) { updated[2]={...updated[2],done:true}; bXP+=updated[2].xp; bT+=updated[2].tokens; }
    if (!updated[3].done && newMsgCount >= 3)                                              { updated[3]={...updated[3],done:true}; bXP+=updated[3].xp; bT+=updated[3].tokens; }
    setQuests(updated);
    return { bonusXP:bXP, bonusTokens:bT };
  }, [quests]);

  const sendMessage = useCallback(async (overrideText) => {
    const source = overrideText ?? input;
    if (!source.trim() || loading) return;
    const userMsg     = source.trim();
    const newMsgCount = msgCount + 1;
    setMsgCount(newMsgCount);
    setMessages(prev => [...prev, { role:"user", content:userMsg }]);
    setInput("");
    setLoading(true);
    const baseXP = 8 + Math.floor(userMsg.length / 20);

    try {
      const history = messages.slice(-8).map(m => ({ role:m.role, content:m.content }));
      const res = await fetch("/api/chat", {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({
          model:"llama-3.3-70b-versatile",
          max_tokens:1000,
          system:PERSONAS[persona].systemPrompt,
          messages:[...history, { role:"user", content:userMsg }],
          persona: persona,
          sheet: process?.sheet, // ← sends selected sheet to api/chat.js
        }),
      });
      if (!res.ok) throw new Error(res.status);
      const data  = await res.json();
      const reply = data.content?.[0]?.text || "Let me try that again — could you rephrase?";
      setMessages(prev => [...prev, { role:"assistant", content:reply }]);
      const { bonusXP, bonusTokens } = checkQuests(newMsgCount, userMsg);
      awardXP(baseXP + bonusXP, 3 + bonusTokens);
    } catch {
      setMessages(prev => [...prev, { role:"assistant", content:"Connection hiccup — please try again in a moment!" }]);
      awardXP(baseXP, 1);
    }
    if (alive.current) setLoading(false);
  }, [input, loading, messages, persona, msgCount, process, checkQuests, awardXP, alive]);

  const handleKey = useCallback(e => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }, [sendMessage]);

  const resetToL1 = () => {
    setXp(0); setTokens(0); setLevel(1); setMsgCount(0); setQuests(QUESTS);
    try {
      ["botfit_xp","botfit_tokens","botfit_level","botfit_msgcount","botfit_quests"].forEach(k => localStorage.removeItem(k));
    } catch {}
    const lines = {
      ACE:"Reset complete. Let's build that knowledge back up — what would you like to train on?",
      CODE:"BACK TO ZERO! Let's earn those levels back even faster. Give me a case!",
      NEJI:"Fresh start! 💜 That's okay — every day is a new beginning. How are you feeling today?",
    };
    setMessages([{ role:"assistant", content:lines[persona] }]);
  };

  const resetPKT = () => {
    setPktResult(null);
    setShowPKT(false);
    setShowPKTResults(false);
    try { localStorage.removeItem("botfit_pkt"); } catch {}
    const lines = {
      ACE:"PKT cleared. Take your time to review the training materials and go again when you're ready.",
      CODE:"PKT reset! Study up and crush it next time — you've got this!",
      NEJI:"PKT cleared! 💜 Don't be hard on yourself — let's review together and try again. You got this!",
    };
    setMessages([{ role:"assistant", content:lines[persona] }]);
  };

  const loadDemoState = () => {
    // Level 3: 500 XP (enough to unlock event outfits + auras)
    const demoXP     = 500;
    const demoTokens = 120;
    const demoLevel  = 3;
    const demoQuests = QUESTS.map(q => ({ ...q, done: true }));
    const demoPKT    = { score: 90, correct: 9, total: 10, passed: true,
                         xp: PKT_BASE_XP, tokens: pktTokenReward(90),
                         month: getCurrentMonthLabel() };
    setXp(demoXP);
    setTokens(demoTokens);
    setLevel(demoLevel);
    setQuests(demoQuests);
    setPktResult(demoPKT);
    try {
      localStorage.setItem("botfit_xp",     String(demoXP));
      localStorage.setItem("botfit_tokens", String(demoTokens));
      localStorage.setItem("botfit_level",  String(demoLevel));
      localStorage.setItem("botfit_quests", JSON.stringify(demoQuests));
      localStorage.setItem("botfit_pkt",    JSON.stringify(demoPKT));
    } catch {}
    const lines = {
      ACE:  "Demo state loaded! I'm at Level 3, 120 tokens, and PKT passed. Feel free to explore everything!",
      CODE: "Demo state loaded! Level 3, 120 ACN, PKT passed at 90%. Ready to show what I've got!",
      NEJI: "Demo state loaded! 💜 Level 3, all quests done, 120 tokens ready to redeem. Let's go!",
    };
    setMessages([{ role:"assistant", content:lines[persona] || lines.ACE }]);
  };

  // Registration questionnaire removed — every session goes straight from persona
  // pick to process select. No name/department/team is collected up front, so
  // employeeProfile stays null (Guest) until the player opts into Employee Login
  // from the Profile tab, which looks their name up against the already-populated
  // "Registered Employee" sheet instead of collecting new data.
  const selectPersona = id => {
    const isFirstEver = !persona;
    // Save current bot's XP, load the target bot's XP (per-bot progress map)
    try {
      const map = JSON.parse(localStorage.getItem("botfit_bot_xp_map") || "{}");
      if (persona) map[persona] = xp;
      map[id] = map[id] || 0;
      localStorage.setItem("botfit_bot_xp_map", JSON.stringify(map));
      const botXp = map[id] || 0;
      const botLv = xpToLevel(botXp);
      setXp(botXp);
      setLevel(botLv);
      localStorage.setItem("botfit_xp", String(botXp));
      localStorage.setItem("botfit_level", String(botLv));
      setBotXpMap({...map});
    } catch {}
    setPersona(id);
    try { localStorage.setItem("botfit_persona", id); } catch {}
    setMessages([]);
    setScreen("process");
    if (isFirstEver) setTimer(() => setShowTutorial(true), 1200);
  };

  // Employee Login — matches a typed name against the "Registered Employee" sheet
  // (via the same admin-data lookup the old registration flow used) and attaches
  // that identity mid-session. Deliberately does NOT restore xp/tokens/level from
  // the sheet — a player may already be mid-session as a Guest, and overwriting
  // their current progress on login would be surprising. It only attaches identity,
  // which is what unlocks team-scoped features (Bingo) and Sheets/R&R/redemption sync.
  const handleEmployeeLogin = useCallback((profile) => {
    setEmployeeProfile(profile);
    try { localStorage.setItem("botfit_employee", JSON.stringify(profile)); } catch {}
    // A leftover admin session on the same device silently breaks the player-side
    // bingo poll (it gates on "no adminProfile"), with no visible error — logging
    // in as Employee must clear it, mirroring the same clear on the admin-login side.
    if (adminProfile) {
      setAdminProfile(null);
      try { localStorage.removeItem("botfit_admin_profile"); } catch {}
    }
    // The rnrClaimed/rnrAwards lazy-initializers only ran once, at mount, when the
    // name was still unknown — re-run that same scoped lookup now that it's known.
    try {
      setRnrClaimed(JSON.parse(localStorage.getItem(`botfit_rnr_claimed__${profile.name}`) || "[]"));
      setRnrAwards(JSON.parse(localStorage.getItem(`botfit_rnr_awards__${profile.name}`) || "[]"));
    } catch {}
    setShowLoginModal(false);
    setTab("profile");
  }, [adminProfile]);

  const handleGuestContinue = useCallback(() => {
    setShowLoginModal(false);
    setTab("profile");
  }, []);

  const selectProcess = (proc) => {
    setProcess(proc);
    setScreen("chat");
    const nejiGreetings = {
      checkin:    `Hey! 💜 I'm so glad you're here. Before anything else — how are you really doing today? No work talk needed, just you and me.`,
      stress:     `Hey 🧘 I hear you. Stress is real and it's okay to talk about it. What's been weighing on you lately? Let's figure it out together.`,
      motivation: `Let's GO! ⚡ You showed up today and that already counts. Tell me what's going on and let's find your spark again!`,
      teamvibes:  `Hey! 💜 Team energy matters so much. Whether you want to celebrate a win or just share something about the team — I'm all ears!`,
    };
    const aceGreetings = {
      dtp:     `Hi! I'm ACE 📋 DTP training content is still being prepared — but I can already help you understand the process conceptually. What would you like to know about DTP?`,
      eft:     `Hi! I'm ACE 💳 EFT Update training is coming soon! In the meantime, ask me anything about the EFT process and I'll do my best to guide you.`,
      adddata: `Hi! I'm ACE 🗃️ ADD Data content is on its way! For now, ask me anything and I'll help with what I know.`,
    };
    const greetings = {
      ACE:  aceGreetings[proc.id] || `Hi! I'm ACE — your Training Companion. You've selected **${proc.name}** ${proc.icon}. What would you like to learn?`,
      CODE: `Let's GO! Process locked in: **${proc.name}** ${proc.icon}. Rules loaded — give me the case details and I'll give you the decision.`,
      NEJI: nejiGreetings[proc.id] || `Hey! I'm NEJI 💜 We're on **${proc.name}** ${proc.icon}. How are you feeling today? I'm here for work stuff AND the human stuff.`,
    };
    setMessages([{ role:"assistant", content:greetings[persona] }]);
  };

  // ── PERSONA SELECT ────────────────────────────────────
  // Active event theme — derived here so all screens can use it
  const activeTheme = equippedEvent ? EVENT_OUTFITS[equippedEvent] : null;
  const appBg = activeTheme
    ? `linear-gradient(160deg,${activeTheme.bg1},${activeTheme.bg2},${activeTheme.bgMid})`
    : "#060912";

  const pp = p;

  if (screen === "select") return (
    <div style={{ minHeight:"100vh", background:appBg,
      fontFamily:"'Segoe UI',system-ui,sans-serif",
      padding:"0 16px", display:"flex", flexDirection:"column", alignItems:"center",
      transition:"background 1.2s ease" }}>
      <style>{`
        @keyframes bfFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
        @keyframes bfFadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes bfSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes bfShake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}
        @keyframes bfMarquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        @keyframes bfWarnIn{0%{opacity:0;transform:scale(0.8) translateY(-10px)}100%{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes bfFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes bfLvlPop{0%{opacity:0;transform:scale(0.35) translateY(30px)}60%{transform:scale(1.08) translateY(-4px)}100%{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes bfCardGlow{0%{filter:drop-shadow(0 0 12px currentColor)}50%{filter:drop-shadow(0 0 28px currentColor)}100%{filter:drop-shadow(0 0 12px currentColor)}}
        @keyframes bfPulsing{0%,100%{transform:scale(1) translateX(-50%) translateY(-50%)}50%{transform:scale(1.4) translateX(-50%) translateY(-50%)}}
        @keyframes bfBlobFloat{0%,100%{transform:translateY(0px)}50%{transform:translateY(-20px)}}
        .bf-card-active{transition:transform 0.25s,box-shadow 0.25s;cursor:pointer}
        .bf-card-active:hover{transform:translateY(-6px) scale(1.02)}
        .bf-card-locked{cursor:not-allowed;transition:transform 0.15s}
        .bf-card-locked:hover{transform:scale(0.98)}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:#333;border-radius:2px}
        @media(prefers-reduced-motion:reduce){*{animation-duration:0.01ms!important;animation-iteration-count:1!important;transition-duration:0.01ms!important}}
      `}</style>

      {/* ── ACCESS PIN GATE — shown to new users before anything else ── */}
      {!procAuthed && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.95)", zIndex:600,
          display:"flex", alignItems:"center", justifyContent:"center", padding:16,
          fontFamily:"'Segoe UI',system-ui,sans-serif",
          animation:"bfFadeIn 0.2s ease both" }}>
          <div style={{
            background:"linear-gradient(135deg,#0d0d1a,#060912)",
            border:"2px solid #8B00FF", borderRadius:24,
            padding:"36px 28px", maxWidth:360, width:"100%", textAlign:"center",
            boxShadow:"0 0 80px rgba(79,70,229,0.4)",
            animation:"bfLvlPop 0.45s cubic-bezier(0.34,1.56,0.64,1) both",
          }}>
            {/* Ascendix logo */}
            <div style={{ fontSize:36, fontWeight:900, color:"white", letterSpacing:"-2px", marginBottom:6 }}>
              ASCEND<span style={{ color:"#8B00FF" }}>IX</span>
            </div>
            <div style={{ fontSize:10, letterSpacing:4, color:"#8B00FF", fontWeight:700,
              textTransform:"uppercase", marginBottom:20 }}>ACN Operations Platform</div>

            <div style={{ fontSize:18, fontWeight:800, color:"white", marginBottom:6 }}>
              Enter Access PIN
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.60)", marginBottom:24, lineHeight:1.6 }}>
              Enter the PIN provided by your team leader to access Ascendix.
            </div>

            <input
              type="password"
              value={procPwInput}
              onChange={e => { setProcPwInput(e.target.value); setProcPwError(false); }}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  if (procPwInput === PROCESS_PASSWORD) { setProcAuthed(true); setProcPwInput(""); }
                  else { setProcPwError(true); setProcPwInput(""); }
                }
              }}
              placeholder="Enter access PIN"
              autoFocus
              style={{
                width:"100%", padding:"13px 16px", borderRadius:12, boxSizing:"border-box",
                border:`1.5px solid ${procPwError ? "#EF4444" : "rgba(79,70,229,0.4)"}`,
                background:"rgba(255,255,255,0.06)", color:"white", fontSize:16,
                outline:"none", fontFamily:"inherit", marginBottom:8,
                textAlign:"center", letterSpacing:4, transition:"border-color 0.2s",
              }}/>
            {procPwError && (
              <div style={{ fontSize:11, color:"#EF4444", marginBottom:8 }}>❌ Incorrect PIN — please try again</div>
            )}
            <button onClick={() => {
              if (procPwInput === PROCESS_PASSWORD) { setProcAuthed(true); setProcPwInput(""); }
              else { setProcPwError(true); setProcPwInput(""); }
            }} style={{
              width:"100%", padding:"13px", borderRadius:12, border:"none",
              background:"linear-gradient(135deg,#8B00FF,#6600CC)",
              color:"white", fontSize:14, fontWeight:700, cursor:"pointer",
              fontFamily:"inherit", boxShadow:"0 4px 20px rgba(79,70,229,0.4)",
            }}>Unlock Ascendix →</button>
          </div>
        </div>
      )}

      {/* AdminPanel in select screen */}
      {showAdmin && (
        <AdminPanel
          onClose={() => setShowAdmin(false)}
          adminProfile={adminProfile}
          adminTokens={adminTokens}
          setAdminTokens={setAdminTokens}
          adminQuests={adminQuests}
          setAdminQuests={setAdminQuests}
          alreadyAuthed
          isBingoUnlocked={isBingoUnlocked} setIsBingoUnlocked={setIsBingoUnlocked}
          bingoMaxWinners={bingoMaxWinners} setBingoMaxWinners={setBingoMaxWinners}
          bingoEventLabel={bingoEventLabel} setBingoEventLabel={setBingoEventLabel}
          calledNumbers={calledNumbers} setCalledNumbers={setCalledNumbers}
          bingoWinners={bingoWinners} setBingoWinners={setBingoWinners}
          onBingoSet={() => { bingoLastSetRef.current = Date.now(); }}
        />
      )}

      {/* "What is Ascendix?" tutorial — auto-shows once for first-time visitors,
          reopenable anytime via the help icon below */}
      <BotFitTutorial open={showBotFitTutorial} onClose={closeBotFitTutorial} />
      <button
        onClick={() => setShowBotFitTutorial(true)}
        aria-label="What is Ascendix? Open tutorial"
        title="What is Ascendix?"
        style={{
          position:"fixed", bottom:20, right:20, zIndex:200,
          width:44, height:44, borderRadius:"50%", border:"1px solid rgba(139,0,255,0.4)",
          background:"rgba(13,13,26,0.9)", color:"#C4B5FD", fontSize:18, fontWeight:800,
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 4px 20px rgba(139,0,255,0.3)", backdropFilter:"blur(8px)",
        }}
      >?</button>

      {/* Header */}
      <div style={{ textAlign:"center", padding:"48px 0 28px", animation:"bfFadeUp 0.6s ease both" }}>
        <div style={{ fontSize:11, letterSpacing:6, color:"#8B00FF", fontWeight:700, marginBottom:12, textTransform:"uppercase" }}>
          "This is an innovation protoype hosted in a sandbox environment to demonstrate the logic. It uses dummy data for safety"
        </div>
        <h1 style={{ fontSize:"clamp(36px,7vw,64px)", fontWeight:900, color:"white",
          margin:0, letterSpacing:"-2px", lineHeight:1 }}>
          ASCEND<span style={{ color:"#8B00FF" }}>IX</span>
        </h1>
        <p style={{ color:"rgba(255,255,255,0.45)", marginTop:12, fontSize:12,
          letterSpacing:0.5, fontStyle:"italic" }}>
          Ascendix — (ah-SEN-dix) · Ascend + Achievement bot
        </p>
        <p style={{ color:"rgba(255,255,255,0.60)", marginTop:6, fontSize:15, letterSpacing:1 }}>
          Choose your bot for this session — switch anytime.
        </p>
      </div>

      {/* ── Hall of Fame Marquee ── */}
      <div style={{
        width:"100%", maxWidth:900, marginBottom:24,
        borderRadius:16, overflow:"hidden",
        background:"linear-gradient(135deg,rgba(245,158,11,0.07),rgba(245,158,11,0.03))",
        border:"1px solid rgba(245,158,11,0.2)",
      }}>
        {/* Header */}
        <div style={{
          padding:"10px 20px 8px",
          borderBottom:"1px solid rgba(245,158,11,0.15)",
          display:"flex", alignItems:"center", gap:8,
        }}>
          <div style={{ fontSize:16 }}>🏆</div>
          <div style={{ fontSize:10, fontWeight:800, letterSpacing:3,
            textTransform:"uppercase", color:"#F59E0B" }}>Hall of Fame</div>
          <div style={{ fontSize:10, color:"rgba(255,255,255,0.55)",
            marginLeft:"auto" }}>Achievement of the Month</div>
        </div>

        {/* Marquee track */}
        <div style={{ padding:"10px 0", overflow:"hidden", position:"relative" }}>
          {/* Fade edges */}
          <div style={{ position:"absolute", left:0, top:0, bottom:0, width:60,
            background:"linear-gradient(to right, rgba(6,9,18,0.9), transparent)",
            zIndex:2, pointerEvents:"none" }}/>
          <div style={{ position:"absolute", right:0, top:0, bottom:0, width:60,
            background:"linear-gradient(to left, rgba(6,9,18,0.9), transparent)",
            zIndex:2, pointerEvents:"none" }}/>

          {/* Scrolling content — doubled for seamless loop */}
          <div style={{
            display:"inline-flex", gap:0, alignItems:"center",
            animation: hallOfFame.length > 0 ? "bfMarquee 45s linear infinite" : "none",
            whiteSpace:"nowrap", width:"max-content",
            willChange:"transform",
          }}>
            {hallOfFame.length > 0 ? (
              [...hallOfFame, ...hallOfFame].map((entry, i) => {
                const botMap = {
                  "code":"#10B981", "CODE":"#10B981",
                  "ace":"#3B82F6", "ACE":"#3B82F6",
                  "neji":"#8B5CF6", "NEJI":"#8B5CF6",
                  "nova":"#10B981", "NOVA":"#10B981",
                  "pixel":"#8B5CF6", "PIXEL":"#8B5CF6"
                };
                const botName = (entry.bot || entry.persona || "ACE").toUpperCase();
                const botColor = botMap[entry.bot || entry.persona] || "#3B82F6";
                return (
                <div key={i} style={{
                  display:"inline-flex", alignItems:"center", gap:12,
                  padding:"2px 24px 2px 16px",
                  borderRight:"1px solid rgba(245,158,11,0.12)",
                }}>
                  {/* Bot chibi avatar */}
                  <div style={{
                    flexShrink:0, position:"relative",
                    filter:`drop-shadow(0 0 8px ${botColor}99)`,
                  }}>
                    <BotAvatar persona={botName} level={entry.level||1} size={44} showWeapon animated={false}/>
                    {/* Gold star crown badge */}
                    <div style={{
                      position:"absolute", top:-4, right:-4,
                      width:16, height:16, borderRadius:"50%",
                      background:"linear-gradient(135deg,#F59E0B,#D97706)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:8, border:"1.5px solid #0a0a14",
                      boxShadow:"0 0 6px rgba(245,158,11,0.8)",
                    }}>⭐</div>
                  </div>

                  {/* Text info */}
                  <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:13, fontWeight:900, color:"white" }}>{entry.name}</span>
                      <span style={{ fontSize:11, color:botColor, fontWeight:700,
                        background:`${botColor}22`, padding:"1px 7px", borderRadius:20,
                        border:`1px solid ${botColor}44`, letterSpacing:0.5 }}>{entry.bot || entry.persona}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:11, color:"#F59E0B", fontWeight:800 }}>{entry.achievement}</span>
                      <span style={{ fontSize:10, color:"rgba(255,255,255,0.55)" }}>{entry.dept || entry.department}</span>
                    </div>
                  </div>
                </div>
              );})
            ) : (
              <div style={{ padding:"10px 24px", color:"rgba(255,255,255,0.55)" }}>
                {hallOfFameLoading ? "Loading Hall of Fame..." : "No achievements yet"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bot cards — all 4 in one horizontal row */}
      <div style={{ display:"flex", gap:16, flexWrap:"wrap", justifyContent:"center", maxWidth:1200 }}>
        {Object.values(PERSONAS).map((p, i) => {
          const isMyBot   = persona === p.id;
          const isHovered = hoveredP === p.id;

          return (
            <div key={p.id}
              className="bf-card-active"
              onClick={() => selectPersona(p.id)}
              onMouseEnter={() => setHoveredP(p.id)}
              onMouseLeave={() => setHoveredP(null)}
              style={{
                position:"relative", flexShrink:0,
                width:260, height:420,
                borderRadius:24,
                border: isMyBot
                  ? `2px solid ${p.color}`
                  : `1.5px solid ${isHovered ? p.color+"99" : "rgba(255,255,255,0.10)"}`,
                overflow:"hidden",
                boxShadow: isMyBot
                  ? `0 0 40px ${p.colorGlow}, 0 8px 40px rgba(0,0,0,0.6)`
                  : isHovered
                  ? `0 0 60px ${p.colorGlow}99, 0 8px 40px rgba(0,0,0,0.8)`
                  : `0 4px 24px rgba(0,0,0,0.4)`,
                animation: isHovered
                  ? `bfFadeUp 0.6s ease ${0.1+i*0.12}s both, bfCardGlow 1.6s ease-in-out infinite`
                  : `bfFadeUp 0.6s ease ${0.1+i*0.12}s both`,
                filter: isHovered ? "brightness(1.08)" : "brightness(1)",
                transition:"all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                transform: isHovered ? "translateY(-12px) scale(1.04)" : "translateY(0) scale(1)",
                cursor:"pointer",
                display:"flex", flexDirection:"row",
              }}>

              {/* Background */}
              <div style={{
                position:"absolute", inset:0,
                background: isMyBot
                  ? `linear-gradient(135deg, ${p.bg} 0%, #060912 100%)`
                  : isHovered
                  ? `linear-gradient(135deg, ${p.bg}bb 0%, #08080f 100%)`
                  : `linear-gradient(135deg, #0d1020 0%, #060912 100%)`,
                transition:"background 0.4s",
              }}/>

              {/* Glow blob */}
              <div style={{
                position:"absolute", top:-20, left:-20,
                width:200, height:200, borderRadius:"50%",
                background:p.colorGlow, filter:"blur(60px)",
                opacity: isMyBot ? 0.6 : isHovered ? 0.8 : 0.12,
                transition:"opacity 0.4s", pointerEvents:"none",
              }}/>

              {/* LEFT — Character */}
              <div style={{
                position:"absolute", left:0, right:0, top:-5, bottom:0,
                display:"flex", alignItems:"flex-end", justifyContent:"center",
                paddingBottom:0, zIndex:1,
                animation: isMyBot ? "bfFloat 2.5s ease-in-out infinite" : "none",
                filter: isMyBot
                  ? `drop-shadow(0 0 22px ${p.colorGlow}) drop-shadow(0 0 8px ${p.color}88)`
                  : isHovered
                  ? `drop-shadow(0 0 18px ${p.colorGlow}) drop-shadow(0 0 6px ${p.color}66)`
                  : `drop-shadow(0 0 10px ${p.colorGlow})`,
                transition:"filter 0.3s",
              }}>
                <BotAvatar persona={p.id} level={isMyBot ? level : getBotLevel(p.id)} size={350} animated={isMyBot || isHovered} showWeapon/>
              </div>

              {/* BOTTOM — Info + button panel fixed height from bottom */}
              <div style={{
                position:"absolute", bottom:0, left:0, right:0, height:190,
                background:"transparent",
                display:"flex", flexDirection:"column", justifyContent:"flex-end",
                zIndex:5,
              }}>
                {/* Text block */}
                <div style={{
                  padding:"0 16px 12px",
                  textAlign:"center",
                }}>
                  {/* Name */}
                  <div style={{
                    fontSize:26, fontWeight:900, color:"white",
                    letterSpacing:"-1px", lineHeight:1, marginBottom:3,
                    textShadow:"0 0 20px rgba(0,0,0,1), 0 2px 8px rgba(0,0,0,1), -1px -1px 0 rgba(0,0,0,0.9), 1px 1px 0 rgba(0,0,0,0.9)",
                  }}>{p.name}</div>

                  {/* Role */}
                  <div style={{
                    fontSize:8, fontWeight:700, letterSpacing:3,
                    color:p.color, textTransform:"uppercase", marginBottom:8,
                    textShadow:"0 0 12px rgba(0,0,0,1), 0 2px 6px rgba(0,0,0,1)",
                  }}>{p.role}</div>

                  {/* Full name */}
                  <div style={{
                    fontSize:10, color:"rgba(255,255,255,0.90)",
                    marginBottom:6, lineHeight:1.4,
                    textShadow:"0 0 12px rgba(0,0,0,1), 0 2px 6px rgba(0,0,0,1)",
                  }}>{p.full}</div>

                  {/* Tagline */}
                  <div style={{
                    fontSize:11, color:"rgba(255,255,255,0.95)",
                    lineHeight:1.5, marginBottom:12,
                    textShadow:"0 0 12px rgba(0,0,0,1), 0 2px 6px rgba(0,0,0,1)",
                  }}>{p.tagline}</div>
                </div>

              {/* Button row */}
              <div style={{
                padding:"0 14px 14px",
              }}>
                <div style={{
                  padding:"11px 0", borderRadius:12, textAlign:"center",
                  background: isMyBot
                    ? `linear-gradient(135deg,${p.color},${p.colorDark})`      // Active: full solid color — brightest
                    : isHovered
                    ? `linear-gradient(135deg,${p.color}dd,${p.colorDark}dd)`  // Hovered: slightly dimmer
                    : `rgba(255,255,255,0.07)`,                                 // Inactive: dark with subtle border
                  color: isMyBot ? "white" : isHovered ? "white" : p.color,
                  fontSize:12, fontWeight:800, letterSpacing:0.5,
                  transition:"all 0.3s ease",
                  border: isMyBot
                    ? "none"
                    : isHovered
                    ? `1px solid ${p.color}88`
                    : `1px solid ${p.color}33`,
                  // Fix 2: per-bot color glow on button
                  boxShadow: isMyBot
                    ? `0 0 20px ${p.colorGlow}, 0 4px 16px ${p.colorGlow}`      // Active: strong glow
                    : isHovered
                    ? `0 0 12px ${p.colorGlow}88`                               // Hovered: medium glow
                    : `0 0 6px ${p.colorGlow}33`,                               // Inactive: faint glow
                  marginBottom: isMyBot ? 8 : 0,
                }}>
                  {isMyBot ? `Active — Switch or Continue →` : isHovered ? `Select ${p.name} →` : "Select"}
                </div>
                {isMyBot && (
                  <div style={{
                    padding:"7px 0", borderRadius:10, textAlign:"center",
                    background:"rgba(255,255,255,0.04)", border:`1px solid ${p.color}33`,
                    fontSize:10, color:"rgba(255,255,255,0.50)",
                  }}>🔥 Level up to unlock outfits</div>
                )}
              </div>{/* end button row */}
              </div>{/* end bottom panel */}

              {/* ACTIVE badge */}
              {isMyBot && (
                <div style={{
                  position:"absolute", top:14, right:14,
                  background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
                  color:"white", fontSize:9, fontWeight:800, letterSpacing:2,
                  padding:"5px 12px", borderRadius:50, textTransform:"uppercase",
                  boxShadow:`0 2px 16px ${p.colorGlow}`, zIndex:10,
                }}>⭐ ACTIVE</div>
              )}
            </div>
          );
        })}

          {/* ── ACN Admin System Bot Card ── */}
          <div
            className="bf-card-active"
            onClick={() => setShowAdminBot(true)}
            onMouseEnter={() => setHoveredP("ADMIN")}
            onMouseLeave={() => setHoveredP(null)}
            style={{
              position:"relative", flexShrink:0,
              width:260, height:420,
              borderRadius:24,
              border: hoveredP==="ADMIN"
                ? "2px solid rgba(239,68,68,0.9)"
                : "1px solid rgba(239,68,68,0.2)",
              overflow:"hidden",
              boxShadow: hoveredP==="ADMIN"
                ? "0 0 60px rgba(239,68,68,0.5), 0 8px 40px rgba(0,0,0,0.8)"
                : "0 0 20px rgba(239,68,68,0.08)",
              animation:"bfFadeUp 0.6s ease 0.46s both",
              transition:"all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
              transform: hoveredP==="ADMIN" ? "translateY(-12px) scale(1.04)" : "translateY(0) scale(1)",
              cursor:"pointer",
            }}>

            {/* Background */}
            <div style={{
              position:"absolute", inset:0,
              background: hoveredP==="ADMIN"
                ? "linear-gradient(135deg,#1a0505 0%,#060912 100%)"
                : "linear-gradient(135deg,#120404 0%,#060912 100%)",
              transition:"background 0.4s",
            }}/>

            {/* Red glow blob */}
            <div style={{
              position:"absolute", top:-30, right:-30, width:180, height:180,
              borderRadius:"50%", background:"rgba(239,68,68,0.35)", filter:"blur(60px)",
              opacity: hoveredP==="ADMIN" ? 0.9 : 0.25,
              transition:"opacity 0.3s", pointerEvents:"none",
            }}/>

            {/* SYSTEM badge */}
            <div style={{
              position:"absolute", top:14, right:14, zIndex:10,
              background:"linear-gradient(135deg,#7F1D1D,#450a0a)",
              color:"#FCA5A5", fontSize:9, fontWeight:800, letterSpacing:2,
              padding:"5px 12px", borderRadius:50, textTransform:"uppercase",
              border:"1px solid rgba(239,68,68,0.5)",
              boxShadow:"0 2px 12px rgba(239,68,68,0.4)",
            }}>🐉 SYSTEM</div>

            {/* Character — webp avatar fills card */}
            <div style={{
              position:"absolute", left:0, right:0, top:-5, bottom:0,
              zIndex:1,
              animation: hoveredP==="ADMIN" ? "bfFloat 2.5s ease-in-out infinite" : "none",
              filter:`drop-shadow(0 0 ${hoveredP==="ADMIN"?20:10}px rgba(239,68,68,0.7))`,
              transition:"filter 0.3s",
            }}>
              <img
                src={"/assets/bots/acn_admin.webp"}
                alt="ACN Admin"
                style={{ width:"100%", height:"100%", objectFit:"fill", display:"block" }}
              />
            </div>

            {/* Bottom info — transparent, text shadows only */}
            <div style={{
              position:"absolute", bottom:0, left:0, right:0, height:190,
              paddingTop:0, zIndex:5,
              display:"flex", flexDirection:"column", justifyContent:"flex-end",
            }}>
              <div style={{ padding:"0 16px 12px", textAlign:"center" }}>
                {/* Name */}
                <div style={{
                  fontSize:26, fontWeight:900, color:"white",
                  letterSpacing:"-1px", lineHeight:1, marginBottom:3,
                  textShadow:"0 0 20px rgba(0,0,0,1), 0 2px 8px rgba(0,0,0,1)",
                }}>ACN Admin</div>

                {/* Role */}
                <div style={{
                  fontSize:8, fontWeight:700, letterSpacing:3,
                  color:"#FCA5A5", textTransform:"uppercase", marginBottom:8,
                  textShadow:"0 0 12px rgba(0,0,0,1)",
                }}>System Bot</div>

                {/* Exclusive tag instead of tagline */}
                <div style={{
                  display:"inline-flex", alignItems:"center", gap:4,
                  background:"rgba(239,68,68,0.15)", border:"1px solid rgba(239,68,68,0.35)",
                  borderRadius:20, padding:"3px 10px", fontSize:10,
                  color:"rgba(252,165,165,0.9)", marginBottom:12,
                }}>🔮 Exclusive Access</div>
              </div>

              {/* Button */}
              <div style={{ padding:"0 14px 14px" }}>
                <div style={{
                  padding:"11px 0", borderRadius:12, textAlign:"center",
                  background: hoveredP==="ADMIN"
                    ? "linear-gradient(135deg,#DC2626,#7F1D1D)"
                    : "rgba(239,68,68,0.10)",
                  color: hoveredP==="ADMIN" ? "white" : "#FCA5A5",
                  fontSize:12, fontWeight:800, letterSpacing:0.5, transition:"all 0.2s",
                  border:`1px solid ${hoveredP==="ADMIN" ? "transparent" : "rgba(239,68,68,0.25)"}`,
                  boxShadow: hoveredP==="ADMIN"
                    ? "0 0 20px rgba(239,68,68,0.5), 0 4px 16px rgba(239,68,68,0.3)"
                    : "0 0 6px rgba(239,68,68,0.15)",
                }}>
                  {hoveredP==="ADMIN" ? "🐉 Enter Portal →" : "Select"}
                </div>
              </div>
            </div>
          </div>
      </div>

      {/* ACN Admin Bot Modal — username/password login only */}
      {showAdminBot && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.9)", zIndex:600,
          display:"flex", alignItems:"center", justifyContent:"center", padding:20,
          fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
          <div style={{
            background:"linear-gradient(135deg,#0d0d1a,#1a0a2e)",
            border:"2px solid rgba(139,92,246,0.5)", borderRadius:24,
            padding:"32px 28px", maxWidth:360, width:"100%",
            boxShadow:"0 0 80px rgba(139,92,246,0.3)",
            animation:"bfLvlPop 0.45s cubic-bezier(0.34,1.56,0.64,1) both",
          }}>
            <div style={{ textAlign:"center", marginBottom:24 }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🛡️</div>
              <div style={{ fontSize:10, letterSpacing:3, color:"#A78BFA",
                fontWeight:700, textTransform:"uppercase", marginBottom:6 }}>System Bot Access</div>
              <div style={{ fontSize:22, fontWeight:900, color:"white", marginBottom:6 }}>ACN Admin</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.45)", lineHeight:1.5 }}>
                For Team Leaders, SMEs &amp; Managers only.
              </div>
            </div>
            <AdminLoginForm
              adminProfile={adminProfile}
              onSuccess={(profile) => {
                if (profile) {
                  setAdminProfile(profile);
                  try { localStorage.setItem("botfit_admin_profile", JSON.stringify(profile)); } catch {}
                  // An admin session and an employee session on the same device
                  // both gate on their own presence (e.g. the player-side bingo
                  // poll skips entirely while adminProfile is set) — logging in
                  // as one must clear the other, or a leftover session silently
                  // breaks the other role's polling with no visible error.
                  if (employeeProfile) {
                    setEmployeeProfile(null);
                    try { localStorage.removeItem("botfit_employee"); } catch {}
                  }
                }
                setShowAdminBot(false);
                setShowAdmin(true);
              }}
              onCancel={() => setShowAdminBot(false)}
            />
          </div>
        </div>
      )}

      {/* Switch-anytime notice — shown before first bot selection */}
      {!persona && (
        <div style={{
          marginTop:28, padding:"12px 20px", borderRadius:12,
          background:"rgba(59,130,246,0.08)", border:"1px solid rgba(59,130,246,0.2)",
          maxWidth:560, textAlign:"center",
        }}>
          <div style={{ fontSize:12, color:"rgba(147,197,253,0.8)", lineHeight:1.6 }}>
            💡 You can switch between ACE, CODE, and NEJI anytime from the bot select screen.
            Your XP, ACN Tokens, and level progress are saved to your profile.
          </div>
        </div>
      )}

      {/* Dev mode reset */}
      {DEV_MODE && persona && (
        <div style={{ marginTop:20 }}>
          <button onClick={() => {
            const keys = [
              "botfit_persona","botfit_employee","botfit_outfit","botfit_process",
              "botfit_xp","botfit_tokens","botfit_level","botfit_msgcount",
              "botfit_quests","botfit_pkt","botfit_event_outfit",
              // admin profile
              "botfit_admin_profile","botfit_admin_tokens","botfit_admin_quests",
              // bingo state
              "botfit_bingo_unlocked","botfit_bingo_max_winners","botfit_bingo_label",
            ];
            keys.forEach(k => { try { localStorage.removeItem(k); } catch {} });
            setPersona(null); setEmployeeProfile(null);
            setAdminProfile(null); setAdminTokens(200); setAdminQuests(ADMIN_QUESTS);
            setIsBingoUnlocked(false); setBingoMaxWinners(1); setBingoEventLabel("Huddle Bingo");
            setProcAuthed(true); setScreen("select");
            setXp(0); setTokens(0); setLevel(1); setMsgCount(0);
            setQuests(QUESTS); setProcess(null); setMessages([]);
            setOutfit(DEFAULT_OUTFIT); setPktResult(null);
          }} style={{
            padding:"8px 18px", borderRadius:10,
            border:"1px solid rgba(239,68,68,0.3)",
            background:"rgba(239,68,68,0.08)",
            color:"#F87171", fontSize:11, cursor:"pointer",
          }}>🛠 DEV: Reset Everything</button>
        </div>
      )}

      <p style={{ color:"rgba(255,255,255,0.50)", fontSize:12, marginTop:24, marginBottom:24, textAlign:"center" }}>
        Powered by Claude AI ·Innovative Ideas by Jonnalyn Terry · Earn XP and ACN Tokens with every conversation
      </p>
    </div>
  );

  // ── PROCESS SELECT ────────────────────────────────────
  if (screen === "process") {
    const pp = PERSONAS[persona];
    return (<>
      <div style={{ minHeight:"100vh", background:appBg,
        fontFamily:"'Segoe UI',system-ui,sans-serif",
        padding:"0 16px 40px", display:"flex", flexDirection:"column", alignItems:"center",
        transition:"background 1.2s ease" }}>
        <style>{`
          @keyframes bfFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
          @keyframes bfFadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
          @keyframes bfSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
          @keyframes bfFadeIn{from{opacity:0}to{opacity:1}}
          @keyframes bfLvlPop{0%{opacity:0;transform:scale(0.35) translateY(30px)}60%{transform:scale(1.08) translateY(-4px)}100%{opacity:1;transform:scale(1) translateY(0)}}
          .bf-proc-card{transition:transform 0.25s,box-shadow 0.25s,border-color 0.25s;cursor:pointer}
          .bf-proc-card:hover{transform:translateY(-6px) scale(1.03)}
          @keyframes bfMarquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
          @keyframes bfMarqueePause{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        `}</style>

        {/* "What is Ascendix?" tutorial — reopenable from here too, since a
            returning user with a saved persona+process never lands on the
            select screen where the original help icon lives */}
        <BotFitTutorial open={showBotFitTutorial} onClose={closeBotFitTutorial} />
        <button
          onClick={() => setShowBotFitTutorial(true)}
          aria-label="What is Ascendix? Open tutorial"
          title="What is Ascendix?"
          style={{
            position:"fixed", bottom:20, right:20, zIndex:200,
            width:44, height:44, borderRadius:"50%", border:"1px solid rgba(139,0,255,0.4)",
            background:"rgba(13,13,26,0.9)", color:"#C4B5FD", fontSize:18, fontWeight:800,
            cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow:"0 4px 20px rgba(139,0,255,0.3)", backdropFilter:"blur(8px)",
          }}
        >?</button>

        {/* Header with bot preview + live progress */}
        <div style={{ textAlign:"center", padding:"40px 0 28px", animation:"bfFadeUp 0.5s ease both" }}>
          <div style={{ display:"flex", justifyContent:"center", marginBottom:16,
            animation:"bfFloat 3s ease-in-out infinite" }}>
            <BotAvatar persona={persona} level={level} size={56} animated outfit={outfit} equippedEvent={equippedEvent} showWeapon/>
          </div>
          <div style={{ fontSize:11, letterSpacing:4, color:pp.color, fontWeight:700,
            textTransform:"uppercase", marginBottom:4 }}>{pp.name} · Select Process</div>
          {/* Live XP strip */}
          <div style={{ display:"inline-flex", alignItems:"center", gap:14,
            background:"rgba(255,255,255,0.05)", border:`1px solid ${pp.color}33`,
            borderRadius:50, padding:"6px 18px", marginBottom:16, fontSize:12 }}>
            <span style={{ color:pp.color, fontWeight:700 }}>Lv.{level} {LEVEL_NAMES[level-1]}</span>
            <span style={{ color:"rgba(255,255,255,0.55)" }}>·</span>
            <span style={{ color:"rgba(255,255,255,0.5)" }}>⚡ {xp} XP</span>
            <span style={{ color:"rgba(255,255,255,0.55)" }}>·</span>
            <span style={{ color:"#F59E0B" }}>🪙 {tokens}</span>
          </div>
          <h2 style={{ fontSize:"clamp(22px,4vw,36px)", fontWeight:900, color:"white",
            margin:0, letterSpacing:"-1px" }}>What process are we working on?</h2>
          <p style={{ color:"rgba(255,255,255,0.60)", marginTop:10, fontSize:14 }}>
            Choose a category so I can load the right rules
          </p>
        </div>

        {/* ── Latest Process Update Marquee — sourced from the "latestUpdate"
            tab in the BotFit Google Sheet; add a row there and it shows up
            here automatically, no deploy needed ── */}
        <div style={{
          width:"100%", maxWidth:900, marginBottom:20,
          borderRadius:16, overflow:"hidden",
          background:"linear-gradient(135deg,rgba(239,68,68,0.08),rgba(239,68,68,0.03))",
          border:"1px solid rgba(239,68,68,0.25)",
        }}>
          {/* Header */}
          <div style={{
            padding:"10px 20px 8px",
            borderBottom:"1px solid rgba(239,68,68,0.18)",
            display:"flex", alignItems:"center", gap:8,
          }}>
            <div style={{ fontSize:16 }}>📢</div>
            <div style={{ fontSize:10, fontWeight:800, letterSpacing:3,
              textTransform:"uppercase", color:"#F87171" }}>Latest Process Update</div>
          </div>

          {/* Marquee track */}
          <div style={{ padding:"10px 0", overflow:"hidden", position:"relative" }}>
            {/* Fade edges */}
            <div style={{ position:"absolute", left:0, top:0, bottom:0, width:60,
              background:"linear-gradient(to right, rgba(6,9,18,0.9), transparent)",
              zIndex:2, pointerEvents:"none" }}/>
            <div style={{ position:"absolute", right:0, top:0, bottom:0, width:60,
              background:"linear-gradient(to left, rgba(6,9,18,0.9), transparent)",
              zIndex:2, pointerEvents:"none" }}/>

            {/* Scrolling content — doubled for seamless loop */}
            <div style={{
              display:"inline-flex", gap:0, alignItems:"center",
              animation: latestUpdates.length > 0 ? "bfMarquee 45s linear infinite" : "none",
              whiteSpace:"nowrap", width:"max-content",
              willChange:"transform",
            }}>
              {latestUpdates.length > 0 ? (
                [...latestUpdates, ...latestUpdates].map((entry, i) => (
                  <div key={i} style={{
                    display:"inline-flex", alignItems:"center", gap:10,
                    padding:"6px 24px 6px 16px",
                    borderRight:"1px solid rgba(239,68,68,0.15)",
                  }}>
                    <span style={{ fontSize:16, flexShrink:0 }}>{entry.icon}</span>
                    {entry.process && (
                      <span style={{ fontSize:10, color:"#F87171", fontWeight:700,
                        background:"rgba(239,68,68,0.15)", padding:"2px 8px", borderRadius:20,
                        border:"1px solid rgba(239,68,68,0.3)", letterSpacing:0.5, flexShrink:0 }}>
                        {entry.process}
                      </span>
                    )}
                    {entry.scenario && (
                      <span style={{ fontSize:12, color:"white", fontWeight:600 }}>{entry.scenario}</span>
                    )}
                    {entry.scenario && entry.action && (
                      <span style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>→</span>
                    )}
                    {entry.action && (
                      <span style={{ fontSize:12, color:"#F59E0B", fontWeight:700 }}>{entry.action}</span>
                    )}
                    {!entry.scenario && !entry.action && (
                      <span style={{ fontSize:12, color:"white", fontWeight:600 }}>{entry.update}</span>
                    )}
                    {entry.date && (
                      <span style={{ fontSize:10, color:"rgba(255,255,255,0.4)", flexShrink:0 }}>{entry.date}</span>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ padding:"10px 24px", color:"rgba(255,255,255,0.55)" }}>
                  {latestUpdatesLoading ? "Loading updates..." : "No updates yet"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Process cards — persona-specific */}
        {(() => {
          const isACE  = persona === "ACE";
          const isNEJI = persona === "NEJI";
          const isCODE = persona === "CODE";
          const cards  = isACE ? ACE_TOPICS : isNEJI ? NEJI_TOPICS : PROCESSES;

          return (
            <div style={{ display:"flex", gap:12, justifyContent:"center",
              flexWrap:"wrap", width:"100%", maxWidth:960, paddingBottom:4 }}>

              {/* Coming soon header for ACE */}
              {isACE && (
                <div style={{ width:"100%", textAlign:"center", marginBottom:4 }}>
                  <div style={{ display:"inline-flex", alignItems:"center", gap:8,
                    background:"rgba(59,130,246,0.1)", border:"1px solid rgba(59,130,246,0.25)",
                    borderRadius:50, padding:"6px 18px", fontSize:11, color:"#93C5FD", fontWeight:700 }}>
                    🚧 Training content is being prepared — topics will unlock soon!
                  </div>
                </div>
              )}

              {cards.map((proc, i) => {
                const isSoon    = !!proc.comingSoon;
                const isHovered = !isSoon && hoveredProc === proc.id;

                return (
                  <div key={proc.id}
                    className={isSoon ? "" : "bf-proc-card"}
                    onClick={() => !isSoon && selectProcess(proc)}
                    onMouseEnter={() => !isSoon && setHoveredProc(proc.id)}
                    onMouseLeave={() => setHoveredProc(null)}
                    style={{
                      background: isSoon
                        ? "rgba(255,255,255,0.02)"
                        : isHovered
                        ? `linear-gradient(135deg,${pp.bg},#0a0a1a)`
                        : "#0d0d1a",
                      border:`1px solid ${isSoon ? "rgba(255,255,255,0.06)" : isHovered ? pp.color : "rgba(255,255,255,0.08)"}`,
                      borderRadius:18, padding:"20px 16px", width:170, minWidth:150, flexShrink:0,
                      textAlign:"center",
                      boxShadow: isHovered ? `0 0 32px ${pp.colorGlow}` : "none",
                      animation:`bfFadeUp 0.5s ease ${0.1+i*0.1}s both`,
                      position:"relative", overflow:"hidden",
                      transition:"all 0.25s",
                      opacity: isSoon ? 0.55 : 1,
                      cursor: isSoon ? "default" : "pointer",
                    }}>

                    {/* Coming soon ribbon */}
                    {isSoon && (
                      <div style={{
                        position:"absolute", top:10, right:-18,
                        background:"rgba(245,158,11,0.85)", color:"#000",
                        fontSize:8, fontWeight:900, letterSpacing:1,
                        padding:"3px 22px", transform:"rotate(35deg)",
                        textTransform:"uppercase",
                      }}>Soon</div>
                    )}

                    <div style={{ position:"absolute", top:-30, right:-30, width:80, height:80,
                      borderRadius:"50%", background: isSoon ? "rgba(255,255,255,0.05)" : pp.colorGlow,
                      filter:"blur(30px)", opacity: isHovered ? 0.5 : 0.1,
                      transition:"opacity 0.3s", pointerEvents:"none" }}/>

                    <div style={{
                      width:36, height:36, borderRadius:"50%", margin:"0 auto 12px",
                      background: isSoon
                        ? "rgba(255,255,255,0.05)"
                        : isHovered
                        ? `linear-gradient(135deg,${pp.color},${pp.colorDark})`
                        : "rgba(255,255,255,0.08)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:14, fontWeight:900,
                      color: isSoon ? "rgba(255,255,255,0.4)" : isHovered ? "white" : pp.color,
                      transition:"all 0.2s",
                      boxShadow: isHovered ? `0 4px 16px ${pp.colorGlow}` : "none",
                    }}>{proc.label}</div>

                    <div style={{ fontSize:24, marginBottom:8 }}>{proc.icon}</div>
                    <div style={{ fontSize:13, fontWeight:800,
                      color: isSoon ? "rgba(255,255,255,0.55)" : "white",
                      marginBottom:4, lineHeight:1.3 }}>{proc.name}</div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.60)",
                      lineHeight:1.5 }}>{proc.desc}</div>

                    {isHovered && !isSoon && (
                      <div style={{ marginTop:12, fontSize:11, fontWeight:700, color:pp.color }}>
                        {isNEJI ? "Let's go! →" : "Select →"}
                      </div>
                    )}
                    {isSoon && (
                      <div style={{ marginTop:10, fontSize:9, fontWeight:700,
                        color:"rgba(245,158,11,0.7)", letterSpacing:1, textTransform:"uppercase" }}>
                        🚧 Coming Soon
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}


        <div onClick={() => setScreen("select")}
          style={{ marginTop:36, color:"#F59E0B", fontSize:13, fontWeight:600,
            cursor:"pointer", padding:"8px 16px", borderRadius:10,
            background:"rgba(245,158,11,0.08)",
            border:"1px solid rgba(245,158,11,0.3)", transition:"all 0.2s" }}
          onMouseEnter={e => { e.currentTarget.style.color="#FBBF24"; e.currentTarget.style.borderColor="rgba(245,158,11,0.5)"; }}
          onMouseLeave={e => { e.currentTarget.style.color="#F59E0B"; e.currentTarget.style.borderColor="rgba(245,158,11,0.3)"; }}>
          ← Back to Bot Select
        </div>
      </div>

      {/* ── Floating Sidebar Cards ── */}
      <ProcessSidebar
        persona={persona} level={level} xp={xp} tokens={tokens}
        outfit={outfit} employeeProfile={employeeProfile}
        pktResult={pktResult} pp={pp} equippedEvent={equippedEvent}
        onDeductTokens={(newTokens) => {
          setTokens(newTokens);
          try { localStorage.setItem("botfit_tokens", String(newTokens)); } catch {}
        }}
        onViewProfile={() => {
          const firstCard = persona === "ACE" ? ACE_TOPICS[0] : persona === "NEJI" ? NEJI_TOPICS[0] : PROCESSES[0];
          selectProcess(firstCard);
          setTimer(() => {
            if (!employeeProfile) setShowLoginModal(true);
            else setTab("profile");
          }, 150);
        }}
      />
    </>
    );
  }

  // ── CHAT SCREEN ───────────────────────────────────────
  const completedQuests = quests.filter(q => q.done).length;

  return (
    <div style={{ height:"100vh", background:appBg, display:"flex",
      flexDirection:"column", fontFamily:"'Segoe UI',system-ui,sans-serif",
      overflow:"hidden", position:"relative",
      transition:"background 1.2s ease" }}>
      <style>{`
        @keyframes bfFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
        @keyframes bfSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes bfFadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes bfFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes bfPopIn{0%{opacity:0;transform:scale(0.85) translateY(10px)}100%{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes bfSlide{from{opacity:0;transform:translateX(-10px)}to{opacity:1;transform:translateX(0)}}
        @keyframes bfLvlPop{0%{opacity:0;transform:scale(0.35) translateY(30px)}60%{transform:scale(1.08) translateY(-4px)}100%{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes bfGainFloat{0%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(-38px)}}
        @keyframes bfDot{0%,100%{opacity:0.3;transform:scale(0.8)}50%{opacity:1;transform:scale(1)}}
        @keyframes bfPulse{0%{transform:scale(1)}40%{transform:scale(1.18)}75%{transform:scale(0.96)}100%{transform:scale(1)}}
        @keyframes bfEventGlow{0%,100%{opacity:0.55}50%{opacity:1}}
        @keyframes bfEventParticle{0%{transform:translateY(0) rotate(0deg);opacity:0.8}100%{transform:translateY(-60px) rotate(180deg);opacity:0}}
        @keyframes bfHeartRise{0%{transform:translateY(0) scale(1);opacity:0.75}100%{transform:translateY(-55px) scale(0.4);opacity:0}}
        @keyframes bfEmberRise{0%{transform:translateY(0) scale(1);opacity:0.8}100%{transform:translateY(-50px) scale(0.3);opacity:0}}
        .bf-msg-user{animation:bfSlide 0.3s ease both}
        .bf-msg-bot{animation:bfPopIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both}
        .bf-send:hover{transform:scale(1.07)} .bf-send:active{transform:scale(0.92)}
        .bf-proc-pill:hover{opacity:1!important}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-thumb{background:${activeTheme ? activeTheme.accent+"44" : p.color+"44"};border-radius:2px}
        @media(prefers-reduced-motion:reduce){*{animation-duration:0.01ms!important;animation-iteration-count:1!important;transition-duration:0.01ms!important}}
      `}</style>

      {/* "What is Ascendix?" tutorial — reopenable from the chat screen too */}
      <BotFitTutorial open={showBotFitTutorial} onClose={closeBotFitTutorial} />
      <button
        onClick={() => setShowBotFitTutorial(true)}
        aria-label="What is Ascendix? Open tutorial"
        title="What is Ascendix?"
        style={{
          position:"fixed", bottom:20, right:20, zIndex:200,
          width:44, height:44, borderRadius:"50%", border:"1px solid rgba(139,0,255,0.4)",
          background:"rgba(13,13,26,0.9)", color:"#C4B5FD", fontSize:18, fontWeight:800,
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 4px 20px rgba(139,0,255,0.3)", backdropFilter:"blur(8px)",
        }}
      >?</button>

      {/* Employee/Guest login — shown the first time a Guest opens the Profile tab */}
      {showLoginModal && (
        <EmployeeLoginModal
          p={p}
          onLogin={handleEmployeeLogin}
          onGuest={handleGuestContinue}
          onClose={() => setShowLoginModal(false)}
        />
      )}

      {/* Hidden bot SVG source for PiP cloning */}
      <div className="pip-bot-svg" style={{
        position:"absolute", left:-9999, top:-9999,
        width:1, height:1, overflow:"hidden", pointerEvents:"none",
        opacity:0, userSelect:"none",
      }}>
        <BotAvatar persona={persona} level={level} size={110} outfit={outfit}
          equippedEvent={level >= 3 ? equippedEvent : null}/>
      </div>

      {/* ── Event theme ambient overlay ── */}
      {activeTheme && (<>
        {/* radial glow blob top-left */}
        <div style={{
          position:"fixed", top:0, left:0, width:"60%", height:"60%",
          borderRadius:"50%", background:`radial-gradient(circle,${activeTheme.glow1},transparent 70%)`,
          pointerEvents:"none", zIndex:1, animation:"bfEventGlow 3s ease-in-out infinite",
          transform:"translate(-30%,-30%)",
        }}/>
        {/* radial glow blob bottom-right */}
        <div style={{
          position:"fixed", bottom:0, right:0, width:"50%", height:"50%",
          borderRadius:"50%", background:`radial-gradient(circle,${activeTheme.glow2},transparent 70%)`,
          pointerEvents:"none", zIndex:1, animation:"bfEventGlow 3.5s ease-in-out infinite 0.8s",
          transform:"translate(30%,30%)",
        }}/>
        {/* thin accent border on the whole screen */}
        <div style={{
          position:"fixed", inset:0, borderRadius:0, zIndex:1, pointerEvents:"none",
          boxShadow:`inset 0 0 0 1.5px ${activeTheme.borderGlow}`,
        }}/>
        {/* floating particles */}
        {activeTheme.particles === "snow" && [
          {x:"8%",  delay:"0s",   size:14}, {x:"22%", delay:"0.7s", size:11},
          {x:"55%", delay:"1.4s", size:13}, {x:"78%", delay:"0.3s", size:10},
          {x:"92%", delay:"1.1s", size:12},
        ].map((s,i) => (
          <div key={i} style={{
            position:"fixed", bottom:0, left:s.x, fontSize:s.size,
            animation:`bfEventParticle 2.8s ease-in infinite`, animationDelay:s.delay,
            pointerEvents:"none", zIndex:1, opacity:0.7, color:"white",
          }}>❄</div>
        ))}
        {activeTheme.particles === "hearts" && [
          {x:"6%",  delay:"0s",   size:14}, {x:"28%", delay:"0.9s", size:11},
          {x:"60%", delay:"1.6s", size:13}, {x:"82%", delay:"0.4s", size:10},
          {x:"94%", delay:"1.2s", size:12},
        ].map((s,i) => (
          <div key={i} style={{
            position:"fixed", bottom:0, left:s.x, fontSize:s.size,
            animation:`bfHeartRise 2.4s ease-out infinite`, animationDelay:s.delay,
            pointerEvents:"none", zIndex:1, color:activeTheme.accent,
          }}>♥</div>
        ))}
        {activeTheme.particles === "embers" && [
          {x:"5%",  delay:"0s",   size:9},  {x:"20%", delay:"0.6s", size:7},
          {x:"50%", delay:"1.3s", size:8},  {x:"72%", delay:"0.2s", size:7},
          {x:"90%", delay:"1.0s", size:9},
        ].map((s,i) => (
          <div key={i} style={{
            position:"fixed", bottom:0, left:s.x, fontSize:s.size,
            animation:`bfEmberRise 2s ease-out infinite`, animationDelay:s.delay,
            pointerEvents:"none", zIndex:1, color:activeTheme.accent,
          }}>✦</div>
        ))}
        {activeTheme.particles === "feathers" && [
          {x:"8%",  delay:"0s",   size:14, rot:"-20deg"},
          {x:"25%", delay:"0.8s", size:11, rot:"15deg"},
          {x:"45%", delay:"1.6s", size:13, rot:"-10deg"},
          {x:"65%", delay:"0.4s", size:10, rot:"25deg"},
          {x:"82%", delay:"1.2s", size:12, rot:"-15deg"},
        ].map((s,i) => (
          <div key={i} style={{
            position:"fixed", bottom:0, left:s.x,
            fontSize:s.size, transform:`rotate(${s.rot})`,
            animation:`bfEmberRise 3.5s ease-out infinite`, animationDelay:s.delay,
            pointerEvents:"none", zIndex:1, color:"#BAE6FD", opacity:0.85,
          }}>🪶</div>
        ))}
      </>)}

      {showLvlUp && lvlUpNum && persona && (
        <LevelUpModal level={lvlUpNum} persona={persona} onClose={() => setShowLvlUp(false)} outfit={outfit} equippedEvent={equippedEvent}/>
      )}
      {showPKT && persona && (
        <PKTModal persona={persona} onClose={() => setShowPKT(false)} onComplete={handlePKTComplete}/>
      )}
      {showPKTResults && pktResult && persona && (
        <PKTResultsModal result={pktResult} persona={persona} onClose={() => setShowPKTResults(false)}/>
      )}
      {showRetakeLocked && persona && (
        <RetakeLockedModal persona={persona} onClose={() => setShowRetakeLocked(false)}/>
      )}

      {showAdmin && (
        <AdminPanel
          onClose={() => setShowAdmin(false)}
          adminProfile={adminProfile}
          adminTokens={adminTokens}
          setAdminTokens={setAdminTokens}
          adminQuests={adminQuests}
          setAdminQuests={setAdminQuests}
          alreadyAuthed
          isBingoUnlocked={isBingoUnlocked} setIsBingoUnlocked={setIsBingoUnlocked}
          bingoMaxWinners={bingoMaxWinners} setBingoMaxWinners={setBingoMaxWinners}
          bingoEventLabel={bingoEventLabel} setBingoEventLabel={setBingoEventLabel}
          calledNumbers={calledNumbers} setCalledNumbers={setCalledNumbers}
          bingoWinners={bingoWinners} setBingoWinners={setBingoWinners}
          onBingoSet={() => { bingoLastSetRef.current = Date.now(); }}
        />
      )}
      {DEV_MODE && persona && (
        <DevPanel xp={xp} tokens={tokens} level={level}
          setXp={setXp} setTokens={setTokens}
          onReset={resetToL1} onPKTReset={resetPKT}
          onLoadDemo={loadDemoState} persona={persona}/>
      )}

      {/* Background glow */}
      <div style={{ position:"absolute", inset:0, pointerEvents:"none", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-100, right:-100, width:400, height:400, borderRadius:"50%", background:p.colorGlow, filter:"blur(100px)", opacity:0.15 }}/>
        <div style={{ position:"absolute", bottom:-100, left:-100, width:300, height:300, borderRadius:"50%", background:p.colorGlow, filter:"blur(80px)", opacity:0.1 }}/>
      </div>

      {xpGain    && <div style={{ position:"absolute", top:56, right:68, fontWeight:700, fontSize:13, color:p.color,    zIndex:50, animation:"bfGainFloat 2.2s ease forwards",        pointerEvents:"none" }}>+{xpGain} XP</div>}
      {tokenGain && <div style={{ position:"absolute", top:76, right:68, fontWeight:700, fontSize:13, color:"#F59E0B", zIndex:50, animation:"bfGainFloat 2.2s ease 0.15s forwards", pointerEvents:"none" }}>+{tokenGain} ACN</div>}

      {/* ── Header ── */}
      <div style={{ padding:"12px 16px", borderBottom:"1px solid rgba(255,255,255,0.06)",
        display:"flex", alignItems:"center", gap:12,
        background:"rgba(6,9,18,0.9)", backdropFilter:"blur(20px)",
        position:"relative", zIndex:10, flexShrink:0 }}>
        <div onClick={() => setScreen("process")}
          style={{ color:"rgba(255,255,255,0.60)", fontSize:20, cursor:"pointer", userSelect:"none" }}>←</div>
        <div style={{ animation:"bfFloat 3s ease-in-out infinite", flexShrink:0 }}>
          <div style={{
            width:44, height:44, borderRadius:"50%",
            overflow:"hidden", flexShrink:0,
            border:`2px solid ${p.color}55`,
            boxShadow:`0 0 10px ${p.colorGlow}`,
          }}>
            <div style={{ marginLeft:-22, marginTop:-4 }}>
              <BotAvatar persona={persona} level={level} size={88} animated pulse={botPulse} outfit={outfit} equippedEvent={equippedEvent}/>
            </div>
          </div>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2, flexWrap:"wrap" }}>
            <span style={{ color:"white", fontWeight:800, fontSize:16 }}>{p.name}</span>
            <span style={{ fontSize:9, fontWeight:700, color:p.color, letterSpacing:2,
              background:`${p.color}22`, padding:"2px 8px", borderRadius:20, textTransform:"uppercase" }}>{p.role}</span>
            {level >= 2 && <span style={{ fontSize:9, fontWeight:700, color:"#F59E0B",
              background:"#F59E0B22", padding:"2px 7px", borderRadius:20 }}>LVL {level}</span>}
            {/* Process pill — clickable to switch */}
            {process && (
              <span className="bf-proc-pill"
                onClick={() => setScreen("process")}
                style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.7)",
                  background:"rgba(255,255,255,0.08)", padding:"2px 8px",
                  borderRadius:20, cursor:"pointer", opacity:0.8,
                  border:"1px solid rgba(255,255,255,0.12)",
                  transition:"opacity 0.2s" }}>
                {persona === "ACE"
                  ? `📚 Ask ACE: ${process.name} ↓`
                  : persona === "NEJI"
                  ? `${process.icon} ${process.name} ↓`
                  : `${process.icon} ${process.name} ↓`}
              </span>
            )}
          </div>
          <XPBar xp={xp} level={level} color={p.color}/>
        </div>
        <div style={{ display:"flex", gap:8, flexShrink:0, alignItems:"center" }}>
          {/* Float Bot button — always visible */}
          <button onClick={launchFloatingBot} title={pipActive ? "Close floating bot" : "Float bot on screen"} style={{
            width:34, height:34, borderRadius:"50%", border:"none",
            background: pipActive
              ? `linear-gradient(135deg,${p?.color||"#3B82F6"},${p?.colorDark||"#1D4ED8"})`
              : "rgba(255,255,255,0.08)",
            color: pipActive ? "white" : (p?.color||"#3B82F6"),
            fontSize:16, cursor:"pointer", display:"flex",
            alignItems:"center", justifyContent:"center",
            boxShadow: pipActive ? `0 0 14px ${p?.colorGlow||"rgba(59,130,246,0.4)"}` : "none",
            transition:"all 0.2s", flexShrink:0,
            border: pipActive ? "none" : `1px solid ${p?.color||"#3B82F6"}33`,
          }}>
            {pipActive ? "✕" : "⊡"}
          </button>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:16, fontWeight:800, color:p.color }}>{tokens}</div>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.55)", letterSpacing:1 }}>ACN</div>
          </div>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:16, fontWeight:800, color:"white" }}>{xp}</div>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.55)", letterSpacing:1 }}>XP</div>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display:"flex", borderBottom:"1px solid rgba(255,255,255,0.06)",
        background:"rgba(6,9,18,0.8)", flexShrink:0 }}>
        {[
          { id:"chat",      label:"💬 Chat" },
          { id:"quests",    label:`⚡ Quests ${completedQuests}/${quests.length}` },
          { id:"game",      label:"🎮 Game" },
          { id:"customize", label:"✨ Style" },
          { id:"profile",   label:"🤖 Profile" },
        ].map(t => (
          <button key={t.id} onClick={() => {
            if (t.id === "profile" && !employeeProfile) { setShowLoginModal(true); return; }
            setTab(t.id);
          }} style={{
            flex:1, padding:"10px 0", border:"none", background:"transparent",
            color:tab===t.id ? p.color : "rgba(255,255,255,0.35)",
            fontWeight:tab===t.id ? 700 : 400, fontSize:12, letterSpacing:0.5,
            borderBottom:`2px solid ${tab===t.id ? p.color : "transparent"}`,
            cursor:"pointer", transition:"all 0.2s", fontFamily:"inherit",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Chat Tab ── */}
      {tab === "chat" && <>
        <div style={{ flex:1, overflowY:"auto", padding:"16px 16px 8px" }}>

          {/* ── ACE Tutor mode banner ── */}
          {persona === "ACE" && process && (
            <div style={{ marginBottom:14, padding:"10px 14px", borderRadius:14,
              background:"rgba(59,130,246,0.08)", border:"1px solid rgba(59,130,246,0.2)",
              display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ fontSize:20, flexShrink:0 }}>📚</div>
              <div>
                <div style={{ fontSize:12, fontWeight:800, color:"#93C5FD" }}>
                  Tutor Mode — {process.name}
                </div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.60)", marginTop:2, lineHeight:1.4 }}>
                  Ask ACE anything about this topic — no question is too basic.
                </div>
              </div>
            </div>
          )}

          {/* ── NEJI Wellness mode banner ── */}
          {persona === "NEJI" && process && (
            <div style={{ marginBottom:14, padding:"10px 14px", borderRadius:14,
              background:"rgba(139,92,246,0.08)", border:"1px solid rgba(139,92,246,0.2)",
              display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ fontSize:20, flexShrink:0 }}>💜</div>
              <div>
                <div style={{ fontSize:12, fontWeight:800, color:"#C4B5FD" }}>
                  {process.name}
                </div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.60)", marginTop:2, lineHeight:1.4 }}>
                  This is a safe space. Share as much or as little as you want.
                </div>
              </div>
            </div>
          )}

          {messages.map((msg,i) => (
            <div key={i}
              className={msg.role==="user" ? "bf-msg-user" : "bf-msg-bot"}
              style={{ display:"flex", justifyContent:msg.role==="user"?"flex-end":"flex-start",
                marginBottom:12, gap:8, alignItems:"flex-end" }}>
              {msg.role==="assistant" && (
                <div style={{ flexShrink:0, marginBottom:2 }}>
                  <div style={{
                    width:28, height:28, borderRadius:"50%",
                    overflow:"hidden",
                    border:`1px solid ${p.color}44`,
                  }}>
                    <div style={{ marginLeft:-14, marginTop:-3 }}>
                      <BotAvatar persona={persona} level={level} size={56} outfit={outfit} equippedEvent={equippedEvent}/>
                    </div>
                  </div>
                </div>
              )}
              <div style={{
                maxWidth:"78%", padding:"11px 14px",
                borderRadius:msg.role==="user"?"18px 18px 4px 18px":"4px 18px 18px 18px",
                background:msg.role==="user"
                  ? `linear-gradient(135deg,${p.color},${p.colorDark})`
                  : "rgba(255,255,255,0.06)",
                color:"rgba(255,255,255,0.9)", fontSize:14, lineHeight:1.55,
                border:msg.role==="assistant"?"1px solid rgba(255,255,255,0.08)":"none",
                boxShadow:msg.role==="user"?`0 4px 20px ${p.colorGlow}`:"none",
              }} dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}/>
            </div>
          ))}

          {/* Sample test questions — only on a fresh chat (just the auto-greeting),
              so testers have concrete examples of what to ask. Disappears once a
              real message is sent. */}
          {!loading && messages.length === 1 && CODE_SAMPLE_QUESTIONS[process?.id] && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase",
                color:"rgba(255,255,255,0.35)", marginBottom:8 }}>
                Try asking
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {CODE_SAMPLE_QUESTIONS[process.id].map((q, i) => (
                  <button key={i} onClick={() => sendMessage(q)} disabled={loading}
                    style={{
                      textAlign:"left", padding:"9px 13px", borderRadius:12,
                      background:"rgba(255,255,255,0.04)", border:`1px solid ${p.color}33`,
                      color:"rgba(255,255,255,0.8)", fontSize:12.5, lineHeight:1.4,
                      cursor:"pointer", fontFamily:"inherit", transition:"background 0.15s, border-color 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${p.color}1a`; e.currentTarget.style.borderColor = `${p.color}66`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = `${p.color}33`; }}
                  >
                    💬 {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div className="bf-msg-bot" style={{ display:"flex", gap:8, alignItems:"flex-end", marginBottom:12 }}>
              <div style={{
                width:28, height:28, borderRadius:"50%",
                overflow:"hidden",
                border:`1px solid ${p.color}44`,
              }}>
                <div style={{ marginLeft:-14, marginTop:-3 }}>
                  <BotAvatar persona={persona} level={level} size={56} outfit={outfit} equippedEvent={equippedEvent}/>
                </div>
              </div>
              <div style={{ padding:"14px 18px", borderRadius:"4px 18px 18px 18px",
                background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.08)",
                display:"flex", gap:5, alignItems:"center" }}>
                {[0,1,2].map(i => <div key={i} style={{ width:6, height:6, borderRadius:"50%",
                  background:p.color, animation:`bfDot 1.4s ease-in-out ${i*0.2}s infinite` }}/>)}
              </div>
            </div>
          )}
          <div ref={messagesEndRef}/>
        </div>

        {/* Input */}
        <div style={{ padding:"12px 16px 16px", background:"rgba(6,9,18,0.9)",
          backdropFilter:"blur(20px)", borderTop:"1px solid rgba(255,255,255,0.06)", flexShrink:0 }}>

          {/* Compliance warning — fixed above the input so it stays visible
              regardless of scroll position, on every chat screen (ACE/CODE/NEJI
              all share this one input area). */}
          <div style={{
            display:"flex", alignItems:"flex-start", gap:8,
            background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)",
            borderRadius:10, padding:"8px 12px", marginBottom:10,
          }}>
            <span style={{ fontSize:13, flexShrink:0, lineHeight:1.4 }}>⚠️</span>
            <span style={{ fontSize:11, color:"#FCA5A5", lineHeight:1.5, fontWeight:600 }}>
              Do not input real client data — this helps prevent a client data breach.
              Processors found chatting with real client information will be subject to sanctions.
            </span>
          </div>

          <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
            <textarea value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={
                persona === "ACE"
                  ? `Ask ACE about ${process?.name || "this topic"}…`
                  : persona === "NEJI"
                  ? `Share what's on your mind…`
                  : `Ask ${p.name} about ${process?.name || "work"}…`
              }
              disabled={loading} rows={1}
              style={{ flex:1, background:"rgba(255,255,255,0.06)",
                border:`1px solid ${input ? p.color+"66" : "rgba(255,255,255,0.1)"}`,
                borderRadius:14, padding:"11px 14px", color:"white", fontSize:14,
                outline:"none", resize:"none", fontFamily:"inherit",
                lineHeight:1.4, transition:"border-color 0.2s",
                minHeight:44, maxHeight:100 }}/>
            <button className="bf-send" onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              style={{ width:44, height:44, borderRadius:12, border:"none",
                background:input.trim()?`linear-gradient(135deg,${p.color},${p.colorDark})`:"rgba(255,255,255,0.08)",
                color:"white", fontSize:18, cursor:input.trim()?"pointer":"not-allowed",
                display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                boxShadow:input.trim()?`0 4px 20px ${p.colorGlow}`:"none",
                transition:"all 0.2s" }}>↑</button>
          </div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.50)",
            textAlign:"center", marginTop:8 }}>
            {persona === "ACE"
              ? "Ask any question — ACE will explain it clearly · Earn XP"
              : persona === "NEJI"
              ? "Your conversation is private · NEJI is here for you 💜"
              : "Enter to send · Earn XP with every question"}
          </div>
        </div>
      </>}

      {/* ── Quests Tab ── */}
      {tab === "quests" && (
        <div style={{ flex:1, overflowY:"auto", padding:16 }}>

          {/* ── Monthly PKT Card ── */}
          {(() => {
            const done  = pktResult?.passed;
            const tried = pktResult && !pktResult.passed;
            return (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.60)", letterSpacing:2,
                  textTransform:"uppercase", marginBottom:10 }}>
                  📅 Monthly Quest · {getCurrentMonthLabel()}
                </div>
                <div style={{
                  borderRadius:18, padding:"18px 16px", position:"relative", overflow:"hidden",
                  background: done
                    ? `linear-gradient(135deg,${p.color}22,${p.colorDark}18)`
                    : tried ? "rgba(239,68,68,0.08)"
                    : "linear-gradient(135deg,rgba(245,158,11,0.12),rgba(245,158,11,0.04))",
                  border:`1.5px solid ${done ? p.color+"55" : tried ? "rgba(239,68,68,0.35)" : "rgba(245,158,11,0.4)"}`,
                  boxShadow: done ? `0 0 24px ${p.colorGlow}` : "none",
                }}>
                  <div style={{ position:"absolute", top:-30, right:-30, width:100, height:100,
                    borderRadius:"50%", filter:"blur(35px)", pointerEvents:"none",
                    background: done ? p.colorGlow : tried ? "rgba(239,68,68,0.3)" : "rgba(245,158,11,0.3)",
                    opacity:0.4 }}/>

                  <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
                    <div style={{ width:46, height:46, borderRadius:13, flexShrink:0,
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:22,
                      background: done ? `linear-gradient(135deg,${p.color},${p.colorDark})`
                        : tried ? "rgba(239,68,68,0.2)" : "rgba(245,158,11,0.2)" }}>
                      {done ? "✅" : tried ? "⏳" : "📋"}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                        <span style={{ fontSize:14, fontWeight:800, color:"white" }}>
                          Process Knowledge Test
                        </span>
                        <span style={{ fontSize:9, fontWeight:700, letterSpacing:1.5,
                          padding:"2px 8px", borderRadius:20, textTransform:"uppercase",
                          background: done ? `${p.color}33` : tried ? "rgba(239,68,68,0.25)" : "rgba(245,158,11,0.25)",
                          color: done ? p.color : tried ? "#FCA5A5" : "#F59E0B" }}>
                          {done ? "PASSED" : tried ? "RETAKE" : "OPEN"}
                        </span>
                      </div>
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.60)", lineHeight:1.5, marginBottom:10 }}>
                        {done
                          ? `Score: ${pktResult.score}% · ${pktResult.correct}/${pktResult.total} correct`
                          : tried
                          ? `Score: ${pktResult.score}% · Need ${PKT_PASS_SCORE}% to pass`
                          : `Scenario questions · Pass at ${PKT_PASS_SCORE}%`}
                      </div>
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                        <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px",
                          borderRadius:20, color: done ? p.color : "rgba(255,255,255,0.4)",
                          background:`${done ? p.color : "#fff"}11` }}>⚡ {PKT_BASE_XP} XP</span>
                        <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px",
                          borderRadius:20, color: done ? "#F59E0B" : "rgba(255,255,255,0.4)",
                          background:"rgba(245,158,11,0.1)" }}>🪙 Up to 30 ACN</span>
                      </div>
                    </div>
                  </div>

                  {(done || tried) && (
                    <div style={{ marginTop:12 }}>
                      <div style={{ height:5, borderRadius:3, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
                        <div style={{ height:"100%", borderRadius:3, transition:"width 0.6s",
                          width:`${pktResult.score}%`,
                          background: done
                            ? `linear-gradient(90deg,${p.color},${p.colorDark})`
                            : "linear-gradient(90deg,#EF4444,#DC2626)" }}/>
                      </div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.55)", marginTop:4, textAlign:"right" }}>
                        {pktResult.score}%{done ? ` · +${pktResult.tokens} ACN earned` : ` · ${PKT_PASS_SCORE - pktResult.score}% more needed`}
                      </div>
                    </div>
                  )}

                  {!done && (
                    <button onClick={() => tried ? setShowRetakeLocked(true) : setShowPKT(true)} style={{
                      width:"100%", marginTop:14, padding:"11px", borderRadius:13,
                      fontFamily:"inherit", cursor:"pointer", fontSize:13, fontWeight:700,
                      color:"white", border:"none",
                      background: tried
                        ? "rgba(245,158,11,0.25)"
                        : `linear-gradient(135deg,rgba(245,158,11,0.85),rgba(245,158,11,0.55))`,
                    }}>{tried ? "⏳ Waiting for Retake" : "📋 Take the PKT"}</button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── Daily Quests ── */}
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.60)", letterSpacing:2,
            textTransform:"uppercase", marginBottom:12 }}>
            Daily Quests — {completedQuests}/{quests.length} Complete
          </div>
          <div style={{ height:4, borderRadius:2, background:"rgba(255,255,255,0.08)",
            overflow:"hidden", marginBottom:20 }}>
            <div style={{ height:"100%", width:`${(completedQuests/quests.length)*100}%`,
              borderRadius:2, background:`linear-gradient(90deg,${p.color},${p.colorDark})`,
              transition:"width 0.6s" }}/>
          </div>
          {quests.map((q,i) => (
            <div key={q.id} style={{
              display:"flex", alignItems:"center", gap:14,
              padding:"14px 16px", borderRadius:14, marginBottom:10,
              background:q.done?`${p.color}18`:"rgba(255,255,255,0.04)",
              border:`1px solid ${q.done?p.color+"44":"rgba(255,255,255,0.07)"}`,
              animation:`bfFadeUp 0.3s ease ${i*0.08}s both`,
            }}>
              <div style={{ width:28, height:28, borderRadius:"50%", flexShrink:0,
                background:q.done?`linear-gradient(135deg,${p.color},${p.colorDark})`:"rgba(255,255,255,0.08)",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:14, color:"white" }}>{q.done?"✓":i+1}</div>
              <div style={{ flex:1 }}>
                <div style={{ color:q.done?"rgba(255,255,255,0.5)":"rgba(255,255,255,0.85)",
                  fontSize:13, textDecoration:q.done?"line-through":"none" }}>{q.text}</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", marginTop:3 }}>
                  Reward: <span style={{ color:p.color }}>{q.xp} XP</span> + <span style={{ color:"#F59E0B" }}>{q.tokens} ACN</span>
                </div>
              </div>
              {q.done && <div style={{ fontSize:11, color:p.color, fontWeight:700 }}>DONE</div>}
            </div>
          ))}
          {completedQuests === quests.length && (
            <div style={{ textAlign:"center", padding:24, borderRadius:16,
              background:`linear-gradient(135deg,${p.color}22,${p.colorDark}22)`,
              border:`1px solid ${p.color}44`,
              animation:"bfPopIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both" }}>
              <div style={{ fontSize:28, marginBottom:8 }}>🎉</div>
              <div style={{ color:"white", fontWeight:700, fontSize:16 }}>All quests complete!</div>
              <div style={{ color:"rgba(255,255,255,0.5)", fontSize:13, marginTop:4 }}>
                Come back tomorrow for new quests
              </div>
            </div>
          )}

          {/* ── Individual Recognition Quests ── */}
          <div style={{ marginTop:28 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.60)", letterSpacing:2,
                textTransform:"uppercase" }}>🎖 Individual Recognition</div>
              <div style={{ fontSize:9, color:"rgba(16,185,129,0.7)", background:"rgba(16,185,129,0.08)",
                border:"1px solid rgba(16,185,129,0.2)", padding:"2px 8px", borderRadius:20 }}>
                Awarded by R&amp;R Team
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {ADMIN_QUESTS.map((q, i) => {
                const award   = rnrAwards.find(a => a.questId === q.id);
                const claimed = rnrClaimed.includes(q.id);
                return (
                  <div key={q.id} style={{
                    display:"flex", alignItems:"center", gap:14,
                    padding:"14px 16px", borderRadius:14,
                    background: claimed ? `${p.color}18`
                      : award ? "rgba(16,185,129,0.07)"
                      : "rgba(255,255,255,0.03)",
                    border:`1px solid ${
                      claimed ? p.color+"44"
                      : award ? "rgba(16,185,129,0.25)"
                      : "rgba(255,255,255,0.06)"}`,
                    opacity: award || claimed ? 1 : 0.55,
                    animation:`bfFadeUp 0.3s ease ${i*0.05}s both`,
                  }}>
                    <div style={{ fontSize:22, flexShrink:0 }}>{q.icon}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700,
                        color: claimed ? p.color : award ? "#10B981" : "rgba(255,255,255,0.7)",
                        textDecoration: claimed ? "line-through" : "none" }}>
                        {q.text.trim()}
                      </div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.45)", marginTop:2 }}>
                        {q.desc}
                      </div>
                      {award && !claimed && (
                        <div style={{ fontSize:9, color:"#10B981", marginTop:3 }}>
                          🎖 Awarded by {award.awardedBy}
                        </div>
                      )}
                    </div>
                    <div style={{ flexShrink:0, display:"flex", flexDirection:"column",
                      alignItems:"flex-end", gap:4 }}>
                      <span style={{ fontSize:11, fontWeight:800,
                        color: claimed ? p.color : award ? "#F59E0B" : "rgba(255,255,255,0.25)" }}>
                        +{q.tokens} ACN
                      </span>
                      {claimed ? (
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
                          <span style={{ fontSize:10, color:p.color, fontWeight:700 }}>✓ Claimed</span>
                          <button onClick={() => setShowCertificate({
                            award: award || { awardedBy:"R&R Team", awardedAt: new Date().toISOString() },
                            quest: q
                          })} style={{
                            padding:"4px 10px", borderRadius:8, border:`1px solid ${p.color}44`,
                            background:`${p.color}15`, color:p.color,
                            fontSize:9, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                            display:"flex", alignItems:"center", gap:3,
                          }}>📄 Certificate</button>
                        </div>
                      ) : award ? (
                        <button onClick={() => {
                          // Award tokens
                          setTokens(prev => {
                            const next = prev + q.tokens;
                            try { localStorage.setItem("botfit_tokens", String(next)); } catch {}
                            return next;
                          });
                          setTokenGain(q.tokens);
                          setTimeout(() => setTokenGain(null), 2200);
                          // Mark as claimed in state + localStorage (scoped to this employee only)
                          try {
                            const next    = [...rnrClaimed, q.id];
                            const empName = employeeProfile?.name;
                            if (empName) localStorage.setItem(`botfit_rnr_claimed__${empName}`, JSON.stringify(next));
                            setRnrClaimed(next);
                          } catch {}
                          // Refresh awards cache (scoped to this employee only)
                          setRnrAwards(prev => {
                            const next    = [...prev];
                            const empName = employeeProfile?.name;
                            try {
                              if (empName) localStorage.setItem(`botfit_rnr_awards__${empName}`, JSON.stringify(next));
                            } catch {}
                            return next;
                          });
                        }} style={{
                          padding:"5px 14px", borderRadius:8, border:"none", cursor:"pointer",
                          background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
                          color:"white", fontSize:10, fontWeight:700, fontFamily:"inherit",
                        }}>Claim</button>
                      ) : (
                        <span style={{ fontSize:9, color:"rgba(255,255,255,0.25)",
                          display:"flex", alignItems:"center", gap:3 }}>
                          🔒 Pending
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Certificate Modal ── */}
      {showCertificate && (
        <CertificateModal
          award={showCertificate.award}
          quest={showCertificate.quest}
          employeeProfile={employeeProfile}
          onClose={() => setShowCertificate(null)}
        />
      )}

      {/* ── Customize Tab ── */}
      {tab === "customize" && (
        <CustomizeTab persona={persona} level={level} xp={xp} outfit={outfit} equipItem={equipItem} p={p}
          equippedEvent={equippedEvent} equipEventOutfit={equipEventOutfit}/>
      )}

      {/* ── Game Tab ── */}
      {tab === "game" && (
        <GameTab persona={persona} employeeProfile={employeeProfile} awardXP={awardXP} p={p}
          isBingoUnlocked={isBingoUnlocked}
          bingoMaxWinners={bingoMaxWinners}
          bingoEventLabel={bingoEventLabel}
          calledNumbers={calledNumbers}
          teamName={employeeProfile?.teamName || ""}
          onWinReport={(winner) => setBingoWinners(prev => [...prev, winner])}/>
      )}

      {/* ── Profile Tab ── */}
      {tab === "profile" && (
        <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:0, position:"relative" }}>

          {/* ── HEX GRID BG ── */}
          <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", opacity:0.035, pointerEvents:"none", zIndex:0 }} xmlns="http://www.w3.org/2000/svg">
            <defs><pattern id="hxp" x="0" y="0" width="52" height="45" patternUnits="userSpaceOnUse">
              <polygon points="26,2 48,13 48,32 26,43 4,32 4,13" fill="none" stroke="white" strokeWidth="1"/>
            </pattern></defs>
            <rect width="100%" height="100%" fill="url(#hxp)"/>
          </svg>

          {/* ── PULSING XP BAR — TOP ── */}
          {(() => {
            const xpLevels=[0,100,250,500,900,1500];
            const curMin=xpLevels[Math.min(level-1,4)], curMax=xpLevels[Math.min(level,5)]||1500;
            const pct=level>=5?100:Math.min(100,Math.round(((xp-curMin)/(curMax-curMin))*100));
            return (
              <div style={{ padding:"10px 12px 0", position:"relative", zIndex:1, flexShrink:0 }}>
                <div style={{ borderRadius:12, border:`1px solid ${p.color}44`, background:"rgba(0,0,0,0.5)", padding:"8px 12px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:7, height:7, borderRadius:"50%", background:p.color, boxShadow:`0 0 8px ${p.color}`, animation:"bfPulse 1.5s ease-in-out infinite" }}/>
                      <span style={{ fontSize:9, fontWeight:700, letterSpacing:2, color:p.color, textTransform:"uppercase" }}>Lv {level} — {["Starter","Achiever","Top Performer","Legend","Elite"][level-1]}</span>
                    </div>
                    <span style={{ fontSize:9, fontWeight:800, color:"white" }}>{xp}<span style={{ color:"rgba(255,255,255,0.70)", fontWeight:400 }}>/{curMax} XP</span></span>
                  </div>
                  <div style={{ height:6, borderRadius:6, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${pct}%`, borderRadius:6,
                      background:`linear-gradient(90deg,${p.colorDark},${p.color},${p.accent})`,
                      boxShadow:`0 0 14px ${p.color}`, transition:"width 1.2s ease",
                      animation:"bfXpPulse 2.5s ease-in-out infinite" }}/>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Scrollable content */}
          <div style={{ flex:1, overflowY:"auto", position:"relative", zIndex:1 }}>

            {/* ══ LARGE BOT GLASS CARD ══ */}
            {(() => {
              const xpLevels=[0,100,250,500,900,1500];
              const nextLv=Math.min(level+1,5);
              const xpToNext=xpLevels[nextLv]-xp;
              const firstName=employeeProfile?.name?.split(" ")[0]||"Operative";
              const moodMsg = level>=5
                ? `You've reached Elite status, ${firstName}! 🌟`
                : xpToNext<=30
                ? `Only ${xpToNext} XP to Level ${nextLv}! Almost there! ⚡`
                : level===1
                ? `Let's grind to Level 2, ${firstName}! 💪`
                : `Keep going, ${firstName}! Level ${nextLv} awaits! 🔥`;
              return (
                <div style={{ margin:"10px 12px 0", borderRadius:20,
                  background:"rgba(255,255,255,0.04)",
                  backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)",
                  border:`1px solid ${p.color}33`,
                  boxShadow:`0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08), 0 0 40px ${p.colorGlow}33`,
                  overflow:"hidden", position:"relative" }}>

                  {/* Glass inner glow */}
                  <div style={{ position:"absolute", top:-60, left:"50%", transform:"translateX(-50%)", width:200, height:120, background:`radial-gradient(ellipse,${p.colorGlow} 0%,transparent 70%)`, opacity:0.25, pointerEvents:"none" }}/>

                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr" }}>

                    {/* LEFT — Large Bot + Stage */}
                    <div style={{ position:"relative", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end", padding:"16px 0 0", minHeight:260, borderRight:`1px solid rgba(255,255,255,0.06)` }}>

                      {/* Stage spotlight from above */}
                      <div style={{ position:"absolute", top:0, left:"50%", transform:"translateX(-50%)", width:80, height:160,
                        background:`conic-gradient(from 180deg at 50% 0%, transparent 60deg, ${p.colorGlow} 90deg, ${p.colorGlow} 270deg, transparent 300deg)`,
                        opacity:0.18, pointerEvents:"none" }}/>

                      {/* Mood speech bubble — chat style */}
                      <div style={{ position:"absolute", top:10, left:8, right:8, zIndex:4,
                        display:"flex", justifyContent:"center" }}>
                        <div style={{ position:"relative", maxWidth:"90%",
                          background:`linear-gradient(135deg, rgba(15,15,30,0.92), rgba(10,10,20,0.88))`,
                          backdropFilter:"blur(12px)",
                          borderRadius:"18px 18px 18px 4px",
                          padding:"8px 12px",
                          border:`1px solid ${p.color}55`,
                          boxShadow:`0 4px 20px rgba(0,0,0,0.5), 0 0 12px ${p.colorGlow}44`,
                        }}>
                          {/* Bot avatar mini indicator */}
                          <div style={{ display:"flex", alignItems:"flex-start", gap:6 }}>
                            <div style={{ width:6, height:6, borderRadius:"50%", background:p.color,
                              flexShrink:0, marginTop:4,
                              boxShadow:`0 0 6px ${p.color}`, animation:"bfPulse 1.5s ease-in-out infinite" }}/>
                            <div style={{ fontSize:11, color:"white", lineHeight:1.5, fontWeight:500 }}>{moodMsg}</div>
                          </div>
                          {/* Bubble tail — bottom left pointing down to bot */}
                          <div style={{ position:"absolute", bottom:-7, left:14,
                            width:0, height:0,
                            borderLeft:"7px solid transparent",
                            borderRight:"7px solid transparent",
                            borderTop:`7px solid ${p.color}55` }}/>
                          <div style={{ position:"absolute", bottom:-5, left:15,
                            width:0, height:0,
                            borderLeft:"6px solid transparent",
                            borderRight:"6px solid transparent",
                            borderTop:"6px solid rgba(10,10,20,0.90)" }}/>
                        </div>
                      </div>

                      {/* Bot avatar + pedestal as one unit */}
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", zIndex:2, marginTop:55, width:"100%" }}>
                        <div style={{ animation:"bfFloat 3s ease-in-out infinite", filter:`drop-shadow(0 0 28px ${p.colorGlow}) drop-shadow(0 16px 32px rgba(0,0,0,0.8))`, display:"flex", justifyContent:"center" }}>
                          <BotAvatar persona={persona} level={level} size={160} animated outfit={outfit} equippedEvent={level>=3?equippedEvent:null}/>
                        </div>
                        {/* Pedestal sits directly under feet */}
                        <div style={{ width:110, height:14, borderRadius:"50%", marginTop:-10,
                          background:`radial-gradient(ellipse,${p.color}cc 0%,${p.colorGlow} 40%,transparent 70%)`,
                          filter:"blur(5px)" }}/>
                        <div style={{ width:70, height:5, borderRadius:"50%", marginTop:-2,
                          background:`radial-gradient(ellipse,rgba(255,255,255,0.5) 0%,transparent 70%)`,
                          filter:"blur(2px)" }}/>
                      </div>

                      {/* Name + Level badge */}
                      <div style={{ textAlign:"center", padding:"4px 0 14px", zIndex:2, width:"100%", display:"flex", flexDirection:"column", alignItems:"center" }}>
                        <div style={{ fontSize:15, fontWeight:900, color:"white", letterSpacing:"-0.5px" }}>{p.name}</div>
                        <div style={{ display:"inline-flex", alignItems:"center", gap:4, marginTop:4,
                          background:`linear-gradient(135deg,${p.color},${p.colorDark})`,
                          borderRadius:20, padding:"2px 10px",
                          boxShadow:`0 0 12px ${p.colorGlow}`, fontSize:9, fontWeight:900, color:"white", letterSpacing:1 }}>
                          ⭐ LEVEL {level}
                        </div>
                      </div>
                    </div>

                    {/* RIGHT — Identity + Floating Stat Orbs */}
                    <div style={{ padding:"16px 14px", display:"flex", flexDirection:"column", justifyContent:"space-between" }}>

                      {/* Identity — shows the logged-in employee's info once Employee Login
                          is used; falls back to bot identity for Guests */}
                      <div>
                        <div style={{ fontSize:9, fontWeight:700, letterSpacing:3, color:p.color, textTransform:"uppercase", marginBottom:10, paddingBottom:6, borderBottom:`1px solid ${p.color}18` }}>Identity</div>
                        {(employeeProfile ? [
                          { label:"Operative", val:employeeProfile.name,            size:14, weight:900, color:"white" },
                          { label:"Division",  val:employeeProfile.department,      size:11, weight:700, color:"white" },
                          { label:"Unit",      val:employeeProfile.teamName,        size:11, weight:600, color:"rgba(255,255,255,0.90)" },
                          { label:"Commander", val:employeeProfile.teamLeader||"—", size:11, weight:700, color:p.color },
                        ] : [
                          { label:"Assistant", val:p.name, size:14, weight:900, color:"white" },
                          { label:"Role",      val:p.role, size:11, weight:700, color:"rgba(255,255,255,0.90)" },
                        ]).map((f,i)=>(
                          <div key={i} style={{ marginBottom:8 }}>
                            <div style={{ fontSize:8, fontWeight:700, letterSpacing:2, color:p.color, textTransform:"uppercase", marginBottom:1 }}>{f.label}</div>
                            <div style={{ fontSize:f.size, fontWeight:f.weight, color:f.color, lineHeight:1.2 }}>{f.val}</div>
                          </div>
                        ))}
                        {!employeeProfile && (
                          <button onClick={() => setShowLoginModal(true)} style={{
                            marginTop:4, padding:"5px 10px", borderRadius:8, border:`1px solid ${p.color}44`,
                            background:"rgba(255,255,255,0.04)", color:p.color, fontSize:9, fontWeight:700,
                            cursor:"pointer", fontFamily:"inherit",
                          }}>👤 Log In</button>
                        )}
                      </div>

                      {/* Floating Stat Orbs */}
                      <div>
                        <div style={{ fontSize:9, fontWeight:700, letterSpacing:3, color:p.color, textTransform:"uppercase", marginBottom:10, paddingTop:10, borderTop:`1px solid ${p.color}18` }}>Stats</div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center" }}>
                          {[
                            { label:"Messages",  val:msgCount,             color:"#A78BFA", glow:"rgba(167,139,250,0.5)" },
                            { label:"PKT Score", val:pktResult?.score!=null?`${pktResult.score}%`:"—",color:"#34D399", glow:"rgba(52,211,153,0.5)"  },
                            { label:"Total XP",  val:xp,                   color:p.color,   glow:p.colorGlow              },
                            { label:"Bonus",     val:completedQuests*5,    color:"#FBBF24", glow:"rgba(251,191,36,0.5)"  },
                            { label:"Streak",    val:level*10,             color:"#F87171", glow:"rgba(248,113,113,0.5)" },
                          ].map((orb,i)=>(
                            <div key={i} style={{ width:52, height:52, borderRadius:"50%",
                              background:`radial-gradient(circle at 35% 35%, ${orb.color}44, ${orb.color}18)`,
                              border:`1.5px solid ${orb.color}66`,
                              boxShadow:`0 0 14px ${orb.glow}, inset 0 1px 0 rgba(255,255,255,0.15)`,
                              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
                              animation:`bfFloat ${2.5+i*0.3}s ease-in-out ${i*0.2}s infinite`,
                              backdropFilter:"blur(4px)",
                            }}>
                              <div style={{ fontSize:13, fontWeight:900, color:orb.color, lineHeight:1, textShadow:`0 0 8px ${orb.glow}` }}>{orb.val}</div>
                              <div style={{ fontSize:8, color:"rgba(255,255,255,0.80)", letterSpacing:0.3, marginTop:1, textAlign:"center", lineHeight:1.2 }}>{orb.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ══ MILESTONE TIMELINE ══ */}
            <div style={{ margin:"10px 12px 0", background:"rgba(255,255,255,0.03)", borderRadius:14, padding:"12px 14px", border:`1px solid ${p.color}22` }}>
              <div style={{ fontSize:8, fontWeight:700, letterSpacing:2, color:p.color, textTransform:"uppercase", marginBottom:12 }}>⚔️ Milestone Timeline</div>
              <div style={{ position:"relative" }}>
                {/* Timeline line */}
                <div style={{ position:"absolute", top:16, left:16, right:16, height:2, background:`linear-gradient(90deg,${p.color}88,${p.color}22)`, borderRadius:2 }}/>
                <div style={{ display:"flex", justifyContent:"space-between", position:"relative" }}>
                  {[
                    { lv:1, icon:"⚡", label:"Starter",       unlocks:"Bot Access",          color:"#94A3B8" },
                    { lv:2, icon:"🎩", label:"Achiever",       unlocks:"Hats · Badges",       color:"#60A5FA" },
                    { lv:3, icon:"🦸", label:"Top Performer",  unlocks:"Cape · Aura Ring",    color:"#A78BFA" },
                    { lv:4, icon:"👑", label:"Legend",         unlocks:"Gold Skin · Crown",   color:"#F59E0B" },
                    { lv:5, icon:"🌟", label:"Elite",          unlocks:"Particles · Fame",    color:"#EF4444" },
                  ].map((m,i)=>{
                    const unlocked=level>=m.lv, current=level===m.lv;
                    return (
                      <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", width:52 }}>
                        {/* Node */}
                        <div style={{ width:32, height:32, borderRadius:"50%", zIndex:1,
                          background:unlocked?`linear-gradient(135deg,${m.color},${m.color}88)`:"rgba(255,255,255,0.08)",
                          border:current?`2px solid ${m.color}`:`1px solid ${unlocked?m.color+"55":"rgba(255,255,255,0.1)"}`,
                          display:"flex", alignItems:"center", justifyContent:"center", fontSize:14,
                          boxShadow:current?`0 0 16px ${m.color}88`:"none",
                          filter:unlocked?"none":"grayscale(1) brightness(0.4)",
                          transition:"all 0.3s",
                        }}>{m.icon}</div>
                        <div style={{ fontSize:11, fontWeight:800, color:unlocked?m.color:"white", marginTop:5, textAlign:"center", lineHeight:1.3 }}>{m.label}</div>
                        <div style={{ fontSize:10, color:"white", textAlign:"center", lineHeight:1.3, marginTop:2 }}>{m.unlocks}</div>
                        {current && <div style={{ fontSize:9, color:m.color, fontWeight:800, marginTop:2 }}>◆ NOW</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ══ BADGE CASE + RADAR ══ */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, margin:"10px 12px 0" }}>

              {/* Badge Case */}
              <div style={{ background:"rgba(255,255,255,0.03)", borderRadius:14, padding:"12px", border:`1px solid ${p.color}22` }}>
                <div style={{ fontSize:8, fontWeight:700, letterSpacing:2, color:p.color, textTransform:"uppercase", marginBottom:10 }}>🏅 Badge Case</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
                  {[
                    { icon:"💬", label:"Talker",    earned:msgCount>=5,          req:"5 msgs" },
                    { icon:"⚡", label:"Grinder",   earned:xp>=50,               req:"50 XP"  },
                    { icon:"🎯", label:"Hunter",    earned:completedQuests>=1,   req:"1 quest"},
                    { icon:"🧠", label:"Scholar",   earned:!!(pktResult?.passed),req:"PKT ✓"  },
                    { icon:"🔥", label:"Rising",    earned:level>=2,             req:"Lv 2"   },
                    { icon:"👑", label:"Legend",    earned:level>=4,             req:"Lv 4"   },
                  ].map((b,i)=>(
                    <div key={i} style={{ textAlign:"center", padding:"6px 2px", borderRadius:8,
                      background:b.earned?`${p.color}18`:"rgba(255,255,255,0.02)",
                      border:b.earned?`1px solid ${p.color}44`:"1px solid rgba(255,255,255,0.05)",
                      opacity:b.earned?1:0.4, transition:"all 0.3s" }}>
                      <div style={{ fontSize:16, filter:b.earned?`drop-shadow(0 0 5px ${p.color})`:"grayscale(1)" }}>{b.icon}</div>
                      <div style={{ fontSize:8, fontWeight:700, color:b.earned?p.color:"rgba(255,255,255,0.70)", marginTop:2 }}>{b.label}</div>
                      {!b.earned&&<div style={{ fontSize:7, color:"rgba(255,255,255,0.2)" }}>{b.req}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Radar Chart */}
              <div style={{ background:"rgba(255,255,255,0.03)", borderRadius:14, padding:"12px", border:`1px solid ${p.color}22` }}>
                <div style={{ fontSize:8, fontWeight:700, letterSpacing:2, color:p.color, textTransform:"uppercase", marginBottom:4 }}>📊 Skill Radar</div>
                {(() => {
                  const skills=[
                    {label:"Speed",    val:Math.min(100,Math.round((msgCount/20)*100))},
                    {label:"Know.",    val:Math.min(100,Math.round((xp/500)*100))},
                    {label:"Accuracy", val:pktResult?.passed?Math.min(100,pktResult.pct||80):15},
                    {label:"Wellness", val:persona==="NEJI"?Math.min(100,60+level*8):Math.min(100,30+level*10)},
                    {label:"Quests",   val:Math.min(100,Math.round((completedQuests/4)*100))},
                  ];
                  const cx=65,cy=65,r=48,n=skills.length;
                  const angle=(i)=>(Math.PI*2*i/n)-Math.PI/2;
                  const gp=(pct)=>skills.map((_,i)=>`${cx+r*pct*Math.cos(angle(i))},${cy+r*pct*Math.sin(angle(i))}`).join(" ");
                  const dp=skills.map((s,i)=>`${cx+r*(s.val/100)*Math.cos(angle(i))},${cy+r*(s.val/100)*Math.sin(angle(i))}`).join(" ");
                  return (
                    <svg width="130" height="130" style={{ display:"block", margin:"0 auto" }}>
                      {[0.25,0.5,0.75,1].map((pct,i)=><polygon key={i} points={gp(pct)} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>)}
                      {skills.map((_,i)=><line key={i} x1={cx} y1={cy} x2={cx+r*Math.cos(angle(i))} y2={cy+r*Math.sin(angle(i))} stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>)}
                      <polygon points={dp} fill={`${p.color}30`} stroke={p.color} strokeWidth="1.5" style={{filter:`drop-shadow(0 0 5px ${p.color})`}}/>
                      {skills.map((s,i)=>{
                        const lx=cx+(r+14)*Math.cos(angle(i)), ly=cy+(r+14)*Math.sin(angle(i));
                        const px=cx+r*(s.val/100)*Math.cos(angle(i)), py=cy+r*(s.val/100)*Math.sin(angle(i));
                        return (<g key={i}><circle cx={px} cy={py} r="2.5" fill={p.color}/><text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize="6.5" fill="rgba(255,255,255,0.82)" fontFamily="sans-serif">{s.label}</text></g>);
                      })}
                    </svg>
                  );
                })()}
              </div>
            </div>

            {/* ══ INVENTORY PREVIEW ══ */}
            <div style={{ margin:"10px 12px 14px", background:"rgba(255,255,255,0.03)", borderRadius:14, padding:"12px 14px", border:`1px solid ${p.color}22` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div style={{ fontSize:8, fontWeight:700, letterSpacing:2, color:p.color, textTransform:"uppercase" }}>🎒 Outfit Inventory</div>
                <div style={{ fontSize:8, color:"rgba(255,255,255,0.65)" }}>Level up to unlock</div>
              </div>
              <div style={{ display:"flex", gap:8, overflowX:"auto" }}>
                {[
                  { icon:"🎩", label:"Hat",    req:2, slot:"head"   },
                  { icon:"🦸", label:"Cape",   req:2, slot:"back"   },
                  { icon:"⭕", label:"Aura",   req:3, slot:"ring"   },
                  { icon:"⭐", label:"Badge",  req:2, slot:"chest"  },
                  { icon:"🥇", label:"Gold",   req:4, slot:"skin"   },
                  { icon:"🌟", label:"Elite",  req:5, slot:"full"   },
                ].map((item,i)=>{
                  const unlocked=level>=item.req;
                  return (
                    <div key={i} style={{ flexShrink:0, width:52, textAlign:"center", padding:"8px 4px", borderRadius:10,
                      background:unlocked?`${p.color}15`:"rgba(255,255,255,0.03)",
                      border:unlocked?`1px solid ${p.color}44`:"1px dashed rgba(255,255,255,0.12)",
                      position:"relative" }}>
                      {!unlocked && <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:10, background:"rgba(0,0,0,0.3)", fontSize:10 }}>🔒</div>}
                      <div style={{ fontSize:20, filter:unlocked?`drop-shadow(0 0 6px ${p.color})`:"grayscale(1) brightness(0.5)" }}>{item.icon}</div>
                      <div style={{ fontSize:9, fontWeight:700, color:unlocked?p.color:"rgba(255,255,255,0.65)", marginTop:3 }}>{item.label}</div>
                      <div style={{ fontSize:8, color:"rgba(255,255,255,0.2)", marginTop:1 }}>Lv {item.req}</div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>{/* end scrollable */}

          {/* ── ACTION BUTTONS ── */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, padding:"10px 12px", flexShrink:0, borderTop:`1px solid ${p.color}22`, background:"rgba(6,9,18,0.95)" }}>
            <button onClick={() => setScreen("process")} style={{ padding:10, borderRadius:12, border:"none", background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.80)", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>👥 Switch Tracers</button>
            <button onClick={() => setScreen("select")} style={{ padding:10, borderRadius:12, border:"none", background:`linear-gradient(135deg,${p.color},${p.colorDark})`, color:"white", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", boxShadow:`0 0 12px ${p.colorGlow}` }}>🤖 Change Bot</button>
          </div>
          <div style={{ padding:"0 12px 12px", background:"rgba(6,9,18,0.95)" }}>
            <button onClick={() => setShowTutorial(true)} style={{ width:"100%", padding:"9px", borderRadius:12, border:`1px solid ${p.color}22`, background:"rgba(255,255,255,0.02)", color:"rgba(255,255,255,0.75)", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>📖 Replay Tutorial</button>
          </div>

        </div>
      )}
      {/* ── Tutorial Overlay ── */}
      {showTutorial && persona && (
        <TutorialOverlay persona={persona} employeeProfile={employeeProfile} onClose={() => setShowTutorial(false)}/>
      )}

      {/* ── PiP Companion Prompt Toast ── */}
      {showPipPrompt && !pipActive && (
        <div style={{
          position:"fixed", bottom:90, right:16, zIndex:500,
          background:"rgba(10,10,20,0.95)", backdropFilter:"blur(16px)",
          border:`1px solid ${p?.color||"#3B82F6"}55`,
          borderRadius:20, padding:"14px 16px", maxWidth:240,
          boxShadow:`0 8px 32px rgba(0,0,0,0.6), 0 0 20px ${p?.colorGlow||"rgba(59,130,246,0.2)"}`,
          animation:"bfPopIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
          display:"flex", flexDirection:"column", gap:10,
        }}>
          <button onClick={() => setShowPipPrompt(false)} style={{
            position:"absolute", top:8, right:8, width:20, height:20,
            borderRadius:"50%", border:"none", background:"rgba(255,255,255,0.1)",
            color:"rgba(255,255,255,0.5)", fontSize:10, cursor:"pointer",
          }}>✕</button>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ flexShrink:0, animation:"bfFloat 2s ease-in-out infinite",
              filter:`drop-shadow(0 0 8px ${p?.colorGlow||"rgba(59,130,246,0.4)"})` }}>
              <BotAvatar persona={persona} level={level} size={44} outfit={outfit}
                equippedEvent={level >= 3 ? equippedEvent : null}/>
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:800, color:"white" }}>
                {p?.name} is waiting!
              </div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.60)", marginTop:2, lineHeight:1.4 }}>
                Float your companion while you work
              </div>
            </div>
          </div>
          <button onClick={() => { launchFloatingBot(); setShowPipPrompt(false); }} style={{
            width:"100%", padding:"9px", borderRadius:12, border:"none",
            background:`linear-gradient(135deg,${p?.color||"#3B82F6"},${p?.colorDark||"#1D4ED8"})`,
            color:"white", fontSize:12, fontWeight:800, cursor:"pointer",
            fontFamily:"inherit", display:"flex", alignItems:"center",
            justifyContent:"center", gap:6,
          }}>
            ⊡ Float Companion
          </button>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (<ErrorBoundary><AppInner/></ErrorBoundary>);
}
